// Staff / Admin i18n (Phase 2a: AppShell-Menü + Dashboard).
// Ansatz: Deutsche UI-Strings sind die Quelle. `labels` bildet Deutsch -> Zielsprache ab.
// Für DE ist labels leer -> tl() fällt auf den deutschen String zurück (DE bleibt 1:1).
// So bleiben der gesperrte super_admin-Nav (SUPER_ADMIN_NAV) und der Schutz-Test unverändert.

import { createElement, Fragment } from "react";

const translatorCache = new WeakMap();
export const makeTL = (t) => {
  if (!t || typeof t !== "object") return (s) => s;
  if (!translatorCache.has(t)) translatorCache.set(t, (s) => t.admin?.labels?.[s] || s);
  return translatorCache.get(t);
};

// Rich-Variante: übersetzt den GANZEN Satz als EINEN Schlüssel (keine Fragment-Grammatik),
// unterstützt **fett**-Segmente und {token}-Platzhalter und liefert React-Knoten zurück.
// So bleibt die Betonung erhalten, ohne dass Deutsch in FR/IT/EN durchsickert.
export const makeRichTL = (t) => {
  const tl = makeTL(t);
  return (s, vars = {}) => {
    let str = tl(s);
    Object.keys(vars).forEach((k) => { str = str.split(`{${k}}`).join(String(vars[k])); });
    return str.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((p, i) =>
      p.startsWith("**") && p.endsWith("**")
        ? createElement("b", { key: i }, p.slice(2, -2))
        : createElement(Fragment, { key: i }, p));
  };
};

const FR = {
  // Kundenfeedback-Seite + „Heute“-Systemtexte
  "{n} Aufgabe(n) erstellt": "{n} tâche(s) créée(s)",
  "{n} aktualisiert": "{n} mis à jour",
  "Verarbeiten Sie Feedback aus dem Freigabe-Prozess – effizient, strukturiert und nachvollziehbar.": "Traitez les retours issus du processus de validation – de manière efficace, structurée et traçable.",
  "+ {n} heute": "+ {n} aujourd’hui",
  "Suche in Beitrag, Kunde, Kommentar…": "Rechercher dans publication, client, commentaire…",
  "Typ: Alle": "Type : tous",
  "Plattform: Alle": "Plateforme : toutes",
  "Ansicht gespeichert": "Vue enregistrée",
  "Ansicht speichern": "Enregistrer la vue",
  "Ansicht auf diesem Gerät gespeichert": "Vue enregistrée sur cet appareil",
  "Ansicht konnte auf diesem Gerät nicht gespeichert werden": "La vue n’a pas pu être enregistrée sur cet appareil",
  "Ansicht kann ohne Benutzerkennung nicht gespeichert werden": "La vue ne peut pas être enregistrée sans identifiant utilisateur",
  "Speichert Reiter, Filter, Sortierung und Einträge pro Seite nur auf diesem Gerät": "Enregistre l’onglet, les filtres, le tri et le nombre d’entrées par page uniquement sur cet appareil",
  "Zeitraum:": "Période :",
  "Mein Eingang": "Ma boîte de réception",
  "{n} ausgewählt": "{n} sélectionné(s)",
  "Zuweisen an": "Assigner à",
  "Priorität setzen": "Définir la priorité",
  "Als erledigt markieren": "Marquer comme terminé",
  "Aufgabe erstellen": "Créer une tâche",
  "Sortieren:": "Trier :",
  "Neueste zuerst": "Plus récents d’abord",
  "Älteste zuerst": "Plus anciens d’abord",
  "Typ": "Type",
  "Bearbeitung": "Traitement",
  "Zuständig": "Responsable",
  "Kein Feedback für diese Auswahl.": "Aucun retour pour cette sélection.",
  "Aufgabe öffnen": "Ouvrir la tâche",
  "Beitrag öffnen": "Ouvrir la publication",
  "Antworten": "Répondre",
  "Notiz gespeichert": "Note enregistrée",
  "Wählen Sie ein Feedback aus.": "Sélectionnez un retour.",
  "ID: {ref}": "ID : {ref}",
  "ID kopiert": "ID copié",
  "Originalbeitrag (Vorschau)": "Publication d’origine (aperçu)",
  "Kundenrückmeldung": "Retour du client",
  "Notiz bearbeiten": "Modifier la note",
  "Keine interne Notiz.": "Aucune note interne.",
  "Verknüpfte Aufgabe": "Tâche liée",
  "Priorität gesetzt": "Priorité définie",
  "– Zuweisen": "– Assigner",
  "Zugewiesen": "Assigné",
  "Zuweisung entfernt": "Attribution supprimée",
  "Ändern": "Modifier",
  "Fälligkeit angepasst": "Échéance modifiée",
  "Kundenansicht öffnen": "Ouvrir la vue client",
  "Alle Aktivitäten": "Toutes les activités",
  "Änderung umsetzen: {title}": "Appliquer la modification : {title}",
  "Aufgabe {ref} erstellt": "Tâche {ref} créée",
  "Aufgabe aus Kundenfeedback": "Tâche issue du retour client",
  "Wird als verknüpfte interne Aufgabe angelegt und protokolliert.": "Créée comme tâche interne liée et journalisée.",
  "Beschreibung / Änderungswunsch": "Description / demande de modification",
  "Antwort gesendet – wartet auf Kunde": "Réponse envoyée – en attente du client",
  "Wird als Kommentar am Beitrag gespeichert.": "Enregistrée comme commentaire sur la publication.",
  "Antwort schreiben…": "Écrire une réponse…",
  "Nur für das KEWAG-Team sichtbar.": "Visible uniquement par l’équipe KEWAG.",
  "Interne Notiz…": "Note interne…",
  "Morgen": "Demain",
  "Freigabe": "Validation",
  "Wartet auf Kunde": "En attente du client",
  "Feedback eingegangen": "Retour reçu",
  "{name} zugewiesen": "Assigné à {name}",
  "Status auf {status} gesetzt": "Statut défini sur {status}",
  "Priorität auf {prio} gesetzt": "Priorité définie sur {prio}",
  "Interne Notiz aktualisiert": "Note interne mise à jour",
  "Beitrag wartet auf Kundenfreigabe": "Publication en attente de validation client",
  "Neue Antwort zu „{title}“.": "Nouvelle réponse sur « {title} ».",
  // Glocke: Systemtexte des Servers (lib/notif.js)
  "Neuer Änderungswunsch": "Nouvelle demande de modification",
  "Erinnerung: Freigabe ausstehend": "Rappel : validation en attente",
  "Neuer Beitrag wartet auf Ihre Freigabe": "Nouvelle publication en attente de votre validation",
  "Kunde hat freigegeben": "Le client a validé",
  "Beitrag ist live": "Publication en ligne",
  "Frage vom Kunden": "Question du client",
  "Neue Kundenantwort": "Nouvelle réponse du client",
  "Antwort vom KEWAG-Team": "Réponse de l’équipe KEWAG",
  "Marken-Profil: Team informiert": "Profil de marque : équipe informée",
  "Meta-Verbindung prüfen: {tenant}": "Vérifier la connexion Meta : {tenant}",
  "KI-Limit fast erreicht: {tenant}": "Limite IA presque atteinte : {tenant}",
  "Kunde wünscht eine Änderung an „{title}“: {note}": "Le client souhaite une modification de « {title} » : {note}",
  "Kunde wünscht eine Änderung an „{title}“.": "Le client souhaite une modification de « {title} ».",
  "{n} Beitrag wartet auf Ihre Freigabe.": "{n} publication attend votre validation.",
  "{n} Beiträge wartet auf Ihre Freigabe.": "{n} publications attendent votre validation.",
  "„{title}“ liegt für Sie zur Freigabe bereit.": "« {title} » est prête pour votre validation.",
  "„{title}“ wurde vom Kunden freigegeben.": "« {title} » a été validée par le client.",
  "„{title}“ wurde veröffentlicht.": "« {title} » a été publiée.",
  "Neue Frage/Kommentar zu „{title}“.": "Nouvelle question ou nouveau commentaire sur « {title} ».",
  "Bitte den Beitrag „{title}“ prüfen und freigeben.": "Veuillez vérifier et valider la publication « {title} ».",
  "{name} hat das Team über das aktuelle Marken-Profil informiert.": "{name} a informé l’équipe du profil de marque actuel.",
  "{tenant} hat {runs} von {limit} KI-Läufen in diesem Monat verbraucht ({pct}%).": "{tenant} a utilisé {runs} des {limit} exécutions IA ce mois-ci ({pct} %).",
  "Meta-Verbindung von {tenant} benötigt Handlung: {summary}. Bitte im Integrationen-Bereich neu verbinden.": "La connexion Meta de {tenant} nécessite une action : {summary}. Veuillez la reconnecter dans la section Intégrations.",
  "Token ungültig – bitte neu verbinden.": "Jeton invalide – veuillez reconnecter.",
  "Token ungültig": "Jeton invalide",
  "Fehlende Berechtigungen: {scopes}": "Autorisations manquantes : {scopes}",
  "Token läuft in {d} Tag(en) ab – bitte neu verbinden.": "Le jeton expire dans {d} jour(s) – veuillez reconnecter.",
  "Geschütztes Konto": "Compte protégé",
  "Alte Konfigurationsnamen": "Anciens noms de configuration",
  "Schlüsselnamen sind Konfiguration, keine zusätzlichen Admin-Konten. Ein fehlender Startschlüssel ändert kein bestehendes Konto-Passwort.": "Les noms de clés sont de la configuration, pas des comptes administrateur supplémentaires. Une clé de démarrage manquante ne modifie pas le mot de passe d’un compte existant.",
  // Owner/team configuration messages, including parameterized API warnings.
  "SUPER_ADMIN_EMAIL und SUPERADMIN_EMAIL verweisen auf verschiedene Konten": "SUPER_ADMIN_EMAIL et SUPERADMIN_EMAIL désignent des comptes différents",
  "Owner-Passwort unter mehreren Schlüsselnamen mit verschiedenen Werten: {keys}": "Mot de passe du propriétaire défini avec des valeurs différentes sous plusieurs clés : {keys}",
  "Konto unter der Owner-E-Mail hat nicht die Rolle super_admin (keine automatische Hochstufung)": "Le compte associé à l’e-mail du propriétaire n’a pas le rôle super_admin (aucune promotion automatique)",
  "{count} weitere(s) Super-Admin-/Inhaber-Konto(en) vorhanden": "{count} autre(s) compte(s) Super Admin/propriétaire présent(s)",
  "Teamkonto ist zugleich als Owner konfiguriert": "Le compte d’équipe est également configuré comme propriétaire",
  "Teamkonto hat Owner-Rechte": "Le compte d’équipe dispose des droits du propriétaire",
  "Rolle {role} statt {expected}": "Rôle {role} au lieu de {expected}",
  "Altkonto ADMIN_EMAIL hat Owner-Rechte (zweite Owner-Identität)": "L’ancien compte ADMIN_EMAIL dispose des droits du propriétaire (deuxième identité propriétaire)",
  "Altname {key} aktiv - kanonisch: {canonical}": "Ancien nom {key} actif - nom canonique : {canonical}",
  "Owner-E-Mail stammt aus dem Altnamen ADMIN_EMAIL - kanonisch: SUPER_ADMIN_EMAIL": "L’e-mail du propriétaire provient de l’ancien nom ADMIN_EMAIL - nom canonique : SUPER_ADMIN_EMAIL",
  "{key} gesetzt - wirkungslos (beim Start gibt es keinen Passwort-Reset mehr)": "{key} défini - sans effet (le démarrage ne réinitialise plus les mots de passe)",
  "Nur der Super Admin darf den Zugangsstatus sehen.": "Seul le Super Admin peut consulter le statut des accès.",
  // --- Integrationen (Tracking #19) ---
  "Untermenü einklappen": "Replier le sous-menu",
  "Untermenü ausklappen": "Déplier le sous-menu",
  "Verbindung unterbrochen — Token erneuern / erneut verbinden.": "Connexion interrompue — renouveler le jeton / reconnecter.",
  "Handlungsbedarf — Provider noch nicht verfügbar.": "Action requise — fournisseur pas encore disponible.",
  "Provider noch nicht verfügbar.": "Fournisseur pas encore disponible.",
  "Verbindung prüfen (Health-Check).": "Vérifier la connexion (health-check).",
  "Adapter vorbereitet — Provider-Credentials/Freigabe erforderlich.": "Adaptateur préparé — identifiants/validation du fournisseur requis.",
  "So hinterlegen Sie die Zugangsdaten für {name}.": "Voici comment enregistrer les identifiants pour {name}.",
  "Verbindung gültig": "Connexion valide",
  "{v} kopiert": "{v} copié",
  "{name} verbinden": "Connecter {name}",
  "Sofort testen (ohne Speichern)": "Tester maintenant (sans enregistrer)",
  "Der Schlüssel wird nur live geprüft – nicht in der App gespeichert.": "La clé est uniquement vérifiée en direct – elle n’est pas enregistrée dans l’application.",
  "KEWAG-Aufgabe „Zugangsdaten aktivieren\" wurde erstellt.": "La tâche KEWAG « Activer les identifiants » a été créée.",
  "Dauerhaft aktivieren": "Activer durablement",
  "Nach erfolgreichem Test: diese Werte als Emergent-Secret hinterlegen, dann 1× Backend-Neustart – danach ist die Integration verbunden.": "Après un test réussi : enregistrer ces valeurs comme secret Emergent, puis redémarrer le backend une fois – l’intégration est alors connectée.",
  "Konfigurationsseite:": "Page de configuration :",
  "Zur Konfiguration": "Vers la configuration",
  "Konfigurationsseite folgt": "Page de configuration à venir",
  "Konfiguration folgt": "Configuration à venir",
  "Zugangsdaten": "Identifiants",
  "Alle System-Zugangsdaten sind hinterlegt.": "Tous les identifiants système sont enregistrés.",
  "Offene System-Integrationen — direkt zur passenden Konfiguration.": "Intégrations système ouvertes — directement vers la configuration adaptée.",
  "Hinterlegen": "Enregistrer",
  "Gateway-Übersicht wird geladen…": "Chargement de l’aperçu de la passerelle…",
  "KEWAG Social Gateway — Ablauf": "KEWAG Social Gateway — déroulement",
  "Beitrag → Ampel-Gate → Queue → Dry-Run. Nur grün-freigegebene Beiträge dürfen intern in die Queue.": "Publication → Ampel-Gate → file d’attente → simulation. Seules les publications validées en vert peuvent entrer en interne dans la file.",
  "Externe Veröffentlichung deaktiviert": "Publication externe désactivée",
  "Beiträge im Umlauf": "Publications en circulation",
  "freigabe-geprüft": "validation vérifiée",
  "Ampel-Gate": "Ampel-Gate",
  "grün → zugelassen": "vert → admis",
  "Queue (Dry-Run)": "File d’attente (simulation)",
  "{q} wartend · {p} aktiv": "{q} en attente · {p} actives",
  "Simuliert veröffentlicht": "Publication simulée",
  "kein externer Versand": "aucun envoi externe",
  "Vom Ampel-Gate blockiert ·": "Bloquées par l’Ampel-Gate ·",
  "Plattformen (ehrlicher Status)": "Plateformes (statut réel)",
  "Warum blockiert? — Live-Feed": "Pourquoi bloqué ? — flux en direct",
  "Zuletzt geprüft:": "Dernière vérification :",
  "Noch keine Blockierungen protokolliert. Sobald ein nicht-grüner Beitrag eingereiht wird, erscheint der Grund hier.": "Aucun blocage enregistré pour l’instant. Dès qu’une publication non verte est mise en file, le motif apparaît ici.",
  "{a} blockiert": "{a} bloqué",
  "Ampel-Gate: Veröffentlichung gesperrt.": "Ampel-Gate : publication bloquée.",
  "Hilfe & Dokumentation": "Aide & documentation",
  "Alle Dokumente öffnen": "Ouvrir tous les documents",
  "{n}% aller Kunden": "{n} % de tous les clients",
  "Verbundene Kunden": "Clients connectés",
  "Kunden mit Handlungsbedarf": "Clients nécessitant une action",
  "Ablaufende Berechtigungen": "Autorisations arrivant à échéance",
  "in den nächsten 30 Tagen": "dans les 30 prochains jours",
  "Nächste: in {n} Tagen": "Prochaine : dans {n} jours",
  "Kritische Probleme": "Problèmes critiques",
  "Sofortige Aufmerksamkeit": "Attention immédiate",
  "seit letzter Woche": "depuis la semaine dernière",
  "seit {n} Tag": "depuis {n} jour",
  "seit {n} Tagen": "depuis {n} jours",
  "Keine Vergleichsdaten": "Aucune donnée de comparaison",
  "Verlauf konnte nicht geladen werden": "Impossible de charger l’historique",
  "KPI-Verlauf": "Historique des KPI",
  "Echte Tages-Snapshots über alle Mandanten": "Instantanés quotidiens réels sur tous les mandants",
  "Noch nicht genug Verlaufsdaten": "Pas encore assez de données historiques",
  "Bisher 1 Tages-Snapshot erfasst.": "1 instantané quotidien enregistré jusqu’à présent.",
  "Bisher keine Snapshots erfasst.": "Aucun instantané enregistré jusqu’à présent.",
  "Der tägliche Snapshot baut die Kurven über die nächsten Tage automatisch auf.": "L’instantané quotidien construit automatiquement les courbes au cours des prochains jours.",
  "{n} Kunde": "{n} client",
  "{n} Kunden": "{n} clients",
  "Nachfass gesendet: {c} Kunde(n) an {r} Empfänger": "Relance envoyée : {c} client(s) à {r} destinataire(s)",
  "Nicht gesendet": "Non envoyé",
  "Versand fehlgeschlagen": "Échec de l’envoi",
  "Export fehlgeschlagen": "Échec de l’export",
  "Onboarding-Nachfass": "Relance onboarding",
  "Wöchentliche E-Mail ans KEWAG-Team mit allen „fast fertig (2/3)\"-Kunden.": "E-mail hebdomadaire à l’équipe KEWAG avec tous les clients « presque terminés (2/3) ».",
  "Jeden": "Chaque",
  "um": "à",
  "Uhr (UTC)": "h (UTC)",
  "Aktuell fast fertig (2/3)": "Actuellement presque terminés (2/3)",
  "Zuletzt gesendet": "Dernier envoi",
  "Noch nie": "Jamais",
  "3/3 · Fertig diese Woche": "3/3 · Terminés cette semaine",
  "E-Mail-Dienst (Brevo) noch nicht verbunden – der Versand startet automatisch, sobald Brevo hinterlegt ist.": "Le service e-mail (Brevo) n’est pas encore connecté – l’envoi démarre automatiquement dès que Brevo est configuré.",
  "Vollständig onboarded / Woche (8 Wochen)": "Entièrement intégrés / semaine (8 semaines)",
  "Noch keine abgeschlossenen Onboardings in den letzten 8 Wochen.": "Aucun onboarding terminé au cours des 8 dernières semaines.",
  "Jetzt senden (Test)": "Envoyer maintenant (test)",
  "Report exportieren:": "Exporter le rapport :",
  "Unterstützte Plattformen in allen Paketen": "Plateformes prises en charge dans tous les forfaits",
  "Plattformen": "Plateformes",
  "Grundsätzlich unterstützt und in der Paketlogik verfügbar — nicht automatisch verbunden.": "Prises en charge par principe et disponibles dans la logique des forfaits — pas connectées automatiquement.",
  "Standard": "Standard",
  "In Vorbereitung": "En préparation",
  "Prioritäten / Handlungsbedarf": "Priorités / actions requises",
  "Nach Schwere sortiert — kritisch zuerst.": "Triés par gravité — critiques en premier.",
  "Keine offenen Punkte. Alles im grünen Bereich.": "Aucun point ouvert. Tout est au vert.",
  "Alle offenen Punkte anzeigen": "Afficher tous les points ouverts",
  "{p} einrichten": "Configurer {p}",
  "Einheitlicher Verbindungsablauf · nur für den ausgewählten Kunden": "Processus de connexion uniforme · uniquement pour le client sélectionné",
  "Buchung & Verbindung werden ausschliesslich diesem Mandanten zugeordnet.": "La réservation et la connexion sont attribuées exclusivement à ce mandant.",
  "**In Vorbereitung.** Der Adapter für {p} ist fertig gebaut. Für die echte Verbindung fehlen noch die Zugangsdaten und die offizielle Plattform-Freigabe (App Review). Wir zeigen bewusst keinen „Verbunden“-Status, solange nichts echt geprüft wurde.": "**En préparation.** L’adaptateur pour {p} est prêt. Pour la vraie connexion, il manque encore les identifiants et la validation officielle de la plateforme (App Review). Nous n’affichons volontairement aucun statut « Connecté » tant que rien n’a été réellement vérifié.",
  "**In Vorbereitung.** Der Adapter für {p} ist fertig gebaut. Für die echte Verbindung fehlen noch die Zugangsdaten. Wir zeigen bewusst keinen „Verbunden“-Status, solange nichts echt geprüft wurde.": "**En préparation.** L’adaptateur pour {p} est prêt. Pour la vraie connexion, il manque encore les identifiants. Nous n’affichons volontairement aucun statut « Connecté » tant que rien n’a été réellement vérifié.",
  "Kanal buchen": "Réserver le canal",
  "Dieser Kanal ist im Paket des Kunden enthalten.": "Ce canal est inclus dans le forfait du client.",
  "Dieser Kanal ist als Zusatzkanal buchbar.": "Ce canal peut être réservé comme canal supplémentaire.",
  "Gebucht": "Réservé",
  "Buchung entfernen": "Supprimer la réservation",
  "Kanal jetzt buchen": "Réserver le canal maintenant",
  "Buchung nur durch Super Admin möglich.": "Réservation uniquement par le Super Admin.",
  "Was wird benötigt?": "Que faut-il ?",
  "Zugangsdaten aus dem Entwicklerportal der Plattform.": "Identifiants issus du portail développeur de la plateforme.",
  "So bekommen Sie die Zugangsdaten": "Comment obtenir les identifiants",
  "ausblenden": "masquer",
  "anzeigen": "afficher",
  "{p} öffnen": "Ouvrir {p}",
  "Sobald die Plattform freigeschaltet ist, wird dieser Schritt aktiv.": "Cette étape devient active dès que la plateforme est activée.",
  "Verbinden — bald verfügbar": "Connecter — bientôt disponible",
  "Sobald die Zugangsdaten hinterlegt und die Plattform freigeschaltet ist, wird der Verbinden-Schritt aktiv — genau wie bei Meta.": "Dès que les identifiants sont enregistrés et la plateforme activée, l’étape de connexion devient active — comme pour Meta.",
  "Provider-Status": "Statut des fournisseurs",
  "Keine Daten verfügbar": "Aucune donnée disponible",
  "Alle Provider anzeigen": "Afficher tous les fournisseurs",
  "Nur Super Admin": "Super Admin uniquement",
  "Verbindungseinladung senden": "Envoyer une invitation de connexion",
  "Integrations-Management": "Gestion des intégrations",
  "Globale System-Integrationen": "Intégrations système globales",
  "Letzter Test:": "Dernier test :",
  "Offene Punkte:": "Points ouverts :",
  "Verwaltung nur durch Super Admin": "Gestion uniquement par le Super Admin",
  "Noch nicht verfügbar": "Pas encore disponible",
  "Testen": "Tester",
  "Zuletzt geprüft": "Dernière vérification",
  "Verbunden seit": "Connecté depuis",
  "Globale Verwaltung nur durch Super Admin — hier nur Statusansicht.": "Gestion globale uniquement par le Super Admin — ici, affichage du statut seulement.",
  "Wird geladen…": "Chargement…",
  "Noch keine Ereignisse für diese Integration.": "Aucun événement pour cette intégration pour l’instant.",
  "grün": "vert",
  "rot": "rouge",
  "Prüfpunkte": "Points de contrôle",
  "Cutover-Protokoll als CSV exportiert.": "Journal de cutover exporté en CSV.",
  "Export fehlgeschlagen.": "Échec de l’export.",
  "Cutover-Bereitschaft geprüft.": "Préparation au cutover vérifiée.",
  "Prüfung fehlgeschlagen.": "Échec de la vérification.",
  "Stabilitäts-Schwelle: {n} Tage.": "Seuil de stabilité : {n} jours.",
  "Speichern fehlgeschlagen.": "Échec de l’enregistrement.",
  "Cutover durchgeführt — zentrale Datenquelle aktiv.": "Cutover effectué — source de données centrale active.",
  "Cutover fehlgeschlagen.": "Échec du cutover.",
  "Verifikation fehlgeschlagen.": "Échec de la vérification.",
  "Cutover zurückgenommen — Legacy-Fallback wieder aktiv.": "Cutover annulé — le repli legacy est de nouveau actif.",
  "Rollback fehlgeschlagen.": "Échec du rollback.",
  "Diagnose wird geladen…": "Chargement du diagnostic…",
  "System-Diagnose · Cutover-Bereitschaft": "Diagnostic système · préparation au cutover",
  "Live-Zähler der genutzten Statusquelle · Ziel: Legacy-Fallback dauerhaft 0 → sicherer Umstieg auf die zentrale Datenquelle.": "Compteurs en direct de la source de statut utilisée · objectif : repli legacy durablement à 0 → passage sûr à la source de données centrale.",
  "Cutover-bereit — kein Legacy-Fallback aktiv.": "Prêt pour le cutover — aucun repli legacy actif.",
  "Noch nicht cutover-bereit.": "Pas encore prêt pour le cutover.",
  "Alle Provider werden aus der zentralen Zeile bedient. Beobachtung fortsetzen, dann Umstieg freigeben.": "Tous les fournisseurs sont servis par la ligne centrale. Poursuivre l’observation, puis valider le passage.",
  "Legacy-Fallback wurde {n}× verwendet.": "Le repli legacy a été utilisé {n}×.",
  "Nicht alle Provider haben eine gültige zentrale Zeile.": "Tous les fournisseurs n’ont pas de ligne centrale valide.",
  "Default-Auflösung {n}× — zentrale Zeilen prüfen.": "Résolution par défaut {n}× — vérifier les lignes centrales.",
  "Stabilitäts-Beobachtung": "Observation de stabilité",
  "Cutover-Aufgabe erstellt": "Tâche de cutover créée",
  "Bereit": "Prêt",
  "Stabil seit **{d}** Tagen · Ziel **{s}** Tage.": "Stable depuis **{d}** jours · objectif **{s}** jours.",
  "Letzter Fallback:": "Dernier repli :",
  "Kein Fallback registriert.": "Aucun repli enregistré.",
  "Schwelle (Tage):": "Seuil (jours) :",
  "Jetzt prüfen": "Vérifier maintenant",
  "Datenquellen-Modus": "Mode de source de données",
  "Aktiv: **zentrale Datenquelle** — Legacy-Fallback deaktiviert": "Actif : **source de données centrale** — repli legacy désactivé",
  "Aktiv: **Spiegelung + Legacy-Fallback** — zentrale Zeilen führend, Settings-Blob als Sicherheitsnetz.": "Actif : **mise en miroir + repli legacy** — lignes centrales prioritaires, blob de paramètres comme filet de sécurité.",
  "Rückgängig machen": "Annuler",
  "Blockiert: nicht alle Provider haben eine zentrale Zeile": "Bloqué : tous les fournisseurs n’ont pas de ligne centrale",
  "Cutover jetzt durchführen": "Effectuer le cutover maintenant",
  "Provider": "Fournisseur",
  "Konfiguriert": "Configuré",
  "Zentrale Zeile": "Ligne centrale",
  "Aufgelöste Quelle": "Source résolue",
  "Ja": "Oui",
  "Nein": "Non",
  "Cutover-Protokoll": "Journal de cutover",
  "CSV-Export": "Export CSV",
  "Noch keine Cutover-Ereignisse protokolliert.": "Aucun événement de cutover enregistré pour l’instant.",
  "Cutover zurücknehmen?": "Annuler le cutover ?",
  "Der Legacy-Settings-Fallback wird wieder aktiviert und die Stabilitäts-Beobachtung neu gestartet. Es gehen keine Daten verloren (der Settings-Blob wurde nie gelöscht).": "Le repli legacy sur les paramètres est réactivé et l’observation de stabilité redémarre. Aucune donnée n’est perdue (le blob de paramètres n’a jamais été supprimé).",
  "Cutover jetzt durchführen?": "Effectuer le cutover maintenant ?",
  "Ab jetzt ist **integration_connections** die einzige persistente Statusquelle. Der Legacy-Settings-Fallback wird deaktiviert (aber nicht gelöscht — jederzeit per „Rückgängig machen\" umkehrbar).": "Désormais, **integration_connections** est la seule source de statut persistante. Le repli legacy sur les paramètres est désactivé (mais pas supprimé — réversible à tout moment via « Annuler »).",
  "Verifikations-Kette": "Chaîne de vérification",
  "Recheck & Konsistenzprüfung läuft…": "Revérification et contrôle de cohérence en cours…",
  "Blockiert: Nicht alle Provider haben eine gültige zentrale Zeile.": "Bloqué : tous les fournisseurs n’ont pas de ligne centrale valide.",
  "Verifikation nicht grün. Umstellung nur als bewusster Super-Admin-Override (Force).": "Vérification non verte. Basculement uniquement comme dérogation délibérée du Super Admin (Force).",
  "Verifikation grün, aber Beobachtungsphase noch offen (stabil {d} / {s} Tage). Bewusst früher umstellen (Force).": "Vérification verte, mais phase d’observation encore ouverte (stable {d} / {s} jours). Basculer délibérément plus tôt (Force).",
  "Cutover durchführen": "Effectuer le cutover",
  "Trotzdem durchführen (Force)": "Effectuer quand même (Force)",
  "System-Health": "Santé du système",
  "Alle Systeme online": "Tous les systèmes en ligne",
  "Systeme eingeschränkt": "Systèmes limités",
  "Details konnten nicht geladen werden": "Impossible de charger les détails",
  "Kanal gebucht": "Canal réservé",
  "Buchung entfernt": "Réservation supprimée",
  "Änderung fehlgeschlagen": "Échec de la modification",
  "Meta nicht konfiguriert": "Meta non configuré",
  "Buchung, Verbindungsstatus, Tests und Audit-Verlauf je Plattform.": "Réservation, statut de connexion, tests et historique d’audit par plateforme.",
  "Kundennr.:": "N° client :",
  "Paket:": "Forfait :",
  "Verantwortlich:": "Responsable :",
  "Nächste beste Aktion": "Prochaine meilleure action",
  "Provider folgt (Phase 3)": "Fournisseur à venir (phase 3)",
  "Im Paket": "Dans le forfait",
  "Nicht im Paket": "Pas dans le forfait",
  "Verbinden": "Connecter",
  "Audit-Verlauf": "Historique d’audit",
  "Noch keine Integrationsänderungen.": "Aucune modification d’intégration pour l’instant.",
  "Kundensicht": "Vue client",
  "an {e}": "à {e}",
  "{n} / {t} Plattformen": "{n} / {t} plateformes",
  "Testbeitrag veröffentlicht": "Publication test publiée",
  "Veröffentlichung fehlgeschlagen": "Échec de la publication",
  "Testbeitrag gelöscht": "Publication test supprimée",
  "Löschen fehlgeschlagen": "Échec de la suppression",
  "Nachweis per E-Mail gesendet": "Preuve envoyée par e-mail",
  "Keine verbundenen Plattformen zum Prüfen.": "Aucune plateforme connectée à vérifier.",
  "Kundennummer kopiert": "Numéro client copié",
  "Kein Kunde ausgewählt": "Aucun client sélectionné",
  "Wählen Sie in der Kundenübersicht oder in „Ihre nächsten Aktionen\" einen Kunden.": "Sélectionnez un client dans l’aperçu des clients ou dans « Vos prochaines actions ».",
  "Kundennr.": "N° client",
  "{p} Paket": "Forfait {p}",
  "Nicht zugewiesen": "Non attribué",
  "Verbindungsübersicht": "Aperçu des connexions",
  "Nicht gebucht": "Non réservé",
  "Im Paket enthalten": "Inclus dans le forfait",
  "Als Zusatzkanal buchbar": "Réservable comme canal supplémentaire",
  "Beitrag ansehen": "Voir la publication",
  "Erfolgsnachweis (PDF)": "Preuve de réussite (PDF)",
  "Senden…": "Envoi…",
  "Nachweis per E-Mail senden": "Envoyer la preuve par e-mail",
  "Löschen…": "Suppression…",
  "Testbeitrag löschen": "Supprimer la publication test",
  "Ein öffentlich sichtbarer Testbeitrag wird auf {p} veröffentlicht.": "Une publication test visible publiquement sera publiée sur {p}.",
  "Posten…": "Publication…",
  "Jetzt posten": "Publier maintenant",
  "Test-Veröffentlichung": "Publication test",
  "Noch keine Integrationsaktivitäten.": "Aucune activité d’intégration pour l’instant.",
  "Keine internen Notizen hinterlegt.": "Aucune note interne enregistrée.",
  "Plattform verbinden": "Connecter la plateforme",
  "Verbindung prüfen": "Vérifier la connexion",
  "Onboarding abgeschlossen – Details öffnen": "Onboarding terminé – ouvrir les détails",
  "Zur nächsten offenen Onboarding-Aktion springen": "Aller à la prochaine action d’onboarding ouverte",
  "Kundenübersicht nach Plattformstatus": "Aperçu des clients par statut de plateforme",
  "Kunden mit abgeschlossenem Test, aber noch ohne versendeten Nachweis": "Clients avec test terminé, mais sans preuve encore envoyée",
  "Fast fertig (2/3)": "Presque terminé (2/3)",
  "Gesamtstatus": "Statut global",
  "Letzte Prüfung": "Dernière vérification",
  "Kd.": "N°",
  "Fehler:": "Erreur :",
  "Detail öffnen": "Ouvrir le détail",
  "Buchung bearbeiten": "Modifier la réservation",
  "Letzte Prüfungen anzeigen": "Afficher les dernières vérifications",
  "Handlungsbedarf: {n} offen": "Action requise : {n} ouvert(s)",
  "Noch keine Kanäle gebucht": "Aucun canal réservé pour l’instant",
  "Verbindung abschließen": "Finaliser la connexion",
  "Health-Check ausstehend": "Health-check en attente",
  "Alles funktionsfähig": "Tout est fonctionnel",
  "Funktionsf.": "Fonct.",
  "Details öffnen": "Ouvrir les détails",
  "{s}–{e} von {n} Kunden": "{s}–{e} sur {n} clients",
  "{n} pro Seite": "{n} par page",
  "Meta-Daten konnten nicht geladen werden": "Impossible de charger les données Meta",
  "Einladung erstellt und per E-Mail an {e} gesendet.": "Invitation créée et envoyée par e-mail à {e}.",
  "Erinnerung an {e} gesendet.": "Rappel envoyé à {e}.",
  "Meta-Verbindung & Kundeneinladung": "Connexion Meta & invitation client",
  "Einladungslink für {n} — der Kunde verbindet sein eigenes Meta-Konto (ohne Passwort-Weitergabe).": "Lien d’invitation pour {n} — le client connecte son propre compte Meta (sans partage de mot de passe).",
  "Einladungslink für diesen Mandanten — der Kunde verbindet sein eigenes Meta-Konto (ohne Passwort-Weitergabe).": "Lien d’invitation pour ce mandant — le client connecte son propre compte Meta (sans partage de mot de passe).",
  "Demo-Mandant: keine echte Meta-Verbindung möglich.": "Mandant de démonstration : aucune vraie connexion Meta possible.",
  "E-Mail-Dienst (Brevo) ist nicht verbunden — der Versand wird übersprungen. Der Link wird trotzdem erstellt.": "Le service e-mail (Brevo) n’est pas connecté — l’envoi est ignoré. Le lien est tout de même créé.",
  "Einladungs-Historie": "Historique des invitations",
  "{n} offen": "{n} ouvert(s)",
  "Noch keine Einladungen. Erzeugen Sie oben einen Link.": "Aucune invitation pour l’instant. Créez un lien ci-dessus.",
  "{n}× erinnert": "{n}× relancé",
  "Verschlüsselte, einmalig gültige Einladungen · nur Super Admin": "Invitations chiffrées à usage unique · Super Admin uniquement",
  "Token-Übersicht": "Aperçu des tokens",
  "Alle Tokens verwalten": "Gérer tous les tokens",
  "Heute, {t}": "Aujourd’hui, {t}",
  "Gestern, {t}": "Hier, {t}",
  "So verbinden Sie Instagram/Facebook – ca. 5 Minuten.": "Comment connecter Instagram/Facebook – env. 5 minutes.",
  "Meta for Developers öffnen": "Ouvrir Meta for Developers",
  "Meta-App erstellen": "Créer une app Meta",
  "developers.facebook.com › Meine Apps › App erstellen › Typ Business.": "developers.facebook.com › Mes apps › Créer une app › Type Business.",
  "Produkte aktivieren": "Activer les produits",
  "In der App die Produkte Facebook Login und Instagram Graph API hinzufügen.": "Ajouter dans l’app les produits Facebook Login et Instagram Graph API.",
  "App-ID & App-Geheimnis kopieren": "Copier l’ID d’app et le secret d’app",
  "Einstellungen › Allgemein: App-ID und App-Geheimnis (App Secret) kopieren.": "Paramètres › Général : copier l’ID d’app et le secret d’app (App Secret).",
  "App-ID": "ID d’app",
  "App-Geheimnis (App Secret)": "Secret d’app (App Secret)",
  "Integrationen-Seite › Meta verbinden": "Page Intégrations › Connecter Meta",
  "So aktivieren Sie Zahlungen (Stripe/TWINT) – ca. 5 Minuten.": "Comment activer les paiements (Stripe/TWINT) – env. 5 minutes.",
  "Stripe API-Schlüssel öffnen": "Ouvrir les clés API Stripe",
  "Stripe-Dashboard öffnen": "Ouvrir le tableau de bord Stripe",
  "dashboard.stripe.com › Entwickler › API-Schlüssel. Oben rechts den Testmodus aktiviert lassen.": "dashboard.stripe.com › Développeurs › Clés API. Laisser le mode test activé en haut à droite.",
  "Geheimen Schlüssel kopieren": "Copier la clé secrète",
  "Den geheimen Schlüssel (beginnt mit sk_test_…) kopieren.": "Copier la clé secrète (commence par sk_test_…).",
  "Geheimer Schlüssel": "Clé secrète",
  "Abrechnungseinstellungen › Stripe": "Paramètres de facturation › Stripe",
  "So aktivieren Sie den E-Mail-Versand (Brevo) – ca. 3 Minuten.": "Comment activer l’envoi d’e-mails (Brevo) – env. 3 minutes.",
  "Brevo API-Schlüssel öffnen": "Ouvrir les clés API Brevo",
  "Brevo-Konto öffnen": "Ouvrir le compte Brevo",
  "app.brevo.com › SMTP & API › API-Schlüssel.": "app.brevo.com › SMTP & API › Clés API.",
  "API-Schlüssel erzeugen": "Générer une clé API",
  "Neuen API-Schlüssel generieren (v3) und den Schlüssel (xkeysib-…) kopieren.": "Générer une nouvelle clé API (v3) et copier la clé (xkeysib-…).",
  "Absender verifizieren": "Vérifier l’expéditeur",
  "In Brevo die Absender-Adresse bzw. Domain verifizieren (BREVO_SENDER_EMAIL).": "Dans Brevo, vérifier l’adresse ou le domaine de l’expéditeur (BREVO_SENDER_EMAIL).",
  "Brevo API-Key": "Clé API Brevo",
  "Allgemeine Einstellungen › E-Mail (Brevo)": "Paramètres généraux › E-mail (Brevo)",
  "So verbinden Sie Ihre WordPress-Website – ca. 3 Minuten.": "Comment connecter votre site WordPress – env. 3 minutes.",
  "API-Key erzeugen": "Générer une clé API",
  "Unten Zur Konfiguration öffnen › WordPress-Verbindungen › API-Key erzeugen und kopieren.": "Ci-dessous, ouvrir Vers la configuration › Connexions WordPress › générer et copier la clé API.",
  "Im WordPress-Connector eintragen": "Saisir dans le connecteur WordPress",
  "Auf Ihrer WordPress-Website im SocialCheck-Connector den API-Key (Header X-SocialCheck-Key) hinterlegen.": "Sur votre site WordPress, enregistrer la clé API (en-tête X-SocialCheck-Key) dans le connecteur SocialCheck.",
  "Zurück in SocialCheck den Test ausführen – bei Erfolg wird der Status Verbunden.": "De retour dans SocialCheck, lancer le test – en cas de succès, le statut devient Connecté.",
  "Zugangsdaten bereithalten": "Préparer les identifiants",
  "Halten Sie die erforderlichen Schlüssel/IDs des Anbieters bereit.": "Préparez les clés/ID requis du fournisseur.",
  "Integrations-Guide": "Guide des intégrations",
  "Schritt-für-Schritt Einrichtung": "Configuration pas à pas",
  "API & Zugangsrechte": "API & droits d’accès",
  "Übersicht & Verwaltung": "Aperçu & gestion",
  "Troubleshooting": "Dépannage",
  "Hilfe bei typischen Fehlern": "Aide pour les erreurs courantes",
  "Best Practices": "Bonnes pratiques",
  "Empfehlungen & Leitfäden": "Recommandations & guides",
  "7 Tage": "7 jours",
  "90 Tage": "90 jours",
  "Montag": "lundi",
  "Dienstag": "mardi",
  "Mittwoch": "mercredi",
  "Donnerstag": "jeudi",
  "Freitag": "vendredi",
  "Samstag": "samedi",
  "Sonntag": "dimanche",
  "Client Key & Client Secret aus dem TikTok-Entwicklerportal.": "Client Key & Client Secret depuis le portail développeur TikTok.",
  "Auf developers.tiktok.com anmelden und eine App registrieren.": "Se connecter sur developers.tiktok.com et enregistrer une app.",
  "Produkt „Content Posting API“ (Login Kit) hinzufügen und zur Prüfung einreichen.": "Ajouter le produit « Content Posting API » (Login Kit) et le soumettre à examen.",
  "Client Key + Client Secret kopieren und die Redirect-URI eintragen.": "Copier Client Key + Client Secret et saisir l’URI de redirection.",
  "Client ID & Client Secret aus dem LinkedIn-Entwicklerportal.": "Client ID & Client Secret depuis le portail développeur LinkedIn.",
  "Auf linkedin.com/developers eine App erstellen (mit LinkedIn-Firmenseite verknüpfen).": "Créer une app sur linkedin.com/developers (liée à la page entreprise LinkedIn).",
  "Produkte „Share on LinkedIn“ / „Sign In with LinkedIn“ anfordern.": "Demander les produits « Share on LinkedIn » / « Sign In with LinkedIn ».",
  "Client ID + Client Secret kopieren und die Redirect-URI eintragen.": "Copier Client ID + Client Secret et saisir l’URI de redirection.",
  "App ID & App Secret aus dem Pinterest-Entwicklerportal.": "App ID & App Secret depuis le portail développeur Pinterest.",
  "Auf developers.pinterest.com eine App anlegen und Standard-Zugriff beantragen.": "Créer une app sur developers.pinterest.com et demander l’accès standard.",
  "Benötigte Scopes (boards:read, pins:write) auswählen.": "Sélectionner les scopes requis (boards:read, pins:write).",
  "App ID + App Secret kopieren und die Redirect-URI eintragen.": "Copier App ID + App Secret et saisir l’URI de redirection.",
  "OAuth Client ID & Secret aus der Google Cloud Console (YouTube Data API).": "OAuth Client ID & Secret depuis la Google Cloud Console (YouTube Data API).",
  "In der Google Cloud Console ein Projekt anlegen und die „YouTube Data API v3“ aktivieren.": "Dans la Google Cloud Console, créer un projet et activer la « YouTube Data API v3 ».",
  "OAuth-Zustimmungsbildschirm konfigurieren und zur Verifizierung einreichen.": "Configurer l’écran de consentement OAuth et le soumettre à vérification.",
  "OAuth-Client-ID + Secret erstellen und die Redirect-URI eintragen.": "Créer un OAuth Client ID + Secret et saisir l’URI de redirection.",
  "OAuth 2.0 Client ID & Secret aus dem X-Entwicklerportal (kostenpflichtig).": "OAuth 2.0 Client ID & Secret depuis le portail développeur X (payant).",
  "Auf developer.x.com ein Projekt + App im Developer-Portal erstellen (API-Zugang ist kostenpflichtig).": "Sur developer.x.com, créer un projet + une app dans le Developer Portal (l’accès API est payant).",
  "OAuth 2.0 aktivieren und die benötigten Scopes (tweet.write, users.read) setzen.": "Activer OAuth 2.0 et définir les scopes requis (tweet.write, users.read).",
  "OAuth Client ID & Secret aus der Google Cloud Console (Business Profile API).": "OAuth Client ID & Secret depuis la Google Cloud Console (Business Profile API).",
  "In der Google Cloud Console die „Business Profile API“ aktivieren und Zugriff beantragen.": "Dans la Google Cloud Console, activer la « Business Profile API » et demander l’accès.",
  "OAuth-Zustimmungsbildschirm konfigurieren und einreichen.": "Configurer l’écran de consentement OAuth et le soumettre.",
  "Verfügbar": "Disponible",
  "Test starten": "Lancer le test",
  "Tokens erneuern": "Renouveler les tokens",
  "Kunde verbinden": "Connecter le client",
  "Adapter-Live": "Adaptateur en direct",
  "Legacy-Fallback": "Repli legacy",
  "Default": "Par défaut",
  "Statisch": "Statique",
  "Recheck ausgeführt": "Revérification effectuée",
  "Zentrale Zeilen vorhanden": "Lignes centrales présentes",
  "Kein Fallback nötig": "Aucun repli nécessaire",
  "Zentrale Zeile == Live-Recheck": "Ligne centrale == revérification en direct",
  "Übersicht konsistent": "Aperçu cohérent",
  "Matrix-Isolation": "Isolation de la matrice",
  "Verlauf abrufbar": "Historique consultable",
  "Down": "Hors ligne",
  "Aktivitäten": "Activités",
  "Getestet": "Testé",
  "Nachweis versendet": "Preuve envoyée",
  "Funktionsfähig": "Fonctionnel",
  "Probleme": "Problèmes",
  "Ablaufende Tokens": "Tokens arrivant à échéance",
  "3/3 · Fertig": "3/3 · Terminé",
  "2/3 · Getestet": "2/3 · Testé",
  "1/3 · Verbunden": "1/3 · Connecté",
  "Ablaufende Tokens (7 Tage)": "Tokens arrivant à échéance (7 jours)",
  "Ablaufende Tokens (30 Tage)": "Tokens arrivant à échéance (30 jours)",
  "Gültige Tokens": "Tokens valides",
  "Vorbereitet": "Préparé",
  "Adapter vorbereitet": "Adaptateur préparé",
  "Getrennt": "Déconnecté",
  "Alles OK": "Tout est OK",
  "Teilweise OK": "Partiellement OK",
  "Adapter": "Adaptateur",
  "Verbindung": "Connexion",
  "Health": "Santé",
  "E-Mail (Brevo)": "E-mail (Brevo)",
  "Verbunden · Versand aktiv": "Connecté · envoi actif",
  "API-Key hinterlegen zum Aktivieren": "Enregistrer la clé API pour activer",
  "Instagram / Facebook (Meta)": "Instagram / Facebook (Meta)",
  "App-ID & Secret erforderlich": "ID d’app & secret requis",
  "Eigenen Stripe-Key hinterlegen (STRIPE_SECRET_KEY)": "Enregistrer votre propre clé Stripe (STRIPE_SECRET_KEY)",
  "Cloudflare Turnstile": "Cloudflare Turnstile",
  "Verbunden · Spam-Schutz aktiv": "Connecté · protection anti-spam active",
  "Secret erforderlich": "Secret requis",
  "WordPress-Anbindung": "Connexion WordPress",
  "Verbunden · Lead-Intake aktiv": "Connecté · réception des leads active",
  "API-Key generieren": "Générer une clé API",
  "System-Integrationen migriert (Backfill)": "Intégrations système migrées (backfill)",
  "Cutover-Bereitschaft erreicht": "Préparation au cutover atteinte",
  "Cutover-Schwelle geändert": "Seuil de cutover modifié",
  "Cutover durchgeführt": "Cutover effectué",
  "Cutover zurückgenommen": "Cutover annulé",
  "Cutover-Verifikation ausgeführt": "Vérification du cutover effectuée",
  "Meta-Überwachung: Aufgabe erstellt": "Surveillance Meta : tâche créée",
  "Adapter vorbereitet – Credentials fehlen": "Adaptateur préparé – identifiants manquants",
  "Bereit für Verbindung": "Prêt pour la connexion",
  "Credentials fehlen (Meta-Keys)": "Identifiants manquants (clés Meta)",
  "Unterstützt": "Pris en charge",
  "Nicht konfiguriert – Meta-Keys fehlen": "Non configuré – clés Meta manquantes",
  "Vorbereitet – Kundenzustimmung fehlt": "Préparé – consentement du client manquant",
  "Token abgelaufen – neu verbinden": "Token expiré – reconnecter",
  "Nicht begonnen": "Pas commencé",
  "In Entwicklung": "En développement",
  "Adapter bereit": "Adaptateur prêt",
  "Zugangsdaten fehlen": "Identifiants manquants",
  "Zugangsdaten hinterlegt": "Identifiants enregistrés",
  "Zugangsdaten ungültig": "Identifiants invalides",
  "Zugangsdaten gültig": "Identifiants valides",
  "Verbindung läuft": "Connexion en cours",
  "Autorisierung abgelaufen": "Autorisation expirée",
  "Verbindungsfehler": "Erreur de connexion",
  "Nicht getestet": "Non testé",
  "Nicht prüfbar": "Non vérifiable",
  "Eine zentrale KEWAG-Schnittstelle – sichere Einzelverbindungen zu jeder Plattform": "Une interface KEWAG centrale – des connexions individuelles sécurisées vers chaque plateforme",
  "Dry-Run – keine externe Veröffentlichung (Facebook/Instagram/LinkedIn/TikTok etc. NICHT aufgerufen).": "Simulation – aucune publication externe (Facebook/Instagram/LinkedIn/TikTok etc. NON appelés).",
  "Verbindungsprobleme": "Problèmes de connexion",
  "Token/Zugriff prüfen, erneut verbinden": "Vérifier le token/l’accès, reconnecter",
  "Fehlende API-Zugangsdaten": "Identifiants API manquants",
  "System-Zugangsdaten hinterlegen": "Enregistrer les identifiants système",
  "Verbindung vorbereitet": "Connexion préparée",
  "Gebuchte Kanäle autorisieren": "Autoriser les canaux réservés",
  "Unklare Buchungen bestätigen": "Confirmer les réservations incertaines",
  "Kunden ohne verbundene Plattformen": "Clients sans plateformes connectées",
  "Kanäle einrichten": "Configurer les canaux",
  "Tests ausstehend": "Tests en attente",
  "Standard · Integration in Vorbereitung": "Standard · intégration en préparation",
  "Meta (Instagram/Facebook)": "Meta (Instagram/Facebook)",
  "Integration aktiv": "Intégration active",
  "Keine Aktion nötig.": "Aucune action nécessaire.",
  "Noch keine Kanäle gebucht.": "Aucun canal réservé pour l’instant.",
  "Alle gebuchten Kanäle sind funktionsfähig — keine Aktion nötig.": "Tous les canaux réservés sont fonctionnels — aucune action nécessaire.",
  "Erneut verbinden": "Reconnecter",
  "Veröffentlichung blockiert": "Publication bloquée",
  "Keine Veröffentlichung möglich": "Aucune publication possible",
  "Noch keine Veröffentlichung": "Pas encore de publication",
  "Einrichtung erforderlich": "Configuration requise",
  "Bestätigung erforderlich": "Confirmation requise",
  "Zuverlässigkeit unklar": "Fiabilité incertaine",
  "Technische Integration für diese Plattform ist noch nicht verfügbar.": "L’intégration technique pour cette plateforme n’est pas encore disponible.",
  "Meta ist nicht konfiguriert (App-ID & Secret fehlen).": "Meta n’est pas configuré (ID d’app & secret manquants).",
  "Keine aktive Meta-Verbindung für diesen Kanal.": "Aucune connexion Meta active pour ce canal.",
  "Verbindung erfolgreich geprüft.": "Connexion vérifiée avec succès.",
  "Verbindung fehlgeschlagen – bitte erneut verbinden.": "Échec de la connexion – veuillez reconnecter.",
  "Verbindung ausstehend.": "Connexion en attente.",
  "Diese Integration ist technisch noch nicht verfügbar.": "Cette intégration n’est pas encore disponible techniquement.",
  "Integrations-Engine": "Moteur d’intégration",
  "Übersicht & Statusmaschine aktiv": "Aperçu & machine à états actifs",
  "Token-Service": "Service de tokens",
  "Verschlüsselung aktiv": "Chiffrement actif",
  "Encryption-Key fehlt — Verbindungen gesperrt": "Clé de chiffrement manquante — connexions bloquées",
  "Webhook-Listener": "Écouteur de webhooks",
  "Echtzeit-Webhooks folgen (Phase 6)": "Webhooks en temps réel à venir (phase 6)",
  "Keine Verbindung": "Aucune connexion",
  "Stripe-Key ungültig oder nicht erreichbar.": "Clé Stripe invalide ou inaccessible.",
  "Cutover jetzt möglich: System-Integrationen auf zentrale Datenquelle umstellen": "Cutover désormais possible : basculer les intégrations système vers la source de données centrale",
  "Legacy-Fallback erneut aufgetreten – Beobachtung neu gestartet": "Repli legacy de nouveau survenu – observation redémarrée",
  "Recheck aller Provider ausgeführt": "Revérification de tous les fournisseurs effectuée",
  "Ein Provider-Recheck warf einen Fehler.": "Une revérification de fournisseur a généré une erreur.",
  "Alle Provider haben eine gültige zentrale Zeile": "Tous les fournisseurs ont une ligne centrale valide",
  "Mindestens ein Provider ohne zentrale Zeile.": "Au moins un fournisseur sans ligne centrale.",
  "Kein Legacy-Fallback nötig (Quelle: zentrale Zeile)": "Aucun repli legacy nécessaire (source : ligne centrale)",
  "Mindestens ein Provider wird via Fallback aufgelöst.": "Au moins un fournisseur est résolu via le repli.",
  "Zentrale Zeile stimmt mit Live-Recheck überein": "La ligne centrale correspond à la revérification en direct",
  "Abweichung zwischen zentraler Zeile und Recheck.": "Écart entre la ligne centrale et la revérification.",
  "Übersicht/Karten baubar & konsistent": "Aperçu/cartes constructibles & cohérents",
  "Übersicht konnte nicht konsistent gebaut werden.": "L’aperçu n’a pas pu être construit de manière cohérente.",
  "Keine System-Zeilen in der Mandanten-Matrix": "Aucune ligne système dans la matrice des mandants",
  "System-Dokumente ohne scope=system gefunden.": "Documents système sans scope=system trouvés.",
  "Verlauf je Provider abrufbar": "Historique consultable par fournisseur",
  "Verlaufsabfrage fehlgeschlagen.": "Échec de la requête d’historique.",
  "Meta noch nicht konfiguriert — App-ID & Secret fehlen": "Meta pas encore configuré — ID d’app & secret manquants",
  "Bereit zum Verbinden": "Prêt à connecter",
  "Meta-Grundlage aktiviert und verschlüsselt gespeichert – OAuth kann gestartet werden.": "Base Meta activée et enregistrée chiffrée – OAuth peut être lancé.",
  "Config-ID gespeichert – OAuth nutzt jetzt Login for Business (config_id, kein scope).": "Config-ID enregistré – OAuth utilise désormais Login for Business (config_id, pas de scope).",
  "Config-ID entfernt – OAuth nutzt wieder scope.": "Config-ID supprimé – OAuth utilise de nouveau scope.",
  "Keine Facebook-Seite zurückgegeben — Berechtigung 'pages_show_list' fehlt.": "Aucune page Facebook renvoyée — l’autorisation 'pages_show_list' manque.",
  "Keine Facebook-Seite zurückgegeben. Das verbundene Meta-Konto verwaltet keine Seite oder ist nicht als Seiten-Admin zugeordnet.": "Aucune page Facebook renvoyée. Le compte Meta connecté ne gère aucune page ou n’est pas attribué comme admin de page.",
  "Meta OAuth": "Meta OAuth",
  "Verbindung gültig.": "Connexion valide.",
  "Instagram-Testbeitrag benötigt ein Bild/Video (Content-Container). Nutzen Sie für den Textnachweis bitte Facebook — der Instagram-Test folgt.": "La publication test Instagram nécessite une image/vidéo (conteneur de contenu). Pour la preuve texte, utilisez Facebook — le test Instagram suivra.",
  "Testbeitrag erfolgreich veröffentlicht.": "Publication test publiée avec succès.",
  "Testbeitrag gelöscht.": "Publication test supprimée.",
  "Keine Verbindung.": "Aucune connexion.",
  "Token konnte nicht entschlüsselt werden.": "Le token n’a pas pu être déchiffré.",
  "Token nicht lesbar.": "Token illisible.",
  "System (Meta-Überwachung)": "Système (surveillance Meta)",
  "Meta-Überwachung": "Surveillance Meta",
  "Keine Kunden in Stufe 2 (2/3)": "Aucun client à l’étape 2 (2/3)",
  "Autorisierung erforderlich": "Autorisation requise",
  "Aufmerksamkeit erforderlich": "Attention requise",
  "App & API": "App & API",
  "WordPress Verbindung": "Connexion WordPress",
  "KI-Dienst": "Service IA",
  "E-Mail Dienst": "Service e-mail",
  "Social (Meta)": "Social (Meta)",
  "Speicherplatz": "Espace de stockage",
  "FastAPI · Antwort ok": "FastAPI · réponse ok",
  "MongoDB · verbunden": "MongoDB · connecté",
  "Authentifizierung": "Authentification",
  "Sitzungsspeicher nicht erreichbar": "Stockage des sessions inaccessible",
  "Objektspeicher": "Stockage d’objets",
  "OpenAI Vision · einsatzbereit": "OpenAI Vision · opérationnel",
  "Kein Schlüssel hinterlegt": "Aucune clé enregistrée",
  "Geplante Jobs": "Tâches planifiées",
  "Tages-Digest · Monatsreport; Kanal-Veröffentlichung noch nicht verfügbar": "Résumé quotidien · rapport mensuel ; publication sur les canaux pas encore disponible",
  "Kein automatisches Backup konfiguriert": "Aucune sauvegarde automatique configurée",
  "Kontaktpflege (Brevo)": "Gestion des contacts (Brevo)",
  "Status nicht lesbar": "Statut illisible",
  "Marken-Profil geändert": "Profil de marque modifié",
  "Brand-Asset hochgeladen": "Ressource de marque téléversée",
  "Team informiert": "Équipe informée",
  "Beitrag geändert": "Publication modifiée",
  "Beitrag reaktiviert": "Publication réactivée",
  "Freigabe-Entscheidung": "Décision de validation",
  "Sammel-Freigabe": "Validation groupée",
  "Fotoauftrag-Aktion": "Action mandat photo",
  "Fotoauftrag erstellt": "Mandat photo créé",
  "Medien hochgeladen": "Médias téléversés",
  "Medien gelöscht": "Médias supprimés",
  "Rechnungs-Aktion": "Action de facturation",
  "Preise aktualisiert": "Prix mis à jour",
  "Ticket-Antwort": "Réponse au ticket",
  "Mandant geändert": "Mandant modifié",
  "Mandant gesperrt": "Mandant bloqué",
  "Mandant aktiviert": "Mandant activé",
  "Passwort geändert": "Mot de passe modifié",
  "Passwort zurückgesetzt (Admin)": "Mot de passe réinitialisé (admin)",
  "Notfall-Zugang ausgelöst": "Accès d’urgence déclenché",
  "Passwort per Notfall-Zugang zurückgesetzt": "Mot de passe réinitialisé via l’accès d’urgence",
  "Passwort-Reset angefordert": "Réinitialisation du mot de passe demandée",
  "Passwort selbst zurückgesetzt": "Mot de passe réinitialisé soi-même",
  "E-Mail Versand": "Envoi d’e-mails",
  "Konfiguriert · verbindbar": "Configuré · connectable",
  "Meta-Einladung erstellt": "Invitation Meta créée",
  "Meta-Einladung widerrufen": "Invitation Meta révoquée",
  "Noch nicht konfiguriert (Google OAuth erforderlich)": "Pas encore configuré (Google OAuth requis)",
  "Stripe-Key erforderlich": "Clé Stripe requise",
  "Technische Integration noch nicht verfügbar": "Intégration technique pas encore disponible",
  "gebucht": "réservé",
  "nicht gebucht": "non réservé",
  // --- Phase 4 supplement ---
  "Setzen Sie ein neues Passwort für": "Définissez un nouveau mot de passe pour",
  "nicht erreichbar": "non accessible",
  // --- Phase 4 (Super-Admin-Werkzeuge) ---
  "Kopieren nicht möglich – bitte manuell markieren.": "Copie impossible – veuillez sélectionner le lien manuellement.",
  "Erzeugung fehlgeschlagen.": "Échec de la génération.",
  "Konto-Wiederherstellung": "Récupération du compte",
  "Notfall-Zugang · nur Passwort-Reset des Systeminhabers": "Accès d'urgence · uniquement réinitialisation du mot de passe du propriétaire du système",
  "Dieses Konto ist ausschliesslich für den Notfall.": "Ce compte est réservé exclusivement aux situations d'urgence.",
  "Sie können **nur** einen Reset-Link für **{who}** erzeugen.": "Vous pouvez **uniquement** générer un lien de réinitialisation pour **{who}**.",
  "den Systeminhaber": "le propriétaire du système",
  "Der Link ist **15 Minuten** gültig und wird **nur einmal** hier angezeigt.": "Le lien est valable **15 minutes** et n'est affiché ici **qu'une seule fois**.",
  "Sie legen **kein Passwort** selbst fest – das erledigt der Inhaber über den Link.": "Vous ne définissez **aucun mot de passe** vous-même – le propriétaire le fait via le lien.",
  "Nach erfolgreicher Nutzung wird dieses Konto **automatisch deaktiviert**.": "Après une utilisation réussie, ce compte est **automatiquement désactivé**.",
  "Verfügbarkeit wird geprüft…": "Vérification de la disponibilité…",
  "Die Wiederherstellung ist derzeit nicht verfügbar. Sie ist entweder nicht scharfgeschaltet oder wurde bereits verwendet.": "La récupération n'est actuellement pas disponible. Elle n'est pas armée ou a déjà été utilisée.",
  "Wird erzeugt…": "Génération en cours…",
  "Einmaligen Reset-Link erzeugen": "Générer un lien de réinitialisation à usage unique",
  "Reset-Link für **{email}** erzeugt. Gültig für **{minutes} Minuten**. Kopieren Sie ihn jetzt – er wird nicht erneut angezeigt.": "Lien de réinitialisation généré pour **{email}**. Valable **{minutes} minutes**. Copiez-le maintenant – il ne sera plus affiché.",
  "Einmaliger Reset-Link": "Lien de réinitialisation à usage unique",
  "Öffnen Sie den Link im Browser und vergeben Sie dort ein neues Passwort für den Systeminhaber. Dieses Notfall-Konto ist danach automatisch deaktiviert.": "Ouvrez le lien dans le navigateur et définissez-y un nouveau mot de passe pour le propriétaire du système. Ce compte d'urgence est ensuite automatiquement désactivé.",
  "Status konnte nicht geladen werden.": "Impossible de charger l'état.",
  "Bitte bestätigen Sie mit Ihrem eigenen Passwort.": "Veuillez confirmer avec votre propre mot de passe.",
  "Das Recovery-Passwort muss mindestens 8 Zeichen lang sein.": "Le mot de passe de récupération doit comporter au moins 8 caractères.",
  "Die beiden Recovery-Passwörter stimmen nicht überein.": "Les deux mots de passe de récupération ne correspondent pas.",
  "Notfall-Wiederherstellung scharfgeschaltet.": "Récupération d'urgence armée.",
  "Scharfschalten fehlgeschlagen.": "Échec de l'armement.",
  "Notfall-Wiederherstellung deaktiviert.": "Récupération d'urgence désactivée.",
  "Deaktivieren fehlgeschlagen.": "Échec de la désactivation.",
  "Notfall-Wiederherstellung einrichten": "Configurer la récupération d'urgence",
  "Unabhängiges Zweitkonto, das im Ernstfall Ihren Zugang wiederherstellt.": "Second compte indépendant qui restaure votre accès en cas d'urgence.",
  "Diese Funktion ist ausschliesslich für den Systeminhaber (Super Admin) verfügbar.": "Cette fonction est réservée exclusivement au propriétaire du système (super administrateur).",
  "Die Konto-Wiederherstellung ist in dieser Umgebung nicht aktiviert.": "La récupération de compte n'est pas activée dans cet environnement.",
  "Status:": "État :",
  "Scharfgeschaltet ✔": "Armée ✔",
  "Nicht scharfgeschaltet": "Non armée",
  "Notfall-Konto:": "Compte d'urgence :",
  "Zuletzt verwendet:": "Dernière utilisation :",
  "Das Notfall-Konto darf **nur** Ihren Passwort-Reset auslösen – **keinen** App-/Datenzugriff.": "Le compte d'urgence peut **uniquement** déclencher la réinitialisation de votre mot de passe – **aucun** accès à l'application ou aux données.",
  "Das Recovery-Passwort setzen **Sie selbst** und bewahren es sicher auf (nie im Chat).": "**Vous définissez vous-même** le mot de passe de récupération et le conservez en lieu sûr (jamais dans le chat).",
  "Nach einer erfolgreichen Wiederherstellung wird das Konto **automatisch deaktiviert**.": "Après une récupération réussie, le compte est **automatiquement désactivé**.",
  "Ihr eigenes Passwort (Bestätigung)": "Votre propre mot de passe (confirmation)",
  "Passwort von admin@socialcheck.ch": "Mot de passe de admin@socialcheck.ch",
  "Recovery-Passwort (für das Notfall-Konto)": "Mot de passe de récupération (pour le compte d'urgence)",
  "Mindestens 8 Zeichen": "Au moins 8 caractères",
  "Recovery-Passwort bestätigen": "Confirmer le mot de passe de récupération",
  "Recovery-Passwort wiederholen": "Répéter le mot de passe de récupération",
  "Wird gespeichert…": "Enregistrement…",
  "Recovery-Passwort neu setzen": "Redéfinir le mot de passe de récupération",
  "Scharfschalten": "Armer",
  "Notfall-Wiederherstellung deaktivieren": "Désactiver la récupération d'urgence",
  "Das Passwort muss mindestens 8 Zeichen lang sein.": "Le mot de passe doit comporter au moins 8 caractères.",
  "Die beiden Passwörter stimmen nicht überein.": "Les deux mots de passe ne correspondent pas.",
  "Das Zurücksetzen ist fehlgeschlagen.": "La réinitialisation a échoué.",
  "Passwort sicher zurücksetzen": "Réinitialiser le mot de passe en toute sécurité",
  "SocialCheck · Konto-Wiederherstellung": "SocialCheck · Récupération du compte",
  "Reset-Link wird geprüft…": "Vérification du lien de réinitialisation…",
  "Dieser Reset-Link ist ungültig, abgelaufen oder wurde bereits verwendet.": "Ce lien de réinitialisation est invalide, expiré ou a déjà été utilisé.",
  "Zur Anmeldung": "Vers la connexion",
  "Setzen Sie ein neues Passwort für **{email}**.": "Définissez un nouveau mot de passe pour **{email}**.",
  "das Konto": "le compte",
  "Neues Passwort": "Nouveau mot de passe",
  "Passwort bestätigen": "Confirmer le mot de passe",
  "Passwort wiederholen": "Répéter le mot de passe",
  "Wird gesetzt…": "Définition en cours…",
  "Passwort setzen": "Définir le mot de passe",
  "Das Passwort wurde sicher zurückgesetzt. Alle bisherigen Sitzungen wurden abgemeldet. Das Notfall-Konto ist jetzt wieder deaktiviert.": "Le mot de passe a été réinitialisé en toute sécurité. Toutes les sessions précédentes ont été déconnectées. Le compte d'urgence est de nouveau désactivé.",
  "Bitte bestätigen Sie Ihr Passwort.": "Veuillez confirmer votre mot de passe.",
  "Notfall-Zugang · Super-Admin-Passwort": "Accès d'urgence · mot de passe super administrateur",
  "Einmaliger, sicherer Reset ohne E-Mail-Versand.": "Réinitialisation unique et sécurisée sans envoi d'e-mail.",
  "Diese Funktion ist ausschliesslich für das KEWAG-Admin-Konto verfügbar.": "Cette fonction est réservée exclusivement au compte administrateur KEWAG.",
  "Wichtige Sicherheitshinweise": "Consignes de sécurité importantes",
  "Der Reset-Link wird **nur einmalig** hier angezeigt und ist **15 Minuten** gültig.": "Le lien de réinitialisation n'est affiché ici **qu'une seule fois** et est valable **15 minutes**.",
  "Sie legen **kein Passwort** selbst fest – das erledigt der Super-Admin über den Link.": "Vous ne définissez **aucun mot de passe** vous-même – le super administrateur le fait via le lien.",
  "Nach dem Erzeugen ist der Notfall-Zugang **dauerhaft deaktiviert**.": "Après la génération, l'accès d'urgence est **définitivement désactivé**.",
  "Der Link darf **nicht** per Chat oder E-Mail weitergegeben werden.": "Le lien ne doit **pas** être transmis par chat ou par e-mail.",
  "Der einmalige Notfall-Zugang ist nicht (mehr) verfügbar. Er wurde bereits verwendet und ist dauerhaft deaktiviert.": "L'accès d'urgence à usage unique n'est plus disponible. Il a déjà été utilisé et est définitivement désactivé.",
  "Der Notfall-Zugang ist für Teamkonten geschlossen. Teamkonten erhalten keinen Reset-Link für den Systeminhaber.": "L'accès d'urgence est fermé pour les comptes d'équipe. Les comptes d'équipe ne reçoivent aucun lien de réinitialisation pour le propriétaire du système.",
  "Zugangskonfiguration & Einrichtung": "Configuration des accès & mise en place",
  "Wertfreier Status: nur Schlüsselnamen und maskierte Konten – keine Secret-Werte.": "Statut sans valeurs : uniquement des noms de clés et des comptes masqués – aucune valeur secrète.",
  "Konfigurationswerte allein sind kein Nachweis eines erfolgreichen Logins.": "Les valeurs de configuration seules ne prouvent pas une connexion réussie.",
  "Zugangsstatus konnte nicht geladen werden.": "Impossible de charger le statut des accès.",
  "Owner (Super Admin)": "Propriétaire (Super Admin)",
  "Altkonto ADMIN_EMAIL": "Ancien compte ADMIN_EMAIL",
  "Altname": "ancien nom",
  "Schlüssel": "Clé",
  "Konfiguration": "Configuration",
  "Einrichtung": "Mise en place",
  "Login beobachtet": "Connexion observée",
  "kein Konto": "aucun compte",
  "konfiguriert": "configuré",
  "Kunden": "Clients",
  "aus": "désactivé",
  "Konflikt": "Conflit",
  "eingerichtet": "en place",
  "Erstanmeldung offen": "Première connexion en attente",
  "Konto fehlt": "Compte manquant",
  "übernommen": "appliqué",
  "unverändert": "inchangé",
  "Ausgangsstand erfasst": "état initial enregistré",
  "nicht gesetzt": "non défini",
  "nur Altname gesetzt": "seul l’ancien nom est défini",
  "ungültig – nicht übernommen": "invalide – non appliqué",
  "Übernahme läuft": "application en cours",
  "Geheimnis-Übernahme": "Reprise du secret",
  "zuletzt übernommen: {date}": "dernière application : {date}",
  "Ausgangsstand: {date}": "état initial : {date}",
  "Letzte Prüfung beim Backend-Start: {date}": "Dernière vérification au démarrage du backend : {date}",
  "Neue Werte wirken erst nach Backend-Neustart (Vorschau) bzw. Neu veröffentlichen (LIVE); Speichern allein ändert kein Passwort.": "Les nouvelles valeurs ne prennent effet qu’après un redémarrage du backend (aperçu) ou une nouvelle publication (LIVE) ; l’enregistrement seul ne modifie aucun mot de passe.",
  "Passwort fehlt": "Mot de passe manquant",
  "Ihr Passwort ({email}) zur Bestätigung": "Votre mot de passe ({email}) pour confirmation",
  "Passwort eingeben": "Saisir le mot de passe",
  "Öffnen Sie den Link im Browser des Super-Admins und vergeben Sie dort ein neues Passwort. Der Notfall-Zugang ist jetzt dauerhaft deaktiviert.": "Ouvrez le lien dans le navigateur du super administrateur et définissez-y un nouveau mot de passe. L'accès d'urgence est désormais définitivement désactivé.",
  "Einrichtung konnte nicht gestartet werden.": "Impossible de démarrer la configuration.",
  "Code ungültig. Bitte erneut versuchen.": "Code invalide. Veuillez réessayer.",
  "Zwei-Faktor-Authentifizierung erforderlich": "Authentification à deux facteurs requise",
  "Deine Organisation verlangt 2FA. Bitte richte sie jetzt ein, um fortzufahren.": "Votre organisation exige la 2FA. Veuillez la configurer maintenant pour continuer.",
  "2FA ist jetzt aktiv. Bewahre diese Backup-Codes sicher auf – jeder ist einmalig nutzbar.": "La 2FA est maintenant active. Conservez ces codes de secours en lieu sûr – chacun est utilisable une seule fois.",
  "Codes kopieren": "Copier les codes",
  "Weiter zur App": "Continuer vers l'application",
  "1. Öffne deine Authenticator-App (z. B. Google Authenticator, Microsoft Authenticator).": "1. Ouvrez votre application d'authentification (p. ex. Google Authenticator, Microsoft Authenticator).",
  "2. Scanne den QR-Code oder gib den Schlüssel manuell ein.": "2. Scannez le code QR ou saisissez la clé manuellement.",
  "3. Trage den angezeigten 6-stelligen Code ein.": "3. Saisissez le code à 6 chiffres affiché.",
  "2FA QR-Code": "Code QR 2FA",
  "Manueller Schlüssel": "Clé manuelle",
  "Authenticator-Code": "Code de l'authentificateur",
  "2FA aktivieren & fortfahren": "Activer la 2FA et continuer",
  "Neu": "Nouveau",
  "Geprüft": "Vérifié",
  "Demo aktiv": "Démo active",
  "Einladungslink kopiert": "Lien d'invitation copié",
  "Neuer Einladungslink kopiert (48 h gültig)": "Nouveau lien d'invitation copié (valable 48 h)",
  "Als geprüft markiert": "Marqué comme vérifié",
  "Anfrage freigegeben": "Demande approuvée",
  "Demo-Zugang erstellt": "Accès démo créé",
  "Zugangs- und Demo-Anfragen von socialcheck.ch prüfen, freigeben und Demo-Zugänge erstellen.": "Examiner et approuver les demandes d'accès et de démo de socialcheck.ch et créer des accès démo.",
  "E-Mail-Dienst verbunden": "Service e-mail connecté",
  "E-Mail-Dienst nicht verbunden": "Service e-mail non connecté",
  "Firma, Name, E-Mail…": "Entreprise, nom, e-mail…",
  "Keine Demo-Anfragen für diesen Filter.": "Aucune demande de démo pour ce filtre.",
  "Upgrade gewünscht": "Mise à niveau souhaitée",
  "Nur Super Admin kann bearbeiten": "Seul le super administrateur peut modifier",
  "Prüfen": "Vérifier",
  "Link": "Lien",
  "Einladung senden": "Envoyer l'invitation",
  "Audit": "Audit",
  "Ein 24-Stunden-Demo-Zugang wurde angelegt.": "Un accès démo de 24 heures a été créé.",
  "Die Einladung wurde per E-Mail versendet.": "L'invitation a été envoyée par e-mail.",
  "E-Mail-Dienst nicht verbunden — bitte Einladungslink manuell teilen.": "Service e-mail non connecté — veuillez partager le lien d'invitation manuellement.",
  "E-Mail versendet": "E-mail envoyé",
  "Einladungslink (24 h gültig)": "Lien d'invitation (valable 24 h)",
  "Zu den Demo-Zugängen": "Vers les accès démo",
  "Kein Mandant für dieses Demo-Konto vorhanden": "Aucun mandant disponible pour ce compte démo",
  "Kunden-Cockpit geöffnet (nur-lesend)": "Cockpit client ouvert (lecture seule)",
  "Als Kunde angelegt — Nr. {num}, {plan}": "Créé en tant que client — n° {num}, {plan}",
  "/Mt.": "/mois",
  "Demo um 24 Stunden verlängert": "Démo prolongée de 24 heures",
  "Demo sofort abgelaufen": "Démo expirée immédiatement",
  "Demo-Zugang gesperrt": "Accès démo bloqué",
  "Demo gelöscht (anonymisiert)": "Démo supprimée (anonymisée)",
  "Demo-Zugängen": "accès démo",
  "Aktive und abgelaufene 24-Stunden-Demo-Mandanten verwalten.": "Gérer les mandants de démo de 24 heures actifs et expirés.",
  "Noch keine Demo-Zugänge erstellt.": "Aucun accès démo créé pour le moment.",
  "Nicht aktiviert": "Non activé",
  "Läuft ab in {x}": "Expire dans {x}",
  "Ablauf:": "Expiration :",
  "gültig bis {d}": "valable jusqu'au {d}",
  "Min": "min",
  "Jetzt ablaufen": "Expirer maintenant",
  "Sperren": "Bloquer",
  "Demo löschen": "Supprimer la démo",
  "E-Mail-Dienst nicht verbunden – Link kann kopiert werden": "Service e-mail non connecté – le lien peut être copié",
  "Demo löschen (anonymisieren)?": "Supprimer la démo (anonymiser) ?",
  "Name, E-Mail und Firma von {name} werden anonymisiert, der Zugang gesperrt und aus den Listen ausgeblendet. Das Audit-Log bleibt erhalten. Diese Aktion kann nicht rückgängig gemacht werden.": "Le nom, l'e-mail et l'entreprise de {name} seront anonymisés, l'accès bloqué et masqué des listes. Le journal d'audit est conservé. Cette action est irréversible.",
  "In zahlenden Kunden umwandeln": "Convertir en client payant",
  "{name} wird zu einem echten Mandanten mit Kundennummer und Vollzugang (ohne 24h-Ablauf). Die Demo-Inhalte bleiben erhalten. Bitte Paket & Preis wählen.": "{name} devient un mandant réel avec un numéro de client et un accès complet (sans expiration à 24 h). Les contenus de démo sont conservés. Veuillez choisir le forfait et le prix.",
  "Paket wählen": "Choisir le forfait",
  "Preis (CHF / Monat)": "Prix (CHF / mois)",
  "z. B. 290": "p. ex. 290",
  "Vorschlag aus der Paket-Preisliste — überschreibbar. Preise pflegbar unter „Einstellungen“.": "Suggestion issue de la liste de prix des forfaits — modifiable. Les prix se gèrent sous « Paramètres ».",
  "DB-Backup erstellt ({n} Collections)": "Sauvegarde de la base de données créée ({n} collections)",
  "Backup fehlgeschlagen": "Échec de la sauvegarde",
  "{v} als aktuelle Version erfasst (Live-Stand {c})": "{v} enregistrée comme version actuelle (état en direct {c})",
  "Erfassung fehlgeschlagen": "Échec de l'enregistrement",
  "{v} ist jetzt aktuell · {n} als „ersetzt“ markiert": "{v} est désormais actuelle · {n} marquée(s) comme « remplacée »",
  "Zeitüberschreitung": "Délai dépassé",
  "{v} ist jetzt die aktuelle Version": "{v} est désormais la version actuelle",
  "{v} archiviert": "{v} archivée",
  "Datenbank wiederhergestellt": "Base de données restaurée",
  "Rollback-Plan bestätigt & protokolliert.": "Plan de restauration confirmé et journalisé.",
  "Produktion (Live)": "Production (en direct)",
  "Preview (Entwicklung)": "Préversion (développement)",
  "Umgebungen · Live-Status": "Environnements · état en direct",
  "Erneut prüfen": "Vérifier à nouveau",
  "Diese Umgebung": "Cet environnement",
  "Prüfe…": "Vérification…",
  "Erreichbar · Live": "Accessible · en direct",
  "Nicht erreichbar": "Non accessible",
  "Deploy erforderlich ·": "Déploiement requis ·",
  "{n} Commit(s) hinter Preview": "{n} commit(s) de retard sur la préversion",
  "Produktion {pv} · Preview {prev}": "Production {pv} · préversion {prev}",
  "Auf Preview-Stand": "Au niveau de la préversion",
  "Produktion neuer als Preview": "Production plus récente que la préversion",
  "Commit unterschiedlich": "Commit différent",
  "Vergleich nicht möglich": "Comparaison impossible",
  "Preview-Identität nicht ermittelt · Commit fehlt": "Identité de la préversion non déterminée · commit manquant",
  "Produktions-Identität nicht ermittelt · Commit fehlt": "Identité de la production non déterminée · commit manquant",
  "Ungültige Commit-ID gemeldet": "ID de commit signalé invalide",
  "Commit nicht in der Historie · Vergleich nicht möglich": "Commit absent de l’historique · comparaison impossible",
  "Jetzt deployen · Kurzanleitung": "Déployer maintenant · guide rapide",
  "Preview gründlich testen (dieser Stand: **{v}**).": "Tester minutieusement la préversion (cet état : **{v}**).",
  "In Emergent oben auf **Deploy** klicken.": "Dans Emergent, cliquer en haut sur **Deploy**.",
  "**„Deploy Now“** bestätigen und den bestehenden Deployment-Slot (app.socialcheck.ch) ersetzen.": "Confirmer **« Deploy Now »** et remplacer l'emplacement de déploiement existant (app.socialcheck.ch).",
  "~10–15 Min warten → Produktion läuft danach auf **{v}**, diese Karte zeigt dann automatisch „Auf Preview-Stand“.": "Attendre ~10–15 min → la production tourne ensuite sur **{v}**, cette carte affiche alors automatiquement « Au niveau de la préversion ».",
  "Update eines bestehenden Deployments = **keine Zusatzkosten** (50 Credits/Monat pro App). Rollback jederzeit möglich. Ich (Assistent) deploye nicht selbst — das bleibt bewusst deine Freigabe.": "La mise à jour d'un déploiement existant = **aucun coût supplémentaire** (50 crédits/mois par application). Restauration possible à tout moment. Je (l'assistant) ne déploie pas moi-même — cela reste délibérément votre validation.",
  "Commit": "Commit",
  "Branch": "Branche",
  "Gemeldete Umgebung": "Environnement signalé",
  "verbunden": "connectée",
  "Von hier aus nicht erreichbar": "Non accessible depuis ici",
  ". Mögliche Gründe: Cloudflare/CORS blockiert den Cross-Abruf, die Umgebung wurde noch nicht mit dieser Version deployt, oder sie ist offline.": ". Causes possibles : Cloudflare/CORS bloque l'appel entre domaines, l'environnement n'a pas encore été déployé avec cette version, ou il est hors ligne.",
  "Kein erfundener Status": "Aucun état inventé",
  "— öffne die Umgebung direkt, um ihren echten Live-Status zu sehen.": "— ouvrez directement l'environnement pour voir son véritable état en direct.",
  "Jede Karte ruft den echten öffentlichen Endpoint": "Chaque carte appelle le véritable point de terminaison public",
  "der jeweiligen Umgebung ab (Version/Commit/Branch/DB). Die Umgebung, die du gerade geöffnet hast, ist immer 100 % live & prüfbar.": "de l'environnement concerné (version/commit/branche/BD). L'environnement que vous venez d'ouvrir est toujours 100 % en direct et vérifiable.",
  "Aktuelle Version": "Version actuelle",
  "entspricht dem laufenden Code": "correspond au code en cours d'exécution",
  "Stand ist echt & live.": "L'état est réel et en direct.",
  "Laufender Code ist neuer als die als „aktuell“ markierte Version": "Le code en cours d'exécution est plus récent que la version marquée « actuelle »",
  "Der Commit der aktuellen Version {v} ist in dieser Umgebung nicht auffindbar.": "Le commit de la version actuelle {v} est introuvable dans cet environnement.",
  "Live-Stand ist {n} Commit(s) neuer als {v}.": "L'état en direct est {n} commit(s) plus récent que {v}.",
  "Laufender Commit": "Commit en cours d'exécution",
  "committet": "commité le",
  "Als aktuelle Version erfassen": "Enregistrer comme version actuelle",
  "Vorherige stabile": "Précédente stable",
  "Vorschau (RC)": "Préversion (RC)",
  "Stabil": "Stable",
  "Ersetzt": "Remplacée",
  "Archiviert": "Archivée",
  "Versionen gesamt": "Total des versions",
  "HEAD-Commit": "Commit HEAD",
  "Versionen vergleichen: {n}/2 ausgewählt": "Comparer les versions : {n}/2 sélectionnées",
  "{n} Vorschau-Version(en) offen — jüngste: **{v}**": "{n} version(s) de préversion ouverte(s) — la plus récente : **{v}**",
  "Alle Vorschau als aktuell übernehmen": "Adopter toutes les préversions comme actuelles",
  "Ersetzte/archivierte ausblenden": "Masquer les remplacées/archivées",
  "Ersetzte/archivierte anzeigen ({n})": "Afficher les remplacées/archivées ({n})",
  "Aktuell": "Actuelle",
  "Geschützt": "Protégée",
  "Build": "Build",
  "DB-Migration:": "Migration BD :",
  "ja": "oui",
  "nein": "non",
  "Test:": "Test :",
  "Rollback": "Restauration",
  "verfügbar": "disponible",
  "n/v": "n/d",
  "Ausgewählt": "Sélectionnée",
  "Vergleichen": "Comparer",
  "Screenshots ({n})": "Captures d'écran ({n})",
  "Rollback vorbereiten": "Préparer la restauration",
  "Als aktuell setzen": "Définir comme actuelle",
  "Snapshots & DB-Backups": "Instantanés et sauvegardes de la BD",
  "Backup jetzt erstellen": "Créer une sauvegarde maintenant",
  "Noch keine Backups registriert.": "Aucune sauvegarde enregistrée pour le moment.",
  "{n} Collections": "{n} collections",
  "Auto": "Auto",
  "fehlt": "manquante",
  "Beim Start jedes Arbeitspakets wird automatisch ein Vorher-Snapshot angelegt und mit der jeweiligen Version verlinkt — als 1-Klick-Rollback-Ziel.": "Au début de chaque lot de travail, un instantané préalable est créé automatiquement et lié à la version concernée — comme cible de restauration en un clic.",
  "Echte Versionsdaten (Code + Backup + Belege).": "Données de version réelles (code + sauvegarde + justificatifs).",
  "Umgebung": "Environnement",
  "Autor": "Auteur",
  "DB-Migration": "Migration BD",
  "DB-Backup": "Sauvegarde BD",
  "Teststatus": "État des tests",
  "Rollback verfügbar / getestet": "Restauration disponible / testée",
  "Module": "Modules",
  "Dateien seit dieser Version geändert": "Fichiers modifiés depuis cette version",
  "Screenshots": "Captures d'écran",
  "Golden-Master-Referenzen dieser Version.": "Références golden master de cette version.",
  "Vergleich": "Comparaison",
  "Echter git-Vergleich zwischen zwei Versionen.": "Comparaison git réelle entre deux versions.",
  "Geänderte Dateien: {n}": "Fichiers modifiés : {n}",
  "Version archivieren?": "Archiver la version ?",
  "{v} wird als „archiviert“ markiert. Die Version bleibt erhalten und wiederherstellbar.": "{v} sera marquée comme « archivée ». La version est conservée et restaurable.",
  "Alle Vorschau-Versionen übernehmen?": "Adopter toutes les préversions ?",
  "Die jüngste Vorschau wird zur aktuellen Version; ältere Vorschau-Versionen werden als „ersetzt“ markiert (bleiben erhalten).": "La préversion la plus récente devient la version actuelle ; les préversions plus anciennes sont marquées comme « remplacées » (elles sont conservées).",
  "Wird aktuell": "Devient actuelle",
  "Als „ersetzt“ markiert": "Marquée comme « remplacée »",
  "Als aktuelle Version setzen?": "Définir comme version actuelle ?",
  "{v} wird zur aktuellen Version. Ein „-rc“-Suffix wird zur Release-Version. Die bisherige aktuelle Version bleibt erhalten (stabil/vorherige).": "{v} devient la version actuelle. Un suffixe « -rc » devient la version de release. La version actuelle précédente est conservée (stable/précédente).",
  "Rollback vorbereiten — {v}": "Préparer la restauration — {v}",
  "Kein automatischer Rollback. Prüfe den Plan und bestätige ausdrücklich.": "Aucune restauration automatique. Vérifiez le plan et confirmez explicitement.",
  "Zielversion": "Version cible",
  "Betroffene Module": "Modules concernés",
  "Betroffene Dateien": "Fichiers concernés",
  "vorhanden": "présente",
  "Rollback-Plan": "Plan de restauration",
  "Wiederherstellungsplan": "Plan de rétablissement",
  "Ich habe den Plan geprüft und möchte den Rollback über den Plattform-Rollback vorbereiten.": "J'ai vérifié le plan et souhaite préparer la restauration via la restauration de la plateforme.",
  "Datenbank auf Snapshot zurücksetzen (optional, destruktiv)": "Restaurer la base de données sur un instantané (facultatif, destructif)",
  "Vorschau wird geladen…": "Chargement de l'aperçu…",
  "Kein verlinktes Backup für diese Version — DB-Wiederherstellung nicht verfügbar. (Nur Code-Rollback möglich.)": "Aucune sauvegarde liée à cette version — restauration de la BD indisponible. (Seule la restauration du code est possible.)",
  "Snapshot:": "Instantané :",
  "Collections": "collections",
  "Entfernt würden:": "Seraient supprimés :",
  "Nicht im Backup (bleiben erhalten):": "Absents de la sauvegarde (conservés) :",
  "Zum Bestätigen „WIEDERHERSTELLEN“ eintippen": "Saisir « WIEDERHERSTELLEN » pour confirmer",
  "Datenbank wiederherstellen": "Restaurer la base de données",
  "Vor der Wiederherstellung wird automatisch ein Sicherheits-Snapshot des aktuellen Standes erstellt.": "Avant la restauration, un instantané de sécurité de l'état actuel est créé automatiquement.",
  "Rollback-Plan bestätigen": "Confirmer le plan de restauration",
  "Aktuellen Stand als Version festhalten": "Enregistrer l'état actuel comme version",
  "Erfasst den echten laufenden Git-Stand (Commit": "Enregistre l'état Git réel en cours d'exécution (commit",
  ") als neue aktuelle Version.": ") comme nouvelle version actuelle.",
  "{n} Commit(s) seit {who}.": "{n} commit(s) depuis {who}.",
  "der letzten Version": "la dernière version",
  "Bezeichnung": "Désignation",
  "z. B. Integrationen-Schaltzentrale Phase 2": "p. ex. centre de contrôle des intégrations phase 2",
  "Ohne Eingabe wird automatisch „Laufender Stand · {c}“ verwendet.": "Sans saisie, « État en cours · {c} » est utilisé automatiquement.",
  "Notiz (optional)": "Note (facultative)",
  "Was wurde in diesem Stand abgeschlossen?": "Qu'est-ce qui a été achevé dans cet état ?",
  "Version erfassen": "Enregistrer la version",
  "{d} Tg. {h} Std.": "{d} j {h} h",
  "{h} Std.": "{h} h",
  "Jury-Link kopiert.": "Lien du jury copié.",
  "Kopieren nicht möglich – Link bitte manuell markieren.": "Copie impossible – veuillez sélectionner le lien manuellement.",
  "Öffentlicher Jury-Link kopiert.": "Lien public du jury copié.",
  "QR-Code ist noch nicht bereit.": "Le code QR n'est pas encore prêt.",
  "QR-Code als PNG heruntergeladen.": "Code QR téléchargé au format PNG.",
  "Download nicht möglich.": "Téléchargement impossible.",
  "A4-Poster (PDF) erstellt.": "Affiche A4 (PDF) créée.",
  "Poster konnte nicht erstellt werden.": "Impossible de créer l'affiche.",
  "A4-Poster (4 Sprachen) erstellt.": "Affiche A4 (4 langues) créée.",
  "Erledigt.": "Terminé.",
  "Jury-Demo für 7 Tage aktiviert.": "Démo jury activée pour 7 jours.",
  "Neuer Einladungslink erzeugt.": "Nouveau lien d'invitation généré.",
  "Jury-Demo entsperrt.": "Démo jury débloquée.",
  "Neue Zugänge gesperrt.": "Nouveaux accès bloqués.",
  "Alle Jury-Sitzungen beendet.": "Toutes les sessions du jury terminées.",
  "Zugang ist wieder öffentlich.": "L'accès est de nouveau public.",
  "Zugang jetzt nur über Einladungslink.": "L'accès se fait désormais uniquement via le lien d'invitation.",
  "Neuen Einladungslink erzeugen? Der bisherige Link wird sofort ungültig.": "Générer un nouveau lien d'invitation ? Le lien précédent devient immédiatement invalide.",
  "Neue Jury-Zugänge sperren? Laufende Sitzungen bleiben bis zum Timeout aktiv.": "Bloquer les nouveaux accès du jury ? Les sessions en cours restent actives jusqu'au délai d'expiration.",
  "Wirklich ALLE laufenden Jury-Sitzungen sofort beenden und deren Demo-Mandanten löschen?": "Terminer vraiment IMMÉDIATEMENT toutes les sessions du jury en cours et supprimer leurs mandants de démo ?",
  "Jury-Demo-Kontrolle": "Contrôle de la démo jury",
  "Steuern Sie die passwortlose, isolierte Jury-Demo (Preview). Der Zugang ist standardmässig öffentlich über den Jury-Link erreichbar und läuft automatisch nach 7 Tagen ab.": "Pilotez la démo jury isolée et sans mot de passe (préversion). L'accès est par défaut public via le lien du jury et expire automatiquement après 7 jours.",
  "endet in {x}": "se termine dans {x}",
  "kein aktiver Zeitraum": "aucune période active",
  "{n} aktive Sitzung(en)": "{n} session(s) active(s)",
  "nur mit Einladungslink": "uniquement avec lien d'invitation",
  "öffentlich": "public",
  "Sprache des Links / QR-Codes": "Langue du lien / du code QR",
  "Die Jury-Demo startet automatisch in der gewählten Sprache (Auto = Sprache des Geräts).": "La démo jury démarre automatiquement dans la langue choisie (Auto = langue de l'appareil).",
  "Einladungslink für die Jury": "Lien d'invitation pour le jury",
  "Öffentlicher Jury-Link": "Lien public du jury",
  "Showcase-Ziel (Produktion): https://app.socialcheck.ch/jury-demo": "Cible de la vitrine (production) : https://app.socialcheck.ch/jury-demo",
  "QR-Code zum Öffnen der öffentlichen, passwortlosen Jury-Demo": "Code QR pour ouvrir la démo jury publique et sans mot de passe",
  "QR-Code für die Jury": "Code QR pour le jury",
  "Mit dem Smartphone scannen, um die öffentliche, passwortlose Jury-Demo zu öffnen. Der Code enthält ausschliesslich den öffentlichen Link – keine Sitzungs-IDs, Tokens oder personenbezogenen Daten.": "Scannez avec le smartphone pour ouvrir la démo jury publique et sans mot de passe. Le code contient uniquement le lien public – aucun identifiant de session, jeton ou donnée personnelle.",
  "Öffentlichen Jury-Link kopieren": "Copier le lien public du jury",
  "QR als PNG herunterladen": "Télécharger le QR en PNG",
  "QR-Code als PNG herunterladen": "Télécharger le code QR au format PNG",
  "A4-Handout (PDF)": "Prospectus A4 (PDF)",
  "A4-Poster mit QR als PDF herunterladen": "Télécharger l'affiche A4 avec QR au format PDF",
  "A4-Poster (4 Sprachen)": "Affiche A4 (4 langues)",
  "A4-Poster mit QR in vier Sprachen (DE/FR/IT/EN) als PDF herunterladen": "Télécharger l'affiche A4 avec QR en quatre langues (DE/FR/IT/EN) au format PDF",
  "Aktivieren (7 Tage)": "Activer (7 jours)",
  "Neuen Link erzeugen": "Générer un nouveau lien",
  "Öffentlich freigeben": "Rendre public",
  "Mit Einladungslink absichern": "Protéger par lien d'invitation",
  "Entsperren": "Débloquer",
  "Alle Sitzungen beenden": "Terminer toutes les sessions",
  // --- Backups + Zusatzleistungen (Phase 3) ---
  "Sicherungsstatus der SocialCheck-Plattform.": "État des sauvegardes de la plateforme SocialCheck.",
  "Kein manuelles Backup-Modul aktiv": "Aucun module de sauvegarde manuelle actif",
  "Datensicherungen werden automatisch auf Infrastruktur-Ebene durch KEWAG bzw. den Betreiber verwaltet. Ein Self-Service-Export ist derzeit nicht verfügbar.": "Les sauvegardes de données sont gérées automatiquement au niveau de l'infrastructure par KEWAG ou l'exploitant. Un export en self-service n'est actuellement pas disponible.",
  "Verwaltung": "Gestion",
  "KEWAG / Infrastruktur": "KEWAG / Infrastructure",
  "Automatische Sicherung": "Sauvegarde automatique",
  "Datenbank": "Base de données",
  "Serverseitig gespeichert": "Stocké côté serveur",
  "Self-Service-Export": "Export en self-service",
  "Nicht verfügbar": "Non disponible",
  "Auf Anfrage bei KEWAG": "Sur demande auprès de KEWAG",
  "Objektspeicher (Medien & Uploads) wird redundant und persistent gespeichert.": "Le stockage d'objets (médias et téléversements) est enregistré de manière redondante et persistante.",
  "Für einen vollständigen Datenexport oder eine Wiederherstellung kontaktieren Sie bitte das KEWAG-Team.": "Pour un export complet des données ou une restauration, veuillez contacter l'équipe KEWAG.",
  "Ein integriertes Backup-/Restore-Modul mit Historie kann bei Bedarf ergänzt werden.": "Un module intégré de sauvegarde/restauration avec historique peut être ajouté si nécessaire.",
  "Service": "Service",
  "Als erledigt markiert": "Marqué comme traité",
  "Anfrage abgelehnt": "Demande refusée",
  "Upgrade- und Zusatz-Anfragen der Kunden — Speicher, Kanäle, Services und Pakete.": "Demandes d'upgrade et de services supplémentaires des clients — stockage, canaux, services et forfaits.",
  "Keine Anfragen.": "Aucune demande.",
  "Anfragen": "Demandes",
  // --- KiWorkspace Compliance (Phase 3) ---
  "Compliance": "Conformité",
  // --- KiWorkspace suppl (Phase 3) ---
  "Termin entfernen": "Supprimer la date",
  // --- KiWorkspace (Phase 3) ---
  "Markensicher": "Sûr pour la marque",
  "Storytelling": "Storytelling",
  "Verkaufsstark": "Orienté vente",
  "Kreativ": "Créatif",
  "Brauchbar": "Utilisable",
  "Verbesserung": "À améliorer",
  "Problematisch": "Problématique",
  "Nicht verwenden": "Ne pas utiliser",
  "Manuelle Prüfung": "Vérification manuelle",
  "von 100": "sur 100",
  "Diese Aktion verbraucht echte KI-Credits": "Cette action consomme de vrais crédits IA",
  "Verbraucht KI-Credits": "Consomme des crédits IA",
  "Laden fehlgeschlagen": "Échec du chargement",
  "Lauf wurde bereits ausgeführt — keine doppelten Credits.": "L'exécution a déjà eu lieu — pas de crédits en double.",
  "Keine neu zu bewertenden Bilder.": "Aucune nouvelle image à évaluer.",
  "{done} Bild(er) bewertet": "{done} image(s) évaluée(s)",
  "{done} Bild(er) bewertet, {failed} fehlgeschlagen": "{done} image(s) évaluée(s), {failed} en échec",
  "Bewertung fehlgeschlagen": "Échec de l'évaluation",
  "Bitte mindestens ein Bild auswählen": "Veuillez sélectionner au moins une image",
  "KI-Vorschlag erstellt": "Proposition IA créée",
  "KI-Generierung fehlgeschlagen": "Échec de la génération IA",
  "KI-Limits für diesen Mandanten gespeichert": "Limites IA enregistrées pour ce client",
  "Änderungen übernommen": "Modifications appliquées",
  "Ungültiges Datum.": "Date invalide.",
  "Der geplante Termin darf nicht in der Vergangenheit liegen.": "La date planifiée ne peut pas être dans le passé.",
  "Freigabe-Beitrag aktualisiert": "Publication de validation mise à jour",
  "Beitrag geplant & zur Kundenfreigabe vorbereitet": "Publication planifiée et préparée pour la validation du client",
  "Beitrag zur Kundenfreigabe vorbereitet": "Publication préparée pour la validation du client",
  "Als Entwurf gespeichert": "Enregistré comme brouillon",
  "Vorschlag abgelehnt": "Proposition refusée",
  "Aktion fehlgeschlagen": "Échec de l'action",
  "Bitte zuerst einen Kunden auswählen.": "Veuillez d'abord sélectionner un client.",
  "KI-Service nicht verbunden — Analyse und Vorschläge sind derzeit nicht verfügbar.": "Service IA non connecté — l'analyse et les propositions ne sont pas disponibles pour le moment.",
  "Marken-Profil nur zu {pct}% vollständig. Vorschläge benötigen verstärkte manuelle Prüfung.": "Profil de marque complété à seulement {pct}%. Les propositions nécessitent une vérification manuelle renforcée.",
  "KI-Service nicht verbunden.": "Service IA non connecté.",
  "KI-Läufe wurden durch KEWAG (global) pausiert.": "Les exécutions IA ont été suspendues par KEWAG (globalement).",
  "KI-Läufe wurden für diesen Mandanten pausiert.": "Les exécutions IA ont été suspendues pour ce client.",
  "Bestehende Ergebnisse bleiben lesbar; kostenpflichtige KI-Läufe sind gesperrt.": "Les résultats existants restent consultables ; les exécutions IA payantes sont bloquées.",
  "KI-Limits": "Limites IA",
  "Achtung: {pct}% des KI-Monatslimits erreicht. Weitere Läufe werden bei 100% gesperrt.": "Attention : {pct}% de la limite mensuelle IA atteinte. D'autres exécutions seront bloquées à 100%.",
  "1. Bilder & Auswahl": "1. Images & sélection",
  "Bewerte…": "Évaluation…",
  "Auswahl bewerten": "Évaluer la sélection",
  "Massenanalyse: alle {n} unbewerteten": "Analyse en masse : les {n} non évaluées",
  "Empfohlen": "Recommandées",
  "Keine Bilder in dieser Ansicht.": "Aucune image dans cette vue.",
  "Unbewertet": "Non évaluée",
  "Noch nicht bewertet": "Pas encore évaluée",
  "2. KI-Vorschläge": "2. Propositions IA",
  "Erzeuge…": "Génération…",
  "Neuen Vorschlag erzeugen": "Générer une nouvelle proposition",
  "KI erstellt den Vorschlag…": "L'IA crée la proposition…",
  "Erzeuge aus der Bildauswahl einen Beitrag im Modus „{mode}“.": "Générez une publication à partir de la sélection d'images en mode « {mode} ».",
  "Wähle links Bilder aus, um einen Vorschlag zu erzeugen.": "Sélectionnez des images à gauche pour générer une proposition.",
  "Vorschlag mit KI erzeugen": "Générer une proposition avec l'IA",
  "Empfohlene Bildauswahl": "Sélection d'images recommandée",
  "Keine Bilder ausgewählt.": "Aucune image sélectionnée.",
  "Titel / Hook": "Titre / accroche",
  "Titel": "Titre",
  "Hook (optional)": "Accroche (facultatif)",
  "Haupttext": "Texte principal",
  "Call-to-Action": "Appel à l'action",
  "Call-to-Action (optional)": "Appel à l'action (facultatif)",
  "Hashtags (durch Leerzeichen getrennt)": "Hashtags (séparés par des espaces)",
  "z. B. bern gastronomie frisch": "p. ex. berne gastronomie frais",
  "Plattformvarianten": "Variantes par plateforme",
  "Zeichen": "caractères",
  "Hashtags": "hashtags",
  "Begründung & verwendete Markenregeln": "Justification & règles de marque utilisées",
  "Offene Fragen": "Questions ouvertes",
  "Übernehmen": "Appliquer",
  "Vorschlag bearbeiten": "Modifier la proposition",
  "Deep-Link zu diesem Vorschlag kopieren": "Copier le lien direct vers cette proposition",
  "Link kopiert — direkt zu diesem KI-Vorschlag": "Lien copié — directement vers cette proposition IA",
  "Kopieren fehlgeschlagen": "Échec de la copie",
  "Link kopieren": "Copier le lien",
  "Bearbeitet von {by}": "Modifié par {by}",
  "3. Prüfung & Aktionen": "3. Vérification & actions",
  "Noch keine Prüfung — erst einen Vorschlag erzeugen.": "Aucune vérification pour l'instant — générez d'abord une proposition.",
  "Gesamtbewertung · Konfidenz": "Évaluation globale · confiance",
  "Marken-Passung": "Adéquation à la marque",
  "Inhaltsqualität": "Qualité du contenu",
  "Bildqualität": "Qualité de l'image",
  "Erkannte Stärken": "Points forts détectés",
  "Hinweise": "Remarques",
  "Risiken": "Risques",
  "Keine kritischen Risiken erkannt": "Aucun risque critique détecté",
  "Compliance-Problem": "Problème de conformité",
  "Blockiert: {reason} — kann nicht zur Kundenfreigabe vorbereitet werden.": "Bloqué : {reason} — ne peut pas être préparé pour la validation du client.",
  "Geplant für (optional)": "Planifié pour (facultatif)",
  "Leer = ungeplant zur Freigabe · Zeitzone Europe/Zürich · Beitrag, Freigabe & Kalender teilen denselben Termin.": "Vide = non planifié pour validation · fuseau horaire Europe/Zurich · la publication, la validation et le calendrier partagent la même date.",
  "Freigabe-Beitrag aktualisieren": "Mettre à jour la publication de validation",
  "Zur Freigabe vorbereiten": "Préparer pour la validation",
  "Variante": "Variante",
  "Vorschlag ablehnen": "Refuser la proposition",
  "KI-Verbrauch & Limits": "Consommation IA & limites",
  "KI-Limits für diesen Mandanten": "Limites IA pour ce client",
  "KI-Service nicht verbunden": "Service IA non connecté",
  "KI-Läufe (Monat)": "Exécutions IA (mois)",
  "davon Bildbewertungen": "dont évaluations d'images",
  "davon Vorschläge": "dont propositions",
  "Läufe gesamt": "Exécutions au total",
  "Global pausiert": "Suspendu globalement",
  "Für diesen Mandanten pausiert": "Suspendu pour ce client",
  "Optionaler Grund (wird im Audit-Log protokolliert).": "Motif facultatif (consigné dans le journal d'audit).",
  "z. B. Bildauswahl passt nicht, Text zu werblich …": "p. ex. la sélection d'images ne convient pas, texte trop promotionnel …",
  "Kostenpflichtigen KI-Lauf bestätigen": "Confirmer l'exécution IA payante",
  "Bitte vor dem Start prüfen.": "Veuillez vérifier avant de démarrer.",
  "Umfang": "Étendue",
  "{n} Bild(er)": "{n} image(s)",
  "1 Vorschlag": "1 proposition",
  "Anbieter / Modell": "Fournisseur / modèle",
  "Voraussichtliche Kosten": "Coûts estimés",
  "Echte Kosten derzeit nicht verfügbar (Abrechnung über KI-Läufe)": "Coûts réels indisponibles pour le moment (facturation via les exécutions IA)",
  "Diese Aktion verbraucht echte KI-Credits.": "Cette action consomme de vrais crédits IA.",
  "Kostenpflichtigen KI-Lauf starten": "Démarrer l'exécution IA payante",
  "Mandantenbezogene Kostenkontrolle. Der globale KEWAG-Schalter hat immer Vorrang.": "Contrôle des coûts par client. L'interrupteur global KEWAG a toujours la priorité.",
  "KI für diesen Mandanten aktiviert": "IA activée pour ce client",
  "Max. KI-Läufe / Monat": "Max. exécutions IA / mois",
  "Max. Bildanalysen / Monat": "Max. analyses d'images / mois",
  "Max. Video-Generierungen / Monat": "Max. générations de vidéos / mois",
  "Monatsbudget (CHF, optional)": "Budget mensuel (CHF, facultatif)",
  "Leer = kein Limit. Echte Token-/Kostenwerte liefert der Anbieter nicht — es gelten die konfigurierten Lauf-Limits.": "Vide = aucune limite. Le fournisseur ne fournit pas de valeurs réelles de tokens/coûts — les limites d'exécution configurées s'appliquent.",
  // --- MarkenProfil (Phase 3) ---
  "Kein Profil": "Aucun profil",
  "Ort / Region": "Localité / région",
  "Google Business Link": "Lien Google Business",
  "Social-Kanäle": "Canaux sociaux",
  "Manuell": "Manuel",
  "Google Business": "Google Business",
  "Leistungen": "Prestations",
  "Zielkunden": "Clients cibles",
  "Tonalität (z. B. sachlich, freundlich, edel, lokal)": "Tonalité (p. ex. factuelle, chaleureuse, haut de gamme, locale)",
  "Verbotene Begriffe (nie verwenden)": "Termes interdits (à ne jamais utiliser)",
  "No-Go-Themen": "Sujets à éviter",
  "Beispieltexte, die gut waren": "Exemples de textes réussis",
  "Besonderheiten / Saisonalität": "Particularités / saisonnalité",
  "vor {n} Minute": "il y a {n} minute",
  "vor {n} Minuten": "il y a {n} minutes",
  "vor {n} Stunde": "il y a {n} heure",
  "vor {n} Stunden": "il y a {n} heures",
  "vor {n} Tag": "il y a {n} jour",
  "Kundenfreigabe angefordert": "Validation du client demandée",
  "KI-Vorschau aktualisiert": "Aperçu IA actualisé",
  "Profil {version} aktiviert – Freigabe-E-Mail an {recipient} gesendet": "Profil {version} activé – e-mail de validation envoyé à {recipient}",
  "Brevo nicht verbunden – keine E-Mail": "Brevo non connecté – pas d'e-mail",
  "kein Kunden-Kontakt hinterlegt – keine E-Mail": "aucun contact client enregistré – pas d'e-mail",
  "E-Mail-Versand fehlgeschlagen": "Échec de l'envoi de l'e-mail",
  "Profil {version} aktiviert": "Profil {version} activé",
  "Profil {version} aktiviert ({reason})": "Profil {version} activé ({reason})",
  "Version {version} wiederhergestellt": "Version {version} restaurée",
  "Alle Kunden und deren Markenprofile": "Tous les clients et leurs profils de marque",
  "Freig.": "Valid.",
  "Nächste": "Prochaine",
  "Profil": "Profil",
  "Marken-Profilen": "profils de marque",
  "Kundenübersicht – Zusammenfassung": "Aperçu des clients – résumé",
  "Höchste Vollständigkeit": "Complétude maximale",
  "Nächste Veröffentlichung": "Prochaine publication",
  "Bitte Kunde wählen.": "Veuillez choisir un client.",
  "Die Kunden-Stimme für {name}. Wird für bessere Content-Vorschläge verwendet.": "La voix client de {name}. Utilisée pour de meilleures suggestions de contenu.",
  "Zuletzt gespeichert:": "Dernier enregistrement :",
  "Nicht gespeicherte Änderungen": "Modifications non enregistrées",
  "Profil-Vollständigkeit": "Complétude du profil",
  "Version": "Version",
  "Zuletzt aktualisiert": "Dernière actualisation",
  "Freigegeben vom Kunden": "Validé par le client",
  "am": "le",
  "Noch offen": "Encore en attente",
  "Kundenfreigabe anfordern": "Demander la validation du client",
  "Vorherige Version": "Version précédente",
  "Profil-Export": "Exporter le profil",
  "Als JSON": "En JSON",
  "Unternehmens- & Markeninformationen": "Informations sur l'entreprise & la marque",
  "Bevorzugte Sprache": "Langue préférée",
  "Positionierung / Markenversprechen": "Positionnement / promesse de marque",
  "Visuelle Markenregeln": "Règles visuelles de la marque",
  "Logo": "Logo",
  "Kein Logo hinterlegt": "Aucun logo enregistré",
  "Farbpalette": "Palette de couleurs",
  "Fertig": "Terminé",
  "Keine Farben hinterlegt": "Aucune couleur enregistrée",
  "Bildsprache": "Langage visuel",
  "Unerwünschte Bildmotive": "Motifs visuels indésirables",
  "Entwurf speichern": "Enregistrer le brouillon",
  "Änderungen vergleichen": "Comparer les modifications",
  "Profil aktivieren": "Activer le profil",
  "KI mit Profil testen": "Tester l'IA avec le profil",
  "Nur das KEWAG-Team kann bearbeiten.": "Seule l'équipe KEWAG peut modifier.",
  "Freigabe-E-Mail zuletzt an {to} · {datetime}": "Dernier e-mail de validation à {to} · {datetime}",
  "Beim Aktivieren wird automatisch eine Freigabe-E-Mail mit PDF-Richtlinie an den Kunden gesendet.": "Lors de l'activation, un e-mail de validation avec la directive PDF est automatiquement envoyé au client.",
  "Freigabe-E-Mail mit PDF-Richtlinie geht automatisch an den Kunden, sobald Brevo verbunden ist.": "L'e-mail de validation avec la directive PDF est envoyé automatiquement au client dès que Brevo est connecté.",
  "KI-Vorschau": "Aperçu IA",
  "Vorschau aktualisieren": "Actualiser l'aperçu",
  "Noch keine Angaben – Profil ausfüllen.": "Aucune information pour l'instant – remplissez le profil.",
  "Kanäle & Quellen": "Canaux & sources",
  "Fehlt": "Manquant",
  "Live-Sync (Reichweite/Interaktionen) wird nach Anbindung von Meta / Google Business aktiv.": "La synchronisation en direct (portée/interactions) s'active après la connexion de Meta / Google Business.",
  "Freigaberegeln & Compliance": "Règles de validation & conformité",
  "Regeln bearbeiten": "Modifier les règles",
  "Regel hinzufügen": "Ajouter une règle",
  "Keine Regeln hinterlegt.": "Aucune règle enregistrée.",
  "Beispiel-Post-Vorschau": "Aperçu d'un post d'exemple",
  "Kein Beispielbild": "Aucune image d'exemple",
  "Noch kein Beispieltext hinterlegt.": "Aucun texte d'exemple enregistré.",
  "Versionsverlauf": "Historique des versions",
  "Aktuelle Version: {version}. Eine frühere Version wiederherstellen.": "Version actuelle : {version}. Restaurer une version antérieure.",
  "Noch keine gespeicherten Versionen. Versionen entstehen beim Aktivieren des Profils.": "Aucune version enregistrée pour l'instant. Les versions sont créées lors de l'activation du profil.",
  "Wiederherstellen": "Restaurer",
  "Vergleich mit zuletzt aktivierter Version {version}.": "Comparaison avec la dernière version activée {version}.",
  "Noch keine aktivierte Vorversion – zeigt alle gefüllten Felder als neu.": "Aucune version antérieure activée – affiche tous les champs remplis comme nouveaux.",
  "Keine Unterschiede – Profil entspricht der aktivierten Version.": "Aucune différence – le profil correspond à la version activée.",
  "Beispiel": "Exemple",
  // --- Phase 3 Batch 5 ---
  "Jetzt neu verbinden": "Reconnecter maintenant",
  // --- Phase 3 Batch 4: MetaConnect Restlabels ---
  "Konto:": "Compte :",
  "Token gültig bis:": "Jeton valable jusqu'au :",
  // --- Phase 3 Batch 3: MetaConnect + Backend-Meldungen ---
  "Betriebsbereit": "Opérationnel",
  "Handlungsbedarf": "Action requise",
  "Verbindung ausstehend": "Connexion en attente",
  "Deaktiviert": "Désactivé",
  "Meta App-ID (META_APP_ID)": "Meta App-ID (META_APP_ID)",
  "Meta App-Secret (META_APP_SECRET)": "Meta App-Secret (META_APP_SECRET)",
  "Redirect-URI (META_REDIRECT_URI)": "URI de redirection (META_REDIRECT_URI)",
  "OAuth-Konfig-ID (META_OAUTH_CONFIG_ID) — optional": "ID de config OAuth (META_OAUTH_CONFIG_ID) — facultatif",
  "Token-Verschlüsselung (INTEGRATION_ENCRYPTION_KEY)": "Chiffrement des jetons (INTEGRATION_ENCRYPTION_KEY)",
  "Handlung": "Action",
  "Offen · gültig": "Ouvert · valable",
  "Verwendet": "Utilisé",
  "Widerrufen": "Révoqué",
  "Ungültig": "Non valide",
  "Kopieren nicht möglich": "Copie impossible",
  "Meta-Grundlage aktiviert.": "Base Meta activée.",
  "Meta-Grundlage deaktiviert.": "Base Meta désactivée.",
  "Bitte zuerst einen Testmandanten wählen.": "Veuillez d'abord choisir un mandant de test.",
  "Meta-Verbindung getrennt.": "Connexion Meta déconnectée.",
  "Trennen fehlgeschlagen": "Échec de la déconnexion",
  "Asset-Auswahl gespeichert.": "Sélection de la ressource enregistrée.",
  "Speichern fehlgeschlagen": "Échec de l'enregistrement",
  "Geprüft:": "Vérifié :",
  "Handlungsbedarf:": "Action requise :",
  "Läuft bald ab:": "Bientôt expiré :",
  "Aufgaben erstellt:": "Tâches créées :",
  "Token-Überwachung gespeichert.": "Surveillance des jetons enregistrée.",
  "Einladung erstellt und per E-Mail an {email} gesendet.": "Invitation créée et envoyée par e-mail à {email}.",
  "Einladung erstellt. E-Mail-Dienst (Brevo) ist nicht verbunden — bitte Link manuell teilen.": "Invitation créée. Le service e-mail (Brevo) n'est pas connecté — veuillez partager le lien manuellement.",
  "Einladung erstellt, E-Mail konnte nicht gesendet werden — bitte Link manuell teilen.": "Invitation créée, l'e-mail n'a pas pu être envoyé — veuillez partager le lien manuellement.",
  "Einladungslink erstellt (72 h gültig).": "Lien d'invitation créé (valable 72 h).",
  "Einladung fehlgeschlagen": "Échec de l'invitation",
  "Erinnerung an {email} gesendet.": "Rappel envoyé à {email}.",
  "E-Mail-Dienst (Brevo) ist nicht verbunden — Erinnerung konnte nicht gesendet werden.": "Le service e-mail (Brevo) n'est pas connecté — le rappel n'a pas pu être envoyé.",
  "Erinnerung konnte nicht gesendet werden.": "Le rappel n'a pas pu être envoyé.",
  "Erinnerung fehlgeschlagen": "Échec du rappel",
  "Einladung widerrufen.": "Invitation révoquée.",
  "Widerrufen fehlgeschlagen": "Échec de la révocation",
  "Meta Business Suite — Verbindung (Preview)": "Meta Business Suite — connexion (aperçu)",
  "System-Grundlage (global) + echte OAuth-Verbindung pro Mandant · nur Super Admin": "Base système (globale) + connexion OAuth réelle par mandant · super administrateur uniquement",
  "System-Grundlage": "Base système",
  "Kanonische Redirect-URI (in der Meta App exakt eintragen)": "URI de redirection canonique (à saisir exactement dans l'app Meta)",
  "Kopieren": "Copier",
  "Token-Überwachung (täglich automatisch)": "Surveillance des jetons (automatique, quotidienne)",
  "Läuft ein Token ab oder wird ungültig, erstellt das System automatisch eine KEWAG-Aufgabe — und schliesst sie bei Erholung wieder.": "Si un jeton expire ou devient invalide, le système crée automatiquement une tâche KEWAG — et la referme dès rétablissement.",
  "Vorwarnung (Tage vor Ablauf)": "Préavis (jours avant expiration)",
  "Auto-Erinnerung Einladung (Std.)": "Rappel auto. invitation (h)",
  "Alle Tokens jetzt prüfen": "Vérifier tous les jetons maintenant",
  "Meta-Grundlage aktivieren (direkt hier — verschlüsselt gespeichert)": "Activer la base Meta (directement ici — stockée de manière chiffrée)",
  "App-ID und App-Geheimcode aus der Meta-Konsole (Einstellungen → Allgemeines). Wird live bei Meta geprüft und verschlüsselt gespeichert — kein Emergent-Secrets-Umweg nötig.": "App-ID et clé secrète de l'app depuis la console Meta (Paramètres → Général). Vérifiées en direct chez Meta et stockées de manière chiffrée — sans passer par les secrets Emergent.",
  "App-ID (z. B. 1558250479126312)": "App-ID (p. ex. 1558250479126312)",
  "App-Geheimcode (App Secret)": "Clé secrète de l'app (App Secret)",
  "Tipp: Bei „Error validating client secret“ ist der Geheimcode falsch/unvollständig — in der Meta-Konsole neben „App-Geheimcode“ auf „Anzeigen“/„Zurücksetzen“ und vollständig kopieren. Alternativ als Emergent-Secrets möglich ({vars}).": "Astuce : si « Error validating client secret » apparaît, la clé secrète est incorrecte/incomplète — dans la console Meta, à côté de « Clé secrète de l'app », cliquez sur « Afficher »/« Réinitialiser » et copiez-la en entier. Possible aussi via les secrets Emergent ({vars}).",
  "Aktiviert & verschlüsselt gespeichert": "Activé & stocké de manière chiffrée",
  "Ampel-Übersicht · alle Mandanten-Verbindungen": "Aperçu des voyants · toutes les connexions des mandants",
  "Noch keine Mandanten-Meta-Verbindungen. Verbinde unten einen Testmandanten.": "Aucune connexion Meta de mandant pour l'instant. Connectez un mandant de test ci-dessous.",
  "Verbindung · interner Testmandant": "Connexion · mandant de test interne",
  "Testmandant": "Mandant de test",
  "— Mandant wählen —": "— Choisir un mandant —",
  "(Demo — nicht verbindbar)": "(Démo — non connectable)",
  "Zuerst Zugangsdaten hinterlegen": "Enregistrer d'abord les identifiants",
  "Verbindung teilen mit Kunde": "Partager la connexion avec le client",
  "Erzeugen Sie einen sicheren, einmalig gültigen Einladungslink (72 Stunden). Der Kunde autorisiert sein eigenes Meta-Konto selbst — ohne Passwort-Weitergabe an KEWAG.": "Générez un lien d'invitation sécurisé et à usage unique (72 heures). Le client autorise lui-même son propre compte Meta — sans communiquer de mot de passe à KEWAG.",
  "Auch per E-Mail an den Kunden senden": "Envoyer aussi par e-mail au client",
  "Vorschlag: Haupt-Kontakt des Mandanten — überschreibbar.": "Suggestion : contact principal du mandant — modifiable.",
  "E-Mail-Dienst (Brevo) ist nicht verbunden — der Versand wird übersprungen. Der Link wird trotzdem erstellt und angezeigt.": "Le service e-mail (Brevo) n'est pas connecté — l'envoi est ignoré. Le lien est tout de même créé et affiché.",
  "Einladungslink erzeugen (72 h)": "Générer un lien d'invitation (72 h)",
  "Hinweis: Der Kunde kann den Link erst zur Verbindung nutzen, sobald die Meta-Grundlage aktiviert ist.": "Remarque : le client ne pourra utiliser le lien pour se connecter qu'une fois la base Meta activée.",
  "Neuer Einladungslink — an den Kunden senden:": "Nouveau lien d'invitation — à envoyer au client :",
  "erstellt": "créé le",
  "gültig bis": "valable jusqu'au",
  "verwendet": "utilisé le",
  "gesendet": "envoyé",
  "vorgemerkt": "prévu",
  "erinnert": "rappelé",
  "Erinnerung senden": "Envoyer un rappel",
  "Noch keine Verbindung für diesen Mandanten. Klicke „Mit Meta verbinden“, um den OAuth-Flow zu starten.": "Aucune connexion pour ce mandant. Cliquez sur « Se connecter avec Meta » pour démarrer le flux OAuth.",
  "Token läuft in {d} Tag(en) ab — Meta-Tokens gelten ~60 Tage. Jetzt neu verbinden, um Unterbrechungen zu vermeiden.": "Le jeton expire dans {d} jour(s) — les jetons Meta sont valables ~60 jours. Reconnectez-vous maintenant pour éviter les interruptions.",
  "Token gültig — noch ca. {d} Tage (Meta-Tokens laufen nach ~60 Tagen ab).": "Jeton valable — encore env. {d} jours (les jetons Meta expirent après ~60 jours).",
  "Gewährte Berechtigungen": "Autorisations accordées",
  "Fehlende Berechtigungen": "Autorisations manquantes",
  "Business-Portfolios": "Portfolios Business",
  "Keine Portfolios gefunden.": "Aucun portfolio trouvé.",
  "Referenz-Abgleich (sichere Zuordnung)": "Comparaison de référence (attribution sécurisée)",
  "Ziel-Seite (Referenz)": "Page cible (référence)",
  "Portfolio (Referenz)": "Portfolio (référence)",
  "Business-Asset (Referenz)": "Ressource Business (référence)",
  "Von der Meta Pages API zurückgegebene Seiten:": "Pages renvoyées par l'API Meta Pages :",
  "Keine Seite geladen — Endpoint": "Aucune page chargée — endpoint",
  "Meta-Fehlercode": "code d'erreur Meta",
  "fehlende Berechtigung:": "autorisation manquante :",
  "Noch keine Seiten geladen (Verbindung ausstehend).": "Aucune page chargée pour l'instant (connexion en attente).",
  "stimmt mit Referenz überein ✓": "correspond à la référence ✓",
  "Asset auswählen (Facebook-Seite + verknüpftes Instagram)": "Sélectionner la ressource (page Facebook + Instagram lié)",
  "— Seite wählen —": "— Choisir une page —",
  "(kein IG)": "(pas d'IG)",
  "Auswahl speichern": "Enregistrer la sélection",
  "Ausgewählt:": "Sélectionné :",
  "Verbindung testen": "Tester la connexion",
  "Verbindung trennen": "Déconnecter",
  "Verbindung jetzt prüfen": "Vérifier la connexion maintenant",
  "Meta App im Entwicklungsmodus angenommen — nur autorisierte Admin-/Tester-/Developer-Konten können sich verbinden. Kein Advanced Access / kein App Review bestätigt.": "App Meta supposée en mode développement — seuls les comptes admin/testeur/développeur autorisés peuvent se connecter. Aucun accès avancé / aucune revue d'app confirmée.",
  "WordPress API-Key vorhanden.": "Clé API WordPress présente.",
  "Kein WordPress API-Key hinterlegt.": "Aucune clé API WordPress enregistrée.",
  "Meta ist konfiguriert und verbindbar.": "Meta est configuré et connectable.",
  "Meta App-ID & Secret fehlen.": "L'App-ID et le Secret Meta sont manquants.",
  "E-Mail-Versand ist konfiguriert.": "L'envoi d'e-mails est configuré.",
  "Brevo API-Key fehlt.": "La clé API Brevo est manquante.",
  "Kein gültiger Stripe-Key hinterlegt.": "Aucune clé Stripe valide enregistrée.",
  "Nicht konfiguriert – Zugangsdaten fehlen.": "Non configuré – identifiants manquants.",
  // --- Phase 3 Batch 2: Integrationen/MetaConnectDialog ---
  "Kundenverbindungen": "Connexions clients",
  "Provider & Systeme": "Fournisseurs & systèmes",
  "Verlauf & Prüfungen": "Historique & vérifications",
  "Integrationen konnten nicht geladen werden": "Impossible de charger les intégrations",
  "Meta erfolgreich verbunden.": "Meta connecté avec succès.",
  "Meta-Verbindung fehlgeschlagen.": "Échec de la connexion Meta.",
  "Kein Instagram-Business-Konto mit der Facebook-Seite verknüpft.": "Aucun compte professionnel Instagram lié à la page Facebook.",
  "Keine Facebook-Seite gefunden.": "Aucune page Facebook trouvée.",
  "Zuletzt geprüft – bitte kurz warten.": "Vérifié récemment – veuillez patienter un instant.",
  "Integrationen neu geprüft.": "Intégrations revérifiées.",
  "Ansicht aktualisiert.": "Vue actualisée.",
  "Prüfung fehlgeschlagen": "Échec de la vérification",
  "Test fehlgeschlagen": "Échec du test",
  "Dokumentation wird vorbereitet.": "La documentation est en préparation.",
  "Zentrale Verwaltung von Plattform-Verbindungen, Berechtigungen und Integrations-Health.": "Gestion centralisée des connexions de plateformes, des autorisations et de l'état des intégrations.",
  "Letzte Aktualisierung:": "Dernière actualisation :",
  "Prüfungen & Diagnose sind nur für den Super Admin sichtbar.": "Les vérifications et le diagnostic ne sont visibles que par le super administrateur.",
  "Audit-Log öffnen": "Ouvrir le journal d'audit",
  "Zeiten in Tabellen und Karten entsprechen der letzten erfolgreichen Synchronisation oder Prüfung.": "Les heures dans les tableaux et les cartes correspondent à la dernière synchronisation ou vérification réussie.",
  "Ehrlicher Live-Status: nur echte, geprüfte Verbindungen werden als verbunden angezeigt.": "État en direct honnête : seules les connexions réelles et vérifiées sont affichées comme connectées.",
  "System-Setup": "Configuration du système",
  "OAuth & Assets": "OAuth & ressources",
  "Test & Speichern": "Test & enregistrement",
  "Meta-Status konnte nicht geladen werden": "Impossible de charger l'état de Meta",
  "diesem Kunden": "ce client",
  "OAuth konnte nicht gestartet werden": "Impossible de démarrer l'OAuth",
  "Einladungslink erstellt (72 Stunden, einmalig).": "Lien d'invitation créé (72 heures, usage unique).",
  "Einladungslink fehlgeschlagen": "Échec du lien d'invitation",
  "Link kopiert": "Lien copié",
  "Bitte App-ID und App-Secret eingeben.": "Veuillez saisir l'App-ID et l'App-Secret.",
  "Meta aktiviert und verschlüsselt gespeichert — Sie können jetzt verbinden.": "Meta activé et enregistré de manière chiffrée — vous pouvez maintenant vous connecter.",
  "Aktivierung fehlgeschlagen": "Échec de l'activation",
  "Redirect-URI kopiert": "URI de redirection copiée",
  "Bitte numerische Configuration ID eingeben.": "Veuillez saisir une Configuration ID numérique.",
  "Config-ID gespeichert — OAuth nutzt jetzt Login for Business (config_id).": "Config-ID enregistrée — l'OAuth utilise désormais Login for Business (config_id).",
  "Config-ID konnte nicht gespeichert werden": "Impossible d'enregistrer la Config-ID",
  "Config-ID entfernt — OAuth nutzt wieder scope.": "Config-ID supprimée — l'OAuth réutilise scope.",
  "Config-ID konnte nicht entfernt werden": "Impossible de supprimer la Config-ID",
  "Meta App ID (global)": "Meta App ID (global)",
  "Meta App Secret (global)": "Meta App Secret (global)",
  "OAuth Redirect URI (global)": "OAuth Redirect URI (global)",
  "OAuth Config ID (global)": "OAuth Config ID (global)",
  "Hinterlegt": "Enregistré",
  "Erforderlich": "Requis",
  "Optional": "Facultatif",
  "Business Portfolio": "Business Portfolio",
  "Facebook-Seite": "Page Facebook",
  "Instagram-Konto": "Compte Instagram",
  "Gewährte Scopes": "Scopes accordés",
  "Letzter Health-Check": "Dernier contrôle de santé",
  "Meta (Instagram / Facebook) verbinden": "Connecter Meta (Instagram / Facebook)",
  "System-Setup global · Verbindung nur für den ausgewählten Kunden": "Configuration système globale · connexion uniquement pour le client sélectionné",
  "Ausgewählter Kunde": "Client sélectionné",
  "Die Verbindung wird ausschliesslich diesem Mandanten zugeordnet.": "La connexion est attribuée exclusivement à ce mandant.",
  "System-Setup (global)": "Configuration du système (globale)",
  "Einmalig für das System – nicht pro Kunde speichern.": "Une seule fois pour le système – ne pas enregistrer par client.",
  "OAuth Config ID (Login for Business)": "OAuth Config ID (Login for Business)",
  "Config-ID entfernen (zurück zu scope)": "Supprimer la Config-ID (revenir à scope)",
  "Neue Configuration ID (überschreibt)": "Nouvelle Configuration ID (écrase)",
  "z. B. 1234567890123456": "p. ex. 1234567890123456",
  "Meta-Systemgrundlage: Betriebsbereit": "Base système Meta : opérationnelle",
  "Meta-Systemgrundlage: Nicht betriebsbereit": "Base système Meta : non opérationnelle",
  "Meta App-ID und App-Secret fehlen — bitte als Emergent-Secret hinterlegen.": "L'App-ID et l'App-Secret Meta sont manquants — veuillez les enregistrer comme secret Emergent.",
  "Verschlüsselung nicht aktiv (INTEGRATION_ENCRYPTION_KEY) — Tokens könnten nicht sicher gespeichert werden.": "Chiffrement inactif (INTEGRATION_ENCRYPTION_KEY) — les jetons ne pourraient pas être stockés en toute sécurité.",
  "Meta-Grundlage betriebsbereit — OAuth kann gestartet werden.": "Base Meta opérationnelle — l'OAuth peut être démarré.",
  "Jetzt aktivieren — kein Neustart nötig": "Activer maintenant — aucun redémarrage nécessaire",
  "Wo finde ich das?": "Où puis-je trouver cela ?",
  "App-ID und App-Secret aus Ihrer Meta-App eingeben. Wir prüfen sie live bei Meta und speichern sie verschlüsselt — ohne Emergent-Secret, ohne Rebuild.": "Saisissez l'App-ID et l'App-Secret de votre app Meta. Nous les vérifions en direct chez Meta et les stockons de manière chiffrée — sans secret Emergent, sans rebuild.",
  "Anleitung ausblenden": "Masquer le guide",
  "Schritt-für-Schritt: Wo finde ich App-ID & Secret?": "Étape par étape : où trouver l'App-ID et le Secret ?",
  "Meta App-ID": "Meta App-ID",
  "Meta App-Secret": "Meta App-Secret",
  "wird verschlüsselt gespeichert": "stocké de manière chiffrée",
  "Aktivieren & prüfen": "Activer & vérifier",
  "Verbindung für diesen Kunden": "Connexion pour ce client",
  "Verbindungsart": "Type de connexion",
  "Einladungslink": "Lien d'invitation",
  "72 Stunden · einmalig": "72 heures · usage unique",
  "Nur nach erfolgreichem OAuth werden Token und Assets diesem Kunden zugeordnet.": "Ce n'est qu'après un OAuth réussi que les jetons et les ressources sont attribués à ce client.",
  "Einladungslink erstellen": "Créer un lien d'invitation",
  "OAuth ist erst möglich, wenn die Systemgrundlage betriebsbereit ist.": "L'OAuth n'est possible que lorsque la base système est opérationnelle.",
  "Gültig bis": "Valable jusqu'au",
  "· nur einmal verwendbar": "· utilisable une seule fois",
  "Nach erfolgreicher Autorisierung": "Après une autorisation réussie",
  "Bestätigen & zu Meta": "Confirmer & aller à Meta",
  "OAuth starten": "Démarrer l'OAuth",
  "Später": "Plus tard",
  "Wichtig:": "Important :",
  "Meta ist aktiv!": "Meta est actif !",
  "Diese Redirect-URI in der Meta-App unter „Valid OAuth Redirect URIs\" eintragen:": "Saisissez cette URI de redirection dans l'app Meta sous « Valid OAuth Redirect URIs » :",
  "Meta für {name} verbinden": "Connecter Meta pour {name}",
  "Klicken Sie jetzt unten auf „Meta für {name} verbinden“ und melden Sie sich mit Ihrem Facebook-Konto an.": "Cliquez maintenant ci-dessous sur « Connecter Meta pour {name} » et connectez-vous avec votre compte Facebook.",
  "Die globale Meta-Systemintegration bleibt getrennt. OAuth-Verbindung, Tokens und Assets werden ausschliesslich beim aktuell ausgewählten Kunden ({name}) gespeichert.": "L'intégration système Meta globale reste séparée. La connexion OAuth, les jetons et les ressources sont stockés exclusivement chez le client actuellement sélectionné ({name}).",
  "Sie verbinden Meta jetzt ausschliesslich mit dem Mandanten {name}.": "Vous connectez désormais Meta exclusivement au mandant {name}.",
  "<strong>Instagram und Facebook gehören beide zu Meta.</strong> Sie richten das hier nur <strong>einmal</strong> ein — egal, ob Sie vorher auf die Instagram- oder die Facebook-Kachel geklickt haben. Diese eine Aktivierung schaltet <strong>beide</strong> Kanäle frei.": "<strong>Instagram et Facebook appartiennent tous deux à Meta.</strong> Vous ne configurez cela qu'<strong>une seule fois</strong> ici — que vous ayez cliqué auparavant sur la tuile Instagram ou Facebook. Cette activation unique débloque <strong>les deux</strong> canaux.",
  "Bei <strong class='text-foreground/80'>Facebook Login for Business</strong> erforderlich: numerische Configuration ID aus dem Meta-Dashboard. Der OAuth-Flow nutzt dann <code class='text-foreground/80'>config_id</code> statt <code class='text-foreground/80'>scope</code>. Öffentliche Kennung – kein Secret, kein Neustart nötig.": "Requis pour <strong class='text-foreground/80'>Facebook Login for Business</strong> : Configuration ID numérique issue du tableau de bord Meta. Le flux OAuth utilise alors <code class='text-foreground/80'>config_id</code> au lieu de <code class='text-foreground/80'>scope</code>. Identifiant public – aucun secret, aucun redémarrage nécessaire.",
  "Eine Art <strong class='text-foreground/80'>Benutzername</strong> Ihrer Meta-App (nur Zahlen).": "Une sorte de <strong class='text-foreground/80'>nom d'utilisateur</strong> de votre app Meta (chiffres uniquement).",
  "Das dazugehörige <strong class='text-foreground/80'>Passwort</strong> — wird verschlüsselt gespeichert und nie angezeigt.": "Le <strong class='text-foreground/80'>mot de passe</strong> correspondant — stocké de manière chiffrée et jamais affiché.",
  "<li><strong class='text-foreground'>1.</strong> Öffnen Sie <a class='text-primary hover:underline' href='https://developers.facebook.com/apps/' target='_blank' rel='noreferrer'>developers.facebook.com/apps</a> und wählen Sie Ihre App (oder „App erstellen“ → Typ <em>Business</em>).</li><li><strong class='text-foreground'>2.</strong> Links im Menü: <em>App-Einstellungen → Grundlegendes</em>. Ganz oben steht die <strong class='text-foreground'>App-ID</strong>.</li><li><strong class='text-foreground'>3.</strong> Beim <strong class='text-foreground'>App-Geheimnis</strong> auf „Anzeigen“ klicken (Passwort nötig) — oder „Zurücksetzen“ für ein neues, falls das alte nicht mehr stimmt. Wert vollständig kopieren.</li><li><strong class='text-foreground'>4.</strong> Beide Werte hier oben eintragen und <em>„Aktivieren &amp; prüfen“</em> klicken. Fertig — Instagram &amp; Facebook sind aktiv.</li><li class='pt-1 text-[10px]'>Tipp: Für den späteren Login-Schritt muss zusätzlich das Produkt „Facebook Login“ hinzugefügt und die unten stehende Redirect-URI eingetragen sein.</li>": "<li><strong class='text-foreground'>1.</strong> Ouvrez <a class='text-primary hover:underline' href='https://developers.facebook.com/apps/' target='_blank' rel='noreferrer'>developers.facebook.com/apps</a> et sélectionnez votre app (ou « Créer une app » → type <em>Business</em>).</li><li><strong class='text-foreground'>2.</strong> Dans le menu de gauche : <em>Paramètres de l'app → Général</em>. Tout en haut figure l'<strong class='text-foreground'>App-ID</strong>.</li><li><strong class='text-foreground'>3.</strong> Pour la <strong class='text-foreground'>clé secrète de l'app</strong>, cliquez sur « Afficher » (mot de passe requis) — ou « Réinitialiser » pour en obtenir une nouvelle si l'ancienne n'est plus valable. Copiez la valeur en entier.</li><li><strong class='text-foreground'>4.</strong> Saisissez les deux valeurs ci-dessus et cliquez sur <em>« Activer &amp; vérifier »</em>. Terminé — Instagram &amp; Facebook sont actifs.</li><li class='pt-1 text-[10px]'>Astuce : pour l'étape de connexion ultérieure, le produit « Facebook Login » doit également être ajouté et l'URI de redirection ci-dessous renseignée.</li>",
  // --- Phase 3 Batch 1: NextActions/Nachrichten/MetaConnectPublic/Abrechnung ---
  "Ihre nächsten Aktionen": "Vos prochaines actions",
  "Problem": "Problème",
  "Fällig": "Échéance",
  "Meta-Verbindung starten": "Démarrer la connexion Meta",
  "Alle Aktionen anzeigen →": "Afficher toutes les actions →",
  "Noch keine offenen Aktionen. Alles im grünen Bereich.": "Aucune action en attente. Tout est au vert.",
  "Verbindung unterbrochen": "Connexion interrompue",
  "Verbindung getrennt": "Connexion coupée",
  "Autorisierung ausstehend": "Autorisation en attente",
  "Nicht konfiguriert": "Non configuré",
  "Buchung prüfen": "Vérifier la réservation",
  "Test ausstehend": "Test en attente",
  "Keine Aktion nötig": "Aucune action requise",
  "Health-Check ausführen": "Effectuer un contrôle de santé",
  "Erneut verbinden / Token erneuern": "Reconnecter / renouveler le jeton",
  "Verbinden (Kundenzustimmung)": "Connecter (accord du client)",
  "Zugangsdaten hinterlegen": "Enregistrer les identifiants",
  "Provider-Freigabe abwarten": "Attendre la validation du fournisseur",
  "Neu verbinden": "Reconnecter",
  "Bei Bedarf buchen": "Réserver si nécessaire",
  "Demo anfragen": "Demander une démo",
  "Pocket": "Pocket",
  "gerade eben": "à l'instant",
  "vor {n} Min": "il y a {n} min",
  "vor {n} Std": "il y a {n} h",
  "vor {n} Tagen": "il y a {n} j",
  "Benachrichtigungen & Aktivitäten aus SocialCheck — alles live aus echten Daten.": "Notifications et activités de SocialCheck — en direct, à partir de données réelles.",
  "Inhalt wartet auf Freigabe": "contenu en attente de validation",
  "Inhalte warten auf Freigabe": "contenus en attente de validation",
  "Zu den Freigaben": "Vers les validations",
  "Keine Nachrichten in dieser Kategorie.": "Aucun message dans cette catégorie.",
  "Neuer Lead": "Nouveau lead",
  "Fotoauftrag": "Mandat photo",
  "Support-Ticket": "Ticket de support",
  "Sichere Autorisierung über Meta. Sie geben KEWAG kein Passwort — die Verbindung erfolgt direkt bei Meta über Ihr eigenes Konto.": "Autorisation sécurisée via Meta. Vous ne communiquez aucun mot de passe à KEWAG — la connexion s'effectue directement chez Meta via votre propre compte.",
  "Link abgelaufen": "Lien expiré",
  "Dieser Einladungslink ist nicht mehr gültig (72 Stunden überschritten). Bitte fordern Sie bei KEWAG einen neuen Link an.": "Ce lien d'invitation n'est plus valable (les 72 heures sont dépassées). Veuillez demander un nouveau lien à KEWAG.",
  "Link zurückgezogen": "Lien révoqué",
  "Dieser Einladungslink wurde von KEWAG zurückgezogen. Bitte wenden Sie sich an Ihr KEWAG-Team.": "Ce lien d'invitation a été révoqué par KEWAG. Veuillez contacter votre équipe KEWAG.",
  "Bereits verbunden": "Déjà connecté",
  "Diese Einladung wurde bereits verwendet. Ihr Meta-Konto ist verbunden — es ist nichts weiter zu tun.": "Cette invitation a déjà été utilisée. Votre compte Meta est connecté — rien d'autre à faire.",
  "Ungültiger Link": "Lien invalide",
  "Dieser Einladungslink ist ungültig. Bitte prüfen Sie den Link oder fordern Sie bei KEWAG einen neuen an.": "Ce lien d'invitation n'est pas valide. Veuillez vérifier le lien ou en demander un nouveau à KEWAG.",
  "Der Link konnte nicht geprüft werden.": "Le lien n'a pas pu être vérifié.",
  "Die Verbindung konnte nicht gestartet werden.": "La connexion n'a pas pu être démarrée.",
  "Einladung wird geprüft…": "Vérification de l'invitation…",
  "Etwas ist schiefgelaufen": "Une erreur s'est produite",
  "Meta-Konto verbinden": "Connecter le compte Meta",
  "KEWAG möchte Ihre Facebook-Seite und Ihr Instagram-Konto betreuen. Verbinden Sie Ihr Meta-Konto mit einem Klick — sicher und ohne Passwort-Weitergabe.": "KEWAG souhaite gérer votre page Facebook et votre compte Instagram. Connectez votre compte Meta en un clic — en toute sécurité et sans partage de mot de passe.",
  "Die Verbindung ist derzeit vorübergehend nicht verfügbar. Bitte versuchen Sie es in Kürze erneut oder kontaktieren Sie KEWAG.": "La connexion est momentanément indisponible. Veuillez réessayer sous peu ou contacter KEWAG.",
  "Mit Meta verbinden": "Se connecter avec Meta",
  "Verschlüsselte, einmalig gültige Einladung": "Invitation chiffrée, valable une seule fois",
  "Vielen Dank — verbunden!": "Merci — connecté !",
  "Verbindung nicht abgeschlossen": "Connexion non finalisée",
  "Ihr Meta-Konto wurde erfolgreich mit KEWAG verbunden. Sie können dieses Fenster jetzt schliessen — KEWAG kümmert sich um den Rest.": "Votre compte Meta a été connecté avec succès à KEWAG. Vous pouvez fermer cette fenêtre — KEWAG s'occupe du reste.",
  "Die Verbindung konnte nicht abgeschlossen werden. Bitte öffnen Sie Ihren Einladungslink erneut oder kontaktieren Sie Ihr KEWAG-Team.": "La connexion n'a pas pu être finalisée. Veuillez rouvrir votre lien d'invitation ou contacter votre équipe KEWAG.",
  "Nur für KEWAG Admin / Super Admin.": "Réservé aux administrateurs KEWAG / super administrateurs.",
  "Fehler beim Laden.": "Erreur lors du chargement.",
  "Abrechnungseinstellungen gespeichert.": "Paramètres de facturation enregistrés.",
  "Zurück zu Rechnungen": "Retour aux factures",
  "Firmendaten, IBAN & QR-Rechnung, Nummernkreis und E-Mail-Versand — Voraussetzung für Swiss QR-Rechnungen.": "Données de l'entreprise, IBAN et facture QR, plage de numéros et envoi d'e-mails — condition préalable aux factures QR suisses.",
  "Swiss QR-Rechnung ist bereit — der QR-Zahlteil wird dem Rechnungs-PDF angehängt.": "La facture QR suisse est prête — la section de paiement QR est jointe au PDF de la facture.",
  "QR-Rechnung noch nicht bereit: Firma, IBAN/QR-IBAN, PLZ und Ort ausfüllen und „QR aktiviert“ einschalten.": "Facture QR pas encore prête : renseignez l'entreprise, l'IBAN/QR-IBAN, le NPA et la localité, puis activez « QR activé ».",
  "Brevo E-Mail-Versand ist verbunden — Rechnungen können per E-Mail versendet werden.": "L'envoi d'e-mails Brevo est connecté — les factures peuvent être envoyées par e-mail.",
  "Brevo noch nicht verbunden: E-Mail-Versand wird aktiv, sobald BREVO_API_KEY & BREVO_SENDER_EMAIL gesetzt sind.": "Brevo pas encore connecté : l'envoi d'e-mails s'activera dès que BREVO_API_KEY et BREVO_SENDER_EMAIL seront définis.",
  "Absender / Firma": "Expéditeur / entreprise",
  "Erscheint als Zahlungsempfänger (Kreditor) auf Rechnung und QR-Zahlteil.": "Apparaît comme bénéficiaire (créancier) sur la facture et la section de paiement QR.",
  "Firmenname": "Nom de l'entreprise",
  "Strasse": "Rue",
  "Nr.": "N°",
  "Zusätzliche Adresszeilen (PDF-Kopf)": "Lignes d'adresse supplémentaires (en-tête PDF)",
  "Optional, eine Zeile pro Eintrag.": "Facultatif, une ligne par entrée.",
  "Steuer": "Taxe",
  "UID/MWST-Nummer und Normalsatz.": "Numéro IDE/TVA et taux normal.",
  "UID / MWST-Nummer": "Numéro IDE / TVA",
  "z. B. CHE-123.456.789 MWST": "p. ex. CHE-123.456.789 TVA",
  "MwSt-Satz (%)": "Taux de TVA (%)",
  "Bankverbindung": "Coordonnées bancaires",
  "IBAN für normale QR-Rechnung oder QR-IBAN für QR-Referenz-Verfahren.": "IBAN pour une facture QR normale ou QR-IBAN pour la procédure avec référence QR.",
  "Normale IBAN (ohne QR-Referenz).": "IBAN normal (sans référence QR).",
  "Für QR-Referenz-Verfahren (IID 30000–31999). Hat Vorrang.": "Pour la procédure avec référence QR (IID 30000–31999). Prioritaire.",
  "Swiss QR-Zahlteil im Rechnungs-PDF aktivieren": "Activer la section de paiement Swiss QR dans le PDF de la facture",
  "Rechnungsstellung": "Facturation",
  "Nummernkreis, Zahlungsfrist und Bedingungen.": "Plage de numéros, délai de paiement et conditions.",
  "Rechnungsnummern-Präfix": "Préfixe des numéros de facture",
  "Ergibt z. B. INV-2026-0001": "Donne p. ex. INV-2026-0001",
  "Zahlungsfrist (Tage)": "Délai de paiement (jours)",
  "Währung": "Devise",
  "Zahlungsbedingungen (PDF-Fusszeile)": "Conditions de paiement (pied de page PDF)",
  "Zahlbar innert 30 Tagen netto.": "Payable net à 30 jours.",
  "E-Mail-Versand": "Envoi d'e-mails",
  "Anzeige-Absender & Antwort-Adresse. Der technische Versand läuft über Brevo (Server-Zugangsdaten).": "Expéditeur affiché et adresse de réponse. L'envoi technique passe par Brevo (identifiants serveur).",
  "Absender-Name": "Nom de l'expéditeur",
  "Absender-E-Mail (Anzeige)": "E-mail de l'expéditeur (affichage)",
  "Technischer Absender = BREVO_SENDER_EMAIL (verifiziert).": "Expéditeur technique = BREVO_SENDER_EMAIL (vérifié).",
  "Antwort-an (Reply-To)": "Répondre à (Reply-To)",
  "Einstellungen speichern": "Enregistrer les paramètres",
  "Benutzer anlegen": "Créer un utilisateur",
  "Zugang erstellt.": "Accès créé.",
  "Neuen Zugang erstellen – per Einladungslink oder mit direkt gesetztem Passwort.": "Créer un nouvel accès – par lien d'invitation ou avec un mot de passe défini directement.",
  "Konto": "Compte",
  "ist aktiv. Bitte diese Zugangsdaten sicher an die Person weitergeben:": "est actif. Veuillez transmettre ces identifiants en toute sécurité à la personne :",
  "wurde aktualisiert und ist aktiv. Bitte diese Zugangsdaten sicher weitergeben:": "a été mis à jour et est actif. Veuillez transmettre ces identifiants en toute sécurité :",
  "Passwort": "Mot de passe",
  "Das Passwort wird aus Sicherheitsgründen nur jetzt angezeigt. Empfehlung: Person ändert es beim ersten Login und aktiviert 2FA.": "Pour des raisons de sécurité, le mot de passe n'est affiché que maintenant. Recommandation : la personne le modifie à la première connexion et active la 2FA.",
  "Einladung wurde per E-Mail versendet.": "L'invitation a été envoyée par e-mail.",
  "Brevo nicht verbunden – bitte diesen Aktivierungslink weitergeben:": "Brevo non connecté – veuillez transmettre ce lien d'activation :",
  "Schliessen": "Fermer",
  "Name": "Nom",
  "Rolle": "Rôle",
  "Kunde / Mandant": "Client / mandant",
  "Kunde wählen…": "Choisir un client…",
  "Bereich / Team (optional)": "Domaine / équipe (facultatif)",
  "z. B. Kundenbetreuung": "p. ex. suivi client",
  "Passwort direkt setzen (optional)": "Définir directement un mot de passe (facultatif)",
  "leer = Einladungslink": "vide = lien d'invitation",
  "Generieren": "Générer",
  "Mit Passwort wird das Konto sofort aktiv (kein Einladungslink). Ohne Passwort geht eine Einladung raus.": "Avec un mot de passe, le compte est actif immédiatement (pas de lien d'invitation). Sans mot de passe, une invitation est envoyée.",
  "Abbrechen": "Annuler",
  "Konto anlegen": "Créer le compte",
  "Einladen": "Inviter",
  "Team": "Équipe",
  "Super Admin": "Super Admin",
  "Admin": "Admin",
  "Finanzen": "Finances",
  "Editor": "Éditeur",
  "Reviewer": "Réviseur",
  "Mitarbeiter": "Collaborateur",
  "Viewer": "Observateur",
  "Starkes Passwort generiert": "Mot de passe fort généré",
  "Kopiert": "Copié",
  "Name und E-Mail erforderlich": "Nom et e-mail requis",
  "Bitte einen Kunden wählen": "Veuillez choisir un client",
  "Passwort: mindestens 8 Zeichen.": "Mot de passe : au moins 8 caractères.",
  "Konto aktiv angelegt – Passwort direkt gesetzt": "Compte créé et actif – mot de passe défini directement",
  "Bestehendes Konto aktualisiert – Passwort neu gesetzt": "Compte existant mis à jour – nouveau mot de passe défini",
  "Einladung per E-Mail gesendet": "Invitation envoyée par e-mail",
  "Einladung erstellt (Brevo nicht verbunden – Link kopieren)": "Invitation créée (Brevo non connecté – copier le lien)",
  "Diese E-Mail ist bereits vergeben": "Cette adresse e-mail est déjà utilisée",
  "Diese E-Mail ist bereits vergeben. Bitte die Aktualisierung des bestehenden Kontos bestätigen.": "Cette adresse e-mail est déjà utilisée. Veuillez confirmer la mise à jour du compte existant.",
  "Fortfahren aktualisiert das bestehende Konto und setzt das Passwort neu.": "Continuer met à jour le compte existant et redéfinit le mot de passe.",
  "Fortfahren aktualisiert das bestehende Konto / sendet eine neue Einladung.": "Continuer met à jour le compte existant / envoie une nouvelle invitation.",
  "Ich möchte das bestehende Konto aktualisieren.": "Je souhaite mettre à jour le compte existant.",
  "Diese E-Mail ist bereits vergeben. Bestehende Konten über die Benutzerliste verwalten.": "Cette adresse e-mail est déjà utilisée. Gérez les comptes existants via la liste des utilisateurs.",
  "Bestehende Konten werden über die Benutzerliste verwaltet (Rolle, Passwort, erneute Einladung) – nicht über dieses Formular.": "Les comptes existants se gèrent via la liste des utilisateurs (rôle, mot de passe, nouvelle invitation) – pas via ce formulaire.",
  "Aktiv": "Actif",
  "Nicht aktiv": "Inactif",
  "Inaktiv": "Inactif",
  "Eingeladen": "Invité",
  "Ausstehend": "En attente",
  "Abgelaufen": "Expiré",
  "Gesperrt": "Bloqué",
  "Alle gelesen": "Tout marquer lu",
  "offene Freigabe(n)": "approbation(s) ouverte(s)",
  "Keine offenen Freigaben": "Aucune approbation ouverte",
  "Mandanten mit offenen Freigaben": "Mandants avec approbations ouvertes",
  "aktiv": "actif",
  "Demo & Test": "Démo & test",
  "KEWAG-Team öffnen": "Ouvrir l'équipe KEWAG",
  "Ihr Testzugang läuft bald ab.": "Votre accès d'essai expire bientôt.",
  "Testzugang, keine echte Veröffentlichung. Alle Social-Aktionen sind Dry-Run.": "Accès d'essai, aucune publication réelle. Toutes les actions sociales sont en dry-run.",
  "Läuft ab:": "Expire :",
  "Upgrade angefragt — KEWAG meldet sich": "Upgrade demandé — KEWAG vous recontacte",
  "Wird gesendet…": "Envoi en cours…",
  "Jetzt upgraden": "Mettre à niveau",
  "KEWAG-Team · Kunden-Cockpit": "Équipe KEWAG · cockpit client",
  "In der Kundensicht deaktiviert – im echten Kundenlogin aktiv": "Désactivé en vue client – actif dans le vrai login client",
  "Zurück zum KEWAG Control OS": "Retour au KEWAG Control OS",
  "Sie sehen aktuell das Kunden-Cockpit von": "Vous consultez actuellement le cockpit client de",
  "Super Admin · Kundenaktionen aktiv": "Super admin · actions client actives",
  "Nur-Lese-Modus": "Mode lecture seule",
  "Aktionen sind deaktiviert – zum Bearbeiten zurück ins KEWAG Control OS.": "Les actions sont désactivées – revenez au KEWAG Control OS pour modifier.",
  "Zurück zum Beitrag im Control OS": "Retour à la publication dans Control OS",
  "Intern bearbeiten": "Modifier en interne",
  "Kundenansicht verlassen": "Quitter la vue client",
  "Zum Bearbeiten wechseln": "Passer en mode édition",
  "Finanz-Übersicht": "Aperçu financier",
  "Abos & Pakete": "Abonnements & forfaits",
  "Abrechnungseinstellungen": "Paramètres de facturation",
  "Kunden / Mandanten": "Clients / mandants",
  "KEWAG-Arbeitsbereich": "Espace de travail KEWAG",
  "Beitragsübersicht": "Aperçu des publications",
  "Freigaben / Ampel": "Approbations / feu tricolore",
  "Medienbibliothek": "Médiathèque",
  "Kundenübersicht": "Aperçu des clients",
  "KEWAG-Team": "Équipe KEWAG",
  "Demo-Anfragen": "Demandes de démo",
  "Supportfälle": "Cas de support",
  "Systemübersicht": "Aperçu du système",
  "WordPress-Verbindungen": "Connexions WordPress",
  "Allgemeine Einstellungen": "Paramètres généraux",
  "Backup-Einstellungen": "Paramètres de sauvegarde",
  "KEWAG Finanzen": "KEWAG Finances",
  "Kunden-Mitarbeiter": "Collaborateurs client",
  "offen seit": "ouvert depuis",
  "Tagen": "jours",
  "Tage": "jours",
  "Mt.": "mois",
  "T.": "j",
  "Offene Angebote ohne Reaktion seit ≥": "Offres ouvertes sans réponse depuis ≥",
  "Angebot(e)": "offre(s)",
  "Angebote verwalten": "Gérer les offres",
  "Keine Angebote in": "Aucune offre en",
  "Nachfass-Erinnerung an Kunde + intern senden": "Envoyer un rappel de relance au client + interne",
  "E-Mail-Versand aktiv, sobald Brevo verbunden ist": "Envoi d'e-mails actif dès que Brevo est connecté",
  "Nachfass-Erinnerung gesendet": "Rappel de relance envoyé",
  "Aktualisieren": "Actualiser",
  "Erneut versuchen": "Réessayer",
  "Verwalten": "Gérer",
  "Jury-Demo · Puls": "Démo jury · pouls",
  "Restlaufzeit": "Temps restant",
  "Aktive Sitzungen": "Sessions actives",
  "Zugang: öffentlich": "Accès : public",
  "Zugang: nur mit Einladungslink": "Accès : lien d'invitation uniquement",
  "Wird geprüft…": "Vérification…",
  "Läuft bald ab": "Expire bientôt",
  "Gesperrt": "Bloqué",
  "Inaktiv": "Inactif",
  "endet am": "se termine le",
  "Tg.": "j",
  "Std.": "h",
  "Min.": "min",
  "Sek.": "s",
  "abgelaufen": "expiré",
  "Das Jury-Fenster ist abgelaufen – die Demo ist aktuell nicht erreichbar. Jetzt reaktivieren.": "La fenêtre du jury a expiré – la démo est actuellement indisponible. Réactivez maintenant.",
  "Erinnerung: Das Jury-Fenster läuft in": "Rappel : la fenêtre du jury expire dans",
  "Rechtzeitig verlängern, damit die Demo online bleibt.": "Prolongez à temps pour garder la démo en ligne.",
  "Reaktivieren (7 Tage)": "Réactiver (7 jours)",
  "Um 7 Tage verlängern": "Prolonger de 7 jours",
  "Neue Jury-Zugänge sind gesperrt. Entsperren unter": "Les nouveaux accès jury sont bloqués. Débloquez sous",
  "Jury-Demo für 7 Tage reaktiviert.": "Démo jury réactivée pour 7 jours.",
  "Um 7 Tage verlängert.": "Prolongée de 7 jours.",
  "Aktion fehlgeschlagen.": "Échec de l'action.",
  "Verwerfen": "Ignorer",
  "Fortsetzen": "Continuer",
  "Zuweisen": "Attribuer",
  "Alle Pakete": "Tous les forfaits",
  "Alle Verantwortlichen": "Tous les responsables",
  "Alle Filter zurücksetzen": "Réinitialiser tous les filtres",
  "Ampelfilter entfernen": "Supprimer le filtre feu",
  "Seite": "Page",
  "Nutzer": "Utilisateurs",
  "Zeit": "Heure",
  "Ziel": "Cible",
  "Domain": "Domaine",
  "Paket filtern": "Filtrer par forfait",
  "Verantwortlicher filtern": "Filtrer par responsable",
  "Betreuer": "Responsable",
  "Marge": "Marge",
  "Profitabilität": "Rentabilité",
  "Integration": "Intégration",
  "Dashboard": "Tableau de bord",
  "Cockpit": "Cockpit",
  "Orange": "Orange",
  "Neutral": "Neutre",
  "Änderung/Blocker": "Modification/blocage",
  "Offene Frage": "Question ouverte",
  "Live": "Live",
  "Onboarding": "Onboarding",
  "Rot · Änderung/Blocker": "Rouge · modification/blocage",
  "Blau · offene Frage": "Bleu · question ouverte",
  "Orange · wartet auf Freigabe": "Orange · en attente de validation",
  "Grün · freigegeben": "Vert · validé",
  "Neutral · keine offene Freigabe": "Neutre · aucune validation en attente",
  "Orange · seit > 3 Tagen": "Orange · depuis > 3 jours",
  "Keine Einträge.": "Aucune entrée.",
  "Keine KI-Läufe an diesem Tag": "Aucune exécution IA ce jour",
  "Keine Mandanten entsprechen den aktuellen Filtern.": "Aucun mandant ne correspond aux filtres actuels.",
  "offen": "ouvert",
  "überfällig": "en retard",
  "kein Limit": "aucune limite",
  "Läufe": "exécutions",
  "Lauf": "exécution",
  "aktive Abos": "abonnements actifs",
  "Live-Übersicht für": "Aperçu en direct pour",
  "Portfolioweite Live-Übersicht über alle Mandanten": "Aperçu en direct de tous les mandants du portefeuille",
  "Mandantenmodus": "Mode mandant",
  "Mandanten-Ampel": "Feu des mandants",
  "Aktive Mandanten": "Mandants actifs",
  "Live-Kundenentscheidungsstatus · klicken zum Filtern · Zeile öffnet den Mandanten.": "Statut des décisions client en direct · cliquer pour filtrer · la ligne ouvre le mandant.",
  "Die Mandanten-Ampel konnte nicht geladen werden.": "Impossible de charger le feu des mandants.",
  "Kunde suchen": "Rechercher un client",
  "Kunde suchen…": "Rechercher un client…",
  "Kundennummer": "Numéro de client",
  "Kundendaten bearbeiten": "Modifier les données client",
  "Kunden-Cockpit ansehen": "Voir le cockpit client",
  "Arbeitsbereich": "Espace de travail",
  "Arbeitsbereich öffnen": "Ouvrir l'espace de travail",
  "Intern": "Interne",
  "Kunde wählen": "Choisir un client",
  "Demo erstellen": "Créer une démo",
  "Support öffnen": "Ouvrir le support",
  "Kunde anlegen": "Créer un client",
  "Heute wichtig": "Important aujourd'hui",
  "Deine Prioritäten – direkt bearbeiten": "Vos priorités – à traiter directement",
  "Alles erledigt – keine offenen Prioritäten.": "Tout est fait – aucune priorité ouverte.",
  "Beiträge zur Freigabe": "Publications à valider",
  "Neue Demo-Anfragen": "Nouvelles demandes de démo",
  "Demos laufen heute ab": "Démos expirant aujourd'hui",
  "Mein Tag": "Ma journée",
  "Deine offenen To-dos – Aufgaben, Feedback & überfällige Freigaben": "Vos tâches ouvertes – tâches, retours & validations en retard",
  "Alles erledigt – dein Tag ist frei.": "Tout est fait – votre journée est libre.",
  "Meine Aufgaben": "Mes tâches",
  "Mein Kundenfeedback": "Mes retours clients",
  "Überfällige Freigaben": "Validations en retard",
  "offen in meinem Eingang": "ouvert dans ma boîte",
  "Geplante Beiträge ": "Publications planifiées ",
  "Umsatz-Übersicht": "Aperçu du chiffre d'affaires",
  "Wiederkehrender Monatsumsatz aktiver Kunden": "Revenu mensuel récurrent des clients actifs",
  "MRR · Monatsumsatz": "MRR · revenu mensuel",
  "Noch keine aktiven Abos.": "Aucun abonnement actif pour l'instant.",
  "KI-Kostenkontrolle": "Contrôle des coûts IA",
  "KI-Kosten": "Coûts IA",
  "KI-Kosten übersteigen den Umsatz": "Les coûts IA dépassent le chiffre d'affaires",
  "KI-Läufe": "Exécutions IA",
  "KI-Läufe am": "Exécutions IA le",
  "KI-Läufe pro Tag": "Exécutions IA par jour",
  "KI-Vorschläge": "Suggestions IA",
  "KI aktiv": "IA active",
  "KI pausiert": "IA en pause",
  "Bildbewertungen": "Évaluations d'images",
  "Vorschläge": "Suggestions",
  "Videos": "Vidéos",
  "Fehlgeschlagen": "Échec",
  "Globale Lauf-Auslastung": "Utilisation globale des exécutions",
  "Mandanten nahe am Limit": "Mandants proches de la limite",
  "Letzte KI-Aktivität": "Dernière activité IA",
  "Noch keine KI-Läufe in diesem Zeitraum": "Aucune exécution IA sur cette période",
  "Kosten: Abrechnung über reale Läufe (keine erfundenen Beträge)": "Coûts : facturation sur les exécutions réelles (aucun montant inventé)",
  "Kostenpflichtige KI-Läufe pausiert (globaler Kill-Switch)": "Exécutions IA payantes en pause (kill-switch global)",
  "KI-Kostensatz nicht konfiguriert — im Finanz-Cockpit einstellen.": "Taux de coût IA non configuré — à définir dans le cockpit financier.",
  "Verbunden": "Connecté",
  "Nicht verbunden": "Non connecté",
  "MRR": "MRR",
  "ARR": "ARR",
  "Demo-Zugänge": "Accès démo",
  "Anfragen von socialcheck.ch verwalten": "Gérer les demandes de socialcheck.ch",
  "Upgrade-Anfragen": "Demandes d'upgrade",
  "E-Mail verbunden": "E-mail connecté",
  "E-Mail nicht verbunden": "E-mail non connecté",
  "Neue Anfragen": "Nouvelles demandes",
  "Aktive Demos": "Démos actives",
  "Heute ablaufend": "Expirant aujourd'hui",
  "Abgelaufen": "Expiré",
  "Integration wartet auf Aktivierung": "Une intégration attend son activation",
  "Integrationen warten auf Aktivierung": "Des intégrations attendent leur activation",
  "Zugangsdaten getestet – Secret hinterlegen & Backend neu starten.": "Identifiants testés – enregistrer le secret & redémarrer le backend.",
  "Lückenloses Protokoll aller Aktionen und Ampel-Änderungen.": "Journal complet de toutes les actions et changements de feu.",
  "Nur Integrationen": "Intégrations uniquement",
  "Nur Kundenansicht (Super Admin)": "Vue client uniquement (super admin)",
  "Aktueller Mandant": "Mandant actuel",
  "Integrations-Ereignis": "Événement d'intégration",
  "Vom Super Admin in der Kundenansicht ausgeführt": "Exécuté par le super admin en vue client",
  "SA · Kundenansicht": "SA · vue client",
  "Einträgen": "entrées",
  "Ampel": "Feu",
  "Beitrag erstellt": "Publication créée",
  "Beitrag bearbeitet": "Publication modifiée",
  "Beitrag gelöscht": "Publication supprimée",
  "Beitrag dupliziert": "Publication dupliquée",
  "Beitrag archiviert": "Publication archivée",
  "Beitrag wiederhergestellt": "Publication restaurée",
  "Automatisch veröffentlicht": "Publié automatiquement",
  "Kommentar": "Commentaire",
  "Mitglied hinzugefügt": "Membre ajouté",
  "Mitglied entfernt": "Membre supprimé",
  "Rolle geändert": "Rôle modifié",
  "Mandant erstellt": "Mandant créé",
  "Benachrichtigungen geändert": "Notifications modifiées",
  "Integration verbunden": "Intégration connectée",
  "Integration getestet": "Intégration testée",
  "Zugangsdaten geprüft": "Identifiants vérifiés",
  "Aktivierung offen": "Activation en attente",
  "Integrationen neu geprüft": "Intégrations revérifiées",
  "Kanal-Buchung geändert": "Réservation de canal modifiée",
  "Integrationen migriert": "Intégrations migrées",
  "Meta-OAuth gestartet": "OAuth Meta démarré",
  "Meta verbunden": "Meta connecté",
  "Meta-Asset ausgewählt": "Ressource Meta sélectionnée",
  "Meta neu geprüft": "Meta revérifié",
  "Meta getrennt": "Meta déconnecté",
  "Meta-Überwachung: Aufgabe": "Surveillance Meta : tâche",
  "Meta-Überwachung konfiguriert": "Surveillance Meta configurée",
  "Systemstatus": "État du système",
  "Ehrlicher Live-Status — echte Prüfungen, keine statischen Werte.": "État en direct honnête — vérifications réelles, aucune valeur statique.",
  "Letzte Prüfung:": "Dernière vérification :",
  "Auto-Aktualisierung alle 60 s": "Actualisation auto toutes les 60 s",
  "Versionen & Wiederherstellung": "Versions & restauration",
  "Systemstatus konnte nicht geladen werden": "Impossible de charger l'état du système",
  "Systemstatus konnte nicht geladen werden.": "Impossible de charger l'état du système.",
  "Serverantwort": "Réponse du serveur",
  "Status wird geprüft…": "Vérification de l'état…",
  "Kerndienste OK:": "Services principaux OK :",
  "Integrationen verbunden": "Intégrations connectées",
  "Kerndienste": "Services principaux",
  "Operative Kennzahlen": "Indicateurs opérationnels",
  "Offene Leads": "Leads ouverts",
  "Offene Fotoaufträge": "Missions photo ouvertes",
  "Inhalte zur Freigabe": "Contenus à valider",
  "OK": "OK",
  "Eingeschränkt": "Limité",
  "Betrieb normal — alle Kerndienste laufen.": "Fonctionnement normal — tous les services principaux tournent.",
  "Betrieb eingeschränkt — ein Kerndienst meldet eine Warnung.": "Fonctionnement limité — un service principal signale un avertissement.",
  "Störung — mindestens ein Kerndienst ist nicht verfügbar.": "Panne — au moins un service principal est indisponible.",
  "„Nicht verbunden\" ist kein Fehler — diese Dienste werden nach Eintragen der Schlüssel automatisch aktiv.": "« Non connecté » n'est pas une erreur — ces services s'activent automatiquement dès que les clés sont saisies.",
  "Kein Betreuer": "Sans responsable",
  "Nur Kunden ohne persönlichen KEWAG-Betreuer anzeigen": "Afficher uniquement les clients sans responsable KEWAG personnel",
  "Entwürfe fortsetzen": "Continuer les brouillons",
  "Entwurf verworfen": "Brouillon supprimé",
  "Nachfassen fällig": "Relance à faire",
  "Nachfassen": "Relancer",
  "Annehmen": "Accepter",
  "Ablehnen": "Refuser",
  "Zurücksetzen": "Réinitialiser",
  "Zum Kunden": "Vers le client",
  "In Kunde umwandeln": "Convertir en client",
  "Angenommen": "Accepté",
  "Abgelehnt": "Refusé",
  "Gesendet": "Envoyé",
  "angenommen": "acceptées",
  "abgelehnt": "refusées",
  "nachfassen fällig": "relance à faire",
  "Offenes Volumen:": "Volume ouvert :",
  "Abschlussquote:": "Taux de conversion :",
  "Ø bis Abschluss:": "Ø jusqu'à la conclusion :",
  "Gewonnen (Monat):": "Gagné (mois) :",
  "Trend · 6 Monate": "Tendance · 6 mois",
  "Gewonnen": "Gagné",
  "Verloren": "Perdu",
  "Gefiltert auf": "Filtré sur",
  "Angebote ausblenden": "Masquer les offres",
  "Die E-Mail-Nachfass-Erinnerung (an Kunde + intern) wird automatisch versendet, sobald Brevo verbunden ist. Die Übersicht hier ist bereits live.": "Le rappel de relance par e-mail (au client + interne) est envoyé automatiquement dès que Brevo est connecté. L'aperçu ici est déjà en direct.",
  "Verworfen": "Ignoré",
  "Kein persönlicher Betreuer": "Aucun responsable personnel",
  "Kein persönlicher KEWAG-Betreuer zugewiesen – der Kunde sieht nur den KEWAG-Team-Kontakt.": "Aucun responsable KEWAG personnel attribué – le client ne voit que le contact de l'équipe KEWAG.",
  "Dieser Kunde sieht aktuell nur den KEWAG-Team-Kontakt. Weisen Sie eine:n Hauptbetreuer:in zu.": "Ce client ne voit actuellement que le contact de l'équipe KEWAG. Attribuez un·e responsable principal·e.",
  "KEWAG-Team wählen…": "Choisir l'équipe KEWAG…",
  "Begrüssungs-E-Mail an den Kunden senden": "Envoyer un e-mail de bienvenue au client",
  "Begrüssungs-E-Mail an den Kunden gesendet": "E-mail de bienvenue envoyé au client",
  "Begrüssung gesendet": "Message de bienvenue envoyé",
  "Hauptbetreuer zugewiesen": "Responsable principal attribué",
  "Zuweisung ok – Begrüssungs-E-Mail konnte nicht gesendet werden.": "Attribution ok – l'e-mail de bienvenue n'a pas pu être envoyé.",
  "Brevo nicht verbunden": "Brevo non connecté",
  "Unprofitabel": "Non rentable",
  "Angebot angenommen": "Offre acceptée",
  "Angebot abgelehnt": "Offre refusée",
  "Status zurückgesetzt": "Statut réinitialisé",
  "Ihre Anfragen an das KEWAG-Team – wir sind für Sie da.": "Vos demandes à l'équipe KEWAG – nous sommes là pour vous.",
  "Neue Support-Anfrage": "Nouvelle demande de support",
  "Ihre Nachricht": "Votre message",
  "Support-Anfrage senden": "Envoyer la demande",
  "Keine Anfragen": "Aucune demande",
  "Sie haben eine Frage oder einen Wunsch? Erstellen Sie eine Anfrage – das KEWAG-Team kümmert sich darum.": "Vous avez une question ou un souhait ? Créez une demande – l'équipe KEWAG s'en occupe.",
  "Aktualisiert": "Mis à jour",
  "Anfrage auswählen": "Sélectionner une demande",
  "Anfrage": "Demande",
  "Ticket als PDF herunterladen": "Télécharger le ticket en PDF",
  "Als PDF": "En PDF",
  "Kundensicht ist nur-lesend – Antworten sind hier deaktiviert.": "La vue client est en lecture seule – les réponses sont désactivées ici.",
  "Diese Anfrage ist erledigt. Für ein neues Anliegen bitte eine neue Anfrage erstellen.": "Cette demande est terminée. Pour une nouvelle demande, veuillez en créer une nouvelle.",
  "Nachricht an das KEWAG-Team schreiben…": "Écrire un message à l'équipe KEWAG…",
  "Foto aufnehmen": "Prendre une photo",
  "Bild wählen": "Choisir une image",
  "PDF / Datei": "PDF / fichier",
  "Erlaubt: Bilder, PDF, Word, Excel, TXT, CSV · max. 20 MB pro Datei, 10 Anhänge.": "Autorisé : images, PDF, Word, Excel, TXT, CSV · max. 20 Mo par fichier, 10 pièces jointes.",
  "Dieser Dateityp ist nicht erlaubt.": "Ce type de fichier n'est pas autorisé.",
  "Datei zu gross – maximal 20 MB pro Datei.": "Fichier trop volumineux – maximum 20 Mo par fichier.",
  "Maximal 10 Anhänge pro Nachricht.": "Maximum 10 pièces jointes par message.",
  "Anhänge überschreiten 100 MB pro Nachricht.": "Les pièces jointes dépassent 100 Mo par message.",
  "Upload fehlgeschlagen": "Échec du téléversement",
  "Bitte warten, bis alle Anhänge hochgeladen sind.": "Veuillez attendre la fin du téléversement des pièces jointes.",
  "Anfrage gesendet – KEWAG meldet sich.": "Demande envoyée – KEWAG vous recontacte.",
  "(Anhang)": "(Pièce jointe)",
  "Datei nicht verfügbar": "Fichier non disponible",
  "PDF-Erstellung fehlgeschlagen": "Échec de la création du PDF",
  "Erneut": "Réessayer",
  "Fehler": "Erreur",
  "Erledigt": "Terminé",
  "Lädt…": "Chargement…",
  // Nav-Gruppen
  "Hauptmenü": "Menu principal", "Kommunikation": "Communication", "Abrechnung": "Facturation",
  "System": "Système", "Arbeit": "Travail", "Auswertung & Aufgaben": "Analyses & tâches",
  // Nav-Punkte
  "Übersicht": "Aperçu", "Leads": "Leads", "Kunden": "Clients", "Fotoaufträge": "Mandats photo",
  "Inhalte": "Contenus", "Freigaben": "Validations", "Kalender": "Calendrier",
  "Marken-Profil": "Profil de marque", "Reports": "Rapports", "Speicher & Medien": "Stockage & médias",
  "Support": "Support", "Nachrichten": "Messages", "Abos / Pakete": "Abonnements / forfaits",
  "Rechnungen": "Factures", "Integrationen": "Intégrations", "Benutzer & Rollen": "Utilisateurs & rôles",
  "Audit-Log": "Journal d'audit", "System Health": "État du système", "Einstellungen": "Paramètres",
  "Tagesübersicht": "Aperçu du jour", "WordPress-Leads": "Leads WordPress",
  "Inhalte bearbeiten": "Modifier les contenus", "Aufgaben": "Tâches", "Monatsreports": "Rapports mensuels",
  "Support-Tickets": "Tickets de support",
  // super_admin Nav (Golden Master) + Shell-Extras
  "Start": "Démarrage", "Vertrieb & Kunden": "Ventes & clients", "Content & Freigaben": "Contenu & validations",
  "Heute wichtig / Aufgaben": "Important aujourd'hui / tâches", "Leads & Verkauf": "Leads & ventes",
  "Angebote": "Offres", "Beiträge / Inhalte": "Publications / contenus", "Medien & Speicher": "Médias & stockage",
  "Marken-Profile": "Profils de marque", "Zahlungen": "Paiements", "Zusatzleistungen": "Prestations supplémentaires",
  "WordPress": "WordPress", "Backups": "Sauvegardes",
  "VORSCHAU · Testdaten": "APERÇU · Données de test", "Alle Rechte vorbehalten": "Tous droits réservés",
  "Datenschutz": "Confidentialité", "AGB": "CGV", "Impressum": "Mentions légales", "Hilfe & Support": "Aide & support",
  // Shell
  "Mandant": "Mandant", "Alle Mandanten": "Tous les mandants", "Mandant wechseln": "Changer de mandant",
  // Sidebar-Abschnitte (Admin + Cockpit)
  "Tägliche Arbeit": "Travail quotidien", "Kunden & Mandanten": "Clients & mandants",
  "Inhalte & Freigaben": "Contenus & validations", "Finanzen & Vertrieb": "Finances & ventes",
  "Kommunikation & Service": "Communication & service", "System & Sicherheit": "Système & sécurité",
  "Einstellungen & Konfiguration": "Paramètres & configuration",
  "Meine Übersicht": "Mon aperçu", "Inhalte & Planung": "Contenus & planification",
  "Abrechnung": "Facturation", "Service & Konto": "Service & compte",
  "Kein Mandant zugewiesen.": "Aucun mandant attribué.", "Ihre Rolle": "Votre rôle",
  "Angemeldet als": "Connecté en tant que",
  "roleHint": "Les rôles et droits sont gérés dans « Utilisateurs & rôles ».",
  "Super Admin": "Super Admin", "KEWAG Admin": "KEWAG Admin", "KEWAG Editor": "KEWAG Editor",
  "KEWAG Support": "KEWAG Support", "Kunde / Reviewer": "Client / relecteur", "Viewer": "Observateur",
  "Kunden-Cockpit öffnen": "Ouvrir le cockpit client", "Benachrichtigungen": "Notifications",
  "Abmelden": "Se déconnecter", "Menü einklappen": "Réduire le menu", "Menü ausklappen": "Déplier le menu",
  "Kundensicht aktiv:": "Vue client active :",
  "— du siehst die App als dieser Kunde.": "— vous voyez l'app en tant que ce client.",
  "Zurück zur Admin-Ansicht": "Retour à la vue admin",
  // Dashboard
  "Guten Morgen": "Bonjour", "Guten Tag": "Bonjour", "Guten Abend": "Bonsoir",
  "Hier ist die aktuelle Live-Übersicht": "Voici l'aperçu en direct actuel", "für": "pour",
  "Bericht erstellen": "Créer un rapport", "Neue Leads": "Nouveaux leads", "Zur Freigabe": "À valider",
  "Geplante Beiträge": "Publications planifiées", "Offene Tickets": "Tickets ouverts",
  "heute": "aujourd'hui", "aktuell": "actuel", "Sehr gut": "Très bon", "Gut": "Bon", "Achtung": "Attention",
  "Neue Leads aus socialcheck.ch": "Nouveaux leads de socialcheck.ch",
  "Fotoaufträge vom Handy": "Mandats photo depuis le mobile", "System Status": "État du système",
  "Freigaben (Ampel)": "Validations (feu)",
  "Geplante Beiträge (nächste 7 Tage)": "Publications planifiées (7 prochains jours)",
  "Offene Support-Tickets": "Tickets de support ouverts", "Mandanten-Übersicht": "Aperçu des mandants",
  "Aktivität": "Activité", "Alle Leads": "Tous les leads", "Alle anzeigen": "Tout afficher",
  "Details": "Détails", "Öffnen": "Ouvrir", "Alle Kunden": "Tous les clients",
  "Keine neuen Leads.": "Aucun nouveau lead.", "Keine Fotoaufträge.": "Aucun mandat photo.",
  "Keine offenen Tickets.": "Aucun ticket ouvert.", "Keine Aktivität.": "Aucune activité.",
  "Lädt aktuelle Daten…": "Chargement des données…",
  "Rot · Entwurf": "Rouge · Brouillon", "Orange · Zur Freigabe": "Orange · À valider",
  "Grün · Freigegeben": "Vert · Validé", "Blau · Veröffentlicht": "Bleu · Publié", "Gesamt": "Total",
  "Paket": "Forfait", "Status": "Statut", "Beiträge": "Publications", "Geplant": "Planifié",
  "Nächste Aktion": "Prochaine action", "Zeige": "Affiche", "bis": "à", "von": "sur", "Mandanten": "mandants",
  "Zurück": "Précédent", "Weiter": "Suivant",
  "Demo-Anfrage": "Demande démo", "Paket-Anfrage": "Demande forfait", "Kontakt": "Contact",
  "Website-Check": "Check du site", "Beratung": "Conseil", "Zugang": "Accès",
  "Zur Prüfung": "À vérifier", "Freigegeben": "Validé", "Veröffentlicht": "Publié",
  "Hoch": "Élevée", "Mittel": "Moyenne", "Niedrig": "Faible",
  "In Arbeit": "En cours", "Aktiv": "Actif",
  // Kunden
  "Kunde": "Client", "Testphase": "Phase d'essai", "Ausstehend": "En attente", "Gekündigt": "Résilié",
  "seit": "depuis", "diesen Monat": "ce mois", "ø unverändert": "ø inchangé",
  "Aktive Kunden": "Clients actifs", "Kunden in Testphase": "Clients en essai",
  "Ausstehende Aktivierung": "Activation en attente", "Gekündigte Kunden": "Clients résiliés",
  "Kundenverwaltung": "Gestion des clients",
  "Alle Kunden, Abos und Aktivitäten zentral im Blick.": "Tous les clients, abonnements et activités en un coup d'œil.",
  "Exportieren": "Exporter", "exportiert": "exportés", "Verantwortlich": "Responsable", "Alle": "Tous",
  "Suche nach Kunde, Domain, E-Mail…": "Rechercher client, domaine, e-mail…",
  "Weitere Filter": "Plus de filtres", "Erweiterte Filter folgen in Kürze.": "Filtres avancés bientôt disponibles.",
  "Kontakt": "Contact", "Nächster Schritt": "Prochaine étape", "Aktionen": "Actions",
  "Keine Kunden gefunden.": "Aucun client trouvé.", "Details anzeigen": "Afficher les détails",
  "Cockpit öffnen": "Ouvrir le cockpit", "Abo verwalten": "Gérer l'abonnement",
  "Kundendetails": "Détails client", "Kundennummer:": "N° client :", "Zum Kunden-Cockpit": "Vers le cockpit client",
  "Vertrag": "Contrat", "Rechnung": "Facture", "Notizen": "Notes", "Mehr": "Plus", "Verlauf": "Historique",
  "Firma": "Entreprise", "Telefon": "Téléphone", "Sprache": "Langue", "Vertrag seit": "Contrat depuis",
  "Nächste Rechnung": "Prochaine facture", "Paket ändern": "Changer de forfait", "Nutzung": "Utilisation",
  "Benutzer": "Utilisateurs", "Speicher": "Stockage", "API-Aufrufe (Monat)": "Appels API (mois)",
  "Nicht erfasst": "Non suivi", "Bearbeiten": "Modifier", "Keine Notizen hinterlegt.": "Aucune note enregistrée.",
  "Vertrag gestartet": "Contrat démarré", "geplant": "planifié", "Keine offenen Aufgaben.": "Aucune tâche ouverte.",
  "Bearbeitung im Kunden-Cockpit möglich.": "Modifiable dans le cockpit client.",
  "Offene Freigaben": "Validations ouvertes", "Fotoaufträge zu prüfen": "Mandats photo à vérifier",
  "Heute, ": "Aujourd'hui, ", "Morgen, ": "Demain, ",
  // Support
  "Tickets zwischen": "Tickets entre", "KEWAG und Kunden": "KEWAG et clients", "dir und KEWAG": "vous et KEWAG",
  "Neues Ticket": "Nouveau ticket", "Support-Anfrage": "Demande de support", "Betreff": "Objet",
  "Priorität": "Priorité", "Nachricht": "Message", "Senden": "Envoyer", "Normal": "Normal",
  "Betreff und Nachricht erforderlich": "Objet et message requis", "Ticket erstellt": "Ticket créé",
  "Keine Tickets. Erstelle ein neues Ticket bei Fragen.": "Aucun ticket. Créez-en un si vous avez des questions.",
  "Offen": "Ouvert", "In Bearbeitung": "En traitement", "Geschlossen": "Fermé",
  "Antworten…": "Répondre…", "Antwort senden": "Envoyer la réponse", "Status aktualisiert": "Statut mis à jour",
  "Ampel-Kurzhilfe": "Aide-mémoire feu", "Rot": "Rouge", "Grün": "Vert", "Blau": "Bleu",
  "Entwurf / intern.": "Brouillon / interne.", "Zur Kundenfreigabe.": "Pour validation client.",
  "Freigegeben / bereit / live.": "Validé / prêt / en ligne.", "Veröffentlicht / reportet.": "Publié / rapporté.",
  "Lädt…": "Chargement…",
  "Nachricht(en)": "message(s)",
  // ---- KundenDetail ----
  "Kunde seit": "Client depuis", "Kundensicht öffnen": "Ouvrir la vue client", "Zu Freigaben": "Vers les validations",
  "Ampel-Verteilung": "Répartition des feux",
  "Rot – Entwurf / intern": "Rouge – Brouillon / interne", "Orange – Zur Kundenfreigabe": "Orange – Validation client",
  "Grün – Freigegeben / bereit / live": "Vert – Validé / prêt / en ligne", "Blau – Veröffentlicht / reportet": "Bleu – Publié / rapporté",
  "Abo & nächste Aktion": "Abonnement & prochaine action", "Abo-Status": "Statut de l'abonnement", "Preis": "Prix",
  "Nächste Aktion:": "Prochaine action :", "Freigaben offen": "validations ouvertes", "keine offene Aktion": "aucune action ouverte",
  "Mandanten-Details": "Détails du mandant", "Kunden-Cockpit": "Cockpit client",
  "Direkt zum öffentlichen Bereich dieses Kunden.": "Accès direct à l'espace public de ce client.",
  "Direkt zum Kunden-Cockpit": "Directement au cockpit client", "Ansprechpartner": "Personne de contact",
  "E-Mail": "E-mail", "Website": "Site web", "Sprachen": "Langues", "Vertragsstatus": "Statut du contrat",
  "Vertrag bis": "Contrat jusqu'au", "monatlich kündbar": "résiliable mensuellement", "Storage": "Stockage",
  "API Calls (Monat)": "Appels API (mois)", "Details & Einstellungen": "Détails & paramètres",
  "Letzte Aktivitäten": "Activité récente", "Noch keine Aktivitäten protokolliert.": "Aucune activité enregistrée.",
  "Nächste Schritte": "Prochaines étapes", "Alle Aufgaben": "Toutes les tâches",
  "Monatsreport prüfen": "Vérifier le rapport mensuel", "Report ist bereit zur Prüfung.": "rapport prêt à être vérifié.",
  "Neues Shooting einplanen": "Planifier un nouveau shooting", "Nächsten Fotoauftrag planen und briefen.": "Planifier et briefer le prochain mandat photo.",
  "Rechnungsstatus prüfen": "Vérifier le statut de facturation", "Nächste Rechnung am": "Prochaine facture le", "fällig.": "exigible.",
  "Anzeigen": "Afficher", "Planen": "Planifier", "Tickets offen": "Tickets ouverts", "Änderungswünsche": "Demandes de modification",
  "vs. Vormonat": "vs mois préc.",
  "Entwürfe": "Brouillons", "Warten auf Freigabe": "En attente de validation", "Veröffentlicht diesen Monat": "Publié ce mois",
  "Beitrag erstellen": "Créer une publication", "Kanal": "Canal", "Alle Kanäle": "Tous les canaux", "Alle Status": "Tous les statuts",
  "Entwurf": "Brouillon", "Zeitraum": "Période", "Liste": "Liste", "Planung": "Planification",
  "Beiträge suchen...": "Rechercher des publications...", "Filter zurücksetzen": "Réinitialiser les filtres",
  "Beitrag": "Publication", "Geplant für": "Planifié pour", "Keine Beiträge gefunden.": "Aucune publication trouvée.",
  "Vorschau": "Aperçu", "Freigeben": "Valider", "Als Entwurf": "En brouillon",
  "Keinen Beitrag ausgewählt.": "Aucune publication sélectionnée.", "Beitrags-Vorschau & Details": "Aperçu & détails de la publication",
  "Beitragstext": "Texte de la publication", "Mehr anzeigen": "Afficher plus", "Weniger anzeigen": "Afficher moins", "Kanäle": "Canaux",
  "Geplanter Veröffentlichungstermin": "Date de publication planifiée", "Nicht geplant": "Non planifié", "Medien": "Médias",
  "Wartet auf Freigabe": "En attente de validation", "Heute fällig": "À échéance aujourd'hui", "Diesen Monat freigegeben": "Validé ce mois",
  "Freigabe-Eingang": "Boîte de validation", "Sortierung: Fälligkeitsdatum": "Tri : échéance", "Sortierung: Status": "Tri : statut",
  "Alle Freigaben anzeigen": "Voir toutes les validations", "von:": "par :",
  "Keine Freigabe ausgewählt.": "Aucune validation sélectionnée.", "Live-Vorschau": "Aperçu en direct", "In neuem Tab öffnen": "Ouvrir dans un nouvel onglet",
  "Content-Check": "Vérification du contenu", "Keine Prüfung hinterlegt.": "Aucune vérification enregistrée.",
  "Erstellt von": "Créé par", "Erstellt am": "Créé le", "Geplant am": "Planifié le", "Anhang": "Pièce jointe",
  "Entscheidung & Kundenfeedback": "Décision & retour client", "Kundenfeedback": "Retour client",
  "Noch kein Kundenfeedback.": "Aucun retour client pour l'instant.", "Frist": "Délai",
  "Inhalt ohne Änderungen freigeben und publizieren.": "Valider et publier le contenu sans modifications.",
  "Freigabe mit Auflagen": "Valider avec conditions",
  "Mit Kommentaren freigeben und zur Umsetzung zurücksenden.": "Valider avec commentaires et renvoyer pour mise en œuvre.",
  "Nicht freigeben": "Ne pas valider", "Inhalt ablehnen und zur Überarbeitung zurücksenden.": "Refuser le contenu et renvoyer pour révision.",
  "Freigabe-Verlauf": "Historique des validations", "Gesamten Verlauf anzeigen": "Voir tout l'historique",
  "Offene Aufträge": "Mandats ouverts", "gesamt": "au total", "Diese Woche geplant": "Planifié cette semaine",
  "Uploads ausstehend": "Uploads en attente", "Bilder": "Images", "Bilder freigegeben": "Images validées",
  "Neuer Auftrag": "Nouveau mandat", "Abgeschlossen": "Terminé", "Archiv": "Archives", "Keine Aufträge.": "Aucun mandat.",
  "Alle Fotoaufträge anzeigen": "Voir tous les mandats photo", "Kein Auftrag ausgewählt.": "Aucun mandat sélectionné.",
  "Briefing & Shotlist": "Briefing & liste de plans", "Briefing bearbeiten": "Modifier le briefing", "Ziel des Shootings": "Objectif du shooting",
  "Shooting-Datum": "Date du shooting", "Fotograf": "Photographe", "Benötigte Motive": "Motifs requis",
  "Mood & Referenzen": "Ambiance & références", "Referenz": "Référence", "Ablauf & Timing": "Déroulé & timing",
  "Briefing offen": "Briefing ouvert", "Termin fixiert": "Date fixée", "Shooting erfolgt": "Shooting effectué",
  "Auswahl offen": "Sélection ouverte", "Final freigegeben": "Validé final", "Storniert": "Annulé",
  "Uploads & Auswahl": "Uploads & sélection", "Upload-Status": "Statut de l'upload", "Bilder hochgeladen": "images téléchargées",
  "Letzter Upload:": "Dernier upload :", "Upload öffnen": "Ouvrir l'upload", "Auswahl & Freigabe": "Sélection & validation",
  "Auswahlstatus": "Statut de sélection", "Vom Kunden ausgewählt": "Sélectionné par le client", "Kunden-Feedback": "Retour client",
  "Kunde zur Auswahl senden": "Envoyer au client pour sélection", "Finalisierung": "Finalisation",
  "Nach Kundenfreigabe Final auswählen & freigeben.": "Après validation du client, sélectionner et valider le final.",
  "Final freigeben": "Valider le final", "Duplizieren": "Dupliquer", "Archivieren": "Archiver", "Abbrechen": "Annuler",
  "Hohe Priorität": "Priorité élevée", "Warten auf Kunde": "En attente du client", "Gelöst diesen Monat": "Résolu ce mois",
  "Ticket-Queue": "File des tickets", "In Bearb.": "En cours", "Warten": "Attente", "Gelöst": "Résolu",
  "Tickets durchsuchen...": "Rechercher des tickets...", "Keine Tickets.": "Aucun ticket.", "Alle Tickets anzeigen": "Voir tous les tickets",
  "Kritisch": "Critique", "Kein Ticket ausgewählt.": "Aucun ticket sélectionné.", "Erstellt:": "Créé :",
  "Antwort an Kunde": "Répondre au client", "Interne Notiz": "Note interne",
  "Interne Notiz schreiben (nur für das Team sichtbar)...": "Rédiger une note interne (visible par l'équipe uniquement)...",
  "Nachricht an den Kunden schreiben...": "Écrire un message au client...", "Anhänge": "Pièces jointes",
  "Dateien anhängen oder hier ablegen": "Joindre des fichiers ou déposer ici", "Notiz speichern": "Enregistrer la note",
  "Ticket-Details": "Détails du ticket", "Ziel:": "Objectif :", "Fällig:": "Échéance :", "Zugewiesen an": "Assigné à",
  "Modul": "Module", "Kategorie": "Catégorie", "Login & Zugriff": "Connexion & accès", "Veröffentlichung": "Publication",
  "Technik": "Technique", "Änderungswunsch": "Demande de modification", "Allgemein": "Général", "Letzte Aktivität": "Dernière activité",
  "Erstellt": "Créé", "Schnellaktionen": "Actions rapides", "Ticket zuweisen": "Assigner le ticket", "Ticket schliessen": "Fermer le ticket",
  "Offener Betrag": "Montant ouvert", "Rechnungen offen": "factures ouvertes", "Fällige Rechnungen": "Factures échues",
  "Rechnung überfällig": "facture en retard", "Rechnungen überfällig": "factures en retard", "Bezahlt diesen Monat": "Payé ce mois",
  "Nächste Abbuchung": "Prochain prélèvement", "Abo:": "Abo :", "Suchen…": "Rechercher…", "Bezahlt": "Payé", "Überfällig": "En retard",
  "In Mahnung": "En relance", "Rechnung #": "Facture #", "Betrag": "Montant", "Fällig am": "Échéance", "Aktion": "Action",
  "Erinnern": "Relancer", "Bezahlen": "Payer", "Keine Rechnungen für diesen Filter.": "Aucune facture pour ce filtre.",
  "/ Seite": "/ page", "Zahlungsverlauf": "Historique des paiements", "Datum": "Date", "Beschreibung": "Description",
  "Beleg/Rechnung": "Reçu/Facture", "Methode": "Méthode", "Erfolgreich": "Réussi", "Noch keine Zahlungen.": "Aucun paiement.",
  "Alle Zahlungen anzeigen": "Voir tous les paiements", "Abo & Zahlung": "Abonnement & paiement", "Monatlicher Preis": "Prix mensuel",
  "Zahlungsmethode": "Méthode de paiement", "Rechnungsadresse": "Adresse de facturation", "Aktueller Mahnstatus": "Statut de relance actuel",
  "Nutzung (aktuell)": "Utilisation (actuelle)", "Offene Posten & Aktionen": "Postes ouverts & actions", "Gesamt offen": "Total ouvert",
  "davon überfällig": "dont en retard", "Rechnung senden": "Envoyer la facture", "Zahlung prüfen": "Vérifier le paiement",
  "Mahnung senden": "Envoyer une relance", "Paket anpassen": "Ajuster le forfait",
  "Stripe/TWINT vorbereitet — nicht verbunden. Aktionen werden im Protokoll erfasst.": "Stripe/TWINT préparé — non connecté. Les actions sont enregistrées dans le journal.",
  "Branche": "Secteur", "Tonalität": "Ton", "Typische Begriffe": "Termes typiques", "Noch kein Marken-Profil hinterlegt.": "Aucun profil de marque enregistré.",
  "Noch keine Benutzer.": "Aucun utilisateur.", "Name": "Nom", "Rolle": "Rôle",
  "Benutzer & Rollen werden unter „Benutzer & Rollen\" verwaltet.": "Les utilisateurs & rôles sont gérés dans « Utilisateurs & rôles ».",
  "Kein Protokoll vorhanden.": "Aucun journal disponible.", "Kein Abo": "Aucun abonnement",
  "Kein Ansprechpartner für Kundensicht vorhanden": "Aucun contact pour la vue client", "Kundensicht geöffnet": "Vue client ouverte",
  "Ampel-Status aktualisiert": "Statut du feu mis à jour", "Inhalt freigegeben": "Contenu validé",
  "Mit Auflagen zurückgesendet": "Renvoyé avec conditions", "Inhalt abgelehnt": "Contenu refusé",
  "Auswahl an Kunden gesendet": "Sélection envoyée au client", "Final freigegeben (Meldung)": "Validé final",
  "Auftrag archiviert": "Mandat archivé", "Auftrag dupliziert": "Mandat dupliqué", "Auftrag abgebrochen": "Mandat annulé",
  "Aktion ausgeführt": "Action exécutée", "Interne Notiz gespeichert": "Note interne enregistrée",
  "Antwort an Kunde gesendet": "Réponse envoyée au client", "Ticket aktualisiert": "Ticket mis à jour",
  "Neuer Fotoauftrag – Formular folgt.": "Nouveau mandat photo – formulaire à venir.", "Upload-Bereich folgt.": "Zone d'upload à venir.",
  "Zuweisung – Auswahl folgt.": "Attribution – sélection à venir.", "Mehr Filter": "Plus de filtres",
  "Inhalte warten auf Kundenfreigabe.": "Contenus en attente de validation client.", "Region": "Région", "Location": "Lieu", "mehr": "plus",
  // --- RollenRechte Phase-3 FR ---
  "2FA aktiv": "2FA active",
  "2FA für diese Rolle verpflichtend": "2FA obligatoire pour ce rôle",
  "2FA für diesen Benutzer verpflichtend": "2FA obligatoire pour cet utilisateur",
  "2FA inaktiv": "2FA inactive",
  "2FA ist aktiv": "La 2FA est active",
  "2FA ist nicht eingerichtet": "La 2FA n'est pas configurée",
  "2FA verwalten": "Gérer la 2FA",
  "2FA wurde zurückgesetzt": "La 2FA a été réinitialisée",
  "2FA zurücksetzen (Gerät verloren)": "Réinitialiser la 2FA (appareil perdu)",
  "2FA-Pflicht": "2FA obligatoire",
  "2FA-Pflicht (Rollen) gespeichert": "2FA obligatoire (rôles) enregistrée",
  "2FA-Pflicht entfernt": "Obligation 2FA retirée",
  "2FA-Pflicht für Benutzer aktiviert": "2FA obligatoire activée pour l'utilisateur",
  "2FA-Pflicht für alle setzen": "Rendre la 2FA obligatoire pour tous",
  "2FA-Pflicht offen": "2FA obligatoire en attente",
  "2FA-Pflicht pro Rolle": "2FA obligatoire par rôle",
  "2FA: Alle": "2FA : toutes",
  "Abgelaufene Einladungen": "Invitations expirées",
  "Akteur / Ziel suchen…": "Rechercher acteur / cible…",
  "Aktion: Alle": "Action : toutes",
  "Aktive Kunden / Mandanten": "Clients / mandants actifs",
  "Aktive Mandanten ohne Freigabe-Berechtigten.": "Mandants actifs sans responsable d'approbation.",
  "Aktive Rollen": "Rôles actifs",
  "Aktivieren": "Activer",
  "Aktiviert": "Activé",
  "Aktivierungslink ist abgelaufen – erneut senden.": "Le lien d'activation a expiré – renvoyer.",
  "Aktuell keine Zugriffsprobleme – es würde keine Mail versendet.": "Aucun problème d'accès actuellement – aucun e-mail ne serait envoyé.",
  "Aktueller Status": "Statut actuel",
  "Alle Benutzer sind zugewiesen.": "Tous les utilisateurs sont affectés.",
  "Alle Bereiche deaktiviert: Dieser Editor kann keine Kundendaten bearbeiten.": "Tous les domaines désactivés : cet éditeur ne peut modifier aucune donnée client.",
  "Alle Rollen & Rechte": "Tous les rôles et droits",
  "Alle Rollen & Rechte anzeigen": "Afficher tous les rôles et droits",
  "Alle Team-Mitglieder haben 2FA aktiv.": "Tous les membres de l'équipe ont la 2FA active.",
  "Alle aktiven Kunden haben einen Reviewer.": "Tous les clients actifs ont un réviseur.",
  "Alle mit 2FA": "Tous avec 2FA",
  "Alle rollen- und benutzerbezogenen Änderungen.": "Toutes les modifications liées aux rôles et aux utilisateurs.",
  "Alle sichtbaren auswählen": "Sélectionner tous les visibles",
  "Auch mit Freigabe kann der Editor kritische Felder nicht ändern — nur nicht-kritische Angaben im jeweiligen Bereich.": "Même avec l'autorisation, l'éditeur ne peut pas modifier les champs critiques — uniquement les informations non critiques du domaine concerné.",
  "Audit-Log exportiert": "Journal d'audit exporté",
  "Ausstehende Aktionen": "Actions en attente",
  "Auswahl aufheben": "Annuler la sélection",
  "Bearbeitungsrechte gespeichert.": "Droits de modification enregistrés.",
  "Beginn, Laufzeit, Kündigungsfrist, Intervall, Verlängerung": "Début, durée, délai de résiliation, intervalle, renouvellement",
  "Beim nächsten Login wird die Einrichtung erzwungen.": "La configuration sera imposée à la prochaine connexion.",
  "Benutzer aktiviert": "Utilisateur activé",
  "Benutzer aktualisiert": "Utilisateur mis à jour",
  "Benutzer bearbeiten": "Modifier l'utilisateur",
  "Benutzer deaktiviert": "Utilisateur désactivé",
  "Benutzer einladen": "Inviter un utilisateur",
  "Benutzer exportiert": "utilisateurs exportés",
  "Benutzer löschen": "Supprimer l'utilisateur",
  "Benutzer ohne Zuweisung": "Utilisateurs sans affectation",
  "Benutzer suchen…": "Rechercher un utilisateur…",
  "Benutzer verwalten, Rollen steuern, Kunden zuweisen und Einladungen zentral bearbeiten.": "Gérer les utilisateurs, contrôler les rôles, affecter des clients et gérer les invitations de manière centralisée.",
  "Benutzerdetails": "Détails de l'utilisateur",
  "Benutzern": "utilisateurs",
  "Bereich": "Domaine",
  "Bereich / Team": "Domaine / équipe",
  "Bereich / Team: Alle": "Domaine / équipe : tous",
  "Betreuer: Alle": "Responsable : tous",
  "Betreuung": "Responsable",
  "Bitte einen Kunden / Mandanten wählen": "Veuillez choisir un client / mandant",
  "Brevo nicht verbunden – Vorschau-Modus": "Brevo non connecté – mode aperçu",
  "Das Konto, alle Sitzungen und Kundenzuweisungen werden entfernt. Diese Aktion kann nicht rückgängig gemacht werden.": "Le compte, toutes les sessions et affectations client seront supprimés. Cette action est irréversible.",
  "Deaktivieren": "Désactiver",
  "Deaktiviert oder wegen Fehlversuchen gesperrt.": "Désactivé ou bloqué en raison de tentatives échouées.",
  "Details ansehen": "Voir les détails",
  "Editor darf nur zugewiesene Kunden bearbeiten. Kritische Felder (Paket, Preis, Vertrag, Rechnung, Sperre, Login-E-Mail) bleiben ausschliesslich dem Super Admin vorbehalten.": "L'éditeur ne peut modifier que les clients qui lui sont affectés. Les champs critiques (forfait, prix, contrat, facture, blocage, e-mail de connexion) restent exclusivement réservés au super administrateur.",
  "Effektive Rechte": "Droits effectifs",
  "Eingeschr.": "Limité",
  "Einladung abgelaufen": "Invitation expirée",
  "Einladung erneut senden": "Renvoyer l'invitation",
  "Einladung gesendet": "Invitation envoyée",
  "Einladung neu erstellt (Brevo nicht verbunden – kein Versand)": "Invitation recréée (Brevo non connecté – pas d'envoi)",
  "Einladung per E-Mail erneut gesendet": "Invitation renvoyée par e-mail",
  "Einladungen": "Invitations",
  "Einladungen aktiviert": "invitations activées",
  "Einladungen neu gesendet": "Invitations renvoyées",
  "Einladungsstatus": "Statut de l'invitation",
  "Einladungsstatus: Alle": "Statut d'invitation : tous",
  "Einträge": "entrées",
  "Empfänger": "destinataires",
  "Empfänger gesendet.": "destinataire(s).",
  "Report an": "Rapport envoyé à",
  "Endgültig löschen": "Supprimer définitivement",
  "Erneut senden": "Renvoyer",
  "Filter aufheben": "Réinitialiser le filtre",
  "Firmenname, Domain, Adresse, Betreuer, Notiz, Sprache": "Nom de l'entreprise, domaine, adresse, responsable, note, langue",
  "Gefiltert: nur Mitglieder ohne aktives 2FA": "Filtré : uniquement les membres sans 2FA active",
  "Gesamtbenutzer": "Nombre total d'utilisateurs",
  "Gestern": "Hier",
  "Gilt systemweit über alle Mandanten – keine Mandantenzuweisung nötig. „Super Admin\" ist reserviert und nicht vergebbar.": "S'applique à l'échelle du système à tous les mandants – aucune affectation de mandant nécessaire. « Super Admin » est réservé et non attribuable.",
  "Global": "Global",
  "Globale (Team-/System-)Rollen kann nur der Super Admin vergeben.": "Seul le super administrateur peut attribuer les rôles globaux (équipe/système).",
  "Globale Rolle": "Rôle global",
  "Globale Rolle (KEWAG-Team / System)": "Rôle global (équipe KEWAG / système)",
  "Hauptbetreuer": "Responsable principal",
  "Hauptbetreuer ändern": "Changer le responsable principal",
  "Heute": "Aujourd'hui",
  "Hinweis: Gilt bereits über die Rollen-Pflicht.": "Remarque : déjà appliqué via l'obligation de rôle.",
  "Inaktive / gesperrte Konten": "Comptes inactifs / bloqués",
  "Interne KEWAG-Mitarbeitende mit Zugriff auf alle Mandanten. Kunden-Nutzer findest du weiter unten unter „Benutzerverwaltung\".": "Collaborateurs internes KEWAG ayant accès à tous les mandants. Les utilisateurs clients se trouvent plus bas sous « Gestion des utilisateurs ».",
  "Jetzt testen/senden": "Tester/envoyer maintenant",
  "KEWAG-Team / Mitarbeiter": "Équipe KEWAG / collaborateurs",
  "Keine": "Aucune",
  "Keine Benutzer.": "Aucun utilisateur.",
  "Keine Einträge gefunden.": "Aucune entrée trouvée.",
  "Keine Kunden zugewiesen.": "Aucun client affecté.",
  "Keine Kundenbenutzer gefunden.": "Aucun utilisateur client trouvé.",
  "Keine Rolle/Zuweisung – keine Rechte.": "Aucun rôle/affectation – aucun droit.",
  "Keine abgelaufenen Einladungen.": "Aucune invitation expirée.",
  "Keine inaktiven Konten.": "Aucun compte inactif.",
  "Keine offenen Einladungen.": "Aucune invitation en attente.",
  "Keine Änderungen protokolliert.": "Aucune modification enregistrée.",
  "Klicken: nur Mitglieder ohne aktives 2FA anzeigen": "Cliquer : afficher uniquement les membres sans 2FA active",
  "Kontaktdaten": "Coordonnées",
  "Konten, Sitzungen und Kundenzuweisungen werden entfernt. Dein eigenes Konto und der letzte Super Admin werden automatisch übersprungen. Diese Aktion kann nicht rückgängig gemacht werden.": "Les comptes, sessions et affectations client seront supprimés. Votre propre compte et le dernier super administrateur sont automatiquement ignorés. Cette action est irréversible.",
  "Kunde: Alle": "Client : tous",
  "Kunden & Zuweisungen": "Clients et affectations",
  "Kunden entfernen": "Retirer des clients",
  "Kunden hinzufügen": "Ajouter des clients",
  "Kunden ohne Reviewer": "Clients sans réviseur",
  "Kunden suchen…": "Rechercher des clients…",
  "Kunden zuweisen": "Affecter des clients",
  "Kunden-Cockpit konnte nicht geöffnet werden": "Impossible d'ouvrir le cockpit client",
  "Kundenbenutzer": "Utilisateurs clients",
  "Kundenbenutzer ohne Mandant – Kunde zuweisen.": "Utilisateurs clients sans mandant – affecter un client.",
  "Kundenbezogene Rolle": "Rôle lié au client",
  "Kundenbezogene Rolle (pro Mandant)": "Rôle lié au client (par mandant)",
  "Kundendaten-Bearbeitungsrechte": "Droits de modification des données client",
  "Kundendaten-Rechte": "Droits sur les données client",
  "Kundenprofil bearbeiten": "Modifier le profil client",
  "Kundenseitige Benutzer (Reviewer, Kunden-Mitarbeiter, Viewer …). KEWAG-Team findest du im Tab „KEWAG-Team\".": "Utilisateurs côté client (réviseur, collaborateur client, observateur …). L'équipe KEWAG se trouve dans l'onglet « Équipe KEWAG ».",
  "Kundenstammdaten": "Données de base du client",
  "Kundenzuweisungen": "Affectations client",
  "Kundenübersicht & Zuweisungen": "Aperçu des clients et affectations",
  "Lege fest, für welche Rollen die Zwei-Faktor-Authentifizierung verpflichtend ist. Betroffene Benutzer ohne 2FA müssen sie beim nächsten Login einrichten. „Super Admin\" ist zum Schutz nicht listbar – aktiviere 2FA dort direkt am Konto.": "Définissez pour quels rôles l'authentification à deux facteurs est obligatoire. Les utilisateurs concernés sans 2FA devront la configurer à la prochaine connexion. « Super Admin » n'est pas listé par sécurité – activez la 2FA directement sur ce compte.",
  "Letzte Änderung": "Dernière modification",
  "Letzte Änderungen": "Dernières modifications",
  "Löschen": "Supprimer",
  "Mitarbeiter (Kunde)": "Collaborateur (client)",
  "Name A–Z": "Nom A–Z",
  "Name und Bereich / Team anpassen.": "Ajuster le nom et le domaine / l'équipe.",
  "Noch kein Hauptbetreuer festgelegt.": "Aucun responsable principal défini.",
  "Noch keine Benutzer zugewiesen.": "Aucun utilisateur affecté pour l'instant.",
  "Noch keine Team-Mitglieder.": "Aucun membre d'équipe pour l'instant.",
  "Offene Einladungen": "Invitations en attente",
  "Offene und abgelaufene Einladungen zentral verwalten.": "Gérer de manière centralisée les invitations en attente et expirées.",
  "Onboarding-Fortschritt": "Progression de l'onboarding",
  "Paket & Kontingente": "Forfait et contingents",
  "Paket, gebuchte Plattformen, Kontingente": "Forfait, plateformes réservées, contingents",
  "Paket: Alle": "Forfait : tous",
  "Pflicht": "Obligatoire",
  "Preis, Zahlungsziel, Rechnungsadresse & -E-Mail": "Prix, échéance de paiement, adresse et e-mail de facturation",
  "Profil-Status": "Statut du profil",
  "Quelle je Spalte: „Global\" = KEWAG-Teamrolle (alle Mandanten) · sonst pro Kunde/Mandant vererbt.": "Source par colonne : « Global » = rôle d'équipe KEWAG (tous les mandants) · sinon hérité par client/mandant.",
  "Rechnungsdaten": "Données de facturation",
  "Recht": "Droit",
  "Report deaktiviert": "Rapport désactivé",
  "Report-Vorschau": "Aperçu du rapport",
  "Reviewer (Kunde)": "Réviseur (client)",
  "Rolle ändern": "Changer de rôle",
  "Rolle, Zuweisungen, Status und effektive Rechte.": "Rôle, affectations, statut et droits effectifs.",
  "Rolle: Alle": "Rôle : tous",
  "Rollen & Rechte": "Rôles et droits",
  "Rollen & Rechte – Schnellübersicht": "Rôles et droits – aperçu rapide",
  "Rollen geändert": "Rôles modifiés",
  "Rollenerklärung": "Explication des rôles",
  "Rollentyp": "Type de rôle",
  "Rollenverteilung": "Répartition des rôles",
  "Schließen": "Fermer",
  "So sähe die E-Mail aus. In Produktion (Brevo verbunden) wird sie automatisch versendet.": "Voici à quoi ressemblerait l'e-mail. En production (Brevo connecté), il est envoyé automatiquement.",
  "Sortierung: Rolle": "Tri : rôle",
  "Speichern": "Enregistrer",
  "Status ändern": "Changer le statut",
  "Status: Alle": "Statut : tous",
  "Super Admin – Vollzugriff auf alle Kunden.": "Super Admin – accès complet à tous les clients.",
  "Systeminhaber": "Propriétaire du système",
  "Systeminhaber – einziger Super Admin": "Propriétaire du système – unique super administrateur",
  "Systeminhaber – geschützt": "Propriétaire du système – protégé",
  "Team verwalten": "Gérer l'équipe",
  "Team-Mitglied": "Membre d'équipe",
  "Team-Mitglied anlegen": "Créer un membre d'équipe",
  "Tipp: Wenn die Person nur vorübergehend keinen Zugriff haben soll, nutze stattdessen „Deaktivieren\".": "Astuce : si la personne ne doit temporairement plus avoir accès, utilisez plutôt « Désactiver ».",
  "Unternehmens- und Abo-Status": "Statut de l'entreprise et de l'abonnement",
  "Unverändert": "Inchangé",
  "Vertragsdaten": "Données contractuelles",
  "Vollständige Berechtigungs-Matrix aller Bereiche und Rollen.": "Matrice complète des autorisations pour tous les domaines et rôles.",
  "Vollständiges Audit-Log": "Journal d'audit complet",
  "Vorname, Nachname, Telefon, Funktion": "Prénom, nom, téléphone, fonction",
  "Vorschau erstellt (Brevo nicht verbunden – kein realer Versand).": "Aperçu créé (Brevo non connecté – aucun envoi réel).",
  "Weitere Aktionen": "Autres actions",
  "Weitere Teammitglieder": "Autres membres de l'équipe",
  "Wöchentlicher Report aktiviert": "Rapport hebdomadaire activé",
  "Wöchentlicher Report an KEWAG Admins": "Rapport hebdomadaire aux administrateurs KEWAG",
  "Zugewiesene Benutzer": "Utilisateurs affectés",
  "Zugewiesene Kunden": "Clients affectés",
  "Zugriffsprobleme": "Problèmes d'accès",
  "Zuletzt aktiv": "Dernière activité",
  "Zuweisungen bearbeiten": "Modifier les affectations",
  "Zuweisungen öffnen": "Ouvrir les affectations",
  "Zuweisungs- & Profil-Status": "Statut des affectations et du profil",
  "Zwei-Faktor-Authentifizierung für": "Authentification à deux facteurs pour",
  "ausgewählt": "sélectionné(s)",
  "gesperrt": "bloqué",
  "ggü. Vorwoche": "p.r. à la semaine précédente",
  "inaktiv": "inactif",
  "ohne 2FA": "sans 2FA",
  "vollständig": "complet",
  "vs. letzter 30 Tage": "vs 30 derniers jours",
  "weitere": "de plus",
  "zuletzt": "dernier",
  "Übersicht der Berechtigungen pro Rolle.": "Aperçu des autorisations par rôle.",
  "übersprungen": "ignoré(s)",
  "übersprungen (Systeminhaber/geschützt)": "ignoré(s) (propriétaire du système/protégé)",
  "von": "sur",
  "offen": "en attente",
  "Seite": "Page",
  "KEWAG-Betreuung": "Suivi KEWAG",
  "Benutzer gelöscht": "utilisateur(s) supprimé(s)",
  "Benutzer „{name}\" gelöscht": "Utilisateur « {name} » supprimé",
  "2FA-Pflicht für {n} Mitglied(er) gesetzt": "2FA obligatoire définie pour {n} membre(s)",
  "Editor (Team)": "Éditeur (équipe)",
  "Account Manager": "Account Manager",
  "Möchtest du {name} ({email}) endgültig löschen? Das Konto, alle Sitzungen und Kundenzuweisungen werden entfernt. Diese Aktion kann nicht rückgängig gemacht werden.": "Voulez-vous supprimer définitivement {name} ({email}) ? Le compte, toutes les sessions et affectations client seront supprimés. Cette action est irréversible.",
  "Möchtest du die {n} ausgewählten Benutzer endgültig löschen? Konten, Sitzungen und Kundenzuweisungen werden entfernt. Dein eigenes Konto und der letzte Super Admin werden automatisch übersprungen. Diese Aktion kann nicht rückgängig gemacht werden.": "Voulez-vous supprimer définitivement les {n} utilisateurs sélectionnés ? Les comptes, sessions et affectations client seront supprimés. Votre propre compte et le dernier super administrateur sont automatiquement ignorés. Cette action est irréversible.",
  "Beiträge & Inhalte": "Publications et contenus",
  "Alle Mandanten sehen": "Voir tous les mandants",
  "Kunden & Rollen verwalten": "Gérer clients et rôles",
  "Rechnungen, Abos & Abrechnung": "Factures, abonnements et facturation",
  "Inhalte erstellen / bearbeiten": "Créer / modifier des contenus",
  "Fotos & Freigaben verwalten": "Gérer photos et approbations",
  "Zur Freigabe einreichen": "Soumettre pour approbation",
  "Freigaben / Änderungen wünschen": "Approbations / demandes de modification",
  "Kommentare": "Commentaires",
  "Integrationen & System": "Intégrations et système",
  "Nur Ansicht": "Lecture seule",
  "Voller Zugriff auf alle Mandanten, Benutzer, Rollen und Systemeinstellungen.": "Accès complet à tous les mandants, utilisateurs, rôles et paramètres système.",
  "Verwaltet KEWAG-administrative Aufgaben und hat erweiterten Systemzugriff.": "Gère les tâches administratives KEWAG et dispose d'un accès système étendu.",
  "Verwaltet Rechnungen, Abos und Abrechnung über alle Mandanten; liest Kundenstammdaten und Reports. Kein Zugriff auf Inhalte, Freigaben oder Benutzerverwaltung.": "Gère les factures, abonnements et la facturation sur tous les mandants ; consulte les données de base clients et les rapports. Aucun accès aux contenus, approbations ou gestion des utilisateurs.",
  "Erstellt und bearbeitet Inhalte und verwaltet Fotoaufträge.": "Crée et modifie des contenus et gère les commandes de photos.",
  "Unterstützt Nutzer, verwaltet Freigaben und Änderungen.": "Assiste les utilisateurs, gère les approbations et les modifications.",
  "Reicht Inhalte ein, prüft und gibt Inhalte frei.": "Soumet des contenus, les vérifie et les approuve.",
  "Kundenseitiger Mitarbeiter: sieht Inhalte, kann kommentieren und Fotos senden – gibt aber NICHT frei (Freigabe bleibt beim Reviewer).": "Collaborateur côté client : voit les contenus, peut commenter et envoyer des photos – mais n'approuve PAS (l'approbation reste au réviseur).",
  "Nur lesender Zugriff auf zugewiesene Inhalte.": "Accès en lecture seule aux contenus attribués.",
  "Finanzen (Rechnungen/Abos)": "Finances (factures/abonnements)",
  "Benutzer & Rollen verwalten": "Gérer utilisateurs et rôles",
  "Kunde/Mandant verwalten": "Gérer client/mandant",
  "Inhalte erstellen/bearbeiten": "Créer/modifier des contenus",
  "Freigeben (Ampel grün)": "Approuver (feu vert)",
  "Kommentieren": "Commenter",
  "Ansehen / Lesen": "Consulter / lire",
  "Benutzer hinzugefügt": "Utilisateur ajouté",
  "Benutzer geändert": "Utilisateur modifié",
  "Benutzer entfernt": "Utilisateur retiré",
  "Benutzer eingeladen": "Utilisateur invité",
  "Status geändert": "Statut modifié",
  "Einladung erneut gesendet": "Invitation renvoyée",
  "Hauptbetreuer gesetzt": "Responsable principal défini",
  "Hauptbetreuer entfernt": "Responsable principal retiré",
  "Begrüssungs-E-Mail gesendet": "E-mail de bienvenue envoyé",
  "Kundenprofil vollständig ausgefüllt und Benutzer optimal zugewiesen.": "Profil client entièrement rempli et utilisateurs affectés de manière optimale.",
  "Profil und Benutzer-Zuweisungen noch nicht vollständig.": "Profil et affectations d'utilisateurs pas encore complets.",
  "Kundenprofil und Zuweisungen weitgehend offen.": "Profil client et affectations largement incomplets.",
  "Vollzugriff": "Accès complet",
  "Nur Freigabe": "Approbation uniquement",
  "Kommentieren & Fotos": "Commenter et photos",
  "Supportzugriff": "Accès support",
  "Nur Lesen": "Lecture seule",
  "Kundendaten": "Données client",
  "Abo & Preis": "Abonnement et prix",
  "Plattformen & Speicher": "Plateformes et stockage",
  "Vertrag & Abrechnung": "Contrat et facturation",
  "Prüfung & Erstellen": "Vérification et création",
  "Weiter zu Abo & Preis": "Continuer vers Abonnement et prix",
  "Weiter zu Plattformen & Speicher": "Continuer vers Plateformes et stockage",
  "Weiter zu Vertrag & Abrechnung": "Continuer vers Contrat et facturation",
  "Weiter zu Prüfung & Erstellen": "Continuer vers Vérification et création",
  "Angebot geladen – Kunde aus angenommenem Angebot erstellen.": "Offre chargée – créer le client à partir de l'offre acceptée.",
  "Angebot konnte nicht geladen werden.": "Impossible de charger l'offre.",
  "Entwurf geladen – du kannst fortfahren.": "Brouillon chargé – vous pouvez continuer.",
  "Entwurf konnte nicht geladen werden.": "Impossible de charger le brouillon.",
  "Firmenname / Projektname ist erforderlich": "Le nom de l'entreprise / du projet est obligatoire",
  "Kunde „{name}\" angelegt (Nr. {nr}).": "Client « {name} » créé (n° {nr}).",
  "Einladung gesendet.": "Invitation envoyée.",
  "Einladung vorbereitet.": "Invitation préparée.",
  "Entwurf gespeichert": "Brouillon enregistré",
  "Bitte zuerst den Firmennamen erfassen": "Veuillez d'abord saisir le nom de l'entreprise",
  "Angebot-PDF erstellt": "PDF de l'offre créé",
  "PDF konnte nicht erstellt werden": "Impossible de créer le PDF",
  "Bitte eine Kunden-E-Mail erfassen": "Veuillez saisir une e-mail client",
  "Angebot an {to} gesendet": "Offre envoyée à {to}",
  "Konfiguration konnte nicht geladen werden.": "Impossible de charger la configuration.",
  "Neuen SocialCheck-Kunden mit Abo, Plattformen, Speicher und Abrechnung erfassen.": "Saisir un nouveau client SocialCheck avec abonnement, plateformes, stockage et facturation.",
  "Abo & Paket wählen": "Choisir l'abonnement et le forfait",
  "Zusatzleistungen (Add-ons)": "Services supplémentaires (add-ons)",
  "Plattformen in den Paketen": "Plateformes dans les forfaits",
  "Speicherpaket & Limits": "Forfait de stockage et limites",
  "Zusammenfassung": "Récapitulatif",
  "Firmenname / Projektname": "Nom de l'entreprise / du projet",
  "Hauptansprechpartner": "Interlocuteur principal",
  "Kundentyp": "Type de client",
  "Rechnungsadresse · Strasse": "Adresse de facturation · rue",
  "App-Domain (optional)": "Domaine de l'app (facultatif)",
  "PLZ": "NPA",
  "Ort": "Localité",
  "UID / MWST (optional)": "IDE / TVA (facultatif)",
  "Land": "Pays",
  "Zeitzone": "Fuseau horaire",
  "Abo-Start": "Début de l'abonnement",
  "Vertragslaufzeit": "Durée du contrat",
  "Rechnungsintervall": "Intervalle de facturation",
  "Zahlungsziel": "Délai de paiement",
  "Rabatt (%)": "Remise (%)",
  "Setup-Gebühr (einmalig)": "Frais d'installation (unique)",
  "Serie / Erneuerung": "Série / renouvellement",
  "z. B. Blütenzauber GmbH": "p. ex. Blütenzauber GmbH",
  "Vor- und Nachname": "Prénom et nom",
  "Branche wählen": "Choisir un secteur",
  "Strasse und Nr.": "Rue et n°",
  "Laufzeit": "Durée",
  "Intervall": "Intervalle",
  "Erneuerung": "Renouvellement",
  "Weitere Kontaktpersonen hinzufügen": "Ajouter d'autres personnes de contact",
  "Beliebt": "Populaire",
  "Beiträge / Monat": "publications / mois",
  "GB Speicher": "Go de stockage",
  "einmalig": "unique",
  "/ Monat": "/ mois",
  "Inklusive": "Inclus",
  "Nicht inklusive": "Non inclus",
  "Plattform": "Plateforme",
  "Speicherpaket wählen": "Choisir le forfait de stockage",
  "Speichernutzung – Warnstufen": "Utilisation du stockage – niveaux d'alerte",
  "Vollständigkeit": "Complétude",
  "Sehr gut!": "Très bien !",
  "Fast geschafft": "Presque terminé",
  "Weiter ausfüllen": "Continuer à remplir",
  "Fast bereit zum Erstellen.": "Presque prêt à créer.",
  "Pflichtfelder ausfüllen.": "Remplir les champs obligatoires.",
  "Ausgewähltes Paket": "Forfait sélectionné",
  "Inkludierte Plattformen": "Plateformes incluses",
  "Plattformen inklusive": "plateformes incluses",
  "Speicherpaket": "Forfait de stockage",
  "(monatlich)": "(mensuel)",
  "Einmalige Kosten": "Coûts uniques",
  "Monatliche Gesamtkosten": "Coût mensuel total",
  "inkl. MwSt.": "TVA incl.",
  "Angebot als PDF": "Offre en PDF",
  "Angebot senden": "Envoyer l'offre",
  "E-Mail-Versand (Brevo) nicht verbunden – PDF herunterladen und manuell versenden.": "Envoi d'e-mails (Brevo) non connecté – télécharger le PDF et l'envoyer manuellement.",
  "Angebot als PDF per E-Mail senden": "Envoyer l'offre en PDF par e-mail",
  "E-Mail-Versand (Brevo) ist nicht verbunden": "L'envoi d'e-mails (Brevo) n'est pas connecté",
  "inklusive": "inclus",
  "Einmalig (Setup / Kurzcheck)": "Unique (installation / check court)",
  "Rabatt": "Remise",
  "/ Mt.": "/ mois",
  "{pct}% ausgefüllt · Schritt {step} von 5": "{pct}% rempli · étape {step} sur 5",
  "Als Entwurf speichern": "Enregistrer comme brouillon",
  "2 GB Speicher": "2 Go de stockage",
  "Auf Anfrage": "Sur demande",
  "auf Anfrage": "sur demande",
  "2 GB + {n} GB": "2 Go + {n} Go",
  "Neukunde": "Nouveau client",
  "Bestehender Kunde": "Client existant",
  "Aus Lead umgewandelt": "Converti depuis un lead",
  "Demo-Kunde": "Client démo",
  "Monatlich kündbar": "Résiliable mensuellement",
  "12 Monate": "12 mois",
  "24 Monate": "24 mois",
  "36 Monate": "36 mois",
  "Monatlich": "Mensuel",
  "Quartalsweise": "Trimestriel",
  "Halbjährlich": "Semestriel",
  "Jährlich": "Annuel",
  "10 Tage": "10 jours",
  "14 Tage": "14 jours",
  "30 Tage": "30 jours",
  "60 Tage": "60 jours",
  "Off": "Désactivé",
  "Automatisch verlängern": "Renouvellement automatique",
  "Manuell prüfen": "Vérification manuelle",
  "Deutsch": "Allemand",
  "Französisch": "Français",
  "Italienisch": "Italien",
  "Englisch": "Anglais",
  "Schweiz": "Suisse",
  "Liechtenstein": "Liechtenstein",
  "Deutschland": "Allemagne",
  "Österreich": "Autriche",
  "Blumen & Floristik": "Fleurs et fleuristerie",
  "Gastronomie & Restaurant": "Gastronomie et restaurant",
  "Handwerk & Bau": "Artisanat et construction",
  "Gesundheit & Praxis": "Santé et cabinet",
  "Beauty & Wellness": "Beauté et bien-être",
  "Detailhandel & Retail": "Commerce de détail",
  "Immobilien": "Immobilier",
  "Beratung & Dienstleistung": "Conseil et services",
  "Bildung & Coaching": "Formation et coaching",
  "Sport & Fitness": "Sport et fitness",
  "Auto & Mobilität": "Auto et mobilité",
  "Garten & Landschaft": "Jardin et paysage",
  "Technologie & IT": "Technologie et informatique",
  "Andere": "Autre",
  "Zusatzkanal": "Canal supplémentaire",
  "Zusatzbeitrag": "Publication supplémentaire",
  "Design-Beitrag": "Publication design",
  "Reel": "Reel",
  "Community Management": "Community Management",
  "2 GB inklusive": "2 Go inclus",
  "+10 GB": "+10 Go",
  "+50 GB": "+50 Go",
  "+100 GB": "+100 Go",
  "Mehr als 100 GB": "Plus de 100 Go",
  "Warnung": "Avertissement",
  "Starke Warnung": "Avertissement fort",
  "Erste Warnung bei 80%.": "Premier avertissement à 80 %.",
  "Zweite Warnung bei 95%.": "Deuxième avertissement à 95 %.",
  "Upload blockieren oder Speichererweiterung verlangen.": "Bloquer l'upload ou exiger une extension du stockage.",
};

const IT = {
  // Kundenfeedback-Seite + „Heute“-Systemtexte
  "{n} Aufgabe(n) erstellt": "{n} attività create",
  "{n} aktualisiert": "{n} aggiornati",
  "Verarbeiten Sie Feedback aus dem Freigabe-Prozess – effizient, strukturiert und nachvollziehbar.": "Gestisci i feedback del processo di approvazione – in modo efficiente, strutturato e tracciabile.",
  "+ {n} heute": "+ {n} oggi",
  "Suche in Beitrag, Kunde, Kommentar…": "Cerca in post, cliente, commento…",
  "Typ: Alle": "Tipo: tutti",
  "Plattform: Alle": "Piattaforma: tutte",
  "Ansicht gespeichert": "Vista salvata",
  "Ansicht speichern": "Salva vista",
  "Ansicht auf diesem Gerät gespeichert": "Vista salvata su questo dispositivo",
  "Ansicht konnte auf diesem Gerät nicht gespeichert werden": "Impossibile salvare la vista su questo dispositivo",
  "Ansicht kann ohne Benutzerkennung nicht gespeichert werden": "La vista non può essere salvata senza ID utente",
  "Speichert Reiter, Filter, Sortierung und Einträge pro Seite nur auf diesem Gerät": "Salva scheda, filtri, ordinamento e voci per pagina solo su questo dispositivo",
  "Zeitraum:": "Periodo:",
  "Mein Eingang": "La mia casella",
  "{n} ausgewählt": "{n} selezionati",
  "Zuweisen an": "Assegna a",
  "Priorität setzen": "Imposta priorità",
  "Als erledigt markieren": "Segna come completato",
  "Aufgabe erstellen": "Crea attività",
  "Sortieren:": "Ordina:",
  "Neueste zuerst": "Prima i più recenti",
  "Älteste zuerst": "Prima i più vecchi",
  "Typ": "Tipo",
  "Bearbeitung": "Elaborazione",
  "Zuständig": "Responsabile",
  "Kein Feedback für diese Auswahl.": "Nessun feedback per questa selezione.",
  "Aufgabe öffnen": "Apri attività",
  "Beitrag öffnen": "Apri post",
  "Antworten": "Rispondi",
  "Notiz gespeichert": "Nota salvata",
  "Wählen Sie ein Feedback aus.": "Seleziona un feedback.",
  "ID: {ref}": "ID: {ref}",
  "ID kopiert": "ID copiato",
  "Originalbeitrag (Vorschau)": "Post originale (anteprima)",
  "Kundenrückmeldung": "Riscontro del cliente",
  "Notiz bearbeiten": "Modifica nota",
  "Keine interne Notiz.": "Nessuna nota interna.",
  "Verknüpfte Aufgabe": "Attività collegata",
  "Priorität gesetzt": "Priorità impostata",
  "– Zuweisen": "– Assegna",
  "Zugewiesen": "Assegnato",
  "Zuweisung entfernt": "Assegnazione rimossa",
  "Ändern": "Modifica",
  "Fälligkeit angepasst": "Scadenza modificata",
  "Kundenansicht öffnen": "Apri vista cliente",
  "Alle Aktivitäten": "Tutte le attività",
  "Änderung umsetzen: {title}": "Applicare la modifica: {title}",
  "Aufgabe {ref} erstellt": "Attività {ref} creata",
  "Aufgabe aus Kundenfeedback": "Attività dal feedback del cliente",
  "Wird als verknüpfte interne Aufgabe angelegt und protokolliert.": "Viene creata come attività interna collegata e registrata.",
  "Beschreibung / Änderungswunsch": "Descrizione / richiesta di modifica",
  "Antwort gesendet – wartet auf Kunde": "Risposta inviata – in attesa del cliente",
  "Wird als Kommentar am Beitrag gespeichert.": "Viene salvata come commento al post.",
  "Antwort schreiben…": "Scrivi una risposta…",
  "Nur für das KEWAG-Team sichtbar.": "Visibile solo al team KEWAG.",
  "Interne Notiz…": "Nota interna…",
  "Morgen": "Domani",
  "Freigabe": "Approvazione",
  "Wartet auf Kunde": "In attesa del cliente",
  "Feedback eingegangen": "Feedback ricevuto",
  "{name} zugewiesen": "Assegnato a {name}",
  "Status auf {status} gesetzt": "Stato impostato su {status}",
  "Priorität auf {prio} gesetzt": "Priorità impostata su {prio}",
  "Interne Notiz aktualisiert": "Nota interna aggiornata",
  "Beitrag wartet auf Kundenfreigabe": "Post in attesa di approvazione del cliente",
  "Neue Antwort zu „{title}“.": "Nuova risposta su «{title}».",
  // Glocke: Systemtexte des Servers (lib/notif.js)
  "Neuer Änderungswunsch": "Nuova richiesta di modifica",
  "Erinnerung: Freigabe ausstehend": "Promemoria: approvazione in sospeso",
  "Neuer Beitrag wartet auf Ihre Freigabe": "Nuovo post in attesa della tua approvazione",
  "Kunde hat freigegeben": "Il cliente ha approvato",
  "Beitrag ist live": "Post pubblicato",
  "Frage vom Kunden": "Domanda del cliente",
  "Neue Kundenantwort": "Nuova risposta del cliente",
  "Antwort vom KEWAG-Team": "Risposta del team KEWAG",
  "Marken-Profil: Team informiert": "Profilo del brand: team informato",
  "Meta-Verbindung prüfen: {tenant}": "Verificare la connessione Meta: {tenant}",
  "KI-Limit fast erreicht: {tenant}": "Limite IA quasi raggiunto: {tenant}",
  "Kunde wünscht eine Änderung an „{title}“: {note}": "Il cliente desidera una modifica a «{title}»: {note}",
  "Kunde wünscht eine Änderung an „{title}“.": "Il cliente desidera una modifica a «{title}».",
  "{n} Beitrag wartet auf Ihre Freigabe.": "{n} post attende la tua approvazione.",
  "{n} Beiträge wartet auf Ihre Freigabe.": "{n} post attendono la tua approvazione.",
  "„{title}“ liegt für Sie zur Freigabe bereit.": "«{title}» è pronto per la tua approvazione.",
  "„{title}“ wurde vom Kunden freigegeben.": "«{title}» è stato approvato dal cliente.",
  "„{title}“ wurde veröffentlicht.": "«{title}» è stato pubblicato.",
  "Neue Frage/Kommentar zu „{title}“.": "Nuova domanda o commento su «{title}».",
  "Bitte den Beitrag „{title}“ prüfen und freigeben.": "Verifica e approva il post «{title}».",
  "{name} hat das Team über das aktuelle Marken-Profil informiert.": "{name} ha informato il team sul profilo del brand attuale.",
  "{tenant} hat {runs} von {limit} KI-Läufen in diesem Monat verbraucht ({pct}%).": "{tenant} ha utilizzato {runs} di {limit} esecuzioni IA questo mese ({pct}%).",
  "Meta-Verbindung von {tenant} benötigt Handlung: {summary}. Bitte im Integrationen-Bereich neu verbinden.": "La connessione Meta di {tenant} richiede un intervento: {summary}. Riconnettila nella sezione Integrazioni.",
  "Token ungültig – bitte neu verbinden.": "Token non valido – riconnetti.",
  "Token ungültig": "Token non valido",
  "Fehlende Berechtigungen: {scopes}": "Autorizzazioni mancanti: {scopes}",
  "Token läuft in {d} Tag(en) ab – bitte neu verbinden.": "Il token scade tra {d} giorno/i – riconnetti.",
  "Geschütztes Konto": "Account protetto",
  "Alte Konfigurationsnamen": "Vecchi nomi di configurazione",
  "Schlüsselnamen sind Konfiguration, keine zusätzlichen Admin-Konten. Ein fehlender Startschlüssel ändert kein bestehendes Konto-Passwort.": "I nomi delle chiavi sono configurazione, non account amministratore aggiuntivi. Una chiave iniziale mancante non modifica la password di un account esistente.",
  // Owner/team configuration messages, including parameterized API warnings.
  "SUPER_ADMIN_EMAIL und SUPERADMIN_EMAIL verweisen auf verschiedene Konten": "SUPER_ADMIN_EMAIL e SUPERADMIN_EMAIL indicano account diversi",
  "Owner-Passwort unter mehreren Schlüsselnamen mit verschiedenen Werten: {keys}": "Password del proprietario con valori diversi in più chiavi: {keys}",
  "Konto unter der Owner-E-Mail hat nicht die Rolle super_admin (keine automatische Hochstufung)": "L’account dell’e-mail del proprietario non ha il ruolo super_admin (nessuna promozione automatica)",
  "{count} weitere(s) Super-Admin-/Inhaber-Konto(en) vorhanden": "Presenti {count} altri account Super Admin/proprietario",
  "Teamkonto ist zugleich als Owner konfiguriert": "L’account del team è configurato anche come proprietario",
  "Teamkonto hat Owner-Rechte": "L’account del team ha diritti da proprietario",
  "Rolle {role} statt {expected}": "Ruolo {role} invece di {expected}",
  "Altkonto ADMIN_EMAIL hat Owner-Rechte (zweite Owner-Identität)": "Il vecchio account ADMIN_EMAIL ha diritti da proprietario (seconda identità proprietario)",
  "Altname {key} aktiv - kanonisch: {canonical}": "Vecchio nome {key} attivo - nome canonico: {canonical}",
  "Owner-E-Mail stammt aus dem Altnamen ADMIN_EMAIL - kanonisch: SUPER_ADMIN_EMAIL": "L’e-mail del proprietario proviene dal vecchio nome ADMIN_EMAIL - nome canonico: SUPER_ADMIN_EMAIL",
  "{key} gesetzt - wirkungslos (beim Start gibt es keinen Passwort-Reset mehr)": "{key} impostato - senza effetto (l’avvio non reimposta più le password)",
  "Nur der Super Admin darf den Zugangsstatus sehen.": "Solo il Super Admin può vedere lo stato degli accessi.",
  // --- Integrationen (Tracking #19) ---
  "Untermenü einklappen": "Comprimi sottomenu",
  "Untermenü ausklappen": "Espandi sottomenu",
  "Verbindung unterbrochen — Token erneuern / erneut verbinden.": "Connessione interrotta — rinnovare il token / ricollegare.",
  "Handlungsbedarf — Provider noch nicht verfügbar.": "Intervento necessario — provider non ancora disponibile.",
  "Provider noch nicht verfügbar.": "Provider non ancora disponibile.",
  "Verbindung prüfen (Health-Check).": "Verificare la connessione (health check).",
  "Adapter vorbereitet — Provider-Credentials/Freigabe erforderlich.": "Adattatore predisposto — credenziali/approvazione del provider necessarie.",
  "So hinterlegen Sie die Zugangsdaten für {name}.": "Ecco come inserire le credenziali per {name}.",
  "Verbindung gültig": "Connessione valida",
  "{v} kopiert": "{v} copiato",
  "{name} verbinden": "Collega {name}",
  "Sofort testen (ohne Speichern)": "Testa subito (senza salvare)",
  "Der Schlüssel wird nur live geprüft – nicht in der App gespeichert.": "La chiave viene solo verificata in tempo reale – non viene salvata nell’app.",
  "KEWAG-Aufgabe „Zugangsdaten aktivieren\" wurde erstellt.": "Il compito KEWAG «Attivare le credenziali» è stato creato.",
  "Dauerhaft aktivieren": "Attiva in modo permanente",
  "Nach erfolgreichem Test: diese Werte als Emergent-Secret hinterlegen, dann 1× Backend-Neustart – danach ist die Integration verbunden.": "Dopo un test riuscito: salvare questi valori come secret Emergent, poi riavviare una volta il backend – a quel punto l’integrazione è collegata.",
  "Konfigurationsseite:": "Pagina di configurazione:",
  "Zur Konfiguration": "Alla configurazione",
  "Konfigurationsseite folgt": "Pagina di configurazione in arrivo",
  "Konfiguration folgt": "Configurazione in arrivo",
  "Zugangsdaten": "Credenziali",
  "Alle System-Zugangsdaten sind hinterlegt.": "Tutte le credenziali di sistema sono inserite.",
  "Offene System-Integrationen — direkt zur passenden Konfiguration.": "Integrazioni di sistema aperte — direttamente alla configurazione corretta.",
  "Hinterlegen": "Inserisci",
  "Gateway-Übersicht wird geladen…": "Caricamento panoramica del gateway…",
  "KEWAG Social Gateway — Ablauf": "KEWAG Social Gateway — flusso",
  "Beitrag → Ampel-Gate → Queue → Dry-Run. Nur grün-freigegebene Beiträge dürfen intern in die Queue.": "Post → Ampel-Gate → coda → simulazione. Solo i post approvati in verde possono entrare internamente nella coda.",
  "Externe Veröffentlichung deaktiviert": "Pubblicazione esterna disattivata",
  "Beiträge im Umlauf": "Post in circolazione",
  "freigabe-geprüft": "approvazione verificata",
  "Ampel-Gate": "Ampel-Gate",
  "grün → zugelassen": "verde → ammesso",
  "Queue (Dry-Run)": "Coda (simulazione)",
  "{q} wartend · {p} aktiv": "{q} in attesa · {p} attivi",
  "Simuliert veröffentlicht": "Pubblicazione simulata",
  "kein externer Versand": "nessun invio esterno",
  "Vom Ampel-Gate blockiert ·": "Bloccati dall’Ampel-Gate ·",
  "Plattformen (ehrlicher Status)": "Piattaforme (stato reale)",
  "Warum blockiert? — Live-Feed": "Perché bloccato? — feed in tempo reale",
  "Zuletzt geprüft:": "Ultima verifica:",
  "Noch keine Blockierungen protokolliert. Sobald ein nicht-grüner Beitrag eingereiht wird, erscheint der Grund hier.": "Nessun blocco registrato finora. Non appena un post non verde viene messo in coda, il motivo appare qui.",
  "{a} blockiert": "{a} bloccato",
  "Ampel-Gate: Veröffentlichung gesperrt.": "Ampel-Gate: pubblicazione bloccata.",
  "Hilfe & Dokumentation": "Guida & documentazione",
  "Alle Dokumente öffnen": "Apri tutti i documenti",
  "{n}% aller Kunden": "{n}% di tutti i clienti",
  "Verbundene Kunden": "Clienti collegati",
  "Kunden mit Handlungsbedarf": "Clienti con interventi necessari",
  "Ablaufende Berechtigungen": "Autorizzazioni in scadenza",
  "in den nächsten 30 Tagen": "nei prossimi 30 giorni",
  "Nächste: in {n} Tagen": "Prossima: tra {n} giorni",
  "Kritische Probleme": "Problemi critici",
  "Sofortige Aufmerksamkeit": "Attenzione immediata",
  "seit letzter Woche": "dalla settimana scorsa",
  "seit {n} Tag": "da {n} giorno",
  "seit {n} Tagen": "da {n} giorni",
  "Keine Vergleichsdaten": "Nessun dato di confronto",
  "Verlauf konnte nicht geladen werden": "Impossibile caricare la cronologia",
  "KPI-Verlauf": "Andamento KPI",
  "Echte Tages-Snapshots über alle Mandanten": "Snapshot giornalieri reali su tutti i mandanti",
  "Noch nicht genug Verlaufsdaten": "Dati storici non ancora sufficienti",
  "Bisher 1 Tages-Snapshot erfasst.": "Finora registrato 1 snapshot giornaliero.",
  "Bisher keine Snapshots erfasst.": "Nessuno snapshot registrato finora.",
  "Der tägliche Snapshot baut die Kurven über die nächsten Tage automatisch auf.": "Lo snapshot giornaliero costruisce automaticamente le curve nei prossimi giorni.",
  "{n} Kunde": "{n} cliente",
  "{n} Kunden": "{n} clienti",
  "Nachfass gesendet: {c} Kunde(n) an {r} Empfänger": "Sollecito inviato: {c} cliente/i a {r} destinatari",
  "Nicht gesendet": "Non inviato",
  "Versand fehlgeschlagen": "Invio non riuscito",
  "Export fehlgeschlagen": "Esportazione non riuscita",
  "Onboarding-Nachfass": "Sollecito onboarding",
  "Wöchentliche E-Mail ans KEWAG-Team mit allen „fast fertig (2/3)\"-Kunden.": "E-mail settimanale al team KEWAG con tutti i clienti «quasi completati (2/3)».",
  "Jeden": "Ogni",
  "um": "alle",
  "Uhr (UTC)": "(UTC)",
  "Aktuell fast fertig (2/3)": "Attualmente quasi completati (2/3)",
  "Zuletzt gesendet": "Ultimo invio",
  "Noch nie": "Mai",
  "3/3 · Fertig diese Woche": "3/3 · Completati questa settimana",
  "E-Mail-Dienst (Brevo) noch nicht verbunden – der Versand startet automatisch, sobald Brevo hinterlegt ist.": "Il servizio e-mail (Brevo) non è ancora collegato – l’invio parte automaticamente non appena Brevo è configurato.",
  "Vollständig onboarded / Woche (8 Wochen)": "Onboarding completati / settimana (8 settimane)",
  "Noch keine abgeschlossenen Onboardings in den letzten 8 Wochen.": "Nessun onboarding completato nelle ultime 8 settimane.",
  "Jetzt senden (Test)": "Invia ora (test)",
  "Report exportieren:": "Esporta report:",
  "Unterstützte Plattformen in allen Paketen": "Piattaforme supportate in tutti i pacchetti",
  "Plattformen": "Piattaforme",
  "Grundsätzlich unterstützt und in der Paketlogik verfügbar — nicht automatisch verbunden.": "Supportate in linea di principio e disponibili nella logica dei pacchetti — non collegate automaticamente.",
  "Standard": "Standard",
  "In Vorbereitung": "In preparazione",
  "Prioritäten / Handlungsbedarf": "Priorità / interventi necessari",
  "Nach Schwere sortiert — kritisch zuerst.": "Ordinati per gravità — prima i critici.",
  "Keine offenen Punkte. Alles im grünen Bereich.": "Nessun punto aperto. Tutto in verde.",
  "Alle offenen Punkte anzeigen": "Mostra tutti i punti aperti",
  "{p} einrichten": "Configura {p}",
  "Einheitlicher Verbindungsablauf · nur für den ausgewählten Kunden": "Processo di connessione uniforme · solo per il cliente selezionato",
  "Buchung & Verbindung werden ausschliesslich diesem Mandanten zugeordnet.": "Prenotazione e connessione vengono assegnate esclusivamente a questo mandante.",
  "**In Vorbereitung.** Der Adapter für {p} ist fertig gebaut. Für die echte Verbindung fehlen noch die Zugangsdaten und die offizielle Plattform-Freigabe (App Review). Wir zeigen bewusst keinen „Verbunden“-Status, solange nichts echt geprüft wurde.": "**In preparazione.** L’adattatore per {p} è pronto. Per la connessione reale mancano ancora le credenziali e l’approvazione ufficiale della piattaforma (App Review). Non mostriamo volutamente alcuno stato «Collegato» finché nulla è stato verificato davvero.",
  "**In Vorbereitung.** Der Adapter für {p} ist fertig gebaut. Für die echte Verbindung fehlen noch die Zugangsdaten. Wir zeigen bewusst keinen „Verbunden“-Status, solange nichts echt geprüft wurde.": "**In preparazione.** L’adattatore per {p} è pronto. Per la connessione reale mancano ancora le credenziali. Non mostriamo volutamente alcuno stato «Collegato» finché nulla è stato verificato davvero.",
  "Kanal buchen": "Prenota canale",
  "Dieser Kanal ist im Paket des Kunden enthalten.": "Questo canale è incluso nel pacchetto del cliente.",
  "Dieser Kanal ist als Zusatzkanal buchbar.": "Questo canale è prenotabile come canale aggiuntivo.",
  "Gebucht": "Prenotato",
  "Buchung entfernen": "Rimuovi prenotazione",
  "Kanal jetzt buchen": "Prenota canale ora",
  "Buchung nur durch Super Admin möglich.": "Prenotazione possibile solo dal Super Admin.",
  "Was wird benötigt?": "Cosa serve?",
  "Zugangsdaten aus dem Entwicklerportal der Plattform.": "Credenziali dal portale sviluppatori della piattaforma.",
  "So bekommen Sie die Zugangsdaten": "Come ottenere le credenziali",
  "ausblenden": "nascondi",
  "anzeigen": "mostra",
  "{p} öffnen": "Apri {p}",
  "Sobald die Plattform freigeschaltet ist, wird dieser Schritt aktiv.": "Questo passaggio diventa attivo non appena la piattaforma è abilitata.",
  "Verbinden — bald verfügbar": "Collega — presto disponibile",
  "Sobald die Zugangsdaten hinterlegt und die Plattform freigeschaltet ist, wird der Verbinden-Schritt aktiv — genau wie bei Meta.": "Non appena le credenziali sono inserite e la piattaforma è abilitata, il passaggio di connessione diventa attivo — proprio come per Meta.",
  "Provider-Status": "Stato dei provider",
  "Keine Daten verfügbar": "Nessun dato disponibile",
  "Alle Provider anzeigen": "Mostra tutti i provider",
  "Nur Super Admin": "Solo Super Admin",
  "Verbindungseinladung senden": "Invia invito di connessione",
  "Integrations-Management": "Gestione integrazioni",
  "Globale System-Integrationen": "Integrazioni di sistema globali",
  "Letzter Test:": "Ultimo test:",
  "Offene Punkte:": "Punti aperti:",
  "Verwaltung nur durch Super Admin": "Gestione solo da parte del Super Admin",
  "Noch nicht verfügbar": "Non ancora disponibile",
  "Testen": "Testa",
  "Zuletzt geprüft": "Ultima verifica",
  "Verbunden seit": "Collegato dal",
  "Globale Verwaltung nur durch Super Admin — hier nur Statusansicht.": "Gestione globale solo da parte del Super Admin — qui solo visualizzazione dello stato.",
  "Wird geladen…": "Caricamento…",
  "Noch keine Ereignisse für diese Integration.": "Nessun evento per questa integrazione finora.",
  "grün": "verde",
  "rot": "rosso",
  "Prüfpunkte": "Punti di controllo",
  "Cutover-Protokoll als CSV exportiert.": "Protocollo di cutover esportato in CSV.",
  "Export fehlgeschlagen.": "Esportazione non riuscita.",
  "Cutover-Bereitschaft geprüft.": "Prontezza al cutover verificata.",
  "Prüfung fehlgeschlagen.": "Verifica non riuscita.",
  "Stabilitäts-Schwelle: {n} Tage.": "Soglia di stabilità: {n} giorni.",
  "Speichern fehlgeschlagen.": "Salvataggio non riuscito.",
  "Cutover durchgeführt — zentrale Datenquelle aktiv.": "Cutover eseguito — fonte dati centrale attiva.",
  "Cutover fehlgeschlagen.": "Cutover non riuscito.",
  "Verifikation fehlgeschlagen.": "Verifica non riuscita.",
  "Cutover zurückgenommen — Legacy-Fallback wieder aktiv.": "Cutover annullato — il fallback legacy è di nuovo attivo.",
  "Rollback fehlgeschlagen.": "Rollback non riuscito.",
  "Diagnose wird geladen…": "Caricamento diagnosi…",
  "System-Diagnose · Cutover-Bereitschaft": "Diagnosi di sistema · prontezza al cutover",
  "Live-Zähler der genutzten Statusquelle · Ziel: Legacy-Fallback dauerhaft 0 → sicherer Umstieg auf die zentrale Datenquelle.": "Contatori in tempo reale della fonte di stato utilizzata · obiettivo: fallback legacy stabilmente a 0 → passaggio sicuro alla fonte dati centrale.",
  "Cutover-bereit — kein Legacy-Fallback aktiv.": "Pronto per il cutover — nessun fallback legacy attivo.",
  "Noch nicht cutover-bereit.": "Non ancora pronto per il cutover.",
  "Alle Provider werden aus der zentralen Zeile bedient. Beobachtung fortsetzen, dann Umstieg freigeben.": "Tutti i provider sono serviti dalla riga centrale. Continuare l’osservazione, poi approvare il passaggio.",
  "Legacy-Fallback wurde {n}× verwendet.": "Il fallback legacy è stato usato {n}×.",
  "Nicht alle Provider haben eine gültige zentrale Zeile.": "Non tutti i provider hanno una riga centrale valida.",
  "Default-Auflösung {n}× — zentrale Zeilen prüfen.": "Risoluzione predefinita {n}× — verificare le righe centrali.",
  "Stabilitäts-Beobachtung": "Osservazione della stabilità",
  "Cutover-Aufgabe erstellt": "Compito di cutover creato",
  "Bereit": "Pronto",
  "Stabil seit **{d}** Tagen · Ziel **{s}** Tage.": "Stabile da **{d}** giorni · obiettivo **{s}** giorni.",
  "Letzter Fallback:": "Ultimo fallback:",
  "Kein Fallback registriert.": "Nessun fallback registrato.",
  "Schwelle (Tage):": "Soglia (giorni):",
  "Jetzt prüfen": "Verifica ora",
  "Datenquellen-Modus": "Modalità fonte dati",
  "Aktiv: **zentrale Datenquelle** — Legacy-Fallback deaktiviert": "Attivo: **fonte dati centrale** — fallback legacy disattivato",
  "Aktiv: **Spiegelung + Legacy-Fallback** — zentrale Zeilen führend, Settings-Blob als Sicherheitsnetz.": "Attivo: **mirroring + fallback legacy** — righe centrali prioritarie, blob delle impostazioni come rete di sicurezza.",
  "Rückgängig machen": "Annulla",
  "Blockiert: nicht alle Provider haben eine zentrale Zeile": "Bloccato: non tutti i provider hanno una riga centrale",
  "Cutover jetzt durchführen": "Esegui il cutover ora",
  "Provider": "Provider",
  "Konfiguriert": "Configurato",
  "Zentrale Zeile": "Riga centrale",
  "Aufgelöste Quelle": "Fonte risolta",
  "Ja": "Sì",
  "Nein": "No",
  "Cutover-Protokoll": "Protocollo di cutover",
  "CSV-Export": "Esportazione CSV",
  "Noch keine Cutover-Ereignisse protokolliert.": "Nessun evento di cutover registrato finora.",
  "Cutover zurücknehmen?": "Annullare il cutover?",
  "Der Legacy-Settings-Fallback wird wieder aktiviert und die Stabilitäts-Beobachtung neu gestartet. Es gehen keine Daten verloren (der Settings-Blob wurde nie gelöscht).": "Il fallback legacy sulle impostazioni viene riattivato e l’osservazione della stabilità riparte. Nessun dato va perso (il blob delle impostazioni non è mai stato eliminato).",
  "Cutover jetzt durchführen?": "Eseguire il cutover ora?",
  "Ab jetzt ist **integration_connections** die einzige persistente Statusquelle. Der Legacy-Settings-Fallback wird deaktiviert (aber nicht gelöscht — jederzeit per „Rückgängig machen\" umkehrbar).": "Da ora **integration_connections** è l’unica fonte di stato persistente. Il fallback legacy sulle impostazioni viene disattivato (ma non eliminato — reversibile in qualsiasi momento con «Annulla»).",
  "Verifikations-Kette": "Catena di verifica",
  "Recheck & Konsistenzprüfung läuft…": "Ricontrollo e verifica di coerenza in corso…",
  "Blockiert: Nicht alle Provider haben eine gültige zentrale Zeile.": "Bloccato: non tutti i provider hanno una riga centrale valida.",
  "Verifikation nicht grün. Umstellung nur als bewusster Super-Admin-Override (Force).": "Verifica non verde. Passaggio solo come override consapevole del Super Admin (Force).",
  "Verifikation grün, aber Beobachtungsphase noch offen (stabil {d} / {s} Tage). Bewusst früher umstellen (Force).": "Verifica verde, ma fase di osservazione ancora aperta (stabile {d} / {s} giorni). Passare consapevolmente prima (Force).",
  "Cutover durchführen": "Esegui cutover",
  "Trotzdem durchführen (Force)": "Esegui comunque (Force)",
  "System-Health": "Stato del sistema",
  "Alle Systeme online": "Tutti i sistemi online",
  "Systeme eingeschränkt": "Sistemi limitati",
  "Details konnten nicht geladen werden": "Impossibile caricare i dettagli",
  "Kanal gebucht": "Canale prenotato",
  "Buchung entfernt": "Prenotazione rimossa",
  "Änderung fehlgeschlagen": "Modifica non riuscita",
  "Meta nicht konfiguriert": "Meta non configurato",
  "Buchung, Verbindungsstatus, Tests und Audit-Verlauf je Plattform.": "Prenotazione, stato della connessione, test e cronologia di audit per piattaforma.",
  "Kundennr.:": "N. cliente:",
  "Paket:": "Pacchetto:",
  "Verantwortlich:": "Responsabile:",
  "Nächste beste Aktion": "Prossima azione migliore",
  "Provider folgt (Phase 3)": "Provider in arrivo (fase 3)",
  "Im Paket": "Nel pacchetto",
  "Nicht im Paket": "Non nel pacchetto",
  "Verbinden": "Collega",
  "Audit-Verlauf": "Cronologia di audit",
  "Noch keine Integrationsänderungen.": "Nessuna modifica alle integrazioni finora.",
  "Kundensicht": "Vista cliente",
  "an {e}": "a {e}",
  "{n} / {t} Plattformen": "{n} / {t} piattaforme",
  "Testbeitrag veröffentlicht": "Post di prova pubblicato",
  "Veröffentlichung fehlgeschlagen": "Pubblicazione non riuscita",
  "Testbeitrag gelöscht": "Post di prova eliminato",
  "Löschen fehlgeschlagen": "Eliminazione non riuscita",
  "Nachweis per E-Mail gesendet": "Prova inviata via e-mail",
  "Keine verbundenen Plattformen zum Prüfen.": "Nessuna piattaforma collegata da verificare.",
  "Kundennummer kopiert": "Numero cliente copiato",
  "Kein Kunde ausgewählt": "Nessun cliente selezionato",
  "Wählen Sie in der Kundenübersicht oder in „Ihre nächsten Aktionen\" einen Kunden.": "Selezioni un cliente nella panoramica clienti o in «Le sue prossime azioni».",
  "Kundennr.": "N. cliente",
  "{p} Paket": "Pacchetto {p}",
  "Nicht zugewiesen": "Non assegnato",
  "Verbindungsübersicht": "Panoramica connessioni",
  "Nicht gebucht": "Non prenotato",
  "Im Paket enthalten": "Incluso nel pacchetto",
  "Als Zusatzkanal buchbar": "Prenotabile come canale aggiuntivo",
  "Beitrag ansehen": "Vedi post",
  "Erfolgsnachweis (PDF)": "Prova di successo (PDF)",
  "Senden…": "Invio…",
  "Nachweis per E-Mail senden": "Invia prova via e-mail",
  "Löschen…": "Eliminazione…",
  "Testbeitrag löschen": "Elimina post di prova",
  "Ein öffentlich sichtbarer Testbeitrag wird auf {p} veröffentlicht.": "Un post di prova visibile pubblicamente verrà pubblicato su {p}.",
  "Posten…": "Pubblicazione…",
  "Jetzt posten": "Pubblica ora",
  "Test-Veröffentlichung": "Pubblicazione di prova",
  "Noch keine Integrationsaktivitäten.": "Nessuna attività di integrazione finora.",
  "Keine internen Notizen hinterlegt.": "Nessuna nota interna inserita.",
  "Plattform verbinden": "Collega piattaforma",
  "Verbindung prüfen": "Verifica connessione",
  "Onboarding abgeschlossen – Details öffnen": "Onboarding completato – apri dettagli",
  "Zur nächsten offenen Onboarding-Aktion springen": "Vai alla prossima azione di onboarding aperta",
  "Kundenübersicht nach Plattformstatus": "Panoramica clienti per stato della piattaforma",
  "Kunden mit abgeschlossenem Test, aber noch ohne versendeten Nachweis": "Clienti con test completato, ma senza prova ancora inviata",
  "Fast fertig (2/3)": "Quasi completato (2/3)",
  "Gesamtstatus": "Stato complessivo",
  "Letzte Prüfung": "Ultima verifica",
  "Kd.": "N.",
  "Fehler:": "Errore:",
  "Detail öffnen": "Apri dettaglio",
  "Buchung bearbeiten": "Modifica prenotazione",
  "Letzte Prüfungen anzeigen": "Mostra ultime verifiche",
  "Handlungsbedarf: {n} offen": "Interventi necessari: {n} aperti",
  "Noch keine Kanäle gebucht": "Nessun canale prenotato finora",
  "Verbindung abschließen": "Completa connessione",
  "Health-Check ausstehend": "Health check in sospeso",
  "Alles funktionsfähig": "Tutto funzionante",
  "Funktionsf.": "Funz.",
  "Details öffnen": "Apri dettagli",
  "{s}–{e} von {n} Kunden": "{s}–{e} di {n} clienti",
  "{n} pro Seite": "{n} per pagina",
  "Meta-Daten konnten nicht geladen werden": "Impossibile caricare i dati Meta",
  "Einladung erstellt und per E-Mail an {e} gesendet.": "Invito creato e inviato via e-mail a {e}.",
  "Erinnerung an {e} gesendet.": "Promemoria inviato a {e}.",
  "Meta-Verbindung & Kundeneinladung": "Connessione Meta & invito cliente",
  "Einladungslink für {n} — der Kunde verbindet sein eigenes Meta-Konto (ohne Passwort-Weitergabe).": "Link di invito per {n} — il cliente collega il proprio account Meta (senza condividere la password).",
  "Einladungslink für diesen Mandanten — der Kunde verbindet sein eigenes Meta-Konto (ohne Passwort-Weitergabe).": "Link di invito per questo mandante — il cliente collega il proprio account Meta (senza condividere la password).",
  "Demo-Mandant: keine echte Meta-Verbindung möglich.": "Mandante demo: nessuna connessione Meta reale possibile.",
  "E-Mail-Dienst (Brevo) ist nicht verbunden — der Versand wird übersprungen. Der Link wird trotzdem erstellt.": "Il servizio e-mail (Brevo) non è collegato — l’invio viene saltato. Il link viene comunque creato.",
  "Einladungs-Historie": "Cronologia inviti",
  "{n} offen": "{n} aperti",
  "Noch keine Einladungen. Erzeugen Sie oben einen Link.": "Nessun invito finora. Crei un link qui sopra.",
  "{n}× erinnert": "{n}× sollecitato",
  "Verschlüsselte, einmalig gültige Einladungen · nur Super Admin": "Inviti crittografati monouso · solo Super Admin",
  "Token-Übersicht": "Panoramica token",
  "Alle Tokens verwalten": "Gestisci tutti i token",
  "Heute, {t}": "Oggi, {t}",
  "Gestern, {t}": "Ieri, {t}",
  "So verbinden Sie Instagram/Facebook – ca. 5 Minuten.": "Come collegare Instagram/Facebook – circa 5 minuti.",
  "Meta for Developers öffnen": "Apri Meta for Developers",
  "Meta-App erstellen": "Crea app Meta",
  "developers.facebook.com › Meine Apps › App erstellen › Typ Business.": "developers.facebook.com › Le mie app › Crea app › Tipo Business.",
  "Produkte aktivieren": "Attiva prodotti",
  "In der App die Produkte Facebook Login und Instagram Graph API hinzufügen.": "Aggiungere nell’app i prodotti Facebook Login e Instagram Graph API.",
  "App-ID & App-Geheimnis kopieren": "Copia ID app e segreto app",
  "Einstellungen › Allgemein: App-ID und App-Geheimnis (App Secret) kopieren.": "Impostazioni › Generali: copiare ID app e segreto app (App Secret).",
  "App-ID": "ID app",
  "App-Geheimnis (App Secret)": "Segreto app (App Secret)",
  "Integrationen-Seite › Meta verbinden": "Pagina Integrazioni › Collega Meta",
  "So aktivieren Sie Zahlungen (Stripe/TWINT) – ca. 5 Minuten.": "Come attivare i pagamenti (Stripe/TWINT) – circa 5 minuti.",
  "Stripe API-Schlüssel öffnen": "Apri chiavi API Stripe",
  "Stripe-Dashboard öffnen": "Apri dashboard Stripe",
  "dashboard.stripe.com › Entwickler › API-Schlüssel. Oben rechts den Testmodus aktiviert lassen.": "dashboard.stripe.com › Sviluppatori › Chiavi API. Lasciare attiva la modalità test in alto a destra.",
  "Geheimen Schlüssel kopieren": "Copia chiave segreta",
  "Den geheimen Schlüssel (beginnt mit sk_test_…) kopieren.": "Copiare la chiave segreta (inizia con sk_test_…).",
  "Geheimer Schlüssel": "Chiave segreta",
  "Abrechnungseinstellungen › Stripe": "Impostazioni di fatturazione › Stripe",
  "So aktivieren Sie den E-Mail-Versand (Brevo) – ca. 3 Minuten.": "Come attivare l’invio di e-mail (Brevo) – circa 3 minuti.",
  "Brevo API-Schlüssel öffnen": "Apri chiavi API Brevo",
  "Brevo-Konto öffnen": "Apri account Brevo",
  "app.brevo.com › SMTP & API › API-Schlüssel.": "app.brevo.com › SMTP & API › Chiavi API.",
  "API-Schlüssel erzeugen": "Genera chiave API",
  "Neuen API-Schlüssel generieren (v3) und den Schlüssel (xkeysib-…) kopieren.": "Generare una nuova chiave API (v3) e copiare la chiave (xkeysib-…).",
  "Absender verifizieren": "Verifica mittente",
  "In Brevo die Absender-Adresse bzw. Domain verifizieren (BREVO_SENDER_EMAIL).": "In Brevo verificare l’indirizzo o il dominio del mittente (BREVO_SENDER_EMAIL).",
  "Brevo API-Key": "Chiave API Brevo",
  "Allgemeine Einstellungen › E-Mail (Brevo)": "Impostazioni generali › E-mail (Brevo)",
  "So verbinden Sie Ihre WordPress-Website – ca. 3 Minuten.": "Come collegare il suo sito WordPress – circa 3 minuti.",
  "API-Key erzeugen": "Genera chiave API",
  "Unten Zur Konfiguration öffnen › WordPress-Verbindungen › API-Key erzeugen und kopieren.": "Qui sotto aprire Alla configurazione › Connessioni WordPress › generare e copiare la chiave API.",
  "Im WordPress-Connector eintragen": "Inserisci nel connettore WordPress",
  "Auf Ihrer WordPress-Website im SocialCheck-Connector den API-Key (Header X-SocialCheck-Key) hinterlegen.": "Sul suo sito WordPress inserire la chiave API (header X-SocialCheck-Key) nel connettore SocialCheck.",
  "Zurück in SocialCheck den Test ausführen – bei Erfolg wird der Status Verbunden.": "Tornati in SocialCheck, eseguire il test – in caso di successo lo stato diventa Collegato.",
  "Zugangsdaten bereithalten": "Tenere pronte le credenziali",
  "Halten Sie die erforderlichen Schlüssel/IDs des Anbieters bereit.": "Tenga pronte le chiavi/ID richiesti dal provider.",
  "Integrations-Guide": "Guida alle integrazioni",
  "Schritt-für-Schritt Einrichtung": "Configurazione passo dopo passo",
  "API & Zugangsrechte": "API & diritti di accesso",
  "Übersicht & Verwaltung": "Panoramica & gestione",
  "Troubleshooting": "Risoluzione dei problemi",
  "Hilfe bei typischen Fehlern": "Aiuto per gli errori tipici",
  "Best Practices": "Buone pratiche",
  "Empfehlungen & Leitfäden": "Raccomandazioni & guide",
  "7 Tage": "7 giorni",
  "90 Tage": "90 giorni",
  "Montag": "lunedì",
  "Dienstag": "martedì",
  "Mittwoch": "mercoledì",
  "Donnerstag": "giovedì",
  "Freitag": "venerdì",
  "Samstag": "sabato",
  "Sonntag": "domenica",
  "Client Key & Client Secret aus dem TikTok-Entwicklerportal.": "Client Key & Client Secret dal portale sviluppatori TikTok.",
  "Auf developers.tiktok.com anmelden und eine App registrieren.": "Accedere a developers.tiktok.com e registrare un’app.",
  "Produkt „Content Posting API“ (Login Kit) hinzufügen und zur Prüfung einreichen.": "Aggiungere il prodotto «Content Posting API» (Login Kit) e inviarlo in revisione.",
  "Client Key + Client Secret kopieren und die Redirect-URI eintragen.": "Copiare Client Key + Client Secret e inserire l’URI di reindirizzamento.",
  "Client ID & Client Secret aus dem LinkedIn-Entwicklerportal.": "Client ID & Client Secret dal portale sviluppatori LinkedIn.",
  "Auf linkedin.com/developers eine App erstellen (mit LinkedIn-Firmenseite verknüpfen).": "Creare un’app su linkedin.com/developers (collegata alla pagina aziendale LinkedIn).",
  "Produkte „Share on LinkedIn“ / „Sign In with LinkedIn“ anfordern.": "Richiedere i prodotti «Share on LinkedIn» / «Sign In with LinkedIn».",
  "Client ID + Client Secret kopieren und die Redirect-URI eintragen.": "Copiare Client ID + Client Secret e inserire l’URI di reindirizzamento.",
  "App ID & App Secret aus dem Pinterest-Entwicklerportal.": "App ID & App Secret dal portale sviluppatori Pinterest.",
  "Auf developers.pinterest.com eine App anlegen und Standard-Zugriff beantragen.": "Creare un’app su developers.pinterest.com e richiedere l’accesso standard.",
  "Benötigte Scopes (boards:read, pins:write) auswählen.": "Selezionare gli scope necessari (boards:read, pins:write).",
  "App ID + App Secret kopieren und die Redirect-URI eintragen.": "Copiare App ID + App Secret e inserire l’URI di reindirizzamento.",
  "OAuth Client ID & Secret aus der Google Cloud Console (YouTube Data API).": "OAuth Client ID & Secret dalla Google Cloud Console (YouTube Data API).",
  "In der Google Cloud Console ein Projekt anlegen und die „YouTube Data API v3“ aktivieren.": "Nella Google Cloud Console creare un progetto e attivare la «YouTube Data API v3».",
  "OAuth-Zustimmungsbildschirm konfigurieren und zur Verifizierung einreichen.": "Configurare la schermata di consenso OAuth e inviarla per la verifica.",
  "OAuth-Client-ID + Secret erstellen und die Redirect-URI eintragen.": "Creare un OAuth Client ID + Secret e inserire l’URI di reindirizzamento.",
  "OAuth 2.0 Client ID & Secret aus dem X-Entwicklerportal (kostenpflichtig).": "OAuth 2.0 Client ID & Secret dal portale sviluppatori X (a pagamento).",
  "Auf developer.x.com ein Projekt + App im Developer-Portal erstellen (API-Zugang ist kostenpflichtig).": "Su developer.x.com creare un progetto + un’app nel Developer Portal (l’accesso API è a pagamento).",
  "OAuth 2.0 aktivieren und die benötigten Scopes (tweet.write, users.read) setzen.": "Attivare OAuth 2.0 e impostare gli scope necessari (tweet.write, users.read).",
  "OAuth Client ID & Secret aus der Google Cloud Console (Business Profile API).": "OAuth Client ID & Secret dalla Google Cloud Console (Business Profile API).",
  "In der Google Cloud Console die „Business Profile API“ aktivieren und Zugriff beantragen.": "Nella Google Cloud Console attivare la «Business Profile API» e richiedere l’accesso.",
  "OAuth-Zustimmungsbildschirm konfigurieren und einreichen.": "Configurare la schermata di consenso OAuth e inviarla.",
  "Verfügbar": "Disponibile",
  "Test starten": "Avvia test",
  "Tokens erneuern": "Rinnova token",
  "Kunde verbinden": "Collega cliente",
  "Adapter-Live": "Adattatore live",
  "Legacy-Fallback": "Fallback legacy",
  "Default": "Predefinito",
  "Statisch": "Statico",
  "Recheck ausgeführt": "Ricontrollo eseguito",
  "Zentrale Zeilen vorhanden": "Righe centrali presenti",
  "Kein Fallback nötig": "Nessun fallback necessario",
  "Zentrale Zeile == Live-Recheck": "Riga centrale == ricontrollo live",
  "Übersicht konsistent": "Panoramica coerente",
  "Matrix-Isolation": "Isolamento della matrice",
  "Verlauf abrufbar": "Cronologia consultabile",
  "Down": "Offline",
  "Aktivitäten": "Attività",
  "Getestet": "Testato",
  "Nachweis versendet": "Prova inviata",
  "Funktionsfähig": "Funzionante",
  "Probleme": "Problemi",
  "Ablaufende Tokens": "Token in scadenza",
  "3/3 · Fertig": "3/3 · Completato",
  "2/3 · Getestet": "2/3 · Testato",
  "1/3 · Verbunden": "1/3 · Collegato",
  "Ablaufende Tokens (7 Tage)": "Token in scadenza (7 giorni)",
  "Ablaufende Tokens (30 Tage)": "Token in scadenza (30 giorni)",
  "Gültige Tokens": "Token validi",
  "Vorbereitet": "Predisposto",
  "Adapter vorbereitet": "Adattatore predisposto",
  "Getrennt": "Scollegato",
  "Alles OK": "Tutto OK",
  "Teilweise OK": "Parzialmente OK",
  "Adapter": "Adattatore",
  "Verbindung": "Connessione",
  "Health": "Stato",
  "E-Mail (Brevo)": "E-mail (Brevo)",
  "Verbunden · Versand aktiv": "Collegato · invio attivo",
  "API-Key hinterlegen zum Aktivieren": "Inserire la chiave API per attivare",
  "Instagram / Facebook (Meta)": "Instagram / Facebook (Meta)",
  "App-ID & Secret erforderlich": "ID app & secret richiesti",
  "Eigenen Stripe-Key hinterlegen (STRIPE_SECRET_KEY)": "Inserire la propria chiave Stripe (STRIPE_SECRET_KEY)",
  "Cloudflare Turnstile": "Cloudflare Turnstile",
  "Verbunden · Spam-Schutz aktiv": "Collegato · protezione antispam attiva",
  "Secret erforderlich": "Secret richiesto",
  "WordPress-Anbindung": "Connessione WordPress",
  "Verbunden · Lead-Intake aktiv": "Collegato · ricezione lead attiva",
  "API-Key generieren": "Genera chiave API",
  "System-Integrationen migriert (Backfill)": "Integrazioni di sistema migrate (backfill)",
  "Cutover-Bereitschaft erreicht": "Prontezza al cutover raggiunta",
  "Cutover-Schwelle geändert": "Soglia di cutover modificata",
  "Cutover durchgeführt": "Cutover eseguito",
  "Cutover zurückgenommen": "Cutover annullato",
  "Cutover-Verifikation ausgeführt": "Verifica del cutover eseguita",
  "Meta-Überwachung: Aufgabe erstellt": "Monitoraggio Meta: compito creato",
  "Adapter vorbereitet – Credentials fehlen": "Adattatore predisposto – credenziali mancanti",
  "Bereit für Verbindung": "Pronto per la connessione",
  "Credentials fehlen (Meta-Keys)": "Credenziali mancanti (chiavi Meta)",
  "Unterstützt": "Supportato",
  "Nicht konfiguriert – Meta-Keys fehlen": "Non configurato – chiavi Meta mancanti",
  "Vorbereitet – Kundenzustimmung fehlt": "Predisposto – consenso del cliente mancante",
  "Token abgelaufen – neu verbinden": "Token scaduto – ricollegare",
  "Nicht begonnen": "Non iniziato",
  "In Entwicklung": "In sviluppo",
  "Adapter bereit": "Adattatore pronto",
  "Zugangsdaten fehlen": "Credenziali mancanti",
  "Zugangsdaten hinterlegt": "Credenziali inserite",
  "Zugangsdaten ungültig": "Credenziali non valide",
  "Zugangsdaten gültig": "Credenziali valide",
  "Verbindung läuft": "Connessione in corso",
  "Autorisierung abgelaufen": "Autorizzazione scaduta",
  "Verbindungsfehler": "Errore di connessione",
  "Nicht getestet": "Non testato",
  "Nicht prüfbar": "Non verificabile",
  "Eine zentrale KEWAG-Schnittstelle – sichere Einzelverbindungen zu jeder Plattform": "Un’interfaccia KEWAG centrale – connessioni singole sicure verso ogni piattaforma",
  "Dry-Run – keine externe Veröffentlichung (Facebook/Instagram/LinkedIn/TikTok etc. NICHT aufgerufen).": "Simulazione – nessuna pubblicazione esterna (Facebook/Instagram/LinkedIn/TikTok ecc. NON richiamati).",
  "Verbindungsprobleme": "Problemi di connessione",
  "Token/Zugriff prüfen, erneut verbinden": "Verificare token/accesso, ricollegare",
  "Fehlende API-Zugangsdaten": "Credenziali API mancanti",
  "System-Zugangsdaten hinterlegen": "Inserire le credenziali di sistema",
  "Verbindung vorbereitet": "Connessione predisposta",
  "Gebuchte Kanäle autorisieren": "Autorizzare i canali prenotati",
  "Unklare Buchungen bestätigen": "Confermare le prenotazioni non chiare",
  "Kunden ohne verbundene Plattformen": "Clienti senza piattaforme collegate",
  "Kanäle einrichten": "Configurare i canali",
  "Tests ausstehend": "Test in sospeso",
  "Standard · Integration in Vorbereitung": "Standard · integrazione in preparazione",
  "Meta (Instagram/Facebook)": "Meta (Instagram/Facebook)",
  "Integration aktiv": "Integrazione attiva",
  "Keine Aktion nötig.": "Nessuna azione necessaria.",
  "Noch keine Kanäle gebucht.": "Nessun canale prenotato finora.",
  "Alle gebuchten Kanäle sind funktionsfähig — keine Aktion nötig.": "Tutti i canali prenotati sono funzionanti — nessuna azione necessaria.",
  "Erneut verbinden": "Ricollega",
  "Veröffentlichung blockiert": "Pubblicazione bloccata",
  "Keine Veröffentlichung möglich": "Nessuna pubblicazione possibile",
  "Noch keine Veröffentlichung": "Nessuna pubblicazione finora",
  "Einrichtung erforderlich": "Configurazione necessaria",
  "Bestätigung erforderlich": "Conferma necessaria",
  "Zuverlässigkeit unklar": "Affidabilità incerta",
  "Technische Integration für diese Plattform ist noch nicht verfügbar.": "L’integrazione tecnica per questa piattaforma non è ancora disponibile.",
  "Meta ist nicht konfiguriert (App-ID & Secret fehlen).": "Meta non è configurato (ID app & secret mancanti).",
  "Keine aktive Meta-Verbindung für diesen Kanal.": "Nessuna connessione Meta attiva per questo canale.",
  "Verbindung erfolgreich geprüft.": "Connessione verificata con successo.",
  "Verbindung fehlgeschlagen – bitte erneut verbinden.": "Connessione non riuscita – ricollegare.",
  "Verbindung ausstehend.": "Connessione in sospeso.",
  "Diese Integration ist technisch noch nicht verfügbar.": "Questa integrazione non è ancora disponibile tecnicamente.",
  "Integrations-Engine": "Motore di integrazione",
  "Übersicht & Statusmaschine aktiv": "Panoramica & macchina a stati attive",
  "Token-Service": "Servizio token",
  "Verschlüsselung aktiv": "Crittografia attiva",
  "Encryption-Key fehlt — Verbindungen gesperrt": "Chiave di crittografia mancante — connessioni bloccate",
  "Webhook-Listener": "Listener webhook",
  "Echtzeit-Webhooks folgen (Phase 6)": "Webhook in tempo reale in arrivo (fase 6)",
  "Keine Verbindung": "Nessuna connessione",
  "Stripe-Key ungültig oder nicht erreichbar.": "Chiave Stripe non valida o non raggiungibile.",
  "Cutover jetzt möglich: System-Integrationen auf zentrale Datenquelle umstellen": "Cutover ora possibile: passare le integrazioni di sistema alla fonte dati centrale",
  "Legacy-Fallback erneut aufgetreten – Beobachtung neu gestartet": "Fallback legacy verificatosi di nuovo – osservazione riavviata",
  "Recheck aller Provider ausgeführt": "Ricontrollo di tutti i provider eseguito",
  "Ein Provider-Recheck warf einen Fehler.": "Un ricontrollo del provider ha generato un errore.",
  "Alle Provider haben eine gültige zentrale Zeile": "Tutti i provider hanno una riga centrale valida",
  "Mindestens ein Provider ohne zentrale Zeile.": "Almeno un provider senza riga centrale.",
  "Kein Legacy-Fallback nötig (Quelle: zentrale Zeile)": "Nessun fallback legacy necessario (fonte: riga centrale)",
  "Mindestens ein Provider wird via Fallback aufgelöst.": "Almeno un provider viene risolto tramite fallback.",
  "Zentrale Zeile stimmt mit Live-Recheck überein": "La riga centrale corrisponde al ricontrollo live",
  "Abweichung zwischen zentraler Zeile und Recheck.": "Discrepanza tra riga centrale e ricontrollo.",
  "Übersicht/Karten baubar & konsistent": "Panoramica/schede generabili & coerenti",
  "Übersicht konnte nicht konsistent gebaut werden.": "Non è stato possibile generare la panoramica in modo coerente.",
  "Keine System-Zeilen in der Mandanten-Matrix": "Nessuna riga di sistema nella matrice dei mandanti",
  "System-Dokumente ohne scope=system gefunden.": "Trovati documenti di sistema senza scope=system.",
  "Verlauf je Provider abrufbar": "Cronologia consultabile per provider",
  "Verlaufsabfrage fehlgeschlagen.": "Richiesta della cronologia non riuscita.",
  "Meta noch nicht konfiguriert — App-ID & Secret fehlen": "Meta non ancora configurato — ID app & secret mancanti",
  "Bereit zum Verbinden": "Pronto per il collegamento",
  "Meta-Grundlage aktiviert und verschlüsselt gespeichert – OAuth kann gestartet werden.": "Base Meta attivata e salvata in forma crittografata – OAuth può essere avviato.",
  "Config-ID gespeichert – OAuth nutzt jetzt Login for Business (config_id, kein scope).": "Config-ID salvato – OAuth ora usa Login for Business (config_id, nessuno scope).",
  "Config-ID entfernt – OAuth nutzt wieder scope.": "Config-ID rimosso – OAuth usa di nuovo scope.",
  "Keine Facebook-Seite zurückgegeben — Berechtigung 'pages_show_list' fehlt.": "Nessuna pagina Facebook restituita — manca l’autorizzazione 'pages_show_list'.",
  "Keine Facebook-Seite zurückgegeben. Das verbundene Meta-Konto verwaltet keine Seite oder ist nicht als Seiten-Admin zugeordnet.": "Nessuna pagina Facebook restituita. L’account Meta collegato non gestisce alcuna pagina o non è assegnato come amministratore della pagina.",
  "Meta OAuth": "Meta OAuth",
  "Verbindung gültig.": "Connessione valida.",
  "Instagram-Testbeitrag benötigt ein Bild/Video (Content-Container). Nutzen Sie für den Textnachweis bitte Facebook — der Instagram-Test folgt.": "Il post di prova Instagram richiede un’immagine/video (contenitore di contenuti). Per la prova testuale usi Facebook — il test Instagram seguirà.",
  "Testbeitrag erfolgreich veröffentlicht.": "Post di prova pubblicato con successo.",
  "Testbeitrag gelöscht.": "Post di prova eliminato.",
  "Keine Verbindung.": "Nessuna connessione.",
  "Token konnte nicht entschlüsselt werden.": "Impossibile decrittografare il token.",
  "Token nicht lesbar.": "Token non leggibile.",
  "System (Meta-Überwachung)": "Sistema (monitoraggio Meta)",
  "Meta-Überwachung": "Monitoraggio Meta",
  "Keine Kunden in Stufe 2 (2/3)": "Nessun cliente nella fase 2 (2/3)",
  "Autorisierung erforderlich": "Autorizzazione necessaria",
  "Aufmerksamkeit erforderlich": "Attenzione necessaria",
  "App & API": "App & API",
  "WordPress Verbindung": "Connessione WordPress",
  "KI-Dienst": "Servizio IA",
  "E-Mail Dienst": "Servizio e-mail",
  "Social (Meta)": "Social (Meta)",
  "Speicherplatz": "Spazio di archiviazione",
  "FastAPI · Antwort ok": "FastAPI · risposta ok",
  "MongoDB · verbunden": "MongoDB · collegato",
  "Authentifizierung": "Autenticazione",
  "Sitzungsspeicher nicht erreichbar": "Archivio sessioni non raggiungibile",
  "Objektspeicher": "Object storage",
  "OpenAI Vision · einsatzbereit": "OpenAI Vision · operativo",
  "Kein Schlüssel hinterlegt": "Nessuna chiave inserita",
  "Geplante Jobs": "Job pianificati",
  "Tages-Digest · Monatsreport; Kanal-Veröffentlichung noch nicht verfügbar": "Riepilogo giornaliero · report mensile; pubblicazione sui canali non ancora disponibile",
  "Kein automatisches Backup konfiguriert": "Nessun backup automatico configurato",
  "Kontaktpflege (Brevo)": "Gestione contatti (Brevo)",
  "Status nicht lesbar": "Stato non leggibile",
  "Marken-Profil geändert": "Profilo del marchio modificato",
  "Brand-Asset hochgeladen": "Asset del marchio caricato",
  "Team informiert": "Team informato",
  "Beitrag geändert": "Post modificato",
  "Beitrag reaktiviert": "Post riattivato",
  "Freigabe-Entscheidung": "Decisione di approvazione",
  "Sammel-Freigabe": "Approvazione multipla",
  "Fotoauftrag-Aktion": "Azione incarico foto",
  "Fotoauftrag erstellt": "Incarico foto creato",
  "Medien hochgeladen": "Media caricati",
  "Medien gelöscht": "Media eliminati",
  "Rechnungs-Aktion": "Azione di fatturazione",
  "Preise aktualisiert": "Prezzi aggiornati",
  "Ticket-Antwort": "Risposta al ticket",
  "Mandant geändert": "Mandante modificato",
  "Mandant gesperrt": "Mandante bloccato",
  "Mandant aktiviert": "Mandante attivato",
  "Passwort geändert": "Password modificata",
  "Passwort zurückgesetzt (Admin)": "Password reimpostata (admin)",
  "Notfall-Zugang ausgelöst": "Accesso di emergenza attivato",
  "Passwort per Notfall-Zugang zurückgesetzt": "Password reimpostata tramite accesso di emergenza",
  "Passwort-Reset angefordert": "Reimpostazione password richiesta",
  "Passwort selbst zurückgesetzt": "Password reimpostata autonomamente",
  "E-Mail Versand": "Invio e-mail",
  "Konfiguriert · verbindbar": "Configurato · collegabile",
  "Meta-Einladung erstellt": "Invito Meta creato",
  "Meta-Einladung widerrufen": "Invito Meta revocato",
  "Noch nicht konfiguriert (Google OAuth erforderlich)": "Non ancora configurato (Google OAuth necessario)",
  "Stripe-Key erforderlich": "Chiave Stripe richiesta",
  "Technische Integration noch nicht verfügbar": "Integrazione tecnica non ancora disponibile",
  "gebucht": "prenotato",
  "nicht gebucht": "non prenotato",
  // --- Phase 4 supplement ---
  "Setzen Sie ein neues Passwort für": "Impostate una nuova password per",
  "nicht erreichbar": "non raggiungibile",
  // --- Phase 4 (Super-Admin-Werkzeuge) ---
  "Kopieren nicht möglich – bitte manuell markieren.": "Copia non riuscita – selezionare il link manualmente.",
  "Erzeugung fehlgeschlagen.": "Generazione non riuscita.",
  "Konto-Wiederherstellung": "Ripristino dell'account",
  "Notfall-Zugang · nur Passwort-Reset des Systeminhabers": "Accesso di emergenza · solo reimpostazione della password del titolare del sistema",
  "Dieses Konto ist ausschliesslich für den Notfall.": "Questo account è riservato esclusivamente alle emergenze.",
  "Sie können **nur** einen Reset-Link für **{who}** erzeugen.": "Potete **solo** generare un link di reimpostazione per **{who}**.",
  "den Systeminhaber": "il titolare del sistema",
  "Der Link ist **15 Minuten** gültig und wird **nur einmal** hier angezeigt.": "Il link è valido **15 minuti** e viene mostrato qui **una sola volta**.",
  "Sie legen **kein Passwort** selbst fest – das erledigt der Inhaber über den Link.": "Non impostate **alcuna password** voi stessi – lo fa il titolare tramite il link.",
  "Nach erfolgreicher Nutzung wird dieses Konto **automatisch deaktiviert**.": "Dopo un utilizzo riuscito, questo account viene **disattivato automaticamente**.",
  "Verfügbarkeit wird geprüft…": "Verifica della disponibilità…",
  "Die Wiederherstellung ist derzeit nicht verfügbar. Sie ist entweder nicht scharfgeschaltet oder wurde bereits verwendet.": "Il ripristino non è attualmente disponibile. Non è attivato oppure è già stato utilizzato.",
  "Wird erzeugt…": "Generazione in corso…",
  "Einmaligen Reset-Link erzeugen": "Genera un link di reimpostazione monouso",
  "Reset-Link für **{email}** erzeugt. Gültig für **{minutes} Minuten**. Kopieren Sie ihn jetzt – er wird nicht erneut angezeigt.": "Link di reimpostazione generato per **{email}**. Valido **{minutes} minuti**. Copiatelo ora – non verrà più mostrato.",
  "Einmaliger Reset-Link": "Link di reimpostazione monouso",
  "Öffnen Sie den Link im Browser und vergeben Sie dort ein neues Passwort für den Systeminhaber. Dieses Notfall-Konto ist danach automatisch deaktiviert.": "Aprite il link nel browser e impostate una nuova password per il titolare del sistema. Questo account di emergenza viene poi disattivato automaticamente.",
  "Status konnte nicht geladen werden.": "Impossibile caricare lo stato.",
  "Bitte bestätigen Sie mit Ihrem eigenen Passwort.": "Confermate con la vostra password.",
  "Das Recovery-Passwort muss mindestens 8 Zeichen lang sein.": "La password di ripristino deve contenere almeno 8 caratteri.",
  "Die beiden Recovery-Passwörter stimmen nicht überein.": "Le due password di ripristino non coincidono.",
  "Notfall-Wiederherstellung scharfgeschaltet.": "Ripristino di emergenza attivato.",
  "Scharfschalten fehlgeschlagen.": "Attivazione non riuscita.",
  "Notfall-Wiederherstellung deaktiviert.": "Ripristino di emergenza disattivato.",
  "Deaktivieren fehlgeschlagen.": "Disattivazione non riuscita.",
  "Notfall-Wiederherstellung einrichten": "Configura il ripristino di emergenza",
  "Unabhängiges Zweitkonto, das im Ernstfall Ihren Zugang wiederherstellt.": "Secondo account indipendente che ripristina il vostro accesso in caso di emergenza.",
  "Diese Funktion ist ausschliesslich für den Systeminhaber (Super Admin) verfügbar.": "Questa funzione è disponibile esclusivamente per il titolare del sistema (super amministratore).",
  "Die Konto-Wiederherstellung ist in dieser Umgebung nicht aktiviert.": "Il ripristino dell'account non è attivato in questo ambiente.",
  "Status:": "Stato:",
  "Scharfgeschaltet ✔": "Attivato ✔",
  "Nicht scharfgeschaltet": "Non attivato",
  "Notfall-Konto:": "Account di emergenza:",
  "Zuletzt verwendet:": "Ultimo utilizzo:",
  "Das Notfall-Konto darf **nur** Ihren Passwort-Reset auslösen – **keinen** App-/Datenzugriff.": "L'account di emergenza può **solo** avviare la reimpostazione della password – **nessun** accesso all'app o ai dati.",
  "Das Recovery-Passwort setzen **Sie selbst** und bewahren es sicher auf (nie im Chat).": "**Impostate voi stessi** la password di ripristino e conservatela in modo sicuro (mai nella chat).",
  "Nach einer erfolgreichen Wiederherstellung wird das Konto **automatisch deaktiviert**.": "Dopo un ripristino riuscito, l'account viene **disattivato automaticamente**.",
  "Ihr eigenes Passwort (Bestätigung)": "La vostra password (conferma)",
  "Passwort von admin@socialcheck.ch": "Password di admin@socialcheck.ch",
  "Recovery-Passwort (für das Notfall-Konto)": "Password di ripristino (per l'account di emergenza)",
  "Mindestens 8 Zeichen": "Almeno 8 caratteri",
  "Recovery-Passwort bestätigen": "Conferma la password di ripristino",
  "Recovery-Passwort wiederholen": "Ripeti la password di ripristino",
  "Wird gespeichert…": "Salvataggio…",
  "Recovery-Passwort neu setzen": "Reimposta la password di ripristino",
  "Scharfschalten": "Attiva",
  "Notfall-Wiederherstellung deaktivieren": "Disattiva il ripristino di emergenza",
  "Das Passwort muss mindestens 8 Zeichen lang sein.": "La password deve contenere almeno 8 caratteri.",
  "Die beiden Passwörter stimmen nicht überein.": "Le due password non coincidono.",
  "Das Zurücksetzen ist fehlgeschlagen.": "La reimpostazione non è riuscita.",
  "Passwort sicher zurücksetzen": "Reimposta la password in sicurezza",
  "SocialCheck · Konto-Wiederherstellung": "SocialCheck · Ripristino dell'account",
  "Reset-Link wird geprüft…": "Verifica del link di reimpostazione…",
  "Dieser Reset-Link ist ungültig, abgelaufen oder wurde bereits verwendet.": "Questo link di reimpostazione non è valido, è scaduto o è già stato utilizzato.",
  "Zur Anmeldung": "Vai all'accesso",
  "Setzen Sie ein neues Passwort für **{email}**.": "Impostate una nuova password per **{email}**.",
  "das Konto": "l'account",
  "Neues Passwort": "Nuova password",
  "Passwort bestätigen": "Conferma la password",
  "Passwort wiederholen": "Ripeti la password",
  "Wird gesetzt…": "Impostazione in corso…",
  "Passwort setzen": "Imposta la password",
  "Das Passwort wurde sicher zurückgesetzt. Alle bisherigen Sitzungen wurden abgemeldet. Das Notfall-Konto ist jetzt wieder deaktiviert.": "La password è stata reimpostata in modo sicuro. Tutte le sessioni precedenti sono state disconnesse. L'account di emergenza è ora nuovamente disattivato.",
  "Bitte bestätigen Sie Ihr Passwort.": "Confermate la vostra password.",
  "Notfall-Zugang · Super-Admin-Passwort": "Accesso di emergenza · password super amministratore",
  "Einmaliger, sicherer Reset ohne E-Mail-Versand.": "Reimpostazione unica e sicura senza invio di e-mail.",
  "Diese Funktion ist ausschliesslich für das KEWAG-Admin-Konto verfügbar.": "Questa funzione è disponibile esclusivamente per l'account amministratore KEWAG.",
  "Wichtige Sicherheitshinweise": "Avvertenze di sicurezza importanti",
  "Der Reset-Link wird **nur einmalig** hier angezeigt und ist **15 Minuten** gültig.": "Il link di reimpostazione viene mostrato qui **una sola volta** ed è valido **15 minuti**.",
  "Sie legen **kein Passwort** selbst fest – das erledigt der Super-Admin über den Link.": "Non impostate **alcuna password** voi stessi – lo fa il super amministratore tramite il link.",
  "Nach dem Erzeugen ist der Notfall-Zugang **dauerhaft deaktiviert**.": "Dopo la generazione, l'accesso di emergenza è **disattivato in modo permanente**.",
  "Der Link darf **nicht** per Chat oder E-Mail weitergegeben werden.": "Il link **non** deve essere condiviso tramite chat o e-mail.",
  "Der einmalige Notfall-Zugang ist nicht (mehr) verfügbar. Er wurde bereits verwendet und ist dauerhaft deaktiviert.": "L'accesso di emergenza monouso non è (più) disponibile. È già stato utilizzato ed è disattivato in modo permanente.",
  "Der Notfall-Zugang ist für Teamkonten geschlossen. Teamkonten erhalten keinen Reset-Link für den Systeminhaber.": "L'accesso di emergenza è chiuso per gli account del team. Gli account del team non ricevono alcun link di reset per il proprietario del sistema.",
  "Zugangskonfiguration & Einrichtung": "Configurazione accessi & attivazione",
  "Wertfreier Status: nur Schlüsselnamen und maskierte Konten – keine Secret-Werte.": "Stato senza valori: solo nomi delle chiavi e account mascherati – nessun valore segreto.",
  "Konfigurationswerte allein sind kein Nachweis eines erfolgreichen Logins.": "I soli valori di configurazione non provano un accesso riuscito.",
  "Zugangsstatus konnte nicht geladen werden.": "Impossibile caricare lo stato degli accessi.",
  "Owner (Super Admin)": "Proprietario (Super Admin)",
  "Altkonto ADMIN_EMAIL": "Vecchio account ADMIN_EMAIL",
  "Altname": "vecchio nome",
  "Schlüssel": "Chiave",
  "Konfiguration": "Configurazione",
  "Einrichtung": "Attivazione",
  "Login beobachtet": "Accesso osservato",
  "kein Konto": "nessun account",
  "konfiguriert": "configurato",
  "Kunden": "Clienti",
  "aus": "disattivato",
  "Konflikt": "Conflitto",
  "eingerichtet": "attivo",
  "Erstanmeldung offen": "Primo accesso in sospeso",
  "Konto fehlt": "Account mancante",
  "übernommen": "applicato",
  "unverändert": "invariato",
  "Ausgangsstand erfasst": "stato iniziale registrato",
  "nicht gesetzt": "non impostato",
  "nur Altname gesetzt": "impostato solo il nome precedente",
  "ungültig – nicht übernommen": "non valido – non applicato",
  "Übernahme läuft": "applicazione in corso",
  "Geheimnis-Übernahme": "Applicazione del segreto",
  "zuletzt übernommen: {date}": "ultima applicazione: {date}",
  "Ausgangsstand: {date}": "stato iniziale: {date}",
  "Letzte Prüfung beim Backend-Start: {date}": "Ultimo controllo all’avvio del backend: {date}",
  "Neue Werte wirken erst nach Backend-Neustart (Vorschau) bzw. Neu veröffentlichen (LIVE); Speichern allein ändert kein Passwort.": "I nuovi valori hanno effetto solo dopo il riavvio del backend (anteprima) o una nuova pubblicazione (LIVE); il solo salvataggio non modifica alcuna password.",
  "Passwort fehlt": "Password mancante",
  "Ihr Passwort ({email}) zur Bestätigung": "La vostra password ({email}) per conferma",
  "Passwort eingeben": "Inserisci la password",
  "Öffnen Sie den Link im Browser des Super-Admins und vergeben Sie dort ein neues Passwort. Der Notfall-Zugang ist jetzt dauerhaft deaktiviert.": "Aprite il link nel browser del super amministratore e impostate una nuova password. L'accesso di emergenza è ora disattivato in modo permanente.",
  "Einrichtung konnte nicht gestartet werden.": "Impossibile avviare la configurazione.",
  "Code ungültig. Bitte erneut versuchen.": "Codice non valido. Riprovare.",
  "Zwei-Faktor-Authentifizierung erforderlich": "Autenticazione a due fattori richiesta",
  "Deine Organisation verlangt 2FA. Bitte richte sie jetzt ein, um fortzufahren.": "La vostra organizzazione richiede la 2FA. Configuratela ora per continuare.",
  "2FA ist jetzt aktiv. Bewahre diese Backup-Codes sicher auf – jeder ist einmalig nutzbar.": "La 2FA è ora attiva. Conservate questi codici di backup in modo sicuro – ognuno è utilizzabile una sola volta.",
  "Codes kopieren": "Copia i codici",
  "Weiter zur App": "Continua verso l'app",
  "1. Öffne deine Authenticator-App (z. B. Google Authenticator, Microsoft Authenticator).": "1. Aprite la vostra app di autenticazione (ad es. Google Authenticator, Microsoft Authenticator).",
  "2. Scanne den QR-Code oder gib den Schlüssel manuell ein.": "2. Scansionate il codice QR o inserite la chiave manualmente.",
  "3. Trage den angezeigten 6-stelligen Code ein.": "3. Inserite il codice a 6 cifre visualizzato.",
  "2FA QR-Code": "Codice QR 2FA",
  "Manueller Schlüssel": "Chiave manuale",
  "Authenticator-Code": "Codice dell'autenticatore",
  "2FA aktivieren & fortfahren": "Attiva la 2FA e continua",
  "Neu": "Nuovo",
  "Geprüft": "Verificato",
  "Demo aktiv": "Demo attiva",
  "Einladungslink kopiert": "Link d'invito copiato",
  "Neuer Einladungslink kopiert (48 h gültig)": "Nuovo link d'invito copiato (valido 48 h)",
  "Als geprüft markiert": "Contrassegnato come verificato",
  "Anfrage freigegeben": "Richiesta approvata",
  "Demo-Zugang erstellt": "Accesso demo creato",
  "Zugangs- und Demo-Anfragen von socialcheck.ch prüfen, freigeben und Demo-Zugänge erstellen.": "Esaminare e approvare le richieste di accesso e demo da socialcheck.ch e creare accessi demo.",
  "E-Mail-Dienst verbunden": "Servizio e-mail connesso",
  "E-Mail-Dienst nicht verbunden": "Servizio e-mail non connesso",
  "Firma, Name, E-Mail…": "Azienda, nome, e-mail…",
  "Keine Demo-Anfragen für diesen Filter.": "Nessuna richiesta demo per questo filtro.",
  "Upgrade gewünscht": "Upgrade richiesto",
  "Nur Super Admin kann bearbeiten": "Solo il super amministratore può modificare",
  "Prüfen": "Verifica",
  "Link": "Link",
  "Einladung senden": "Invia invito",
  "Audit": "Audit",
  "Ein 24-Stunden-Demo-Zugang wurde angelegt.": "È stato creato un accesso demo di 24 ore.",
  "Die Einladung wurde per E-Mail versendet.": "L'invito è stato inviato via e-mail.",
  "E-Mail-Dienst nicht verbunden — bitte Einladungslink manuell teilen.": "Servizio e-mail non connesso — condividere il link d'invito manualmente.",
  "E-Mail versendet": "E-mail inviata",
  "Einladungslink (24 h gültig)": "Link d'invito (valido 24 h)",
  "Zu den Demo-Zugängen": "Vai agli accessi demo",
  "Kein Mandant für dieses Demo-Konto vorhanden": "Nessun mandante disponibile per questo account demo",
  "Kunden-Cockpit geöffnet (nur-lesend)": "Cockpit cliente aperto (sola lettura)",
  "Als Kunde angelegt — Nr. {num}, {plan}": "Creato come cliente — n. {num}, {plan}",
  "/Mt.": "/mese",
  "Demo um 24 Stunden verlängert": "Demo prolungata di 24 ore",
  "Demo sofort abgelaufen": "Demo scaduta immediatamente",
  "Demo-Zugang gesperrt": "Accesso demo bloccato",
  "Demo gelöscht (anonymisiert)": "Demo eliminata (anonimizzata)",
  "Demo-Zugängen": "accessi demo",
  "Aktive und abgelaufene 24-Stunden-Demo-Mandanten verwalten.": "Gestire i mandanti demo di 24 ore attivi e scaduti.",
  "Noch keine Demo-Zugänge erstellt.": "Nessun accesso demo ancora creato.",
  "Nicht aktiviert": "Non attivato",
  "Läuft ab in {x}": "Scade tra {x}",
  "Ablauf:": "Scadenza:",
  "gültig bis {d}": "valido fino al {d}",
  "Min": "min",
  "Jetzt ablaufen": "Scadi ora",
  "Sperren": "Blocca",
  "Demo löschen": "Elimina demo",
  "E-Mail-Dienst nicht verbunden – Link kann kopiert werden": "Servizio e-mail non connesso – il link può essere copiato",
  "Demo löschen (anonymisieren)?": "Eliminare la demo (anonimizzare)?",
  "Name, E-Mail und Firma von {name} werden anonymisiert, der Zugang gesperrt und aus den Listen ausgeblendet. Das Audit-Log bleibt erhalten. Diese Aktion kann nicht rückgängig gemacht werden.": "Nome, e-mail e azienda di {name} verranno anonimizzati, l'accesso bloccato e nascosto dagli elenchi. Il registro di audit viene conservato. Questa azione non può essere annullata.",
  "In zahlenden Kunden umwandeln": "Converti in cliente pagante",
  "{name} wird zu einem echten Mandanten mit Kundennummer und Vollzugang (ohne 24h-Ablauf). Die Demo-Inhalte bleiben erhalten. Bitte Paket & Preis wählen.": "{name} diventa un mandante reale con numero cliente e accesso completo (senza scadenza a 24 h). I contenuti demo vengono conservati. Selezionare pacchetto e prezzo.",
  "Paket wählen": "Seleziona pacchetto",
  "Preis (CHF / Monat)": "Prezzo (CHF / mese)",
  "z. B. 290": "ad es. 290",
  "Vorschlag aus der Paket-Preisliste — überschreibbar. Preise pflegbar unter „Einstellungen“.": "Suggerimento dal listino prezzi dei pacchetti — modificabile. I prezzi si gestiscono in «Impostazioni».",
  "DB-Backup erstellt ({n} Collections)": "Backup del database creato ({n} collezioni)",
  "Backup fehlgeschlagen": "Backup non riuscito",
  "{v} als aktuelle Version erfasst (Live-Stand {c})": "{v} registrata come versione attuale (stato live {c})",
  "Erfassung fehlgeschlagen": "Registrazione non riuscita",
  "{v} ist jetzt aktuell · {n} als „ersetzt“ markiert": "{v} è ora attuale · {n} contrassegnata/e come «sostituita»",
  "Zeitüberschreitung": "Timeout",
  "{v} ist jetzt die aktuelle Version": "{v} è ora la versione attuale",
  "{v} archiviert": "{v} archiviata",
  "Datenbank wiederhergestellt": "Database ripristinato",
  "Rollback-Plan bestätigt & protokolliert.": "Piano di rollback confermato e registrato.",
  "Produktion (Live)": "Produzione (live)",
  "Preview (Entwicklung)": "Anteprima (sviluppo)",
  "Umgebungen · Live-Status": "Ambienti · stato live",
  "Erneut prüfen": "Verifica di nuovo",
  "Diese Umgebung": "Questo ambiente",
  "Prüfe…": "Verifica…",
  "Erreichbar · Live": "Raggiungibile · live",
  "Nicht erreichbar": "Non raggiungibile",
  "Deploy erforderlich ·": "Deploy necessario ·",
  "{n} Commit(s) hinter Preview": "{n} commit indietro rispetto all'anteprima",
  "Produktion {pv} · Preview {prev}": "Produzione {pv} · anteprima {prev}",
  "Auf Preview-Stand": "Allineato all'anteprima",
  "Produktion neuer als Preview": "Produzione più recente dell'anteprima",
  "Commit unterschiedlich": "Commit differente",
  "Vergleich nicht möglich": "Confronto non possibile",
  "Preview-Identität nicht ermittelt · Commit fehlt": "Identità dell'anteprima non determinata · commit mancante",
  "Produktions-Identität nicht ermittelt · Commit fehlt": "Identità della produzione non determinata · commit mancante",
  "Ungültige Commit-ID gemeldet": "ID commit segnalato non valido",
  "Commit nicht in der Historie · Vergleich nicht möglich": "Commit non presente nella cronologia · confronto non possibile",
  "Jetzt deployen · Kurzanleitung": "Esegui il deploy ora · guida rapida",
  "Preview gründlich testen (dieser Stand: **{v}**).": "Testare accuratamente l'anteprima (questo stato: **{v}**).",
  "In Emergent oben auf **Deploy** klicken.": "In Emergent, cliccare in alto su **Deploy**.",
  "**„Deploy Now“** bestätigen und den bestehenden Deployment-Slot (app.socialcheck.ch) ersetzen.": "Confermare **«Deploy Now»** e sostituire lo slot di deploy esistente (app.socialcheck.ch).",
  "~10–15 Min warten → Produktion läuft danach auf **{v}**, diese Karte zeigt dann automatisch „Auf Preview-Stand“.": "Attendere ~10–15 min → la produzione girerà poi su **{v}**, questa scheda mostrerà quindi automaticamente «Allineato all'anteprima».",
  "Update eines bestehenden Deployments = **keine Zusatzkosten** (50 Credits/Monat pro App). Rollback jederzeit möglich. Ich (Assistent) deploye nicht selbst — das bleibt bewusst deine Freigabe.": "L'aggiornamento di un deploy esistente = **nessun costo aggiuntivo** (50 crediti/mese per app). Rollback possibile in qualsiasi momento. Io (l'assistente) non eseguo il deploy — resta volutamente una vostra decisione.",
  "Commit": "Commit",
  "Branch": "Branch",
  "Gemeldete Umgebung": "Ambiente segnalato",
  "verbunden": "connesso",
  "Von hier aus nicht erreichbar": "Non raggiungibile da qui",
  ". Mögliche Gründe: Cloudflare/CORS blockiert den Cross-Abruf, die Umgebung wurde noch nicht mit dieser Version deployt, oder sie ist offline.": ". Possibili cause: Cloudflare/CORS blocca la chiamata cross-domain, l'ambiente non è ancora stato distribuito con questa versione, oppure è offline.",
  "Kein erfundener Status": "Nessuno stato inventato",
  "— öffne die Umgebung direkt, um ihren echten Live-Status zu sehen.": "— aprite direttamente l'ambiente per vederne il vero stato live.",
  "Jede Karte ruft den echten öffentlichen Endpoint": "Ogni scheda richiama il vero endpoint pubblico",
  "der jeweiligen Umgebung ab (Version/Commit/Branch/DB). Die Umgebung, die du gerade geöffnet hast, ist immer 100 % live & prüfbar.": "dell'ambiente in questione (versione/commit/branch/DB). L'ambiente che avete appena aperto è sempre live e verificabile al 100 %.",
  "Aktuelle Version": "Versione attuale",
  "entspricht dem laufenden Code": "corrisponde al codice in esecuzione",
  "Stand ist echt & live.": "Lo stato è reale e live.",
  "Laufender Code ist neuer als die als „aktuell“ markierte Version": "Il codice in esecuzione è più recente della versione contrassegnata come «attuale»",
  "Der Commit der aktuellen Version {v} ist in dieser Umgebung nicht auffindbar.": "Il commit della versione attuale {v} non è reperibile in questo ambiente.",
  "Live-Stand ist {n} Commit(s) neuer als {v}.": "Lo stato live è {n} commit più recente di {v}.",
  "Laufender Commit": "Commit in esecuzione",
  "committet": "committato il",
  "Als aktuelle Version erfassen": "Registra come versione attuale",
  "Vorherige stabile": "Precedente stabile",
  "Vorschau (RC)": "Anteprima (RC)",
  "Stabil": "Stabile",
  "Ersetzt": "Sostituita",
  "Archiviert": "Archiviata",
  "Versionen gesamt": "Versioni totali",
  "HEAD-Commit": "Commit HEAD",
  "Versionen vergleichen: {n}/2 ausgewählt": "Confronta versioni: {n}/2 selezionate",
  "{n} Vorschau-Version(en) offen — jüngste: **{v}**": "{n} versione/i di anteprima aperte — la più recente: **{v}**",
  "Alle Vorschau als aktuell übernehmen": "Adotta tutte le anteprime come attuali",
  "Ersetzte/archivierte ausblenden": "Nascondi sostituite/archiviate",
  "Ersetzte/archivierte anzeigen ({n})": "Mostra sostituite/archiviate ({n})",
  "Aktuell": "Attuale",
  "Geschützt": "Protetta",
  "Build": "Build",
  "DB-Migration:": "Migrazione DB:",
  "ja": "sì",
  "nein": "no",
  "Test:": "Test:",
  "Rollback": "Rollback",
  "verfügbar": "disponibile",
  "n/v": "n/d",
  "Ausgewählt": "Selezionata",
  "Vergleichen": "Confronta",
  "Screenshots ({n})": "Screenshot ({n})",
  "Rollback vorbereiten": "Prepara il rollback",
  "Als aktuell setzen": "Imposta come attuale",
  "Snapshots & DB-Backups": "Snapshot e backup del DB",
  "Backup jetzt erstellen": "Crea backup ora",
  "Noch keine Backups registriert.": "Nessun backup registrato finora.",
  "{n} Collections": "{n} collezioni",
  "Auto": "Auto",
  "fehlt": "mancante",
  "Beim Start jedes Arbeitspakets wird automatisch ein Vorher-Snapshot angelegt und mit der jeweiligen Version verlinkt — als 1-Klick-Rollback-Ziel.": "All'inizio di ogni pacchetto di lavoro viene creato automaticamente uno snapshot preliminare, collegato alla versione corrispondente — come destinazione di rollback con un clic.",
  "Echte Versionsdaten (Code + Backup + Belege).": "Dati di versione reali (codice + backup + prove).",
  "Umgebung": "Ambiente",
  "Autor": "Autore",
  "DB-Migration": "Migrazione DB",
  "DB-Backup": "Backup DB",
  "Teststatus": "Stato dei test",
  "Rollback verfügbar / getestet": "Rollback disponibile / testato",
  "Module": "Moduli",
  "Dateien seit dieser Version geändert": "File modificati da questa versione",
  "Screenshots": "Screenshot",
  "Golden-Master-Referenzen dieser Version.": "Riferimenti golden master di questa versione.",
  "Vergleich": "Confronto",
  "Echter git-Vergleich zwischen zwei Versionen.": "Confronto git reale tra due versioni.",
  "Geänderte Dateien: {n}": "File modificati: {n}",
  "Version archivieren?": "Archiviare la versione?",
  "{v} wird als „archiviert“ markiert. Die Version bleibt erhalten und wiederherstellbar.": "{v} verrà contrassegnata come «archiviata». La versione viene conservata ed è ripristinabile.",
  "Alle Vorschau-Versionen übernehmen?": "Adottare tutte le versioni di anteprima?",
  "Die jüngste Vorschau wird zur aktuellen Version; ältere Vorschau-Versionen werden als „ersetzt“ markiert (bleiben erhalten).": "L'anteprima più recente diventa la versione attuale; le anteprime più vecchie vengono contrassegnate come «sostituite» (vengono conservate).",
  "Wird aktuell": "Diventa attuale",
  "Als „ersetzt“ markiert": "Contrassegnata come «sostituita»",
  "Als aktuelle Version setzen?": "Impostare come versione attuale?",
  "{v} wird zur aktuellen Version. Ein „-rc“-Suffix wird zur Release-Version. Die bisherige aktuelle Version bleibt erhalten (stabil/vorherige).": "{v} diventa la versione attuale. Un suffisso «-rc» diventa la versione di release. La versione attuale precedente viene conservata (stabile/precedente).",
  "Rollback vorbereiten — {v}": "Prepara il rollback — {v}",
  "Kein automatischer Rollback. Prüfe den Plan und bestätige ausdrücklich.": "Nessun rollback automatico. Verificate il piano e confermate esplicitamente.",
  "Zielversion": "Versione di destinazione",
  "Betroffene Module": "Moduli interessati",
  "Betroffene Dateien": "File interessati",
  "vorhanden": "presente",
  "Rollback-Plan": "Piano di rollback",
  "Wiederherstellungsplan": "Piano di ripristino",
  "Ich habe den Plan geprüft und möchte den Rollback über den Plattform-Rollback vorbereiten.": "Ho verificato il piano e desidero preparare il rollback tramite il rollback della piattaforma.",
  "Datenbank auf Snapshot zurücksetzen (optional, destruktiv)": "Ripristina il database a uno snapshot (facoltativo, distruttivo)",
  "Vorschau wird geladen…": "Caricamento dell'anteprima…",
  "Kein verlinktes Backup für diese Version — DB-Wiederherstellung nicht verfügbar. (Nur Code-Rollback möglich.)": "Nessun backup collegato a questa versione — ripristino del DB non disponibile. (È possibile solo il rollback del codice.)",
  "Snapshot:": "Snapshot:",
  "Collections": "collezioni",
  "Entfernt würden:": "Verrebbero rimossi:",
  "Nicht im Backup (bleiben erhalten):": "Non nel backup (vengono conservati):",
  "Zum Bestätigen „WIEDERHERSTELLEN“ eintippen": "Digitare «WIEDERHERSTELLEN» per confermare",
  "Datenbank wiederherstellen": "Ripristina il database",
  "Vor der Wiederherstellung wird automatisch ein Sicherheits-Snapshot des aktuellen Standes erstellt.": "Prima del ripristino viene creato automaticamente uno snapshot di sicurezza dello stato attuale.",
  "Rollback-Plan bestätigen": "Conferma il piano di rollback",
  "Aktuellen Stand als Version festhalten": "Fissa lo stato attuale come versione",
  "Erfasst den echten laufenden Git-Stand (Commit": "Registra lo stato Git reale in esecuzione (commit",
  ") als neue aktuelle Version.": ") come nuova versione attuale.",
  "{n} Commit(s) seit {who}.": "{n} commit da {who}.",
  "der letzten Version": "l'ultima versione",
  "Bezeichnung": "Denominazione",
  "z. B. Integrationen-Schaltzentrale Phase 2": "ad es. centro di controllo integrazioni fase 2",
  "Ohne Eingabe wird automatisch „Laufender Stand · {c}“ verwendet.": "Senza inserimento, viene usato automaticamente «Stato in esecuzione · {c}».",
  "Notiz (optional)": "Nota (facoltativa)",
  "Was wurde in diesem Stand abgeschlossen?": "Che cosa è stato completato in questo stato?",
  "Version erfassen": "Registra versione",
  "{d} Tg. {h} Std.": "{d} g {h} h",
  "{h} Std.": "{h} h",
  "Jury-Link kopiert.": "Link della giuria copiato.",
  "Kopieren nicht möglich – Link bitte manuell markieren.": "Copia non riuscita – selezionare il link manualmente.",
  "Öffentlicher Jury-Link kopiert.": "Link pubblico della giuria copiato.",
  "QR-Code ist noch nicht bereit.": "Il codice QR non è ancora pronto.",
  "QR-Code als PNG heruntergeladen.": "Codice QR scaricato in formato PNG.",
  "Download nicht möglich.": "Download non riuscito.",
  "A4-Poster (PDF) erstellt.": "Poster A4 (PDF) creato.",
  "Poster konnte nicht erstellt werden.": "Impossibile creare il poster.",
  "A4-Poster (4 Sprachen) erstellt.": "Poster A4 (4 lingue) creato.",
  "Erledigt.": "Fatto.",
  "Jury-Demo für 7 Tage aktiviert.": "Demo giuria attivata per 7 giorni.",
  "Neuer Einladungslink erzeugt.": "Nuovo link d'invito generato.",
  "Jury-Demo entsperrt.": "Demo giuria sbloccata.",
  "Neue Zugänge gesperrt.": "Nuovi accessi bloccati.",
  "Alle Jury-Sitzungen beendet.": "Tutte le sessioni della giuria terminate.",
  "Zugang ist wieder öffentlich.": "L'accesso è di nuovo pubblico.",
  "Zugang jetzt nur über Einladungslink.": "L'accesso avviene ora solo tramite il link d'invito.",
  "Neuen Einladungslink erzeugen? Der bisherige Link wird sofort ungültig.": "Generare un nuovo link d'invito? Il link precedente diventa immediatamente non valido.",
  "Neue Jury-Zugänge sperren? Laufende Sitzungen bleiben bis zum Timeout aktiv.": "Bloccare i nuovi accessi della giuria? Le sessioni in corso restano attive fino al timeout.",
  "Wirklich ALLE laufenden Jury-Sitzungen sofort beenden und deren Demo-Mandanten löschen?": "Terminare davvero SUBITO tutte le sessioni della giuria in corso ed eliminare i relativi mandanti demo?",
  "Jury-Demo-Kontrolle": "Controllo della demo giuria",
  "Steuern Sie die passwortlose, isolierte Jury-Demo (Preview). Der Zugang ist standardmässig öffentlich über den Jury-Link erreichbar und läuft automatisch nach 7 Tagen ab.": "Gestite la demo giuria isolata e senza password (anteprima). L'accesso è per impostazione predefinita pubblico tramite il link della giuria e scade automaticamente dopo 7 giorni.",
  "endet in {x}": "termina tra {x}",
  "kein aktiver Zeitraum": "nessun periodo attivo",
  "{n} aktive Sitzung(en)": "{n} sessione/i attive",
  "nur mit Einladungslink": "solo con link d'invito",
  "öffentlich": "pubblico",
  "Sprache des Links / QR-Codes": "Lingua del link / del codice QR",
  "Die Jury-Demo startet automatisch in der gewählten Sprache (Auto = Sprache des Geräts).": "La demo giuria si avvia automaticamente nella lingua scelta (Auto = lingua del dispositivo).",
  "Einladungslink für die Jury": "Link d'invito per la giuria",
  "Öffentlicher Jury-Link": "Link pubblico della giuria",
  "Showcase-Ziel (Produktion): https://app.socialcheck.ch/jury-demo": "Destinazione vetrina (produzione): https://app.socialcheck.ch/jury-demo",
  "QR-Code zum Öffnen der öffentlichen, passwortlosen Jury-Demo": "Codice QR per aprire la demo giuria pubblica e senza password",
  "QR-Code für die Jury": "Codice QR per la giuria",
  "Mit dem Smartphone scannen, um die öffentliche, passwortlose Jury-Demo zu öffnen. Der Code enthält ausschliesslich den öffentlichen Link – keine Sitzungs-IDs, Tokens oder personenbezogenen Daten.": "Scansionate con lo smartphone per aprire la demo giuria pubblica e senza password. Il codice contiene solo il link pubblico – nessun ID di sessione, token o dato personale.",
  "Öffentlichen Jury-Link kopieren": "Copia il link pubblico della giuria",
  "QR als PNG herunterladen": "Scarica il QR in PNG",
  "QR-Code als PNG herunterladen": "Scarica il codice QR in formato PNG",
  "A4-Handout (PDF)": "Volantino A4 (PDF)",
  "A4-Poster mit QR als PDF herunterladen": "Scarica il poster A4 con QR in formato PDF",
  "A4-Poster (4 Sprachen)": "Poster A4 (4 lingue)",
  "A4-Poster mit QR in vier Sprachen (DE/FR/IT/EN) als PDF herunterladen": "Scarica il poster A4 con QR in quattro lingue (DE/FR/IT/EN) in formato PDF",
  "Aktivieren (7 Tage)": "Attiva (7 giorni)",
  "Neuen Link erzeugen": "Genera nuovo link",
  "Öffentlich freigeben": "Rendi pubblico",
  "Mit Einladungslink absichern": "Proteggi con link d'invito",
  "Entsperren": "Sblocca",
  "Alle Sitzungen beenden": "Termina tutte le sessioni",
  // --- Backups + Zusatzleistungen (Phase 3) ---
  "Sicherungsstatus der SocialCheck-Plattform.": "Stato dei backup della piattaforma SocialCheck.",
  "Kein manuelles Backup-Modul aktiv": "Nessun modulo di backup manuale attivo",
  "Datensicherungen werden automatisch auf Infrastruktur-Ebene durch KEWAG bzw. den Betreiber verwaltet. Ein Self-Service-Export ist derzeit nicht verfügbar.": "I backup dei dati sono gestiti automaticamente a livello di infrastruttura da KEWAG o dal gestore. Un export self-service al momento non è disponibile.",
  "Verwaltung": "Gestione",
  "KEWAG / Infrastruktur": "KEWAG / Infrastruttura",
  "Automatische Sicherung": "Backup automatico",
  "Datenbank": "Database",
  "Serverseitig gespeichert": "Archiviato lato server",
  "Self-Service-Export": "Export self-service",
  "Nicht verfügbar": "Non disponibile",
  "Auf Anfrage bei KEWAG": "Su richiesta presso KEWAG",
  "Objektspeicher (Medien & Uploads) wird redundant und persistent gespeichert.": "L'archiviazione oggetti (media e caricamenti) è salvata in modo ridondante e persistente.",
  "Für einen vollständigen Datenexport oder eine Wiederherstellung kontaktieren Sie bitte das KEWAG-Team.": "Per un export completo dei dati o un ripristino, contatta il team KEWAG.",
  "Ein integriertes Backup-/Restore-Modul mit Historie kann bei Bedarf ergänzt werden.": "Un modulo integrato di backup/ripristino con cronologia può essere aggiunto se necessario.",
  "Service": "Servizio",
  "Als erledigt markiert": "Contrassegnato come completato",
  "Anfrage abgelehnt": "Richiesta rifiutata",
  "Upgrade- und Zusatz-Anfragen der Kunden — Speicher, Kanäle, Services und Pakete.": "Richieste di upgrade e servizi aggiuntivi dei clienti — archiviazione, canali, servizi e pacchetti.",
  "Keine Anfragen.": "Nessuna richiesta.",
  "Anfragen": "Richieste",
  // --- KiWorkspace Compliance (Phase 3) ---
  "Compliance": "Conformità",
  // --- KiWorkspace suppl (Phase 3) ---
  "Termin entfernen": "Rimuovi la data",
  // --- KiWorkspace (Phase 3) ---
  "Markensicher": "Sicuro per il marchio",
  "Storytelling": "Storytelling",
  "Verkaufsstark": "Orientato alla vendita",
  "Kreativ": "Creativo",
  "Brauchbar": "Utilizzabile",
  "Verbesserung": "Da migliorare",
  "Problematisch": "Problematico",
  "Nicht verwenden": "Non utilizzare",
  "Manuelle Prüfung": "Verifica manuale",
  "von 100": "su 100",
  "Diese Aktion verbraucht echte KI-Credits": "Questa azione consuma crediti IA reali",
  "Verbraucht KI-Credits": "Consuma crediti IA",
  "Laden fehlgeschlagen": "Caricamento non riuscito",
  "Lauf wurde bereits ausgeführt — keine doppelten Credits.": "L'esecuzione è già avvenuta — nessun credito doppio.",
  "Keine neu zu bewertenden Bilder.": "Nessuna nuova immagine da valutare.",
  "{done} Bild(er) bewertet": "{done} immagine/i valutata/e",
  "{done} Bild(er) bewertet, {failed} fehlgeschlagen": "{done} immagine/i valutata/e, {failed} non riuscita/e",
  "Bewertung fehlgeschlagen": "Valutazione non riuscita",
  "Bitte mindestens ein Bild auswählen": "Seleziona almeno un'immagine",
  "KI-Vorschlag erstellt": "Proposta IA creata",
  "KI-Generierung fehlgeschlagen": "Generazione IA non riuscita",
  "KI-Limits für diesen Mandanten gespeichert": "Limiti IA salvati per questo cliente",
  "Änderungen übernommen": "Modifiche applicate",
  "Ungültiges Datum.": "Data non valida.",
  "Der geplante Termin darf nicht in der Vergangenheit liegen.": "La data pianificata non può essere nel passato.",
  "Freigabe-Beitrag aktualisiert": "Post di approvazione aggiornato",
  "Beitrag geplant & zur Kundenfreigabe vorbereitet": "Post pianificato e preparato per l'approvazione del cliente",
  "Beitrag zur Kundenfreigabe vorbereitet": "Post preparato per l'approvazione del cliente",
  "Als Entwurf gespeichert": "Salvato come bozza",
  "Vorschlag abgelehnt": "Proposta rifiutata",
  "Aktion fehlgeschlagen": "Azione non riuscita",
  "Bitte zuerst einen Kunden auswählen.": "Seleziona prima un cliente.",
  "KI-Service nicht verbunden — Analyse und Vorschläge sind derzeit nicht verfügbar.": "Servizio IA non connesso — analisi e proposte al momento non disponibili.",
  "Marken-Profil nur zu {pct}% vollständig. Vorschläge benötigen verstärkte manuelle Prüfung.": "Profilo di marca completo solo al {pct}%. Le proposte richiedono una verifica manuale rafforzata.",
  "KI-Service nicht verbunden.": "Servizio IA non connesso.",
  "KI-Läufe wurden durch KEWAG (global) pausiert.": "Le esecuzioni IA sono state sospese da KEWAG (a livello globale).",
  "KI-Läufe wurden für diesen Mandanten pausiert.": "Le esecuzioni IA sono state sospese per questo cliente.",
  "Bestehende Ergebnisse bleiben lesbar; kostenpflichtige KI-Läufe sind gesperrt.": "I risultati esistenti restano leggibili; le esecuzioni IA a pagamento sono bloccate.",
  "KI-Limits": "Limiti IA",
  "Achtung: {pct}% des KI-Monatslimits erreicht. Weitere Läufe werden bei 100% gesperrt.": "Attenzione: raggiunto il {pct}% del limite mensile IA. Ulteriori esecuzioni saranno bloccate al 100%.",
  "1. Bilder & Auswahl": "1. Immagini & selezione",
  "Bewerte…": "Valutazione…",
  "Auswahl bewerten": "Valuta la selezione",
  "Massenanalyse: alle {n} unbewerteten": "Analisi di massa: tutte le {n} non valutate",
  "Empfohlen": "Consigliate",
  "Keine Bilder in dieser Ansicht.": "Nessuna immagine in questa vista.",
  "Unbewertet": "Non valutata",
  "Noch nicht bewertet": "Non ancora valutata",
  "2. KI-Vorschläge": "2. Proposte IA",
  "Erzeuge…": "Generazione…",
  "Neuen Vorschlag erzeugen": "Genera una nuova proposta",
  "KI erstellt den Vorschlag…": "L'IA sta creando la proposta…",
  "Erzeuge aus der Bildauswahl einen Beitrag im Modus „{mode}“.": "Genera un post dalla selezione di immagini in modalità «{mode}».",
  "Wähle links Bilder aus, um einen Vorschlag zu erzeugen.": "Seleziona le immagini a sinistra per generare una proposta.",
  "Vorschlag mit KI erzeugen": "Genera una proposta con l'IA",
  "Empfohlene Bildauswahl": "Selezione di immagini consigliata",
  "Keine Bilder ausgewählt.": "Nessuna immagine selezionata.",
  "Titel / Hook": "Titolo / hook",
  "Titel": "Titolo",
  "Hook (optional)": "Hook (facoltativo)",
  "Haupttext": "Testo principale",
  "Call-to-Action": "Call to action",
  "Call-to-Action (optional)": "Call to action (facoltativo)",
  "Hashtags (durch Leerzeichen getrennt)": "Hashtag (separati da spazi)",
  "z. B. bern gastronomie frisch": "p. es. berna gastronomia fresco",
  "Plattformvarianten": "Varianti per piattaforma",
  "Zeichen": "caratteri",
  "Hashtags": "hashtag",
  "Begründung & verwendete Markenregeln": "Motivazione & regole di marca utilizzate",
  "Offene Fragen": "Domande aperte",
  "Übernehmen": "Applica",
  "Vorschlag bearbeiten": "Modifica la proposta",
  "Deep-Link zu diesem Vorschlag kopieren": "Copia il link diretto a questa proposta",
  "Link kopiert — direkt zu diesem KI-Vorschlag": "Link copiato — direttamente a questa proposta IA",
  "Kopieren fehlgeschlagen": "Copia non riuscita",
  "Link kopieren": "Copia il link",
  "Bearbeitet von {by}": "Modificato da {by}",
  "3. Prüfung & Aktionen": "3. Verifica & azioni",
  "Noch keine Prüfung — erst einen Vorschlag erzeugen.": "Nessuna verifica finora — genera prima una proposta.",
  "Gesamtbewertung · Konfidenz": "Valutazione complessiva · affidabilità",
  "Marken-Passung": "Coerenza con la marca",
  "Inhaltsqualität": "Qualità del contenuto",
  "Bildqualität": "Qualità dell'immagine",
  "Erkannte Stärken": "Punti di forza rilevati",
  "Hinweise": "Note",
  "Risiken": "Rischi",
  "Keine kritischen Risiken erkannt": "Nessun rischio critico rilevato",
  "Compliance-Problem": "Problema di conformità",
  "Blockiert: {reason} — kann nicht zur Kundenfreigabe vorbereitet werden.": "Bloccato: {reason} — non può essere preparato per l'approvazione del cliente.",
  "Geplant für (optional)": "Pianificato per (facoltativo)",
  "Leer = ungeplant zur Freigabe · Zeitzone Europe/Zürich · Beitrag, Freigabe & Kalender teilen denselben Termin.": "Vuoto = non pianificato per l'approvazione · fuso orario Europe/Zurich · post, approvazione e calendario condividono la stessa data.",
  "Freigabe-Beitrag aktualisieren": "Aggiorna il post di approvazione",
  "Zur Freigabe vorbereiten": "Prepara per l'approvazione",
  "Variante": "Variante",
  "Vorschlag ablehnen": "Rifiuta la proposta",
  "KI-Verbrauch & Limits": "Consumo IA & limiti",
  "KI-Limits für diesen Mandanten": "Limiti IA per questo cliente",
  "KI-Service nicht verbunden": "Servizio IA non connesso",
  "KI-Läufe (Monat)": "Esecuzioni IA (mese)",
  "davon Bildbewertungen": "di cui valutazioni di immagini",
  "davon Vorschläge": "di cui proposte",
  "Läufe gesamt": "Esecuzioni totali",
  "Global pausiert": "Sospeso a livello globale",
  "Für diesen Mandanten pausiert": "Sospeso per questo cliente",
  "Optionaler Grund (wird im Audit-Log protokolliert).": "Motivo facoltativo (registrato nel registro di audit).",
  "z. B. Bildauswahl passt nicht, Text zu werblich …": "p. es. la selezione di immagini non è adatta, testo troppo promozionale …",
  "Kostenpflichtigen KI-Lauf bestätigen": "Conferma l'esecuzione IA a pagamento",
  "Bitte vor dem Start prüfen.": "Verifica prima di avviare.",
  "Umfang": "Ambito",
  "{n} Bild(er)": "{n} immagine/i",
  "1 Vorschlag": "1 proposta",
  "Anbieter / Modell": "Fornitore / modello",
  "Voraussichtliche Kosten": "Costi previsti",
  "Echte Kosten derzeit nicht verfügbar (Abrechnung über KI-Läufe)": "Costi reali al momento non disponibili (fatturazione tramite esecuzioni IA)",
  "Diese Aktion verbraucht echte KI-Credits.": "Questa azione consuma crediti IA reali.",
  "Kostenpflichtigen KI-Lauf starten": "Avvia l'esecuzione IA a pagamento",
  "Mandantenbezogene Kostenkontrolle. Der globale KEWAG-Schalter hat immer Vorrang.": "Controllo dei costi per cliente. L'interruttore globale KEWAG ha sempre la priorità.",
  "KI für diesen Mandanten aktiviert": "IA attivata per questo cliente",
  "Max. KI-Läufe / Monat": "Max. esecuzioni IA / mese",
  "Max. Bildanalysen / Monat": "Max. analisi di immagini / mese",
  "Max. Video-Generierungen / Monat": "Max. generazioni di video / mese",
  "Monatsbudget (CHF, optional)": "Budget mensile (CHF, facoltativo)",
  "Leer = kein Limit. Echte Token-/Kostenwerte liefert der Anbieter nicht — es gelten die konfigurierten Lauf-Limits.": "Vuoto = nessun limite. Il fornitore non fornisce valori reali di token/costi — si applicano i limiti di esecuzione configurati.",
  // --- MarkenProfil (Phase 3) ---
  "Kein Profil": "Nessun profilo",
  "Ort / Region": "Località / regione",
  "Google Business Link": "Link Google Business",
  "Social-Kanäle": "Canali social",
  "Manuell": "Manuale",
  "Google Business": "Google Business",
  "Leistungen": "Servizi",
  "Zielkunden": "Clienti target",
  "Tonalität (z. B. sachlich, freundlich, edel, lokal)": "Tono (p. es. sobrio, cordiale, elegante, locale)",
  "Verbotene Begriffe (nie verwenden)": "Termini vietati (da non usare mai)",
  "No-Go-Themen": "Argomenti da evitare",
  "Beispieltexte, die gut waren": "Testi di esempio riusciti",
  "Besonderheiten / Saisonalität": "Particolarità / stagionalità",
  "vor {n} Minute": "{n} minuto fa",
  "vor {n} Minuten": "{n} minuti fa",
  "vor {n} Stunde": "{n} ora fa",
  "vor {n} Stunden": "{n} ore fa",
  "vor {n} Tag": "{n} giorno fa",
  "Kundenfreigabe angefordert": "Approvazione del cliente richiesta",
  "KI-Vorschau aktualisiert": "Anteprima IA aggiornata",
  "Profil {version} aktiviert – Freigabe-E-Mail an {recipient} gesendet": "Profilo {version} attivato – e-mail di approvazione inviata a {recipient}",
  "Brevo nicht verbunden – keine E-Mail": "Brevo non connesso – nessuna e-mail",
  "kein Kunden-Kontakt hinterlegt – keine E-Mail": "nessun contatto cliente inserito – nessuna e-mail",
  "E-Mail-Versand fehlgeschlagen": "Invio e-mail non riuscito",
  "Profil {version} aktiviert": "Profilo {version} attivato",
  "Profil {version} aktiviert ({reason})": "Profilo {version} attivato ({reason})",
  "Version {version} wiederhergestellt": "Versione {version} ripristinata",
  "Alle Kunden und deren Markenprofile": "Tutti i clienti e i loro profili di marca",
  "Freig.": "Appr.",
  "Nächste": "Prossima",
  "Profil": "Profilo",
  "Marken-Profilen": "profili di marca",
  "Kundenübersicht – Zusammenfassung": "Panoramica clienti – riepilogo",
  "Höchste Vollständigkeit": "Completezza massima",
  "Nächste Veröffentlichung": "Prossima pubblicazione",
  "Bitte Kunde wählen.": "Seleziona un cliente.",
  "Die Kunden-Stimme für {name}. Wird für bessere Content-Vorschläge verwendet.": "La voce del cliente per {name}. Utilizzata per suggerimenti di contenuto migliori.",
  "Zuletzt gespeichert:": "Ultimo salvataggio:",
  "Nicht gespeicherte Änderungen": "Modifiche non salvate",
  "Profil-Vollständigkeit": "Completezza del profilo",
  "Version": "Versione",
  "Zuletzt aktualisiert": "Ultimo aggiornamento",
  "Freigegeben vom Kunden": "Approvato dal cliente",
  "am": "il",
  "Noch offen": "Ancora in sospeso",
  "Kundenfreigabe anfordern": "Richiedi l'approvazione del cliente",
  "Vorherige Version": "Versione precedente",
  "Profil-Export": "Esporta profilo",
  "Als JSON": "Come JSON",
  "Unternehmens- & Markeninformationen": "Informazioni su azienda & marca",
  "Bevorzugte Sprache": "Lingua preferita",
  "Positionierung / Markenversprechen": "Posizionamento / promessa di marca",
  "Visuelle Markenregeln": "Regole visive della marca",
  "Logo": "Logo",
  "Kein Logo hinterlegt": "Nessun logo inserito",
  "Farbpalette": "Palette di colori",
  "Fertig": "Fatto",
  "Keine Farben hinterlegt": "Nessun colore inserito",
  "Bildsprache": "Linguaggio delle immagini",
  "Unerwünschte Bildmotive": "Soggetti visivi indesiderati",
  "Entwurf speichern": "Salva bozza",
  "Änderungen vergleichen": "Confronta le modifiche",
  "Profil aktivieren": "Attiva il profilo",
  "KI mit Profil testen": "Prova l'IA con il profilo",
  "Nur das KEWAG-Team kann bearbeiten.": "Solo il team KEWAG può modificare.",
  "Freigabe-E-Mail zuletzt an {to} · {datetime}": "Ultima e-mail di approvazione a {to} · {datetime}",
  "Beim Aktivieren wird automatisch eine Freigabe-E-Mail mit PDF-Richtlinie an den Kunden gesendet.": "All'attivazione viene inviata automaticamente al cliente un'e-mail di approvazione con la direttiva PDF.",
  "Freigabe-E-Mail mit PDF-Richtlinie geht automatisch an den Kunden, sobald Brevo verbunden ist.": "L'e-mail di approvazione con la direttiva PDF viene inviata automaticamente al cliente non appena Brevo è connesso.",
  "KI-Vorschau": "Anteprima IA",
  "Vorschau aktualisieren": "Aggiorna anteprima",
  "Noch keine Angaben – Profil ausfüllen.": "Nessuna informazione finora – compila il profilo.",
  "Kanäle & Quellen": "Canali & fonti",
  "Fehlt": "Mancante",
  "Live-Sync (Reichweite/Interaktionen) wird nach Anbindung von Meta / Google Business aktiv.": "La sincronizzazione in tempo reale (copertura/interazioni) si attiva dopo il collegamento di Meta / Google Business.",
  "Freigaberegeln & Compliance": "Regole di approvazione & compliance",
  "Regeln bearbeiten": "Modifica regole",
  "Regel hinzufügen": "Aggiungi regola",
  "Keine Regeln hinterlegt.": "Nessuna regola inserita.",
  "Beispiel-Post-Vorschau": "Anteprima di un post di esempio",
  "Kein Beispielbild": "Nessuna immagine di esempio",
  "Noch kein Beispieltext hinterlegt.": "Nessun testo di esempio inserito.",
  "Versionsverlauf": "Cronologia delle versioni",
  "Aktuelle Version: {version}. Eine frühere Version wiederherstellen.": "Versione attuale: {version}. Ripristina una versione precedente.",
  "Noch keine gespeicherten Versionen. Versionen entstehen beim Aktivieren des Profils.": "Nessuna versione salvata finora. Le versioni vengono create all'attivazione del profilo.",
  "Wiederherstellen": "Ripristina",
  "Vergleich mit zuletzt aktivierter Version {version}.": "Confronto con l'ultima versione attivata {version}.",
  "Noch keine aktivierte Vorversion – zeigt alle gefüllten Felder als neu.": "Nessuna versione precedente attivata – mostra tutti i campi compilati come nuovi.",
  "Keine Unterschiede – Profil entspricht der aktivierten Version.": "Nessuna differenza – il profilo corrisponde alla versione attivata.",
  "Beispiel": "Esempio",
  // --- Phase 3 Batch 5 ---
  "Jetzt neu verbinden": "Riconnetti ora",
  // --- Phase 3 Batch 4: MetaConnect Restlabels ---
  "Konto:": "Account:",
  "Token gültig bis:": "Token valido fino al:",
  // --- Phase 3 Batch 3: MetaConnect + Backend-Meldungen ---
  "Betriebsbereit": "Operativo",
  "Handlungsbedarf": "Azione richiesta",
  "Verbindung ausstehend": "Connessione in sospeso",
  "Deaktiviert": "Disattivato",
  "Meta App-ID (META_APP_ID)": "Meta App-ID (META_APP_ID)",
  "Meta App-Secret (META_APP_SECRET)": "Meta App-Secret (META_APP_SECRET)",
  "Redirect-URI (META_REDIRECT_URI)": "URI di reindirizzamento (META_REDIRECT_URI)",
  "OAuth-Konfig-ID (META_OAUTH_CONFIG_ID) — optional": "ID config OAuth (META_OAUTH_CONFIG_ID) — facoltativo",
  "Token-Verschlüsselung (INTEGRATION_ENCRYPTION_KEY)": "Cifratura dei token (INTEGRATION_ENCRYPTION_KEY)",
  "Handlung": "Azione",
  "Offen · gültig": "Aperto · valido",
  "Verwendet": "Utilizzato",
  "Widerrufen": "Revocato",
  "Ungültig": "Non valido",
  "Kopieren nicht möglich": "Copia non riuscita",
  "Meta-Grundlage aktiviert.": "Base Meta attivata.",
  "Meta-Grundlage deaktiviert.": "Base Meta disattivata.",
  "Bitte zuerst einen Testmandanten wählen.": "Seleziona prima un mandante di test.",
  "Meta-Verbindung getrennt.": "Connessione Meta disconnessa.",
  "Trennen fehlgeschlagen": "Disconnessione non riuscita",
  "Asset-Auswahl gespeichert.": "Selezione della risorsa salvata.",
  "Speichern fehlgeschlagen": "Salvataggio non riuscito",
  "Geprüft:": "Verificati:",
  "Handlungsbedarf:": "Azione richiesta:",
  "Läuft bald ab:": "In scadenza:",
  "Aufgaben erstellt:": "Attività create:",
  "Token-Überwachung gespeichert.": "Monitoraggio dei token salvato.",
  "Einladung erstellt und per E-Mail an {email} gesendet.": "Invito creato e inviato via e-mail a {email}.",
  "Einladung erstellt. E-Mail-Dienst (Brevo) ist nicht verbunden — bitte Link manuell teilen.": "Invito creato. Il servizio e-mail (Brevo) non è connesso — condividi il link manualmente.",
  "Einladung erstellt, E-Mail konnte nicht gesendet werden — bitte Link manuell teilen.": "Invito creato, impossibile inviare l'e-mail — condividi il link manualmente.",
  "Einladungslink erstellt (72 h gültig).": "Link di invito creato (valido 72 h).",
  "Einladung fehlgeschlagen": "Invito non riuscito",
  "Erinnerung an {email} gesendet.": "Promemoria inviato a {email}.",
  "E-Mail-Dienst (Brevo) ist nicht verbunden — Erinnerung konnte nicht gesendet werden.": "Il servizio e-mail (Brevo) non è connesso — impossibile inviare il promemoria.",
  "Erinnerung konnte nicht gesendet werden.": "Impossibile inviare il promemoria.",
  "Erinnerung fehlgeschlagen": "Promemoria non riuscito",
  "Einladung widerrufen.": "Invito revocato.",
  "Widerrufen fehlgeschlagen": "Revoca non riuscita",
  "Meta Business Suite — Verbindung (Preview)": "Meta Business Suite — connessione (anteprima)",
  "System-Grundlage (global) + echte OAuth-Verbindung pro Mandant · nur Super Admin": "Base di sistema (globale) + connessione OAuth reale per mandante · solo super admin",
  "System-Grundlage": "Base di sistema",
  "Kanonische Redirect-URI (in der Meta App exakt eintragen)": "URI di reindirizzamento canonico (inserire esattamente nell'app Meta)",
  "Kopieren": "Copia",
  "Token-Überwachung (täglich automatisch)": "Monitoraggio dei token (automatico, giornaliero)",
  "Läuft ein Token ab oder wird ungültig, erstellt das System automatisch eine KEWAG-Aufgabe — und schliesst sie bei Erholung wieder.": "Se un token scade o diventa non valido, il sistema crea automaticamente un'attività KEWAG — e la chiude di nuovo al ripristino.",
  "Vorwarnung (Tage vor Ablauf)": "Preavviso (giorni prima della scadenza)",
  "Auto-Erinnerung Einladung (Std.)": "Promemoria automatico invito (ore)",
  "Alle Tokens jetzt prüfen": "Verifica tutti i token ora",
  "Meta-Grundlage aktivieren (direkt hier — verschlüsselt gespeichert)": "Attiva la base Meta (direttamente qui — memorizzata in forma cifrata)",
  "App-ID und App-Geheimcode aus der Meta-Konsole (Einstellungen → Allgemeines). Wird live bei Meta geprüft und verschlüsselt gespeichert — kein Emergent-Secrets-Umweg nötig.": "App-ID e chiave segreta dell'app dalla console Meta (Impostazioni → Generali). Verificate in tempo reale su Meta e memorizzate in forma cifrata — senza passare dai secret Emergent.",
  "App-ID (z. B. 1558250479126312)": "App-ID (p. es. 1558250479126312)",
  "App-Geheimcode (App Secret)": "Chiave segreta dell'app (App Secret)",
  "Tipp: Bei „Error validating client secret“ ist der Geheimcode falsch/unvollständig — in der Meta-Konsole neben „App-Geheimcode“ auf „Anzeigen“/„Zurücksetzen“ und vollständig kopieren. Alternativ als Emergent-Secrets möglich ({vars}).": "Suggerimento: se compare « Error validating client secret », la chiave segreta è errata/incompleta — nella console Meta, accanto a « Chiave segreta dell'app », clicca su « Mostra »/« Reimposta » e copiala per intero. Possibile anche tramite i secret Emergent ({vars}).",
  "Aktiviert & verschlüsselt gespeichert": "Attivato & memorizzato in forma cifrata",
  "Ampel-Übersicht · alle Mandanten-Verbindungen": "Panoramica semaforo · tutte le connessioni dei mandanti",
  "Noch keine Mandanten-Meta-Verbindungen. Verbinde unten einen Testmandanten.": "Nessuna connessione Meta dei mandanti finora. Collega un mandante di test qui sotto.",
  "Verbindung · interner Testmandant": "Connessione · mandante di test interno",
  "Testmandant": "Mandante di test",
  "— Mandant wählen —": "— Seleziona mandante —",
  "(Demo — nicht verbindbar)": "(Demo — non collegabile)",
  "Zuerst Zugangsdaten hinterlegen": "Inserire prima le credenziali",
  "Verbindung teilen mit Kunde": "Condividi la connessione con il cliente",
  "Erzeugen Sie einen sicheren, einmalig gültigen Einladungslink (72 Stunden). Der Kunde autorisiert sein eigenes Meta-Konto selbst — ohne Passwort-Weitergabe an KEWAG.": "Genera un link di invito sicuro e monouso (72 ore). Il cliente autorizza autonomamente il proprio account Meta — senza condividere password con KEWAG.",
  "Auch per E-Mail an den Kunden senden": "Invia anche via e-mail al cliente",
  "Vorschlag: Haupt-Kontakt des Mandanten — überschreibbar.": "Suggerimento: contatto principale del mandante — modificabile.",
  "E-Mail-Dienst (Brevo) ist nicht verbunden — der Versand wird übersprungen. Der Link wird trotzdem erstellt und angezeigt.": "Il servizio e-mail (Brevo) non è connesso — l'invio viene saltato. Il link viene comunque creato e mostrato.",
  "Einladungslink erzeugen (72 h)": "Genera link di invito (72 h)",
  "Hinweis: Der Kunde kann den Link erst zur Verbindung nutzen, sobald die Meta-Grundlage aktiviert ist.": "Nota: il cliente potrà usare il link per connettersi solo dopo l'attivazione della base Meta.",
  "Neuer Einladungslink — an den Kunden senden:": "Nuovo link di invito — da inviare al cliente:",
  "erstellt": "creato il",
  "gültig bis": "valido fino al",
  "verwendet": "utilizzato il",
  "gesendet": "inviato",
  "vorgemerkt": "in coda",
  "erinnert": "sollecitato",
  "Erinnerung senden": "Invia promemoria",
  "Noch keine Verbindung für diesen Mandanten. Klicke „Mit Meta verbinden“, um den OAuth-Flow zu starten.": "Nessuna connessione per questo mandante. Clicca su «Connetti con Meta» per avviare il flusso OAuth.",
  "Token läuft in {d} Tag(en) ab — Meta-Tokens gelten ~60 Tage. Jetzt neu verbinden, um Unterbrechungen zu vermeiden.": "Il token scade tra {d} giorno/i — i token Meta durano ~60 giorni. Riconnettiti ora per evitare interruzioni.",
  "Token gültig — noch ca. {d} Tage (Meta-Tokens laufen nach ~60 Tagen ab).": "Token valido — ancora circa {d} giorni (i token Meta scadono dopo ~60 giorni).",
  "Gewährte Berechtigungen": "Autorizzazioni concesse",
  "Fehlende Berechtigungen": "Autorizzazioni mancanti",
  "Business-Portfolios": "Portfolio Business",
  "Keine Portfolios gefunden.": "Nessun portfolio trovato.",
  "Referenz-Abgleich (sichere Zuordnung)": "Confronto di riferimento (assegnazione sicura)",
  "Ziel-Seite (Referenz)": "Pagina di destinazione (riferimento)",
  "Portfolio (Referenz)": "Portfolio (riferimento)",
  "Business-Asset (Referenz)": "Risorsa Business (riferimento)",
  "Von der Meta Pages API zurückgegebene Seiten:": "Pagine restituite dall'API Meta Pages:",
  "Keine Seite geladen — Endpoint": "Nessuna pagina caricata — endpoint",
  "Meta-Fehlercode": "codice di errore Meta",
  "fehlende Berechtigung:": "autorizzazione mancante:",
  "Noch keine Seiten geladen (Verbindung ausstehend).": "Nessuna pagina caricata finora (connessione in sospeso).",
  "stimmt mit Referenz überein ✓": "corrisponde al riferimento ✓",
  "Asset auswählen (Facebook-Seite + verknüpftes Instagram)": "Seleziona la risorsa (pagina Facebook + Instagram collegato)",
  "— Seite wählen —": "— Seleziona pagina —",
  "(kein IG)": "(nessun IG)",
  "Auswahl speichern": "Salva la selezione",
  "Ausgewählt:": "Selezionato:",
  "Verbindung testen": "Verifica la connessione",
  "Verbindung trennen": "Disconnetti",
  "Verbindung jetzt prüfen": "Verifica la connessione ora",
  "Meta App im Entwicklungsmodus angenommen — nur autorisierte Admin-/Tester-/Developer-Konten können sich verbinden. Kein Advanced Access / kein App Review bestätigt.": "App Meta presunta in modalità sviluppo — solo gli account admin/tester/sviluppatore autorizzati possono connettersi. Nessun accesso avanzato / nessuna revisione dell'app confermata.",
  "WordPress API-Key vorhanden.": "Chiave API WordPress presente.",
  "Kein WordPress API-Key hinterlegt.": "Nessuna chiave API WordPress inserita.",
  "Meta ist konfiguriert und verbindbar.": "Meta è configurato e collegabile.",
  "Meta App-ID & Secret fehlen.": "App-ID e Secret di Meta mancanti.",
  "E-Mail-Versand ist konfiguriert.": "L'invio e-mail è configurato.",
  "Brevo API-Key fehlt.": "La chiave API Brevo è mancante.",
  "Kein gültiger Stripe-Key hinterlegt.": "Nessuna chiave Stripe valida inserita.",
  "Nicht konfiguriert – Zugangsdaten fehlen.": "Non configurato – credenziali mancanti.",
  // --- Phase 3 Batch 2: Integrationen/MetaConnectDialog ---
  "Kundenverbindungen": "Connessioni clienti",
  "Provider & Systeme": "Provider & sistemi",
  "Verlauf & Prüfungen": "Cronologia & verifiche",
  "Integrationen konnten nicht geladen werden": "Impossibile caricare le integrazioni",
  "Meta erfolgreich verbunden.": "Meta collegato con successo.",
  "Meta-Verbindung fehlgeschlagen.": "Connessione a Meta non riuscita.",
  "Kein Instagram-Business-Konto mit der Facebook-Seite verknüpft.": "Nessun account Instagram Business collegato alla pagina Facebook.",
  "Keine Facebook-Seite gefunden.": "Nessuna pagina Facebook trovata.",
  "Zuletzt geprüft – bitte kurz warten.": "Verificato di recente – attendere un momento.",
  "Integrationen neu geprüft.": "Integrazioni riverificate.",
  "Ansicht aktualisiert.": "Vista aggiornata.",
  "Prüfung fehlgeschlagen": "Verifica non riuscita",
  "Test fehlgeschlagen": "Test non riuscito",
  "Dokumentation wird vorbereitet.": "La documentazione è in preparazione.",
  "Zentrale Verwaltung von Plattform-Verbindungen, Berechtigungen und Integrations-Health.": "Gestione centralizzata delle connessioni delle piattaforme, delle autorizzazioni e dello stato delle integrazioni.",
  "Letzte Aktualisierung:": "Ultimo aggiornamento:",
  "Prüfungen & Diagnose sind nur für den Super Admin sichtbar.": "Le verifiche e la diagnostica sono visibili solo al super admin.",
  "Audit-Log öffnen": "Apri il registro di audit",
  "Zeiten in Tabellen und Karten entsprechen der letzten erfolgreichen Synchronisation oder Prüfung.": "Gli orari nelle tabelle e nelle schede corrispondono all'ultima sincronizzazione o verifica riuscita.",
  "Ehrlicher Live-Status: nur echte, geprüfte Verbindungen werden als verbunden angezeigt.": "Stato in tempo reale onesto: solo le connessioni reali e verificate vengono mostrate come connesse.",
  "System-Setup": "Configurazione del sistema",
  "OAuth & Assets": "OAuth & risorse",
  "Test & Speichern": "Test & salvataggio",
  "Meta-Status konnte nicht geladen werden": "Impossibile caricare lo stato di Meta",
  "diesem Kunden": "questo cliente",
  "OAuth konnte nicht gestartet werden": "Impossibile avviare l'OAuth",
  "Einladungslink erstellt (72 Stunden, einmalig).": "Link di invito creato (72 ore, monouso).",
  "Einladungslink fehlgeschlagen": "Link di invito non riuscito",
  "Link kopiert": "Link copiato",
  "Bitte App-ID und App-Secret eingeben.": "Inserisci App-ID e App-Secret.",
  "Meta aktiviert und verschlüsselt gespeichert — Sie können jetzt verbinden.": "Meta attivato e salvato in forma cifrata — ora puoi connetterti.",
  "Aktivierung fehlgeschlagen": "Attivazione non riuscita",
  "Redirect-URI kopiert": "URI di reindirizzamento copiato",
  "Bitte numerische Configuration ID eingeben.": "Inserisci una Configuration ID numerica.",
  "Config-ID gespeichert — OAuth nutzt jetzt Login for Business (config_id).": "Config-ID salvata — l'OAuth ora usa Login for Business (config_id).",
  "Config-ID konnte nicht gespeichert werden": "Impossibile salvare la Config-ID",
  "Config-ID entfernt — OAuth nutzt wieder scope.": "Config-ID rimossa — l'OAuth usa di nuovo scope.",
  "Config-ID konnte nicht entfernt werden": "Impossibile rimuovere la Config-ID",
  "Meta App ID (global)": "Meta App ID (globale)",
  "Meta App Secret (global)": "Meta App Secret (globale)",
  "OAuth Redirect URI (global)": "OAuth Redirect URI (globale)",
  "OAuth Config ID (global)": "OAuth Config ID (globale)",
  "Hinterlegt": "Inserito",
  "Erforderlich": "Obbligatorio",
  "Optional": "Facoltativo",
  "Business Portfolio": "Business Portfolio",
  "Facebook-Seite": "Pagina Facebook",
  "Instagram-Konto": "Account Instagram",
  "Gewährte Scopes": "Scope concessi",
  "Letzter Health-Check": "Ultimo controllo di stato",
  "Meta (Instagram / Facebook) verbinden": "Connetti Meta (Instagram / Facebook)",
  "System-Setup global · Verbindung nur für den ausgewählten Kunden": "Configurazione di sistema globale · connessione solo per il cliente selezionato",
  "Ausgewählter Kunde": "Cliente selezionato",
  "Die Verbindung wird ausschliesslich diesem Mandanten zugeordnet.": "La connessione viene assegnata esclusivamente a questo mandante.",
  "System-Setup (global)": "Configurazione del sistema (globale)",
  "Einmalig für das System – nicht pro Kunde speichern.": "Una sola volta per il sistema – non salvare per singolo cliente.",
  "OAuth Config ID (Login for Business)": "OAuth Config ID (Login for Business)",
  "Config-ID entfernen (zurück zu scope)": "Rimuovi Config-ID (torna a scope)",
  "Neue Configuration ID (überschreibt)": "Nuova Configuration ID (sovrascrive)",
  "z. B. 1234567890123456": "p. es. 1234567890123456",
  "Meta-Systemgrundlage: Betriebsbereit": "Base di sistema Meta: operativa",
  "Meta-Systemgrundlage: Nicht betriebsbereit": "Base di sistema Meta: non operativa",
  "Meta App-ID und App-Secret fehlen — bitte als Emergent-Secret hinterlegen.": "App-ID e App-Secret di Meta mancanti — inseriscili come secret Emergent.",
  "Verschlüsselung nicht aktiv (INTEGRATION_ENCRYPTION_KEY) — Tokens könnten nicht sicher gespeichert werden.": "Cifratura non attiva (INTEGRATION_ENCRYPTION_KEY) — i token non potrebbero essere memorizzati in modo sicuro.",
  "Meta-Grundlage betriebsbereit — OAuth kann gestartet werden.": "Base Meta operativa — l'OAuth può essere avviato.",
  "Jetzt aktivieren — kein Neustart nötig": "Attiva ora — nessun riavvio necessario",
  "Wo finde ich das?": "Dove lo trovo?",
  "App-ID und App-Secret aus Ihrer Meta-App eingeben. Wir prüfen sie live bei Meta und speichern sie verschlüsselt — ohne Emergent-Secret, ohne Rebuild.": "Inserisci App-ID e App-Secret della tua app Meta. Li verifichiamo in tempo reale su Meta e li memorizziamo in forma cifrata — senza secret Emergent, senza rebuild.",
  "Anleitung ausblenden": "Nascondi la guida",
  "Schritt-für-Schritt: Wo finde ich App-ID & Secret?": "Passo dopo passo: dove trovo App-ID e Secret?",
  "Meta App-ID": "Meta App-ID",
  "Meta App-Secret": "Meta App-Secret",
  "wird verschlüsselt gespeichert": "memorizzato in forma cifrata",
  "Aktivieren & prüfen": "Attiva & verifica",
  "Verbindung für diesen Kunden": "Connessione per questo cliente",
  "Verbindungsart": "Tipo di connessione",
  "Einladungslink": "Link di invito",
  "72 Stunden · einmalig": "72 ore · monouso",
  "Nur nach erfolgreichem OAuth werden Token und Assets diesem Kunden zugeordnet.": "Solo dopo un OAuth riuscito i token e le risorse vengono assegnati a questo cliente.",
  "Einladungslink erstellen": "Crea link di invito",
  "OAuth ist erst möglich, wenn die Systemgrundlage betriebsbereit ist.": "L'OAuth è possibile solo quando la base di sistema è operativa.",
  "Gültig bis": "Valido fino al",
  "· nur einmal verwendbar": "· utilizzabile una sola volta",
  "Nach erfolgreicher Autorisierung": "Dopo un'autorizzazione riuscita",
  "Bestätigen & zu Meta": "Conferma & vai a Meta",
  "OAuth starten": "Avvia l'OAuth",
  "Später": "Più tardi",
  "Wichtig:": "Importante:",
  "Meta ist aktiv!": "Meta è attivo!",
  "Diese Redirect-URI in der Meta-App unter „Valid OAuth Redirect URIs\" eintragen:": "Inserisci questo URI di reindirizzamento nell'app Meta in «Valid OAuth Redirect URIs»:",
  "Meta für {name} verbinden": "Connetti Meta per {name}",
  "Klicken Sie jetzt unten auf „Meta für {name} verbinden“ und melden Sie sich mit Ihrem Facebook-Konto an.": "Clicca ora qui sotto su «Connetti Meta per {name}» e accedi con il tuo account Facebook.",
  "Die globale Meta-Systemintegration bleibt getrennt. OAuth-Verbindung, Tokens und Assets werden ausschliesslich beim aktuell ausgewählten Kunden ({name}) gespeichert.": "L'integrazione di sistema Meta globale rimane separata. La connessione OAuth, i token e le risorse vengono memorizzati esclusivamente presso il cliente attualmente selezionato ({name}).",
  "Sie verbinden Meta jetzt ausschliesslich mit dem Mandanten {name}.": "Ora colleghi Meta esclusivamente al mandante {name}.",
  "<strong>Instagram und Facebook gehören beide zu Meta.</strong> Sie richten das hier nur <strong>einmal</strong> ein — egal, ob Sie vorher auf die Instagram- oder die Facebook-Kachel geklickt haben. Diese eine Aktivierung schaltet <strong>beide</strong> Kanäle frei.": "<strong>Instagram e Facebook appartengono entrambi a Meta.</strong> Qui configuri tutto <strong>una sola volta</strong> — indipendentemente dal fatto che tu abbia cliccato prima sul riquadro Instagram o Facebook. Questa singola attivazione sblocca <strong>entrambi</strong> i canali.",
  "Bei <strong class='text-foreground/80'>Facebook Login for Business</strong> erforderlich: numerische Configuration ID aus dem Meta-Dashboard. Der OAuth-Flow nutzt dann <code class='text-foreground/80'>config_id</code> statt <code class='text-foreground/80'>scope</code>. Öffentliche Kennung – kein Secret, kein Neustart nötig.": "Richiesto per <strong class='text-foreground/80'>Facebook Login for Business</strong>: Configuration ID numerico dal dashboard di Meta. Il flusso OAuth utilizza quindi <code class='text-foreground/80'>config_id</code> invece di <code class='text-foreground/80'>scope</code>. Identificatore pubblico – nessun segreto, nessun riavvio necessario.",
  "Eine Art <strong class='text-foreground/80'>Benutzername</strong> Ihrer Meta-App (nur Zahlen).": "Una sorta di <strong class='text-foreground/80'>nome utente</strong> della tua app Meta (solo numeri).",
  "Das dazugehörige <strong class='text-foreground/80'>Passwort</strong> — wird verschlüsselt gespeichert und nie angezeigt.": "La <strong class='text-foreground/80'>password</strong> corrispondente — memorizzata in forma cifrata e mai visualizzata.",
  "<li><strong class='text-foreground'>1.</strong> Öffnen Sie <a class='text-primary hover:underline' href='https://developers.facebook.com/apps/' target='_blank' rel='noreferrer'>developers.facebook.com/apps</a> und wählen Sie Ihre App (oder „App erstellen“ → Typ <em>Business</em>).</li><li><strong class='text-foreground'>2.</strong> Links im Menü: <em>App-Einstellungen → Grundlegendes</em>. Ganz oben steht die <strong class='text-foreground'>App-ID</strong>.</li><li><strong class='text-foreground'>3.</strong> Beim <strong class='text-foreground'>App-Geheimnis</strong> auf „Anzeigen“ klicken (Passwort nötig) — oder „Zurücksetzen“ für ein neues, falls das alte nicht mehr stimmt. Wert vollständig kopieren.</li><li><strong class='text-foreground'>4.</strong> Beide Werte hier oben eintragen und <em>„Aktivieren &amp; prüfen“</em> klicken. Fertig — Instagram &amp; Facebook sind aktiv.</li><li class='pt-1 text-[10px]'>Tipp: Für den späteren Login-Schritt muss zusätzlich das Produkt „Facebook Login“ hinzugefügt und die unten stehende Redirect-URI eingetragen sein.</li>": "<li><strong class='text-foreground'>1.</strong> Apri <a class='text-primary hover:underline' href='https://developers.facebook.com/apps/' target='_blank' rel='noreferrer'>developers.facebook.com/apps</a> e seleziona la tua app (oppure « Crea app » → tipo <em>Business</em>).</li><li><strong class='text-foreground'>2.</strong> Nel menu a sinistra: <em>Impostazioni dell'app → Generali</em>. In alto trovi l'<strong class='text-foreground'>App-ID</strong>.</li><li><strong class='text-foreground'>3.</strong> Per la <strong class='text-foreground'>chiave segreta dell'app</strong> clicca su « Mostra » (password richiesta) — oppure « Reimposta » per ottenerne una nuova, se la vecchia non è più valida. Copia il valore per intero.</li><li><strong class='text-foreground'>4.</strong> Inserisci entrambi i valori qui sopra e clicca su <em>« Attiva &amp; verifica »</em>. Fatto — Instagram &amp; Facebook sono attivi.</li><li class='pt-1 text-[10px]'>Suggerimento: per il successivo passaggio di login è necessario aggiungere anche il prodotto « Facebook Login » e inserire l'URI di reindirizzamento indicato qui sotto.</li>",
  // --- Phase 3 Batch 1: NextActions/Nachrichten/MetaConnectPublic/Abrechnung ---
  "Ihre nächsten Aktionen": "Le tue prossime azioni",
  "Problem": "Problema",
  "Fällig": "Scadenza",
  "Meta-Verbindung starten": "Avvia la connessione Meta",
  "Alle Aktionen anzeigen →": "Mostra tutte le azioni →",
  "Noch keine offenen Aktionen. Alles im grünen Bereich.": "Nessuna azione in sospeso. Tutto a posto.",
  "Verbindung unterbrochen": "Connessione interrotta",
  "Verbindung getrennt": "Connessione persa",
  "Autorisierung ausstehend": "Autorizzazione in sospeso",
  "Nicht konfiguriert": "Non configurato",
  "Buchung prüfen": "Verificare la prenotazione",
  "Test ausstehend": "Test in sospeso",
  "Keine Aktion nötig": "Nessuna azione necessaria",
  "Health-Check ausführen": "Eseguire il controllo di stato",
  "Erneut verbinden / Token erneuern": "Riconnettere / rinnovare il token",
  "Verbinden (Kundenzustimmung)": "Connetti (consenso del cliente)",
  "Zugangsdaten hinterlegen": "Inserire le credenziali",
  "Provider-Freigabe abwarten": "Attendere l'approvazione del provider",
  "Neu verbinden": "Riconnetti",
  "Bei Bedarf buchen": "Prenotare se necessario",
  "Demo anfragen": "Richiedi una demo",
  "Pocket": "Pocket",
  "gerade eben": "proprio ora",
  "vor {n} Min": "{n} min fa",
  "vor {n} Std": "{n} h fa",
  "vor {n} Tagen": "{n} g fa",
  "Benachrichtigungen & Aktivitäten aus SocialCheck — alles live aus echten Daten.": "Notifiche e attività da SocialCheck — tutto in tempo reale, da dati reali.",
  "Inhalt wartet auf Freigabe": "contenuto in attesa di approvazione",
  "Inhalte warten auf Freigabe": "contenuti in attesa di approvazione",
  "Zu den Freigaben": "Vai alle approvazioni",
  "Keine Nachrichten in dieser Kategorie.": "Nessun messaggio in questa categoria.",
  "Neuer Lead": "Nuovo lead",
  "Fotoauftrag": "Incarico fotografico",
  "Support-Ticket": "Ticket di supporto",
  "Sichere Autorisierung über Meta. Sie geben KEWAG kein Passwort — die Verbindung erfolgt direkt bei Meta über Ihr eigenes Konto.": "Autorizzazione sicura tramite Meta. Non fornisci alcuna password a KEWAG — la connessione avviene direttamente su Meta tramite il tuo account.",
  "Link abgelaufen": "Link scaduto",
  "Dieser Einladungslink ist nicht mehr gültig (72 Stunden überschritten). Bitte fordern Sie bei KEWAG einen neuen Link an.": "Questo link di invito non è più valido (superate le 72 ore). Richiedi un nuovo link a KEWAG.",
  "Link zurückgezogen": "Link revocato",
  "Dieser Einladungslink wurde von KEWAG zurückgezogen. Bitte wenden Sie sich an Ihr KEWAG-Team.": "Questo link di invito è stato revocato da KEWAG. Contatta il tuo team KEWAG.",
  "Bereits verbunden": "Già connesso",
  "Diese Einladung wurde bereits verwendet. Ihr Meta-Konto ist verbunden — es ist nichts weiter zu tun.": "Questo invito è già stato utilizzato. Il tuo account Meta è connesso — non serve altro.",
  "Ungültiger Link": "Link non valido",
  "Dieser Einladungslink ist ungültig. Bitte prüfen Sie den Link oder fordern Sie bei KEWAG einen neuen an.": "Questo link di invito non è valido. Controlla il link oppure richiedine uno nuovo a KEWAG.",
  "Der Link konnte nicht geprüft werden.": "Impossibile verificare il link.",
  "Die Verbindung konnte nicht gestartet werden.": "Impossibile avviare la connessione.",
  "Einladung wird geprüft…": "Verifica dell'invito…",
  "Etwas ist schiefgelaufen": "Qualcosa è andato storto",
  "Meta-Konto verbinden": "Connetti l'account Meta",
  "KEWAG möchte Ihre Facebook-Seite und Ihr Instagram-Konto betreuen. Verbinden Sie Ihr Meta-Konto mit einem Klick — sicher und ohne Passwort-Weitergabe.": "KEWAG desidera gestire la tua pagina Facebook e il tuo account Instagram. Connetti il tuo account Meta con un clic — in modo sicuro e senza condividere password.",
  "Die Verbindung ist derzeit vorübergehend nicht verfügbar. Bitte versuchen Sie es in Kürze erneut oder kontaktieren Sie KEWAG.": "La connessione al momento non è disponibile. Riprova a breve oppure contatta KEWAG.",
  "Mit Meta verbinden": "Connetti con Meta",
  "Verschlüsselte, einmalig gültige Einladung": "Invito cifrato, valido una sola volta",
  "Vielen Dank — verbunden!": "Grazie — connesso!",
  "Verbindung nicht abgeschlossen": "Connessione non completata",
  "Ihr Meta-Konto wurde erfolgreich mit KEWAG verbunden. Sie können dieses Fenster jetzt schliessen — KEWAG kümmert sich um den Rest.": "Il tuo account Meta è stato collegato con successo a KEWAG. Ora puoi chiudere questa finestra — KEWAG si occupa del resto.",
  "Die Verbindung konnte nicht abgeschlossen werden. Bitte öffnen Sie Ihren Einladungslink erneut oder kontaktieren Sie Ihr KEWAG-Team.": "Impossibile completare la connessione. Riapri il tuo link di invito oppure contatta il tuo team KEWAG.",
  "Nur für KEWAG Admin / Super Admin.": "Solo per amministratori KEWAG / super admin.",
  "Fehler beim Laden.": "Errore durante il caricamento.",
  "Abrechnungseinstellungen gespeichert.": "Impostazioni di fatturazione salvate.",
  "Zurück zu Rechnungen": "Torna alle fatture",
  "Firmendaten, IBAN & QR-Rechnung, Nummernkreis und E-Mail-Versand — Voraussetzung für Swiss QR-Rechnungen.": "Dati aziendali, IBAN e fattura QR, numerazione e invio e-mail — requisito per le fatture QR svizzere.",
  "Swiss QR-Rechnung ist bereit — der QR-Zahlteil wird dem Rechnungs-PDF angehängt.": "La fattura QR svizzera è pronta — la sezione di pagamento QR viene allegata al PDF della fattura.",
  "QR-Rechnung noch nicht bereit: Firma, IBAN/QR-IBAN, PLZ und Ort ausfüllen und „QR aktiviert“ einschalten.": "Fattura QR non ancora pronta: compila azienda, IBAN/QR-IBAN, NAP e località e attiva «QR attivato».",
  "Brevo E-Mail-Versand ist verbunden — Rechnungen können per E-Mail versendet werden.": "L'invio e-mail Brevo è connesso — le fatture possono essere inviate via e-mail.",
  "Brevo noch nicht verbunden: E-Mail-Versand wird aktiv, sobald BREVO_API_KEY & BREVO_SENDER_EMAIL gesetzt sind.": "Brevo non ancora connesso: l'invio e-mail si attiverà non appena BREVO_API_KEY e BREVO_SENDER_EMAIL saranno impostati.",
  "Absender / Firma": "Mittente / azienda",
  "Erscheint als Zahlungsempfänger (Kreditor) auf Rechnung und QR-Zahlteil.": "Compare come beneficiario (creditore) sulla fattura e nella sezione di pagamento QR.",
  "Firmenname": "Nome dell'azienda",
  "Strasse": "Via",
  "Nr.": "N.",
  "Zusätzliche Adresszeilen (PDF-Kopf)": "Righe di indirizzo aggiuntive (intestazione PDF)",
  "Optional, eine Zeile pro Eintrag.": "Facoltativo, una riga per voce.",
  "Steuer": "Imposta",
  "UID/MWST-Nummer und Normalsatz.": "Numero IDI/IVA e aliquota ordinaria.",
  "UID / MWST-Nummer": "Numero IDI / IVA",
  "z. B. CHE-123.456.789 MWST": "p. es. CHE-123.456.789 IVA",
  "MwSt-Satz (%)": "Aliquota IVA (%)",
  "Bankverbindung": "Coordinate bancarie",
  "IBAN für normale QR-Rechnung oder QR-IBAN für QR-Referenz-Verfahren.": "IBAN per la fattura QR normale o QR-IBAN per la procedura con riferimento QR.",
  "Normale IBAN (ohne QR-Referenz).": "IBAN normale (senza riferimento QR).",
  "Für QR-Referenz-Verfahren (IID 30000–31999). Hat Vorrang.": "Per la procedura con riferimento QR (IID 30000–31999). Ha la precedenza.",
  "Swiss QR-Zahlteil im Rechnungs-PDF aktivieren": "Attivare la sezione di pagamento Swiss QR nel PDF della fattura",
  "Rechnungsstellung": "Fatturazione",
  "Nummernkreis, Zahlungsfrist und Bedingungen.": "Numerazione, termine di pagamento e condizioni.",
  "Rechnungsnummern-Präfix": "Prefisso dei numeri di fattura",
  "Ergibt z. B. INV-2026-0001": "Genera p. es. INV-2026-0001",
  "Zahlungsfrist (Tage)": "Termine di pagamento (giorni)",
  "Währung": "Valuta",
  "Zahlungsbedingungen (PDF-Fusszeile)": "Condizioni di pagamento (piè di pagina PDF)",
  "Zahlbar innert 30 Tagen netto.": "Pagabile netto entro 30 giorni.",
  "E-Mail-Versand": "Invio e-mail",
  "Anzeige-Absender & Antwort-Adresse. Der technische Versand läuft über Brevo (Server-Zugangsdaten).": "Mittente visualizzato e indirizzo di risposta. L'invio tecnico avviene tramite Brevo (credenziali server).",
  "Absender-Name": "Nome del mittente",
  "Absender-E-Mail (Anzeige)": "E-mail del mittente (visualizzata)",
  "Technischer Absender = BREVO_SENDER_EMAIL (verifiziert).": "Mittente tecnico = BREVO_SENDER_EMAIL (verificato).",
  "Antwort-an (Reply-To)": "Rispondi a (Reply-To)",
  "Einstellungen speichern": "Salva le impostazioni",
  "Benutzer anlegen": "Crea utente",
  "Zugang erstellt.": "Accesso creato.",
  "Neuen Zugang erstellen – per Einladungslink oder mit direkt gesetztem Passwort.": "Crea un nuovo accesso – tramite link di invito o con password impostata direttamente.",
  "Konto": "Account",
  "ist aktiv. Bitte diese Zugangsdaten sicher an die Person weitergeben:": "è attivo. Trasmetti queste credenziali in modo sicuro alla persona:",
  "wurde aktualisiert und ist aktiv. Bitte diese Zugangsdaten sicher weitergeben:": "è stato aggiornato ed è attivo. Trasmetti queste credenziali in modo sicuro:",
  "Passwort": "Password",
  "Das Passwort wird aus Sicherheitsgründen nur jetzt angezeigt. Empfehlung: Person ändert es beim ersten Login und aktiviert 2FA.": "Per motivi di sicurezza la password viene mostrata solo ora. Consiglio: la persona la cambia al primo accesso e attiva la 2FA.",
  "Einladung wurde per E-Mail versendet.": "L'invito è stato inviato via e-mail.",
  "Brevo nicht verbunden – bitte diesen Aktivierungslink weitergeben:": "Brevo non collegato – trasmetti questo link di attivazione:",
  "Schliessen": "Chiudi",
  "Name": "Nome",
  "Rolle": "Ruolo",
  "Kunde / Mandant": "Cliente / mandante",
  "Kunde wählen…": "Scegli un cliente…",
  "Bereich / Team (optional)": "Area / team (facoltativo)",
  "z. B. Kundenbetreuung": "es. assistenza clienti",
  "Passwort direkt setzen (optional)": "Imposta direttamente una password (facoltativo)",
  "leer = Einladungslink": "vuoto = link di invito",
  "Generieren": "Genera",
  "Mit Passwort wird das Konto sofort aktiv (kein Einladungslink). Ohne Passwort geht eine Einladung raus.": "Con una password l'account è subito attivo (nessun link di invito). Senza password viene inviato un invito.",
  "Abbrechen": "Annulla",
  "Konto anlegen": "Crea account",
  "Einladen": "Invita",
  "Team": "Team",
  "Super Admin": "Super Admin",
  "Admin": "Admin",
  "Finanzen": "Finanze",
  "Editor": "Editor",
  "Reviewer": "Revisore",
  "Mitarbeiter": "Collaboratore",
  "Viewer": "Visualizzatore",
  "Starkes Passwort generiert": "Password forte generata",
  "Kopiert": "Copiato",
  "Name und E-Mail erforderlich": "Nome ed e-mail obbligatori",
  "Bitte einen Kunden wählen": "Seleziona un cliente",
  "Passwort: mindestens 8 Zeichen.": "Password: almeno 8 caratteri.",
  "Konto aktiv angelegt – Passwort direkt gesetzt": "Account creato e attivo – password impostata direttamente",
  "Bestehendes Konto aktualisiert – Passwort neu gesetzt": "Account esistente aggiornato – nuova password impostata",
  "Einladung per E-Mail gesendet": "Invito inviato via e-mail",
  "Einladung erstellt (Brevo nicht verbunden – Link kopieren)": "Invito creato (Brevo non collegato – copia il link)",
  "Diese E-Mail ist bereits vergeben": "Questo indirizzo e-mail è già in uso",
  "Diese E-Mail ist bereits vergeben. Bitte die Aktualisierung des bestehenden Kontos bestätigen.": "Questo indirizzo e-mail è già in uso. Conferma l'aggiornamento dell'account esistente.",
  "Fortfahren aktualisiert das bestehende Konto und setzt das Passwort neu.": "Continuando si aggiorna l'account esistente e si reimposta la password.",
  "Fortfahren aktualisiert das bestehende Konto / sendet eine neue Einladung.": "Continuando si aggiorna l'account esistente / si invia un nuovo invito.",
  "Ich möchte das bestehende Konto aktualisieren.": "Voglio aggiornare l'account esistente.",
  "Diese E-Mail ist bereits vergeben. Bestehende Konten über die Benutzerliste verwalten.": "Questo indirizzo e-mail è già in uso. Gestisci gli account esistenti tramite l'elenco utenti.",
  "Bestehende Konten werden über die Benutzerliste verwaltet (Rolle, Passwort, erneute Einladung) – nicht über dieses Formular.": "Gli account esistenti si gestiscono tramite l'elenco utenti (ruolo, password, nuovo invito) – non tramite questo modulo.",
  "Aktiv": "Attivo",
  "Nicht aktiv": "Non attivo",
  "Inaktiv": "Inattivo",
  "Eingeladen": "Invitato",
  "Ausstehend": "In sospeso",
  "Abgelaufen": "Scaduto",
  "Gesperrt": "Bloccato",
  "Alle gelesen": "Segna tutto letto",
  "offene Freigabe(n)": "approvazione/i aperta/e",
  "Keine offenen Freigaben": "Nessuna approvazione aperta",
  "Mandanten mit offenen Freigaben": "Mandanti con approvazioni aperte",
  "aktiv": "attivo",
  "Demo & Test": "Demo & test",
  "KEWAG-Team öffnen": "Apri il team KEWAG",
  "Ihr Testzugang läuft bald ab.": "Il tuo accesso di prova sta per scadere.",
  "Testzugang, keine echte Veröffentlichung. Alle Social-Aktionen sind Dry-Run.": "Accesso di prova, nessuna pubblicazione reale. Tutte le azioni social sono in dry-run.",
  "Läuft ab:": "Scade:",
  "Upgrade angefragt — KEWAG meldet sich": "Upgrade richiesto — KEWAG ti ricontatta",
  "Wird gesendet…": "Invio in corso…",
  "Jetzt upgraden": "Aggiorna ora",
  "KEWAG-Team · Kunden-Cockpit": "Team KEWAG · cockpit cliente",
  "In der Kundensicht deaktiviert – im echten Kundenlogin aktiv": "Disattivato nella vista cliente – attivo nel vero login cliente",
  "Zurück zum KEWAG Control OS": "Torna al KEWAG Control OS",
  "Sie sehen aktuell das Kunden-Cockpit von": "Attualmente stai visualizzando il cockpit cliente di",
  "Super Admin · Kundenaktionen aktiv": "Super admin · azioni cliente attive",
  "Nur-Lese-Modus": "Modalità sola lettura",
  "Aktionen sind deaktiviert – zum Bearbeiten zurück ins KEWAG Control OS.": "Le azioni sono disattivate – torna al KEWAG Control OS per modificare.",
  "Zurück zum Beitrag im Control OS": "Torna al post nel Control OS",
  "Intern bearbeiten": "Modifica internamente",
  "Kundenansicht verlassen": "Esci dalla vista cliente",
  "Zum Bearbeiten wechseln": "Passa alla modifica",
  "Finanz-Übersicht": "Panoramica finanziaria",
  "Abos & Pakete": "Abbonamenti & pacchetti",
  "Abrechnungseinstellungen": "Impostazioni di fatturazione",
  "Kunden / Mandanten": "Clienti / mandanti",
  "KEWAG-Arbeitsbereich": "Area di lavoro KEWAG",
  "Beitragsübersicht": "Panoramica dei post",
  "Freigaben / Ampel": "Approvazioni / semaforo",
  "Medienbibliothek": "Libreria multimediale",
  "Kundenübersicht": "Panoramica clienti",
  "KEWAG-Team": "Team KEWAG",
  "Demo-Anfragen": "Richieste demo",
  "Supportfälle": "Casi di supporto",
  "Systemübersicht": "Panoramica di sistema",
  "WordPress-Verbindungen": "Connessioni WordPress",
  "Allgemeine Einstellungen": "Impostazioni generali",
  "Backup-Einstellungen": "Impostazioni di backup",
  "KEWAG Finanzen": "KEWAG Finanze",
  "Kunden-Mitarbeiter": "Personale cliente",
  "offen seit": "aperto da",
  "Tagen": "giorni",
  "Tage": "giorni",
  "Mt.": "mese",
  "T.": "g",
  "Offene Angebote ohne Reaktion seit ≥": "Offerte aperte senza risposta da ≥",
  "Angebot(e)": "offerta/e",
  "Angebote verwalten": "Gestisci offerte",
  "Keine Angebote in": "Nessuna offerta in",
  "Nachfass-Erinnerung an Kunde + intern senden": "Invia promemoria di sollecito al cliente + interno",
  "E-Mail-Versand aktiv, sobald Brevo verbunden ist": "Invio e-mail attivo non appena Brevo è connesso",
  "Nachfass-Erinnerung gesendet": "Promemoria di sollecito inviato",
  "Aktualisieren": "Aggiorna",
  "Erneut versuchen": "Riprova",
  "Verwalten": "Gestisci",
  "Jury-Demo · Puls": "Demo giuria · battito",
  "Restlaufzeit": "Tempo rimanente",
  "Aktive Sitzungen": "Sessioni attive",
  "Zugang: öffentlich": "Accesso: pubblico",
  "Zugang: nur mit Einladungslink": "Accesso: solo con link di invito",
  "Wird geprüft…": "Verifica…",
  "Läuft bald ab": "In scadenza",
  "Gesperrt": "Bloccato",
  "Inaktiv": "Inattivo",
  "endet am": "termina il",
  "Tg.": "g",
  "Std.": "h",
  "Min.": "min",
  "Sek.": "s",
  "abgelaufen": "scaduto",
  "Das Jury-Fenster ist abgelaufen – die Demo ist aktuell nicht erreichbar. Jetzt reaktivieren.": "La finestra della giuria è scaduta – la demo non è al momento disponibile. Riattiva ora.",
  "Erinnerung: Das Jury-Fenster läuft in": "Promemoria: la finestra della giuria scade tra",
  "Rechtzeitig verlängern, damit die Demo online bleibt.": "Prolunga in tempo per mantenere la demo online.",
  "Reaktivieren (7 Tage)": "Riattiva (7 giorni)",
  "Um 7 Tage verlängern": "Proroga di 7 giorni",
  "Neue Jury-Zugänge sind gesperrt. Entsperren unter": "I nuovi accessi giuria sono bloccati. Sblocca in",
  "Jury-Demo für 7 Tage reaktiviert.": "Demo giuria riattivata per 7 giorni.",
  "Um 7 Tage verlängert.": "Prorogata di 7 giorni.",
  "Aktion fehlgeschlagen.": "Azione non riuscita.",
  "Verwerfen": "Scarta",
  "Fortsetzen": "Continua",
  "Zuweisen": "Assegna",
  "Alle Pakete": "Tutti i pacchetti",
  "Alle Verantwortlichen": "Tutti i responsabili",
  "Alle Filter zurücksetzen": "Reimposta tutti i filtri",
  "Ampelfilter entfernen": "Rimuovi filtro semaforo",
  "Seite": "Pagina",
  "Nutzer": "Utenti",
  "Zeit": "Ora",
  "Ziel": "Destinazione",
  "Domain": "Dominio",
  "Paket filtern": "Filtra per pacchetto",
  "Verantwortlicher filtern": "Filtra per responsabile",
  "Betreuer": "Referente",
  "Marge": "Margine",
  "Profitabilität": "Redditività",
  "Integration": "Integrazione",
  "Dashboard": "Dashboard",
  "Cockpit": "Cockpit",
  "Orange": "Arancione",
  "Neutral": "Neutro",
  "Änderung/Blocker": "Modifica/blocco",
  "Offene Frage": "Domanda aperta",
  "Live": "Live",
  "Onboarding": "Onboarding",
  "Rot · Änderung/Blocker": "Rosso · modifica/blocco",
  "Blau · offene Frage": "Blu · domanda aperta",
  "Orange · wartet auf Freigabe": "Arancione · in attesa di approvazione",
  "Grün · freigegeben": "Verde · approvato",
  "Neutral · keine offene Freigabe": "Neutro · nessuna approvazione in sospeso",
  "Orange · seit > 3 Tagen": "Arancione · da > 3 giorni",
  "Keine Einträge.": "Nessuna voce.",
  "Keine KI-Läufe an diesem Tag": "Nessuna esecuzione IA in questo giorno",
  "Keine Mandanten entsprechen den aktuellen Filtern.": "Nessun mandante corrisponde ai filtri attuali.",
  "offen": "aperto",
  "überfällig": "in ritardo",
  "kein Limit": "nessun limite",
  "Läufe": "esecuzioni",
  "Lauf": "esecuzione",
  "aktive Abos": "abbonamenti attivi",
  "Live-Übersicht für": "Panoramica live per",
  "Portfolioweite Live-Übersicht über alle Mandanten": "Panoramica live su tutti i mandanti del portafoglio",
  "Mandantenmodus": "Modalità mandante",
  "Mandanten-Ampel": "Semaforo mandanti",
  "Aktive Mandanten": "Mandanti attivi",
  "Live-Kundenentscheidungsstatus · klicken zum Filtern · Zeile öffnet den Mandanten.": "Stato decisioni cliente in tempo reale · clicca per filtrare · la riga apre il mandante.",
  "Die Mandanten-Ampel konnte nicht geladen werden.": "Impossibile caricare il semaforo dei mandanti.",
  "Kunde suchen": "Cerca cliente",
  "Kunde suchen…": "Cerca cliente…",
  "Kundennummer": "Numero cliente",
  "Kundendaten bearbeiten": "Modifica dati cliente",
  "Kunden-Cockpit ansehen": "Visualizza cockpit cliente",
  "Arbeitsbereich": "Area di lavoro",
  "Arbeitsbereich öffnen": "Apri area di lavoro",
  "Intern": "Interno",
  "Kunde wählen": "Scegli cliente",
  "Demo erstellen": "Crea demo",
  "Support öffnen": "Apri supporto",
  "Kunde anlegen": "Crea cliente",
  "Heute wichtig": "Importante oggi",
  "Deine Prioritäten – direkt bearbeiten": "Le tue priorità – gestisci subito",
  "Alles erledigt – keine offenen Prioritäten.": "Tutto fatto – nessuna priorità aperta.",
  "Beiträge zur Freigabe": "Post da approvare",
  "Neue Demo-Anfragen": "Nuove richieste demo",
  "Demos laufen heute ab": "Demo in scadenza oggi",
  "Mein Tag": "La mia giornata",
  "Deine offenen To-dos – Aufgaben, Feedback & überfällige Freigaben": "I tuoi to-do aperti – attività, feedback e approvazioni in ritardo",
  "Alles erledigt – dein Tag ist frei.": "Tutto fatto – la tua giornata è libera.",
  "Meine Aufgaben": "Le mie attività",
  "Mein Kundenfeedback": "Il mio feedback clienti",
  "Überfällige Freigaben": "Approvazioni in ritardo",
  "offen in meinem Eingang": "aperto nella mia casella",
  "Geplante Beiträge ": "Post pianificati ",
  "Umsatz-Übersicht": "Panoramica ricavi",
  "Wiederkehrender Monatsumsatz aktiver Kunden": "Ricavo mensile ricorrente dei clienti attivi",
  "MRR · Monatsumsatz": "MRR · ricavo mensile",
  "Noch keine aktiven Abos.": "Ancora nessun abbonamento attivo.",
  "KI-Kostenkontrolle": "Controllo costi IA",
  "KI-Kosten": "Costi IA",
  "KI-Kosten übersteigen den Umsatz": "I costi IA superano i ricavi",
  "KI-Läufe": "Esecuzioni IA",
  "KI-Läufe am": "Esecuzioni IA del",
  "KI-Läufe pro Tag": "Esecuzioni IA al giorno",
  "KI-Vorschläge": "Suggerimenti IA",
  "KI aktiv": "IA attiva",
  "KI pausiert": "IA in pausa",
  "Bildbewertungen": "Valutazioni immagini",
  "Vorschläge": "Suggerimenti",
  "Videos": "Video",
  "Fehlgeschlagen": "Fallito",
  "Globale Lauf-Auslastung": "Utilizzo globale esecuzioni",
  "Mandanten nahe am Limit": "Mandanti vicini al limite",
  "Letzte KI-Aktivität": "Ultima attività IA",
  "Noch keine KI-Läufe in diesem Zeitraum": "Ancora nessuna esecuzione IA in questo periodo",
  "Kosten: Abrechnung über reale Läufe (keine erfundenen Beträge)": "Costi: fatturazione sulle esecuzioni reali (nessun importo inventato)",
  "Kostenpflichtige KI-Läufe pausiert (globaler Kill-Switch)": "Esecuzioni IA a pagamento in pausa (kill-switch globale)",
  "KI-Kostensatz nicht konfiguriert — im Finanz-Cockpit einstellen.": "Tariffa costi IA non configurata — impostare nel cockpit finanziario.",
  "Verbunden": "Connesso",
  "Nicht verbunden": "Non connesso",
  "MRR": "MRR",
  "ARR": "ARR",
  "Demo-Zugänge": "Accessi demo",
  "Anfragen von socialcheck.ch verwalten": "Gestisci le richieste da socialcheck.ch",
  "Upgrade-Anfragen": "Richieste di upgrade",
  "E-Mail verbunden": "E-mail connessa",
  "E-Mail nicht verbunden": "E-mail non connessa",
  "Neue Anfragen": "Nuove richieste",
  "Aktive Demos": "Demo attive",
  "Heute ablaufend": "In scadenza oggi",
  "Abgelaufen": "Scaduto",
  "Integration wartet auf Aktivierung": "Un'integrazione è in attesa di attivazione",
  "Integrationen warten auf Aktivierung": "Integrazioni in attesa di attivazione",
  "Zugangsdaten getestet – Secret hinterlegen & Backend neu starten.": "Credenziali testate – salva il secret e riavvia il backend.",
  "Lückenloses Protokoll aller Aktionen und Ampel-Änderungen.": "Registro completo di tutte le azioni e delle modifiche al semaforo.",
  "Nur Integrationen": "Solo integrazioni",
  "Nur Kundenansicht (Super Admin)": "Solo vista cliente (super admin)",
  "Aktueller Mandant": "Mandante attuale",
  "Integrations-Ereignis": "Evento di integrazione",
  "Vom Super Admin in der Kundenansicht ausgeführt": "Eseguito dal super admin nella vista cliente",
  "SA · Kundenansicht": "SA · vista cliente",
  "Einträgen": "voci",
  "Ampel": "Semaforo",
  "Beitrag erstellt": "Post creato",
  "Beitrag bearbeitet": "Post modificato",
  "Beitrag gelöscht": "Post eliminato",
  "Beitrag dupliziert": "Post duplicato",
  "Beitrag archiviert": "Post archiviato",
  "Beitrag wiederhergestellt": "Post ripristinato",
  "Automatisch veröffentlicht": "Pubblicato automaticamente",
  "Kommentar": "Commento",
  "Mitglied hinzugefügt": "Membro aggiunto",
  "Mitglied entfernt": "Membro rimosso",
  "Rolle geändert": "Ruolo modificato",
  "Mandant erstellt": "Mandante creato",
  "Benachrichtigungen geändert": "Notifiche modificate",
  "Integration verbunden": "Integrazione connessa",
  "Integration getestet": "Integrazione testata",
  "Zugangsdaten geprüft": "Credenziali verificate",
  "Aktivierung offen": "Attivazione in sospeso",
  "Integrationen neu geprüft": "Integrazioni ricontrollate",
  "Kanal-Buchung geändert": "Prenotazione canale modificata",
  "Integrationen migriert": "Integrazioni migrate",
  "Meta-OAuth gestartet": "OAuth Meta avviato",
  "Meta verbunden": "Meta connesso",
  "Meta-Asset ausgewählt": "Asset Meta selezionato",
  "Meta neu geprüft": "Meta ricontrollato",
  "Meta getrennt": "Meta disconnesso",
  "Meta-Überwachung: Aufgabe": "Monitoraggio Meta: attività",
  "Meta-Überwachung konfiguriert": "Monitoraggio Meta configurato",
  "Systemstatus": "Stato del sistema",
  "Ehrlicher Live-Status — echte Prüfungen, keine statischen Werte.": "Stato live onesto — controlli reali, nessun valore statico.",
  "Letzte Prüfung:": "Ultimo controllo:",
  "Auto-Aktualisierung alle 60 s": "Aggiornamento automatico ogni 60 s",
  "Versionen & Wiederherstellung": "Versioni e ripristino",
  "Systemstatus konnte nicht geladen werden": "Impossibile caricare lo stato del sistema",
  "Systemstatus konnte nicht geladen werden.": "Impossibile caricare lo stato del sistema.",
  "Serverantwort": "Risposta del server",
  "Status wird geprüft…": "Verifica dello stato…",
  "Kerndienste OK:": "Servizi principali OK:",
  "Integrationen verbunden": "Integrazioni connesse",
  "Kerndienste": "Servizi principali",
  "Operative Kennzahlen": "Indicatori operativi",
  "Offene Leads": "Lead aperti",
  "Offene Fotoaufträge": "Incarichi foto aperti",
  "Inhalte zur Freigabe": "Contenuti da approvare",
  "OK": "OK",
  "Eingeschränkt": "Limitato",
  "Betrieb normal — alle Kerndienste laufen.": "Funzionamento normale — tutti i servizi principali attivi.",
  "Betrieb eingeschränkt — ein Kerndienst meldet eine Warnung.": "Funzionamento limitato — un servizio principale segnala un avviso.",
  "Störung — mindestens ein Kerndienst ist nicht verfügbar.": "Guasto — almeno un servizio principale non è disponibile.",
  "„Nicht verbunden\" ist kein Fehler — diese Dienste werden nach Eintragen der Schlüssel automatisch aktiv.": "«Non connesso» non è un errore — questi servizi si attivano automaticamente una volta inserite le chiavi.",
  "Kein Betreuer": "Senza referente",
  "Nur Kunden ohne persönlichen KEWAG-Betreuer anzeigen": "Mostra solo i clienti senza referente KEWAG personale",
  "Entwürfe fortsetzen": "Continua le bozze",
  "Entwurf verworfen": "Bozza scartata",
  "Nachfassen fällig": "Sollecito da inviare",
  "Nachfassen": "Sollecita",
  "Annehmen": "Accetta",
  "Ablehnen": "Rifiuta",
  "Zurücksetzen": "Reimposta",
  "Zum Kunden": "Vai al cliente",
  "In Kunde umwandeln": "Converti in cliente",
  "Angenommen": "Accettato",
  "Abgelehnt": "Rifiutato",
  "Gesendet": "Inviato",
  "angenommen": "accettate",
  "abgelehnt": "rifiutate",
  "nachfassen fällig": "sollecito da inviare",
  "Offenes Volumen:": "Volume aperto:",
  "Abschlussquote:": "Tasso di chiusura:",
  "Ø bis Abschluss:": "Ø fino alla chiusura:",
  "Gewonnen (Monat):": "Vinto (mese):",
  "Trend · 6 Monate": "Trend · 6 mesi",
  "Gewonnen": "Vinto",
  "Verloren": "Perso",
  "Gefiltert auf": "Filtrato su",
  "Angebote ausblenden": "Nascondi offerte",
  "Die E-Mail-Nachfass-Erinnerung (an Kunde + intern) wird automatisch versendet, sobald Brevo verbunden ist. Die Übersicht hier ist bereits live.": "Il promemoria di sollecito via e-mail (al cliente + interno) viene inviato automaticamente non appena Brevo è connesso. La panoramica qui è già live.",
  "Verworfen": "Scartato",
  "Kein persönlicher Betreuer": "Nessun referente personale",
  "Kein persönlicher KEWAG-Betreuer zugewiesen – der Kunde sieht nur den KEWAG-Team-Kontakt.": "Nessun referente KEWAG personale assegnato – il cliente vede solo il contatto del team KEWAG.",
  "Dieser Kunde sieht aktuell nur den KEWAG-Team-Kontakt. Weisen Sie eine:n Hauptbetreuer:in zu.": "Attualmente questo cliente vede solo il contatto del team KEWAG. Assegna un referente principale.",
  "KEWAG-Team wählen…": "Scegli il team KEWAG…",
  "Begrüssungs-E-Mail an den Kunden senden": "Invia e-mail di benvenuto al cliente",
  "Begrüssungs-E-Mail an den Kunden gesendet": "E-mail di benvenuto inviata al cliente",
  "Begrüssung gesendet": "Benvenuto inviato",
  "Hauptbetreuer zugewiesen": "Referente principale assegnato",
  "Zuweisung ok – Begrüssungs-E-Mail konnte nicht gesendet werden.": "Assegnazione ok – impossibile inviare l'e-mail di benvenuto.",
  "Brevo nicht verbunden": "Brevo non connesso",
  "Unprofitabel": "Non redditizio",
  "Angebot angenommen": "Offerta accettata",
  "Angebot abgelehnt": "Offerta rifiutata",
  "Status zurückgesetzt": "Stato reimpostato",
  "Ihre Anfragen an das KEWAG-Team – wir sind für Sie da.": "Le sue richieste al team KEWAG – siamo qui per lei.",
  "Neue Support-Anfrage": "Nuova richiesta di supporto",
  "Ihre Nachricht": "Il suo messaggio",
  "Support-Anfrage senden": "Invia la richiesta",
  "Keine Anfragen": "Nessuna richiesta",
  "Sie haben eine Frage oder einen Wunsch? Erstellen Sie eine Anfrage – das KEWAG-Team kümmert sich darum.": "Ha una domanda o un desiderio? Crei una richiesta – il team KEWAG se ne occupa.",
  "Aktualisiert": "Aggiornato",
  "Anfrage auswählen": "Seleziona una richiesta",
  "Anfrage": "Richiesta",
  "Ticket als PDF herunterladen": "Scarica il ticket in PDF",
  "Als PDF": "In PDF",
  "Kundensicht ist nur-lesend – Antworten sind hier deaktiviert.": "La vista cliente è di sola lettura – le risposte sono disattivate qui.",
  "Diese Anfrage ist erledigt. Für ein neues Anliegen bitte eine neue Anfrage erstellen.": "Questa richiesta è conclusa. Per un nuovo tema crei una nuova richiesta.",
  "Nachricht an das KEWAG-Team schreiben…": "Scrivere un messaggio al team KEWAG…",
  "Foto aufnehmen": "Scatta una foto",
  "Bild wählen": "Scegli un'immagine",
  "PDF / Datei": "PDF / file",
  "Erlaubt: Bilder, PDF, Word, Excel, TXT, CSV · max. 20 MB pro Datei, 10 Anhänge.": "Consentito: immagini, PDF, Word, Excel, TXT, CSV · max. 20 MB per file, 10 allegati.",
  "Dieser Dateityp ist nicht erlaubt.": "Questo tipo di file non è consentito.",
  "Datei zu gross – maximal 20 MB pro Datei.": "File troppo grande – massimo 20 MB per file.",
  "Maximal 10 Anhänge pro Nachricht.": "Massimo 10 allegati per messaggio.",
  "Anhänge überschreiten 100 MB pro Nachricht.": "Gli allegati superano 100 MB per messaggio.",
  "Upload fehlgeschlagen": "Caricamento non riuscito",
  "Bitte warten, bis alle Anhänge hochgeladen sind.": "Attenda il completamento del caricamento degli allegati.",
  "Anfrage gesendet – KEWAG meldet sich.": "Richiesta inviata – KEWAG la ricontatta.",
  "(Anhang)": "(Allegato)",
  "Datei nicht verfügbar": "File non disponibile",
  "PDF-Erstellung fehlgeschlagen": "Creazione del PDF non riuscita",
  "Erneut": "Riprova",
  "Fehler": "Errore",
  "Erledigt": "Concluso",
  "Lädt…": "Caricamento…",
  "Hauptmenü": "Menu principale", "Kommunikation": "Comunicazione", "Abrechnung": "Fatturazione",
  "System": "Sistema", "Arbeit": "Lavoro", "Auswertung & Aufgaben": "Analisi e attività",
  "Übersicht": "Panoramica", "Leads": "Lead", "Kunden": "Clienti", "Fotoaufträge": "Incarichi foto",
  "Inhalte": "Contenuti", "Freigaben": "Approvazioni", "Kalender": "Calendario",
  "Marken-Profil": "Profilo del marchio", "Reports": "Report", "Speicher & Medien": "Spazio & media",
  "Support": "Supporto", "Nachrichten": "Messaggi", "Abos / Pakete": "Abbonamenti / pacchetti",
  "Rechnungen": "Fatture", "Integrationen": "Integrazioni", "Benutzer & Rollen": "Utenti e ruoli",
  "Audit-Log": "Registro di audit", "System Health": "Stato del sistema", "Einstellungen": "Impostazioni",
  "Tagesübersicht": "Panoramica del giorno", "WordPress-Leads": "Lead WordPress",
  "Inhalte bearbeiten": "Modifica contenuti", "Aufgaben": "Attività", "Monatsreports": "Report mensili",
  "Support-Tickets": "Ticket di supporto",
  // super_admin Nav (Golden Master) + Shell-Extras
  "Start": "Avvio", "Vertrieb & Kunden": "Vendite e clienti", "Content & Freigaben": "Contenuti e approvazioni",
  "Heute wichtig / Aufgaben": "Importante oggi / attività", "Leads & Verkauf": "Lead e vendite",
  "Angebote": "Offerte", "Beiträge / Inhalte": "Post / contenuti", "Medien & Speicher": "Media e archiviazione",
  "Marken-Profile": "Profili di marca", "Zahlungen": "Pagamenti", "Zusatzleistungen": "Servizi aggiuntivi",
  "WordPress": "WordPress", "Backups": "Backup",
  "VORSCHAU · Testdaten": "ANTEPRIMA · Dati di test", "Alle Rechte vorbehalten": "Tutti i diritti riservati",
  "Datenschutz": "Privacy", "AGB": "Termini", "Impressum": "Note legali", "Hilfe & Support": "Aiuto e supporto",
  "Mandant": "Cliente", "Alle Mandanten": "Tutti i clienti", "Mandant wechseln": "Cambia cliente",
  // Sidebar-Abschnitte (Admin + Cockpit)
  "Tägliche Arbeit": "Lavoro quotidiano", "Kunden & Mandanten": "Clienti & mandanti",
  "Inhalte & Freigaben": "Contenuti & approvazioni", "Finanzen & Vertrieb": "Finanze & vendite",
  "Kommunikation & Service": "Comunicazione & servizio", "System & Sicherheit": "Sistema & sicurezza",
  "Einstellungen & Konfiguration": "Impostazioni & configurazione",
  "Meine Übersicht": "La mia panoramica", "Inhalte & Planung": "Contenuti & pianificazione",
  "Abrechnung": "Fatturazione", "Service & Konto": "Servizio & account",
  "Kein Mandant zugewiesen.": "Nessun cliente assegnato.", "Ihre Rolle": "Il tuo ruolo",
  "Angemeldet als": "Connesso come",
  "roleHint": "Ruoli e permessi si gestiscono in « Utenti e ruoli ».",
  "Super Admin": "Super Admin", "KEWAG Admin": "KEWAG Admin", "KEWAG Editor": "KEWAG Editor",
  "KEWAG Support": "KEWAG Support", "Kunde / Reviewer": "Cliente / revisore", "Viewer": "Visualizzatore",
  "Kunden-Cockpit öffnen": "Apri il cockpit cliente", "Benachrichtigungen": "Notifiche",
  "Abmelden": "Esci", "Menü einklappen": "Comprimi menu", "Menü ausklappen": "Espandi menu",
  "Kundensicht aktiv:": "Vista cliente attiva:",
  "— du siehst die App als dieser Kunde.": "— vedi l'app come questo cliente.",
  "Zurück zur Admin-Ansicht": "Torna alla vista admin",
  "Guten Morgen": "Buongiorno", "Guten Tag": "Buon pomeriggio", "Guten Abend": "Buonasera",
  "Hier ist die aktuelle Live-Übersicht": "Ecco la panoramica live attuale", "für": "per",
  "Bericht erstellen": "Crea rapporto", "Neue Leads": "Nuovi lead", "Zur Freigabe": "Da approvare",
  "Geplante Beiträge": "Post pianificati", "Offene Tickets": "Ticket aperti",
  "heute": "oggi", "aktuell": "attuale", "Sehr gut": "Ottimo", "Gut": "Buono", "Achtung": "Attenzione",
  "Neue Leads aus socialcheck.ch": "Nuovi lead da socialcheck.ch",
  "Fotoaufträge vom Handy": "Incarichi foto dal cellulare", "System Status": "Stato del sistema",
  "Freigaben (Ampel)": "Approvazioni (semaforo)",
  "Geplante Beiträge (nächste 7 Tage)": "Post pianificati (prossimi 7 giorni)",
  "Offene Support-Tickets": "Ticket di supporto aperti", "Mandanten-Übersicht": "Panoramica clienti",
  "Aktivität": "Attività", "Alle Leads": "Tutti i lead", "Alle anzeigen": "Mostra tutto",
  "Details": "Dettagli", "Öffnen": "Apri", "Alle Kunden": "Tutti i clienti",
  "Keine neuen Leads.": "Nessun nuovo lead.", "Keine Fotoaufträge.": "Nessun incarico foto.",
  "Keine offenen Tickets.": "Nessun ticket aperto.", "Keine Aktivität.": "Nessuna attività.",
  "Lädt aktuelle Daten…": "Caricamento dati…",
  "Rot · Entwurf": "Rosso · Bozza", "Orange · Zur Freigabe": "Arancione · Da approvare",
  "Grün · Freigegeben": "Verde · Approvato", "Blau · Veröffentlicht": "Blu · Pubblicato", "Gesamt": "Totale",
  "Paket": "Pacchetto", "Status": "Stato", "Beiträge": "Post", "Geplant": "Pianificato",
  "Nächste Aktion": "Prossima azione", "Zeige": "Mostra", "bis": "a", "von": "di", "Mandanten": "clienti",
  "Zurück": "Indietro", "Weiter": "Avanti",
  "Demo-Anfrage": "Richiesta demo", "Paket-Anfrage": "Richiesta pacchetto", "Kontakt": "Contatto",
  "Website-Check": "Check sito", "Beratung": "Consulenza", "Zugang": "Accesso",
  "Zur Prüfung": "Da verificare", "Freigegeben": "Approvato", "Veröffentlicht": "Pubblicato",
  "Hoch": "Alta", "Mittel": "Media", "Niedrig": "Bassa",
  "In Arbeit": "In lavorazione", "Aktiv": "Attivo",
  // Kunden
  "Kunde": "Cliente", "Testphase": "Fase di prova", "Ausstehend": "In attesa", "Gekündigt": "Disdetto",
  "seit": "dal", "diesen Monat": "questo mese", "ø unverändert": "ø invariato",
  "Aktive Kunden": "Clienti attivi", "Kunden in Testphase": "Clienti in prova",
  "Ausstehende Aktivierung": "Attivazione in attesa", "Gekündigte Kunden": "Clienti disdetti",
  "Kundenverwaltung": "Gestione clienti",
  "Alle Kunden, Abos und Aktivitäten zentral im Blick.": "Tutti i clienti, abbonamenti e attività a colpo d'occhio.",
  "Exportieren": "Esporta", "exportiert": "esportati", "Verantwortlich": "Responsabile", "Alle": "Tutti",
  "Suche nach Kunde, Domain, E-Mail…": "Cerca cliente, dominio, e-mail…",
  "Weitere Filter": "Altri filtri", "Erweiterte Filter folgen in Kürze.": "Filtri avanzati in arrivo.",
  "Kontakt": "Contatto", "Nächster Schritt": "Prossimo passo", "Aktionen": "Azioni",
  "Keine Kunden gefunden.": "Nessun cliente trovato.", "Details anzeigen": "Mostra dettagli",
  "Cockpit öffnen": "Apri il cockpit", "Abo verwalten": "Gestisci abbonamento",
  "Kundendetails": "Dettagli cliente", "Kundennummer:": "N. cliente:", "Zum Kunden-Cockpit": "Al cockpit cliente",
  "Vertrag": "Contratto", "Rechnung": "Fattura", "Notizen": "Note", "Mehr": "Altro", "Verlauf": "Cronologia",
  "Firma": "Azienda", "Telefon": "Telefono", "Sprache": "Lingua", "Vertrag seit": "Contratto dal",
  "Nächste Rechnung": "Prossima fattura", "Paket ändern": "Cambia pacchetto", "Nutzung": "Utilizzo",
  "Benutzer": "Utenti", "Speicher": "Spazio", "API-Aufrufe (Monat)": "Chiamate API (mese)",
  "Nicht erfasst": "Non tracciato", "Bearbeiten": "Modifica", "Keine Notizen hinterlegt.": "Nessuna nota registrata.",
  "Vertrag gestartet": "Contratto avviato", "geplant": "pianificato", "Keine offenen Aufgaben.": "Nessuna attività aperta.",
  "Bearbeitung im Kunden-Cockpit möglich.": "Modificabile nel cockpit cliente.",
  "Offene Freigaben": "Approvazioni aperte", "Fotoaufträge zu prüfen": "Incarichi foto da verificare",
  "Heute, ": "Oggi, ", "Morgen, ": "Domani, ",
  // Support
  "Tickets zwischen": "Ticket tra", "KEWAG und Kunden": "KEWAG e clienti", "dir und KEWAG": "te e KEWAG",
  "Neues Ticket": "Nuovo ticket", "Support-Anfrage": "Richiesta di supporto", "Betreff": "Oggetto",
  "Priorität": "Priorità", "Nachricht": "Messaggio", "Senden": "Invia", "Normal": "Normale",
  "Betreff und Nachricht erforderlich": "Oggetto e messaggio obbligatori", "Ticket erstellt": "Ticket creato",
  "Keine Tickets. Erstelle ein neues Ticket bei Fragen.": "Nessun ticket. Creane uno se hai domande.",
  "Offen": "Aperto", "In Bearbeitung": "In lavorazione", "Geschlossen": "Chiuso",
  "Antworten…": "Rispondi…", "Antwort senden": "Invia risposta", "Status aktualisiert": "Stato aggiornato",
  "Ampel-Kurzhilfe": "Guida semaforo", "Rot": "Rosso", "Grün": "Verde", "Blau": "Blu",
  "Entwurf / intern.": "Bozza / interno.", "Zur Kundenfreigabe.": "Per approvazione cliente.",
  "Freigegeben / bereit / live.": "Approvato / pronto / live.", "Veröffentlicht / reportet.": "Pubblicato / rendicontato.",
  "Lädt…": "Caricamento…",
  "Nachricht(en)": "messaggio/i",
  // ---- KundenDetail ----
  "Kunde seit": "Cliente dal", "Kundensicht öffnen": "Apri vista cliente", "Zu Freigaben": "Alle approvazioni",
  "Ampel-Verteilung": "Distribuzione semaforo",
  "Rot – Entwurf / intern": "Rosso – Bozza / interno", "Orange – Zur Kundenfreigabe": "Arancione – Approvazione cliente",
  "Grün – Freigegeben / bereit / live": "Verde – Approvato / pronto / live", "Blau – Veröffentlicht / reportet": "Blu – Pubblicato / rendicontato",
  "Abo & nächste Aktion": "Abbonamento & prossima azione", "Abo-Status": "Stato abbonamento", "Preis": "Prezzo",
  "Nächste Aktion:": "Prossima azione:", "Freigaben offen": "approvazioni aperte", "keine offene Aktion": "nessuna azione aperta",
  "Mandanten-Details": "Dettagli cliente", "Kunden-Cockpit": "Cockpit cliente",
  "Direkt zum öffentlichen Bereich dieses Kunden.": "Accesso diretto all'area pubblica di questo cliente.",
  "Direkt zum Kunden-Cockpit": "Direttamente al cockpit cliente", "Ansprechpartner": "Referente",
  "E-Mail": "E-mail", "Website": "Sito web", "Sprachen": "Lingue", "Vertragsstatus": "Stato contratto",
  "Vertrag bis": "Contratto fino al", "monatlich kündbar": "disdicibile mensilmente", "Storage": "Spazio",
  "API Calls (Monat)": "Chiamate API (mese)", "Details & Einstellungen": "Dettagli & impostazioni",
  "Letzte Aktivitäten": "Attività recenti", "Noch keine Aktivitäten protokolliert.": "Nessuna attività registrata.",
  "Nächste Schritte": "Prossimi passi", "Alle Aufgaben": "Tutte le attività",
  "Monatsreport prüfen": "Verifica report mensile", "Report ist bereit zur Prüfung.": "report pronto per la verifica.",
  "Neues Shooting einplanen": "Pianifica nuovo shooting", "Nächsten Fotoauftrag planen und briefen.": "Pianifica e istruisci il prossimo incarico foto.",
  "Rechnungsstatus prüfen": "Verifica stato fatture", "Nächste Rechnung am": "Prossima fattura il", "fällig.": "in scadenza.",
  "Anzeigen": "Mostra", "Planen": "Pianifica", "Tickets offen": "Ticket aperti", "Änderungswünsche": "Richieste di modifica",
  "vs. Vormonat": "vs mese prec.",
  "Entwürfe": "Bozze", "Warten auf Freigabe": "In attesa di approvazione", "Veröffentlicht diesen Monat": "Pubblicato questo mese",
  "Beitrag erstellen": "Crea post", "Kanal": "Canale", "Alle Kanäle": "Tutti i canali", "Alle Status": "Tutti gli stati",
  "Entwurf": "Bozza", "Zeitraum": "Periodo", "Liste": "Elenco", "Planung": "Pianificazione",
  "Beiträge suchen...": "Cerca post...", "Filter zurücksetzen": "Reimposta filtri",
  "Beitrag": "Post", "Geplant für": "Pianificato per", "Keine Beiträge gefunden.": "Nessun post trovato.",
  "Vorschau": "Anteprima", "Freigeben": "Approva", "Als Entwurf": "Come bozza",
  "Keinen Beitrag ausgewählt.": "Nessun post selezionato.", "Beitrags-Vorschau & Details": "Anteprima & dettagli post",
  "Beitragstext": "Testo del post", "Mehr anzeigen": "Mostra di più", "Weniger anzeigen": "Mostra meno", "Kanäle": "Canali",
  "Geplanter Veröffentlichungstermin": "Data di pubblicazione pianificata", "Nicht geplant": "Non pianificato", "Medien": "Media",
  "Wartet auf Freigabe": "In attesa di approvazione", "Heute fällig": "In scadenza oggi", "Diesen Monat freigegeben": "Approvato questo mese",
  "Freigabe-Eingang": "Coda approvazioni", "Sortierung: Fälligkeitsdatum": "Ordina: scadenza", "Sortierung: Status": "Ordina: stato",
  "Alle Freigaben anzeigen": "Mostra tutte le approvazioni", "von:": "da:",
  "Keine Freigabe ausgewählt.": "Nessuna approvazione selezionata.", "Live-Vorschau": "Anteprima live", "In neuem Tab öffnen": "Apri in nuova scheda",
  "Content-Check": "Verifica contenuto", "Keine Prüfung hinterlegt.": "Nessuna verifica registrata.",
  "Erstellt von": "Creato da", "Erstellt am": "Creato il", "Geplant am": "Pianificato il", "Anhang": "Allegato",
  "Entscheidung & Kundenfeedback": "Decisione & feedback cliente", "Kundenfeedback": "Feedback cliente",
  "Noch kein Kundenfeedback.": "Ancora nessun feedback cliente.", "Frist": "Scadenza",
  "Inhalt ohne Änderungen freigeben und publizieren.": "Approva e pubblica il contenuto senza modifiche.",
  "Freigabe mit Auflagen": "Approva con riserve",
  "Mit Kommentaren freigeben und zur Umsetzung zurücksenden.": "Approva con commenti e rinvia per l'attuazione.",
  "Nicht freigeben": "Non approvare", "Inhalt ablehnen und zur Überarbeitung zurücksenden.": "Rifiuta il contenuto e rinvia per revisione.",
  "Freigabe-Verlauf": "Cronologia approvazioni", "Gesamten Verlauf anzeigen": "Mostra cronologia completa",
  "Offene Aufträge": "Incarichi aperti", "gesamt": "in totale", "Diese Woche geplant": "Pianificato questa settimana",
  "Uploads ausstehend": "Upload in attesa", "Bilder": "Immagini", "Bilder freigegeben": "Immagini approvate",
  "Neuer Auftrag": "Nuovo incarico", "Abgeschlossen": "Completato", "Archiv": "Archivio", "Keine Aufträge.": "Nessun incarico.",
  "Alle Fotoaufträge anzeigen": "Mostra tutti gli incarichi foto", "Kein Auftrag ausgewählt.": "Nessun incarico selezionato.",
  "Briefing & Shotlist": "Briefing & shotlist", "Briefing bearbeiten": "Modifica briefing", "Ziel des Shootings": "Obiettivo dello shooting",
  "Shooting-Datum": "Data dello shooting", "Fotograf": "Fotografo", "Benötigte Motive": "Soggetti richiesti",
  "Mood & Referenzen": "Mood & riferimenti", "Referenz": "Riferimento", "Ablauf & Timing": "Programma & tempi",
  "Briefing offen": "Briefing aperto", "Termin fixiert": "Data fissata", "Shooting erfolgt": "Shooting effettuato",
  "Auswahl offen": "Selezione aperta", "Final freigegeben": "Approvato finale", "Storniert": "Annullato",
  "Uploads & Auswahl": "Upload & selezione", "Upload-Status": "Stato upload", "Bilder hochgeladen": "immagini caricate",
  "Letzter Upload:": "Ultimo upload:", "Upload öffnen": "Apri upload", "Auswahl & Freigabe": "Selezione & approvazione",
  "Auswahlstatus": "Stato selezione", "Vom Kunden ausgewählt": "Selezionato dal cliente", "Kunden-Feedback": "Feedback cliente",
  "Kunde zur Auswahl senden": "Invia al cliente per la selezione", "Finalisierung": "Finalizzazione",
  "Nach Kundenfreigabe Final auswählen & freigeben.": "Dopo l'approvazione del cliente, seleziona e approva il finale.",
  "Final freigeben": "Approva finale", "Duplizieren": "Duplica", "Archivieren": "Archivia", "Abbrechen": "Annulla",
  "Hohe Priorität": "Alta priorità", "Warten auf Kunde": "In attesa del cliente", "Gelöst diesen Monat": "Risolto questo mese",
  "Ticket-Queue": "Coda ticket", "In Bearb.": "In lav.", "Warten": "Attesa", "Gelöst": "Risolto",
  "Tickets durchsuchen...": "Cerca ticket...", "Keine Tickets.": "Nessun ticket.", "Alle Tickets anzeigen": "Mostra tutti i ticket",
  "Kritisch": "Critico", "Kein Ticket ausgewählt.": "Nessun ticket selezionato.", "Erstellt:": "Creato:",
  "Antwort an Kunde": "Rispondi al cliente", "Interne Notiz": "Nota interna",
  "Interne Notiz schreiben (nur für das Team sichtbar)...": "Scrivi una nota interna (visibile solo al team)...",
  "Nachricht an den Kunden schreiben...": "Scrivi un messaggio al cliente...", "Anhänge": "Allegati",
  "Dateien anhängen oder hier ablegen": "Allega file o trascinali qui", "Notiz speichern": "Salva nota",
  "Ticket-Details": "Dettagli ticket", "Ziel:": "Obiettivo:", "Fällig:": "Scadenza:", "Zugewiesen an": "Assegnato a",
  "Modul": "Modulo", "Kategorie": "Categoria", "Login & Zugriff": "Login & accesso", "Veröffentlichung": "Pubblicazione",
  "Technik": "Tecnica", "Änderungswunsch": "Richiesta di modifica", "Allgemein": "Generale", "Letzte Aktivität": "Ultima attività",
  "Erstellt": "Creato", "Schnellaktionen": "Azioni rapide", "Ticket zuweisen": "Assegna ticket", "Ticket schliessen": "Chiudi ticket",
  "Offener Betrag": "Importo aperto", "Rechnungen offen": "fatture aperte", "Fällige Rechnungen": "Fatture in scadenza",
  "Rechnung überfällig": "fattura scaduta", "Rechnungen überfällig": "fatture scadute", "Bezahlt diesen Monat": "Pagato questo mese",
  "Nächste Abbuchung": "Prossimo addebito", "Abo:": "Abb.:", "Suchen…": "Cerca…", "Bezahlt": "Pagato", "Überfällig": "Scaduto",
  "In Mahnung": "In sollecito", "Rechnung #": "Fattura #", "Betrag": "Importo", "Fällig am": "Scadenza", "Aktion": "Azione",
  "Erinnern": "Sollecita", "Bezahlen": "Paga", "Keine Rechnungen für diesen Filter.": "Nessuna fattura per questo filtro.",
  "/ Seite": "/ pagina", "Zahlungsverlauf": "Cronologia pagamenti", "Datum": "Data", "Beschreibung": "Descrizione",
  "Beleg/Rechnung": "Ricevuta/Fattura", "Methode": "Metodo", "Erfolgreich": "Riuscito", "Noch keine Zahlungen.": "Nessun pagamento.",
  "Alle Zahlungen anzeigen": "Mostra tutti i pagamenti", "Abo & Zahlung": "Abbonamento & pagamento", "Monatlicher Preis": "Prezzo mensile",
  "Zahlungsmethode": "Metodo di pagamento", "Rechnungsadresse": "Indirizzo di fatturazione", "Aktueller Mahnstatus": "Stato solleciti attuale",
  "Nutzung (aktuell)": "Utilizzo (attuale)", "Offene Posten & Aktionen": "Voci aperte & azioni", "Gesamt offen": "Totale aperto",
  "davon überfällig": "di cui scadute", "Rechnung senden": "Invia fattura", "Zahlung prüfen": "Verifica pagamento",
  "Mahnung senden": "Invia sollecito", "Paket anpassen": "Modifica pacchetto",
  "Stripe/TWINT vorbereitet — nicht verbunden. Aktionen werden im Protokoll erfasst.": "Stripe/TWINT predisposto — non connesso. Le azioni vengono registrate nel log.",
  "Branche": "Settore", "Tonalität": "Tono", "Typische Begriffe": "Termini tipici", "Noch kein Marken-Profil hinterlegt.": "Nessun profilo del marchio registrato.",
  "Noch keine Benutzer.": "Nessun utente.", "Name": "Nome", "Rolle": "Ruolo",
  "Benutzer & Rollen werden unter „Benutzer & Rollen\" verwaltet.": "Utenti & ruoli si gestiscono in « Utenti e ruoli ».",
  "Kein Protokoll vorhanden.": "Nessun registro disponibile.", "Kein Abo": "Nessun abbonamento",
  "Kein Ansprechpartner für Kundensicht vorhanden": "Nessun referente per la vista cliente", "Kundensicht geöffnet": "Vista cliente aperta",
  "Ampel-Status aktualisiert": "Stato semaforo aggiornato", "Inhalt freigegeben": "Contenuto approvato",
  "Mit Auflagen zurückgesendet": "Rinviato con riserve", "Inhalt abgelehnt": "Contenuto rifiutato",
  "Auswahl an Kunden gesendet": "Selezione inviata al cliente", "Final freigegeben (Meldung)": "Approvato finale",
  "Auftrag archiviert": "Incarico archiviato", "Auftrag dupliziert": "Incarico duplicato", "Auftrag abgebrochen": "Incarico annullato",
  "Aktion ausgeführt": "Azione eseguita", "Interne Notiz gespeichert": "Nota interna salvata",
  "Antwort an Kunde gesendet": "Risposta inviata al cliente", "Ticket aktualisiert": "Ticket aggiornato",
  "Neuer Fotoauftrag – Formular folgt.": "Nuovo incarico foto – modulo in arrivo.", "Upload-Bereich folgt.": "Area di upload in arrivo.",
  "Zuweisung – Auswahl folgt.": "Assegnazione – selezione in arrivo.", "Mehr Filter": "Altri filtri",
  "Inhalte warten auf Kundenfreigabe.": "Contenuti in attesa di approvazione cliente.", "Region": "Regione", "Location": "Location", "mehr": "altro",
  // --- RollenRechte Phase-3 IT ---
  "2FA aktiv": "2FA attiva",
  "2FA für diese Rolle verpflichtend": "2FA obbligatoria per questo ruolo",
  "2FA für diesen Benutzer verpflichtend": "2FA obbligatoria per questo utente",
  "2FA inaktiv": "2FA inattiva",
  "2FA ist aktiv": "La 2FA è attiva",
  "2FA ist nicht eingerichtet": "La 2FA non è configurata",
  "2FA verwalten": "Gestisci 2FA",
  "2FA wurde zurückgesetzt": "La 2FA è stata reimpostata",
  "2FA zurücksetzen (Gerät verloren)": "Reimposta la 2FA (dispositivo perso)",
  "2FA-Pflicht": "2FA obbligatoria",
  "2FA-Pflicht (Rollen) gespeichert": "2FA obbligatoria (ruoli) salvata",
  "2FA-Pflicht entfernt": "Obbligo 2FA rimosso",
  "2FA-Pflicht für Benutzer aktiviert": "2FA obbligatoria attivata per l'utente",
  "2FA-Pflicht für alle setzen": "Rendi la 2FA obbligatoria per tutti",
  "2FA-Pflicht offen": "2FA obbligatoria in sospeso",
  "2FA-Pflicht pro Rolle": "2FA obbligatoria per ruolo",
  "2FA: Alle": "2FA: tutte",
  "Abgelaufene Einladungen": "Inviti scaduti",
  "Akteur / Ziel suchen…": "Cerca autore / destinatario…",
  "Aktion: Alle": "Azione: tutte",
  "Aktive Kunden / Mandanten": "Clienti / mandanti attivi",
  "Aktive Mandanten ohne Freigabe-Berechtigten.": "Mandanti attivi senza responsabile approvazioni.",
  "Aktive Rollen": "Ruoli attivi",
  "Aktivieren": "Attiva",
  "Aktiviert": "Attivato",
  "Aktivierungslink ist abgelaufen – erneut senden.": "Il link di attivazione è scaduto – invia di nuovo.",
  "Aktuell keine Zugriffsprobleme – es würde keine Mail versendet.": "Attualmente nessun problema di accesso – nessuna e-mail verrebbe inviata.",
  "Aktueller Status": "Stato attuale",
  "Alle Benutzer sind zugewiesen.": "Tutti gli utenti sono assegnati.",
  "Alle Bereiche deaktiviert: Dieser Editor kann keine Kundendaten bearbeiten.": "Tutte le aree disattivate: questo editor non può modificare alcun dato cliente.",
  "Alle Rollen & Rechte": "Tutti i ruoli e diritti",
  "Alle Rollen & Rechte anzeigen": "Mostra tutti i ruoli e diritti",
  "Alle Team-Mitglieder haben 2FA aktiv.": "Tutti i membri del team hanno la 2FA attiva.",
  "Alle aktiven Kunden haben einen Reviewer.": "Tutti i clienti attivi hanno un revisore.",
  "Alle mit 2FA": "Tutti con 2FA",
  "Alle rollen- und benutzerbezogenen Änderungen.": "Tutte le modifiche relative a ruoli e utenti.",
  "Alle sichtbaren auswählen": "Seleziona tutti i visibili",
  "Auch mit Freigabe kann der Editor kritische Felder nicht ändern — nur nicht-kritische Angaben im jeweiligen Bereich.": "Anche con l'autorizzazione, l'editor non può modificare i campi critici — solo le informazioni non critiche dell'area corrispondente.",
  "Audit-Log exportiert": "Registro di audit esportato",
  "Ausstehende Aktionen": "Azioni in sospeso",
  "Auswahl aufheben": "Annulla selezione",
  "Bearbeitungsrechte gespeichert.": "Diritti di modifica salvati.",
  "Beginn, Laufzeit, Kündigungsfrist, Intervall, Verlängerung": "Inizio, durata, termine di disdetta, intervallo, rinnovo",
  "Beim nächsten Login wird die Einrichtung erzwungen.": "La configurazione sarà imposta al prossimo accesso.",
  "Benutzer aktiviert": "Utente attivato",
  "Benutzer aktualisiert": "Utente aggiornato",
  "Benutzer bearbeiten": "Modifica utente",
  "Benutzer deaktiviert": "Utente disattivato",
  "Benutzer einladen": "Invita utente",
  "Benutzer exportiert": "utenti esportati",
  "Benutzer löschen": "Elimina utente",
  "Benutzer ohne Zuweisung": "Utenti senza assegnazione",
  "Benutzer suchen…": "Cerca utente…",
  "Benutzer verwalten, Rollen steuern, Kunden zuweisen und Einladungen zentral bearbeiten.": "Gestisci utenti, controlla i ruoli, assegna clienti e gestisci gli inviti in modo centralizzato.",
  "Benutzerdetails": "Dettagli utente",
  "Benutzern": "utenti",
  "Bereich": "Area",
  "Bereich / Team": "Area / team",
  "Bereich / Team: Alle": "Area / team: tutti",
  "Betreuer: Alle": "Referente: tutti",
  "Betreuung": "Referente",
  "Bitte einen Kunden / Mandanten wählen": "Seleziona un cliente / mandante",
  "Brevo nicht verbunden – Vorschau-Modus": "Brevo non connesso – modalità anteprima",
  "Das Konto, alle Sitzungen und Kundenzuweisungen werden entfernt. Diese Aktion kann nicht rückgängig gemacht werden.": "L'account, tutte le sessioni e le assegnazioni cliente verranno rimossi. Questa azione è irreversibile.",
  "Deaktivieren": "Disattiva",
  "Deaktiviert oder wegen Fehlversuchen gesperrt.": "Disattivato o bloccato per tentativi falliti.",
  "Details ansehen": "Vedi dettagli",
  "Editor darf nur zugewiesene Kunden bearbeiten. Kritische Felder (Paket, Preis, Vertrag, Rechnung, Sperre, Login-E-Mail) bleiben ausschliesslich dem Super Admin vorbehalten.": "L'editor può modificare solo i clienti assegnati. I campi critici (pacchetto, prezzo, contratto, fattura, blocco, e-mail di accesso) restano riservati esclusivamente al super admin.",
  "Effektive Rechte": "Diritti effettivi",
  "Eingeschr.": "Limitato",
  "Einladung abgelaufen": "Invito scaduto",
  "Einladung erneut senden": "Invia di nuovo l'invito",
  "Einladung gesendet": "Invito inviato",
  "Einladung neu erstellt (Brevo nicht verbunden – kein Versand)": "Invito ricreato (Brevo non connesso – nessun invio)",
  "Einladung per E-Mail erneut gesendet": "Invito inviato di nuovo via e-mail",
  "Einladungen": "Inviti",
  "Einladungen aktiviert": "inviti attivati",
  "Einladungen neu gesendet": "Inviti reinviati",
  "Einladungsstatus": "Stato dell'invito",
  "Einladungsstatus: Alle": "Stato invito: tutti",
  "Einträge": "voci",
  "Empfänger": "destinatari",
  "Empfänger gesendet.": "destinatari.",
  "Report an": "Rapporto inviato a",
  "Endgültig löschen": "Elimina definitivamente",
  "Erneut senden": "Invia di nuovo",
  "Filter aufheben": "Rimuovi filtro",
  "Firmenname, Domain, Adresse, Betreuer, Notiz, Sprache": "Ragione sociale, dominio, indirizzo, referente, nota, lingua",
  "Gefiltert: nur Mitglieder ohne aktives 2FA": "Filtrato: solo i membri senza 2FA attiva",
  "Gesamtbenutzer": "Utenti totali",
  "Gestern": "Ieri",
  "Gilt systemweit über alle Mandanten – keine Mandantenzuweisung nötig. „Super Admin\" ist reserviert und nicht vergebbar.": "Vale a livello di sistema per tutti i mandanti – nessuna assegnazione di mandante necessaria. «Super Admin» è riservato e non assegnabile.",
  "Global": "Globale",
  "Globale (Team-/System-)Rollen kann nur der Super Admin vergeben.": "Solo il super admin può assegnare i ruoli globali (team/sistema).",
  "Globale Rolle": "Ruolo globale",
  "Globale Rolle (KEWAG-Team / System)": "Ruolo globale (team KEWAG / sistema)",
  "Hauptbetreuer": "Referente principale",
  "Hauptbetreuer ändern": "Cambia referente principale",
  "Heute": "Oggi",
  "Hinweis: Gilt bereits über die Rollen-Pflicht.": "Nota: già applicato tramite l'obbligo di ruolo.",
  "Inaktive / gesperrte Konten": "Account inattivi / bloccati",
  "Interne KEWAG-Mitarbeitende mit Zugriff auf alle Mandanten. Kunden-Nutzer findest du weiter unten unter „Benutzerverwaltung\".": "Collaboratori interni KEWAG con accesso a tutti i mandanti. Gli utenti cliente si trovano più in basso sotto «Gestione utenti».",
  "Jetzt testen/senden": "Prova/invia ora",
  "KEWAG-Team / Mitarbeiter": "Team KEWAG / collaboratori",
  "Keine": "Nessuna",
  "Keine Benutzer.": "Nessun utente.",
  "Keine Einträge gefunden.": "Nessuna voce trovata.",
  "Keine Kunden zugewiesen.": "Nessun cliente assegnato.",
  "Keine Kundenbenutzer gefunden.": "Nessun utente cliente trovato.",
  "Keine Rolle/Zuweisung – keine Rechte.": "Nessun ruolo/assegnazione – nessun diritto.",
  "Keine abgelaufenen Einladungen.": "Nessun invito scaduto.",
  "Keine inaktiven Konten.": "Nessun account inattivo.",
  "Keine offenen Einladungen.": "Nessun invito in sospeso.",
  "Keine Änderungen protokolliert.": "Nessuna modifica registrata.",
  "Klicken: nur Mitglieder ohne aktives 2FA anzeigen": "Clicca: mostra solo i membri senza 2FA attiva",
  "Kontaktdaten": "Dati di contatto",
  "Konten, Sitzungen und Kundenzuweisungen werden entfernt. Dein eigenes Konto und der letzte Super Admin werden automatisch übersprungen. Diese Aktion kann nicht rückgängig gemacht werden.": "Account, sessioni e assegnazioni cliente verranno rimossi. Il tuo account e l'ultimo super admin vengono saltati automaticamente. Questa azione è irreversibile.",
  "Kunde: Alle": "Cliente: tutti",
  "Kunden & Zuweisungen": "Clienti e assegnazioni",
  "Kunden entfernen": "Rimuovi clienti",
  "Kunden hinzufügen": "Aggiungi clienti",
  "Kunden ohne Reviewer": "Clienti senza revisore",
  "Kunden suchen…": "Cerca clienti…",
  "Kunden zuweisen": "Assegna clienti",
  "Kunden-Cockpit konnte nicht geöffnet werden": "Impossibile aprire il cockpit cliente",
  "Kundenbenutzer": "Utenti cliente",
  "Kundenbenutzer ohne Mandant – Kunde zuweisen.": "Utenti cliente senza mandante – assegna un cliente.",
  "Kundenbezogene Rolle": "Ruolo relativo al cliente",
  "Kundenbezogene Rolle (pro Mandant)": "Ruolo relativo al cliente (per mandante)",
  "Kundendaten-Bearbeitungsrechte": "Diritti di modifica dei dati cliente",
  "Kundendaten-Rechte": "Diritti sui dati cliente",
  "Kundenprofil bearbeiten": "Modifica profilo cliente",
  "Kundenseitige Benutzer (Reviewer, Kunden-Mitarbeiter, Viewer …). KEWAG-Team findest du im Tab „KEWAG-Team\".": "Utenti lato cliente (revisore, collaboratore cliente, osservatore …). Il team KEWAG si trova nella scheda «Team KEWAG».",
  "Kundenstammdaten": "Dati anagrafici del cliente",
  "Kundenzuweisungen": "Assegnazioni cliente",
  "Kundenübersicht & Zuweisungen": "Panoramica clienti e assegnazioni",
  "Lege fest, für welche Rollen die Zwei-Faktor-Authentifizierung verpflichtend ist. Betroffene Benutzer ohne 2FA müssen sie beim nächsten Login einrichten. „Super Admin\" ist zum Schutz nicht listbar – aktiviere 2FA dort direkt am Konto.": "Stabilisci per quali ruoli l'autenticazione a due fattori è obbligatoria. Gli utenti interessati senza 2FA dovranno configurarla al prossimo accesso. «Super Admin» non è elencato per sicurezza – attiva la 2FA direttamente su quell'account.",
  "Letzte Änderung": "Ultima modifica",
  "Letzte Änderungen": "Ultime modifiche",
  "Löschen": "Elimina",
  "Mitarbeiter (Kunde)": "Collaboratore (cliente)",
  "Name A–Z": "Nome A–Z",
  "Name und Bereich / Team anpassen.": "Modifica nome e area / team.",
  "Noch kein Hauptbetreuer festgelegt.": "Nessun referente principale definito.",
  "Noch keine Benutzer zugewiesen.": "Nessun utente assegnato finora.",
  "Noch keine Team-Mitglieder.": "Nessun membro del team finora.",
  "Offene Einladungen": "Inviti in sospeso",
  "Offene und abgelaufene Einladungen zentral verwalten.": "Gestisci in modo centralizzato gli inviti in sospeso e scaduti.",
  "Onboarding-Fortschritt": "Avanzamento onboarding",
  "Paket & Kontingente": "Pacchetto e contingenti",
  "Paket, gebuchte Plattformen, Kontingente": "Pacchetto, piattaforme prenotate, contingenti",
  "Paket: Alle": "Pacchetto: tutti",
  "Pflicht": "Obbligatorio",
  "Preis, Zahlungsziel, Rechnungsadresse & -E-Mail": "Prezzo, scadenza di pagamento, indirizzo ed e-mail di fatturazione",
  "Profil-Status": "Stato del profilo",
  "Quelle je Spalte: „Global\" = KEWAG-Teamrolle (alle Mandanten) · sonst pro Kunde/Mandant vererbt.": "Fonte per colonna: «Global» = ruolo del team KEWAG (tutti i mandanti) · altrimenti ereditato per cliente/mandante.",
  "Rechnungsdaten": "Dati di fatturazione",
  "Recht": "Diritto",
  "Report deaktiviert": "Rapporto disattivato",
  "Report-Vorschau": "Anteprima del rapporto",
  "Reviewer (Kunde)": "Revisore (cliente)",
  "Rolle ändern": "Cambia ruolo",
  "Rolle, Zuweisungen, Status und effektive Rechte.": "Ruolo, assegnazioni, stato e diritti effettivi.",
  "Rolle: Alle": "Ruolo: tutti",
  "Rollen & Rechte": "Ruoli e diritti",
  "Rollen & Rechte – Schnellübersicht": "Ruoli e diritti – panoramica rapida",
  "Rollen geändert": "Ruoli modificati",
  "Rollenerklärung": "Spiegazione dei ruoli",
  "Rollentyp": "Tipo di ruolo",
  "Rollenverteilung": "Distribuzione dei ruoli",
  "Schließen": "Chiudi",
  "So sähe die E-Mail aus. In Produktion (Brevo verbunden) wird sie automatisch versendet.": "Ecco come apparirebbe l'e-mail. In produzione (Brevo connesso) viene inviata automaticamente.",
  "Sortierung: Rolle": "Ordinamento: ruolo",
  "Speichern": "Salva",
  "Status ändern": "Cambia stato",
  "Status: Alle": "Stato: tutti",
  "Super Admin – Vollzugriff auf alle Kunden.": "Super Admin – accesso completo a tutti i clienti.",
  "Systeminhaber": "Titolare del sistema",
  "Systeminhaber – einziger Super Admin": "Titolare del sistema – unico super admin",
  "Systeminhaber – geschützt": "Titolare del sistema – protetto",
  "Team verwalten": "Gestisci team",
  "Team-Mitglied": "Membro del team",
  "Team-Mitglied anlegen": "Crea membro del team",
  "Tipp: Wenn die Person nur vorübergehend keinen Zugriff haben soll, nutze stattdessen „Deaktivieren\".": "Suggerimento: se la persona non deve avere accesso solo temporaneamente, usa invece «Disattiva».",
  "Unternehmens- und Abo-Status": "Stato dell'azienda e dell'abbonamento",
  "Unverändert": "Invariato",
  "Vertragsdaten": "Dati contrattuali",
  "Vollständige Berechtigungs-Matrix aller Bereiche und Rollen.": "Matrice completa delle autorizzazioni per tutte le aree e i ruoli.",
  "Vollständiges Audit-Log": "Registro di audit completo",
  "Vorname, Nachname, Telefon, Funktion": "Nome, cognome, telefono, funzione",
  "Vorschau erstellt (Brevo nicht verbunden – kein realer Versand).": "Anteprima creata (Brevo non connesso – nessun invio reale).",
  "Weitere Aktionen": "Altre azioni",
  "Weitere Teammitglieder": "Altri membri del team",
  "Wöchentlicher Report aktiviert": "Rapporto settimanale attivato",
  "Wöchentlicher Report an KEWAG Admins": "Rapporto settimanale agli admin KEWAG",
  "Zugewiesene Benutzer": "Utenti assegnati",
  "Zugewiesene Kunden": "Clienti assegnati",
  "Zugriffsprobleme": "Problemi di accesso",
  "Zuletzt aktiv": "Ultima attività",
  "Zuweisungen bearbeiten": "Modifica assegnazioni",
  "Zuweisungen öffnen": "Apri assegnazioni",
  "Zuweisungs- & Profil-Status": "Stato assegnazioni e profilo",
  "Zwei-Faktor-Authentifizierung für": "Autenticazione a due fattori per",
  "ausgewählt": "selezionati",
  "gesperrt": "bloccato",
  "ggü. Vorwoche": "risp. alla settimana prec.",
  "inaktiv": "inattivi",
  "ohne 2FA": "senza 2FA",
  "vollständig": "completo",
  "vs. letzter 30 Tage": "vs ultimi 30 giorni",
  "weitere": "altri",
  "zuletzt": "ultimo",
  "Übersicht der Berechtigungen pro Rolle.": "Panoramica delle autorizzazioni per ruolo.",
  "übersprungen": "saltati",
  "übersprungen (Systeminhaber/geschützt)": "saltati (titolare del sistema/protetto)",
  "von": "di",
  "offen": "in sospeso",
  "Seite": "Pagina",
  "KEWAG-Betreuung": "Assistenza KEWAG",
  "Benutzer gelöscht": "utenti eliminati",
  "Benutzer „{name}\" gelöscht": "Utente «{name}» eliminato",
  "2FA-Pflicht für {n} Mitglied(er) gesetzt": "2FA obbligatoria impostata per {n} membro/i",
  "Editor (Team)": "Editor (team)",
  "Account Manager": "Account Manager",
  "Möchtest du {name} ({email}) endgültig löschen? Das Konto, alle Sitzungen und Kundenzuweisungen werden entfernt. Diese Aktion kann nicht rückgängig gemacht werden.": "Vuoi eliminare definitivamente {name} ({email})? L'account, tutte le sessioni e le assegnazioni cliente verranno rimossi. Questa azione è irreversibile.",
  "Möchtest du die {n} ausgewählten Benutzer endgültig löschen? Konten, Sitzungen und Kundenzuweisungen werden entfernt. Dein eigenes Konto und der letzte Super Admin werden automatisch übersprungen. Diese Aktion kann nicht rückgängig gemacht werden.": "Vuoi eliminare definitivamente i {n} utenti selezionati? Account, sessioni e assegnazioni cliente verranno rimossi. Il tuo account e l'ultimo super admin vengono saltati automaticamente. Questa azione è irreversibile.",
  "Beiträge & Inhalte": "Post e contenuti",
  "Alle Mandanten sehen": "Vedere tutti i mandanti",
  "Kunden & Rollen verwalten": "Gestire clienti e ruoli",
  "Rechnungen, Abos & Abrechnung": "Fatture, abbonamenti e fatturazione",
  "Inhalte erstellen / bearbeiten": "Creare / modificare contenuti",
  "Fotos & Freigaben verwalten": "Gestire foto e approvazioni",
  "Zur Freigabe einreichen": "Inviare per approvazione",
  "Freigaben / Änderungen wünschen": "Approvazioni / richieste di modifica",
  "Kommentare": "Commenti",
  "Integrationen & System": "Integrazioni e sistema",
  "Nur Ansicht": "Sola visualizzazione",
  "Voller Zugriff auf alle Mandanten, Benutzer, Rollen und Systemeinstellungen.": "Accesso completo a tutti i mandanti, utenti, ruoli e impostazioni di sistema.",
  "Verwaltet KEWAG-administrative Aufgaben und hat erweiterten Systemzugriff.": "Gestisce le attività amministrative KEWAG e ha accesso esteso al sistema.",
  "Verwaltet Rechnungen, Abos und Abrechnung über alle Mandanten; liest Kundenstammdaten und Reports. Kein Zugriff auf Inhalte, Freigaben oder Benutzerverwaltung.": "Gestisce fatture, abbonamenti e fatturazione su tutti i mandanti; legge i dati anagrafici cliente e i report. Nessun accesso a contenuti, approvazioni o gestione utenti.",
  "Erstellt und bearbeitet Inhalte und verwaltet Fotoaufträge.": "Crea e modifica contenuti e gestisce gli incarichi fotografici.",
  "Unterstützt Nutzer, verwaltet Freigaben und Änderungen.": "Assiste gli utenti, gestisce approvazioni e modifiche.",
  "Reicht Inhalte ein, prüft und gibt Inhalte frei.": "Invia contenuti, li verifica e li approva.",
  "Kundenseitiger Mitarbeiter: sieht Inhalte, kann kommentieren und Fotos senden – gibt aber NICHT frei (Freigabe bleibt beim Reviewer).": "Collaboratore lato cliente: vede i contenuti, può commentare e inviare foto, ma NON approva (l'approvazione resta al revisore).",
  "Nur lesender Zugriff auf zugewiesene Inhalte.": "Accesso in sola lettura ai contenuti assegnati.",
  "Finanzen (Rechnungen/Abos)": "Finanze (fatture/abbonamenti)",
  "Benutzer & Rollen verwalten": "Gestire utenti e ruoli",
  "Kunde/Mandant verwalten": "Gestire cliente/mandante",
  "Inhalte erstellen/bearbeiten": "Creare/modificare contenuti",
  "Freigeben (Ampel grün)": "Approvare (semaforo verde)",
  "Kommentieren": "Commentare",
  "Ansehen / Lesen": "Visualizzare / leggere",
  "Benutzer hinzugefügt": "Utente aggiunto",
  "Benutzer geändert": "Utente modificato",
  "Benutzer entfernt": "Utente rimosso",
  "Benutzer eingeladen": "Utente invitato",
  "Status geändert": "Stato modificato",
  "Einladung erneut gesendet": "Invito reinviato",
  "Hauptbetreuer gesetzt": "Referente principale impostato",
  "Hauptbetreuer entfernt": "Referente principale rimosso",
  "Begrüssungs-E-Mail gesendet": "E-mail di benvenuto inviata",
  "Kundenprofil vollständig ausgefüllt und Benutzer optimal zugewiesen.": "Profilo cliente compilato completamente e utenti assegnati in modo ottimale.",
  "Profil und Benutzer-Zuweisungen noch nicht vollständig.": "Profilo e assegnazioni utenti non ancora completi.",
  "Kundenprofil und Zuweisungen weitgehend offen.": "Profilo cliente e assegnazioni ancora in gran parte da completare.",
  "Vollzugriff": "Accesso completo",
  "Nur Freigabe": "Solo approvazione",
  "Kommentieren & Fotos": "Commentare e foto",
  "Supportzugriff": "Accesso supporto",
  "Nur Lesen": "Sola lettura",
  "Kundendaten": "Dati cliente",
  "Abo & Preis": "Abbonamento e prezzo",
  "Plattformen & Speicher": "Piattaforme e archiviazione",
  "Vertrag & Abrechnung": "Contratto e fatturazione",
  "Prüfung & Erstellen": "Verifica e creazione",
  "Weiter zu Abo & Preis": "Avanti a Abbonamento e prezzo",
  "Weiter zu Plattformen & Speicher": "Avanti a Piattaforme e archiviazione",
  "Weiter zu Vertrag & Abrechnung": "Avanti a Contratto e fatturazione",
  "Weiter zu Prüfung & Erstellen": "Avanti a Verifica e creazione",
  "Angebot geladen – Kunde aus angenommenem Angebot erstellen.": "Offerta caricata – crea il cliente dall'offerta accettata.",
  "Angebot konnte nicht geladen werden.": "Impossibile caricare l'offerta.",
  "Entwurf geladen – du kannst fortfahren.": "Bozza caricata – puoi continuare.",
  "Entwurf konnte nicht geladen werden.": "Impossibile caricare la bozza.",
  "Firmenname / Projektname ist erforderlich": "Il nome dell'azienda / del progetto è obbligatorio",
  "Kunde „{name}\" angelegt (Nr. {nr}).": "Cliente «{name}» creato (n. {nr}).",
  "Einladung gesendet.": "Invito inviato.",
  "Einladung vorbereitet.": "Invito preparato.",
  "Entwurf gespeichert": "Bozza salvata",
  "Bitte zuerst den Firmennamen erfassen": "Inserisci prima il nome dell'azienda",
  "Angebot-PDF erstellt": "PDF dell'offerta creato",
  "PDF konnte nicht erstellt werden": "Impossibile creare il PDF",
  "Bitte eine Kunden-E-Mail erfassen": "Inserisci un'e-mail cliente",
  "Angebot an {to} gesendet": "Offerta inviata a {to}",
  "Konfiguration konnte nicht geladen werden.": "Impossibile caricare la configurazione.",
  "Neuen SocialCheck-Kunden mit Abo, Plattformen, Speicher und Abrechnung erfassen.": "Registra un nuovo cliente SocialCheck con abbonamento, piattaforme, archiviazione e fatturazione.",
  "Abo & Paket wählen": "Scegli abbonamento e pacchetto",
  "Zusatzleistungen (Add-ons)": "Servizi aggiuntivi (add-on)",
  "Plattformen in den Paketen": "Piattaforme nei pacchetti",
  "Speicherpaket & Limits": "Pacchetto di archiviazione e limiti",
  "Zusammenfassung": "Riepilogo",
  "Firmenname / Projektname": "Nome azienda / progetto",
  "Hauptansprechpartner": "Referente principale",
  "Kundentyp": "Tipo di cliente",
  "Rechnungsadresse · Strasse": "Indirizzo di fatturazione · via",
  "App-Domain (optional)": "Dominio app (facoltativo)",
  "PLZ": "CAP",
  "Ort": "Località",
  "UID / MWST (optional)": "IDI / IVA (facoltativo)",
  "Land": "Paese",
  "Zeitzone": "Fuso orario",
  "Abo-Start": "Inizio abbonamento",
  "Vertragslaufzeit": "Durata del contratto",
  "Rechnungsintervall": "Intervallo di fatturazione",
  "Zahlungsziel": "Termine di pagamento",
  "Rabatt (%)": "Sconto (%)",
  "Setup-Gebühr (einmalig)": "Costo di setup (una tantum)",
  "Serie / Erneuerung": "Serie / rinnovo",
  "z. B. Blütenzauber GmbH": "es. Blütenzauber GmbH",
  "Vor- und Nachname": "Nome e cognome",
  "Branche wählen": "Scegli un settore",
  "Strasse und Nr.": "Via e n.",
  "Laufzeit": "Durata",
  "Intervall": "Intervallo",
  "Erneuerung": "Rinnovo",
  "Weitere Kontaktpersonen hinzufügen": "Aggiungi altre persone di contatto",
  "Beliebt": "Popolare",
  "Beiträge / Monat": "post / mese",
  "GB Speicher": "GB di archiviazione",
  "einmalig": "una tantum",
  "/ Monat": "/ mese",
  "Inklusive": "Incluso",
  "Nicht inklusive": "Non incluso",
  "Plattform": "Piattaforma",
  "Speicherpaket wählen": "Scegli il pacchetto di archiviazione",
  "Speichernutzung – Warnstufen": "Utilizzo archiviazione – livelli di avviso",
  "Vollständigkeit": "Completezza",
  "Sehr gut!": "Ottimo!",
  "Fast geschafft": "Quasi fatto",
  "Weiter ausfüllen": "Continua a compilare",
  "Fast bereit zum Erstellen.": "Quasi pronto per la creazione.",
  "Pflichtfelder ausfüllen.": "Compila i campi obbligatori.",
  "Ausgewähltes Paket": "Pacchetto selezionato",
  "Inkludierte Plattformen": "Piattaforme incluse",
  "Plattformen inklusive": "piattaforme incluse",
  "Speicherpaket": "Pacchetto di archiviazione",
  "(monatlich)": "(mensile)",
  "Einmalige Kosten": "Costi una tantum",
  "Monatliche Gesamtkosten": "Costo mensile totale",
  "inkl. MwSt.": "IVA incl.",
  "Angebot als PDF": "Offerta in PDF",
  "Angebot senden": "Invia offerta",
  "E-Mail-Versand (Brevo) nicht verbunden – PDF herunterladen und manuell versenden.": "Invio e-mail (Brevo) non connesso – scarica il PDF e invialo manualmente.",
  "Angebot als PDF per E-Mail senden": "Invia l'offerta in PDF via e-mail",
  "E-Mail-Versand (Brevo) ist nicht verbunden": "L'invio e-mail (Brevo) non è connesso",
  "inklusive": "incluso",
  "Einmalig (Setup / Kurzcheck)": "Una tantum (setup / check rapido)",
  "Rabatt": "Sconto",
  "/ Mt.": "/ mese",
  "{pct}% ausgefüllt · Schritt {step} von 5": "{pct}% compilato · passo {step} di 5",
  "Als Entwurf speichern": "Salva come bozza",
  "2 GB Speicher": "2 GB di archiviazione",
  "Auf Anfrage": "Su richiesta",
  "auf Anfrage": "su richiesta",
  "2 GB + {n} GB": "2 GB + {n} GB",
  "Neukunde": "Nuovo cliente",
  "Bestehender Kunde": "Cliente esistente",
  "Aus Lead umgewandelt": "Convertito da lead",
  "Demo-Kunde": "Cliente demo",
  "Monatlich kündbar": "Disdicibile mensilmente",
  "12 Monate": "12 mesi",
  "24 Monate": "24 mesi",
  "36 Monate": "36 mesi",
  "Monatlich": "Mensile",
  "Quartalsweise": "Trimestrale",
  "Halbjährlich": "Semestrale",
  "Jährlich": "Annuale",
  "10 Tage": "10 giorni",
  "14 Tage": "14 giorni",
  "30 Tage": "30 giorni",
  "60 Tage": "60 giorni",
  "Off": "Off",
  "Automatisch verlängern": "Rinnovo automatico",
  "Manuell prüfen": "Verifica manuale",
  "Deutsch": "Tedesco",
  "Französisch": "Francese",
  "Italienisch": "Italiano",
  "Englisch": "Inglese",
  "Schweiz": "Svizzera",
  "Liechtenstein": "Liechtenstein",
  "Deutschland": "Germania",
  "Österreich": "Austria",
  "Blumen & Floristik": "Fiori e floristica",
  "Gastronomie & Restaurant": "Gastronomia e ristorante",
  "Handwerk & Bau": "Artigianato ed edilizia",
  "Gesundheit & Praxis": "Salute e studio medico",
  "Beauty & Wellness": "Bellezza e benessere",
  "Detailhandel & Retail": "Commercio al dettaglio",
  "Immobilien": "Immobiliare",
  "Beratung & Dienstleistung": "Consulenza e servizi",
  "Bildung & Coaching": "Formazione e coaching",
  "Sport & Fitness": "Sport e fitness",
  "Auto & Mobilität": "Auto e mobilità",
  "Garten & Landschaft": "Giardino e paesaggio",
  "Technologie & IT": "Tecnologia e IT",
  "Andere": "Altro",
  "Zusatzkanal": "Canale aggiuntivo",
  "Zusatzbeitrag": "Post aggiuntivo",
  "Design-Beitrag": "Post design",
  "Reel": "Reel",
  "Community Management": "Community Management",
  "2 GB inklusive": "2 GB inclusi",
  "+10 GB": "+10 GB",
  "+50 GB": "+50 GB",
  "+100 GB": "+100 GB",
  "Mehr als 100 GB": "Più di 100 GB",
  "Warnung": "Avviso",
  "Starke Warnung": "Avviso forte",
  "Erste Warnung bei 80%.": "Primo avviso all'80%.",
  "Zweite Warnung bei 95%.": "Secondo avviso al 95%.",
  "Upload blockieren oder Speichererweiterung verlangen.": "Bloccare l'upload o richiedere un'estensione dell'archiviazione.",
};

const EN = {
  // Kundenfeedback-Seite + „Heute“-Systemtexte
  "{n} Aufgabe(n) erstellt": "{n} task(s) created",
  "{n} aktualisiert": "{n} updated",
  "Verarbeiten Sie Feedback aus dem Freigabe-Prozess – effizient, strukturiert und nachvollziehbar.": "Process feedback from the approval process – efficiently, structured and traceable.",
  "+ {n} heute": "+ {n} today",
  "Suche in Beitrag, Kunde, Kommentar…": "Search post, client, comment…",
  "Typ: Alle": "Type: all",
  "Plattform: Alle": "Platform: all",
  "Ansicht gespeichert": "View saved",
  "Ansicht speichern": "Save view",
  "Ansicht auf diesem Gerät gespeichert": "View saved on this device",
  "Ansicht konnte auf diesem Gerät nicht gespeichert werden": "The view could not be saved on this device",
  "Ansicht kann ohne Benutzerkennung nicht gespeichert werden": "The view cannot be saved without a user ID",
  "Speichert Reiter, Filter, Sortierung und Einträge pro Seite nur auf diesem Gerät": "Saves tab, filters, sorting and entries per page on this device only",
  "Zeitraum:": "Period:",
  "Mein Eingang": "My inbox",
  "{n} ausgewählt": "{n} selected",
  "Zuweisen an": "Assign to",
  "Priorität setzen": "Set priority",
  "Als erledigt markieren": "Mark as done",
  "Aufgabe erstellen": "Create task",
  "Sortieren:": "Sort:",
  "Neueste zuerst": "Newest first",
  "Älteste zuerst": "Oldest first",
  "Typ": "Type",
  "Bearbeitung": "Handling",
  "Zuständig": "Assignee",
  "Kein Feedback für diese Auswahl.": "No feedback for this selection.",
  "Aufgabe öffnen": "Open task",
  "Beitrag öffnen": "Open post",
  "Antworten": "Reply",
  "Notiz gespeichert": "Note saved",
  "Wählen Sie ein Feedback aus.": "Select a feedback item.",
  "ID: {ref}": "ID: {ref}",
  "ID kopiert": "ID copied",
  "Originalbeitrag (Vorschau)": "Original post (preview)",
  "Kundenrückmeldung": "Client feedback",
  "Notiz bearbeiten": "Edit note",
  "Keine interne Notiz.": "No internal note.",
  "Verknüpfte Aufgabe": "Linked task",
  "Priorität gesetzt": "Priority set",
  "– Zuweisen": "– Assign",
  "Zugewiesen": "Assigned",
  "Zuweisung entfernt": "Assignment removed",
  "Ändern": "Change",
  "Fälligkeit angepasst": "Due date changed",
  "Kundenansicht öffnen": "Open client view",
  "Alle Aktivitäten": "All activities",
  "Änderung umsetzen: {title}": "Implement change: {title}",
  "Aufgabe {ref} erstellt": "Task {ref} created",
  "Aufgabe aus Kundenfeedback": "Task from client feedback",
  "Wird als verknüpfte interne Aufgabe angelegt und protokolliert.": "Created as a linked internal task and logged.",
  "Beschreibung / Änderungswunsch": "Description / change request",
  "Antwort gesendet – wartet auf Kunde": "Reply sent – waiting for client",
  "Wird als Kommentar am Beitrag gespeichert.": "Saved as a comment on the post.",
  "Antwort schreiben…": "Write a reply…",
  "Nur für das KEWAG-Team sichtbar.": "Visible to the KEWAG team only.",
  "Interne Notiz…": "Internal note…",
  "Morgen": "Tomorrow",
  "Freigabe": "Approval",
  "Wartet auf Kunde": "Waiting for client",
  "Feedback eingegangen": "Feedback received",
  "{name} zugewiesen": "Assigned to {name}",
  "Status auf {status} gesetzt": "Status set to {status}",
  "Priorität auf {prio} gesetzt": "Priority set to {prio}",
  "Interne Notiz aktualisiert": "Internal note updated",
  "Beitrag wartet auf Kundenfreigabe": "Post awaiting client approval",
  "Neue Antwort zu „{title}“.": "New reply on “{title}”.",
  // Glocke: Systemtexte des Servers (lib/notif.js)
  "Neuer Änderungswunsch": "New change request",
  "Erinnerung: Freigabe ausstehend": "Reminder: approval pending",
  "Neuer Beitrag wartet auf Ihre Freigabe": "New post awaiting your approval",
  "Kunde hat freigegeben": "Client approved",
  "Beitrag ist live": "Post is live",
  "Frage vom Kunden": "Question from client",
  "Neue Kundenantwort": "New client reply",
  "Antwort vom KEWAG-Team": "Reply from the KEWAG team",
  "Marken-Profil: Team informiert": "Brand profile: team notified",
  "Meta-Verbindung prüfen: {tenant}": "Check Meta connection: {tenant}",
  "KI-Limit fast erreicht: {tenant}": "AI limit almost reached: {tenant}",
  "Kunde wünscht eine Änderung an „{title}“: {note}": "Client requests a change to “{title}”: {note}",
  "Kunde wünscht eine Änderung an „{title}“.": "Client requests a change to “{title}”.",
  "{n} Beitrag wartet auf Ihre Freigabe.": "{n} post is awaiting your approval.",
  "{n} Beiträge wartet auf Ihre Freigabe.": "{n} posts are awaiting your approval.",
  "„{title}“ liegt für Sie zur Freigabe bereit.": "“{title}” is ready for your approval.",
  "„{title}“ wurde vom Kunden freigegeben.": "“{title}” was approved by the client.",
  "„{title}“ wurde veröffentlicht.": "“{title}” was published.",
  "Neue Frage/Kommentar zu „{title}“.": "New question or comment on “{title}”.",
  "Bitte den Beitrag „{title}“ prüfen und freigeben.": "Please review and approve the post “{title}”.",
  "{name} hat das Team über das aktuelle Marken-Profil informiert.": "{name} notified the team about the current brand profile.",
  "{tenant} hat {runs} von {limit} KI-Läufen in diesem Monat verbraucht ({pct}%).": "{tenant} has used {runs} of {limit} AI runs this month ({pct}%).",
  "Meta-Verbindung von {tenant} benötigt Handlung: {summary}. Bitte im Integrationen-Bereich neu verbinden.": "The Meta connection for {tenant} needs action: {summary}. Please reconnect it in the Integrations area.",
  "Token ungültig – bitte neu verbinden.": "Token invalid – please reconnect.",
  "Token ungültig": "Token invalid",
  "Fehlende Berechtigungen: {scopes}": "Missing permissions: {scopes}",
  "Token läuft in {d} Tag(en) ab – bitte neu verbinden.": "Token expires in {d} day(s) – please reconnect.",
  "Geschütztes Konto": "Protected account",
  "Alte Konfigurationsnamen": "Legacy configuration names",
  "Schlüsselnamen sind Konfiguration, keine zusätzlichen Admin-Konten. Ein fehlender Startschlüssel ändert kein bestehendes Konto-Passwort.": "Key names are configuration, not additional admin accounts. A missing startup key does not change an existing account password.",
  // Owner/team configuration messages, including parameterized API warnings.
  "SUPER_ADMIN_EMAIL und SUPERADMIN_EMAIL verweisen auf verschiedene Konten": "SUPER_ADMIN_EMAIL and SUPERADMIN_EMAIL refer to different accounts",
  "Owner-Passwort unter mehreren Schlüsselnamen mit verschiedenen Werten: {keys}": "Owner password has different values under multiple key names: {keys}",
  "Konto unter der Owner-E-Mail hat nicht die Rolle super_admin (keine automatische Hochstufung)": "The account at the owner email does not have the super_admin role (no automatic promotion)",
  "{count} weitere(s) Super-Admin-/Inhaber-Konto(en) vorhanden": "{count} additional Super Admin/owner account(s) exist",
  "Teamkonto ist zugleich als Owner konfiguriert": "Team account is also configured as the owner",
  "Teamkonto hat Owner-Rechte": "Team account has owner permissions",
  "Rolle {role} statt {expected}": "Role {role} instead of {expected}",
  "Altkonto ADMIN_EMAIL hat Owner-Rechte (zweite Owner-Identität)": "Legacy account ADMIN_EMAIL has owner permissions (second owner identity)",
  "Altname {key} aktiv - kanonisch: {canonical}": "Legacy name {key} active - canonical: {canonical}",
  "Owner-E-Mail stammt aus dem Altnamen ADMIN_EMAIL - kanonisch: SUPER_ADMIN_EMAIL": "Owner email comes from the legacy name ADMIN_EMAIL - canonical: SUPER_ADMIN_EMAIL",
  "{key} gesetzt - wirkungslos (beim Start gibt es keinen Passwort-Reset mehr)": "{key} is set but has no effect (startup no longer resets passwords)",
  "Nur der Super Admin darf den Zugangsstatus sehen.": "Only the Super Admin may view access status.",
  // --- Integrationen (Tracking #19) ---
  "Untermenü einklappen": "Collapse submenu",
  "Untermenü ausklappen": "Expand submenu",
  "Verbindung unterbrochen — Token erneuern / erneut verbinden.": "Connection interrupted — renew token / reconnect.",
  "Handlungsbedarf — Provider noch nicht verfügbar.": "Action needed — provider not available yet.",
  "Provider noch nicht verfügbar.": "Provider not available yet.",
  "Verbindung prüfen (Health-Check).": "Check connection (health check).",
  "Adapter vorbereitet — Provider-Credentials/Freigabe erforderlich.": "Adapter prepared — provider credentials/approval required.",
  "So hinterlegen Sie die Zugangsdaten für {name}.": "How to store the credentials for {name}.",
  "Verbindung gültig": "Connection valid",
  "{v} kopiert": "{v} copied",
  "{name} verbinden": "Connect {name}",
  "Sofort testen (ohne Speichern)": "Test now (without saving)",
  "Der Schlüssel wird nur live geprüft – nicht in der App gespeichert.": "The key is only checked live – it is not stored in the app.",
  "KEWAG-Aufgabe „Zugangsdaten aktivieren\" wurde erstellt.": "KEWAG task “Activate credentials” has been created.",
  "Dauerhaft aktivieren": "Activate permanently",
  "Nach erfolgreichem Test: diese Werte als Emergent-Secret hinterlegen, dann 1× Backend-Neustart – danach ist die Integration verbunden.": "After a successful test: store these values as an Emergent secret, then restart the backend once – the integration is then connected.",
  "Konfigurationsseite:": "Configuration page:",
  "Zur Konfiguration": "Go to configuration",
  "Konfigurationsseite folgt": "Configuration page coming soon",
  "Konfiguration folgt": "Configuration coming soon",
  "Zugangsdaten": "Credentials",
  "Alle System-Zugangsdaten sind hinterlegt.": "All system credentials are stored.",
  "Offene System-Integrationen — direkt zur passenden Konfiguration.": "Open system integrations — straight to the right configuration.",
  "Hinterlegen": "Store",
  "Gateway-Übersicht wird geladen…": "Loading gateway overview…",
  "KEWAG Social Gateway — Ablauf": "KEWAG Social Gateway — flow",
  "Beitrag → Ampel-Gate → Queue → Dry-Run. Nur grün-freigegebene Beiträge dürfen intern in die Queue.": "Post → Ampel gate → queue → dry run. Only green-approved posts may enter the queue internally.",
  "Externe Veröffentlichung deaktiviert": "External publishing disabled",
  "Beiträge im Umlauf": "Posts in circulation",
  "freigabe-geprüft": "approval checked",
  "Ampel-Gate": "Ampel gate",
  "grün → zugelassen": "green → allowed",
  "Queue (Dry-Run)": "Queue (dry run)",
  "{q} wartend · {p} aktiv": "{q} waiting · {p} active",
  "Simuliert veröffentlicht": "Simulated as published",
  "kein externer Versand": "no external sending",
  "Vom Ampel-Gate blockiert ·": "Blocked by the Ampel gate ·",
  "Plattformen (ehrlicher Status)": "Platforms (honest status)",
  "Warum blockiert? — Live-Feed": "Why blocked? — live feed",
  "Zuletzt geprüft:": "Last checked:",
  "Noch keine Blockierungen protokolliert. Sobald ein nicht-grüner Beitrag eingereiht wird, erscheint der Grund hier.": "No blocks logged yet. As soon as a non-green post is queued, the reason appears here.",
  "{a} blockiert": "{a} blocked",
  "Ampel-Gate: Veröffentlichung gesperrt.": "Ampel gate: publishing blocked.",
  "Hilfe & Dokumentation": "Help & documentation",
  "Alle Dokumente öffnen": "Open all documents",
  "{n}% aller Kunden": "{n}% of all customers",
  "Verbundene Kunden": "Connected customers",
  "Kunden mit Handlungsbedarf": "Customers needing action",
  "Ablaufende Berechtigungen": "Expiring permissions",
  "in den nächsten 30 Tagen": "in the next 30 days",
  "Nächste: in {n} Tagen": "Next: in {n} days",
  "Kritische Probleme": "Critical issues",
  "Sofortige Aufmerksamkeit": "Immediate attention",
  "seit letzter Woche": "since last week",
  "seit {n} Tag": "since {n} day",
  "seit {n} Tagen": "since {n} days",
  "Keine Vergleichsdaten": "No comparison data",
  "Verlauf konnte nicht geladen werden": "History could not be loaded",
  "KPI-Verlauf": "KPI history",
  "Echte Tages-Snapshots über alle Mandanten": "Real daily snapshots across all tenants",
  "Noch nicht genug Verlaufsdaten": "Not enough history data yet",
  "Bisher 1 Tages-Snapshot erfasst.": "1 daily snapshot recorded so far.",
  "Bisher keine Snapshots erfasst.": "No snapshots recorded so far.",
  "Der tägliche Snapshot baut die Kurven über die nächsten Tage automatisch auf.": "The daily snapshot builds the curves automatically over the coming days.",
  "{n} Kunde": "{n} customer",
  "{n} Kunden": "{n} customers",
  "Nachfass gesendet: {c} Kunde(n) an {r} Empfänger": "Follow-up sent: {c} customer(s) to {r} recipient(s)",
  "Nicht gesendet": "Not sent",
  "Versand fehlgeschlagen": "Sending failed",
  "Export fehlgeschlagen": "Export failed",
  "Onboarding-Nachfass": "Onboarding follow-up",
  "Wöchentliche E-Mail ans KEWAG-Team mit allen „fast fertig (2/3)\"-Kunden.": "Weekly email to the KEWAG team with all “almost done (2/3)” customers.",
  "Jeden": "Every",
  "um": "at",
  "Uhr (UTC)": "o’clock (UTC)",
  "Aktuell fast fertig (2/3)": "Currently almost done (2/3)",
  "Zuletzt gesendet": "Last sent",
  "Noch nie": "Never",
  "3/3 · Fertig diese Woche": "3/3 · Done this week",
  "E-Mail-Dienst (Brevo) noch nicht verbunden – der Versand startet automatisch, sobald Brevo hinterlegt ist.": "Email service (Brevo) not connected yet – sending starts automatically once Brevo is set up.",
  "Vollständig onboarded / Woche (8 Wochen)": "Fully onboarded / week (8 weeks)",
  "Noch keine abgeschlossenen Onboardings in den letzten 8 Wochen.": "No completed onboardings in the last 8 weeks.",
  "Jetzt senden (Test)": "Send now (test)",
  "Report exportieren:": "Export report:",
  "Unterstützte Plattformen in allen Paketen": "Platforms supported in all plans",
  "Plattformen": "Platforms",
  "Grundsätzlich unterstützt und in der Paketlogik verfügbar — nicht automatisch verbunden.": "Supported in principle and available in the plan logic — not connected automatically.",
  "Standard": "Standard",
  "In Vorbereitung": "In preparation",
  "Prioritäten / Handlungsbedarf": "Priorities / action needed",
  "Nach Schwere sortiert — kritisch zuerst.": "Sorted by severity — critical first.",
  "Keine offenen Punkte. Alles im grünen Bereich.": "No open items. Everything is green.",
  "Alle offenen Punkte anzeigen": "Show all open items",
  "{p} einrichten": "Set up {p}",
  "Einheitlicher Verbindungsablauf · nur für den ausgewählten Kunden": "Uniform connection flow · only for the selected customer",
  "Buchung & Verbindung werden ausschliesslich diesem Mandanten zugeordnet.": "Booking & connection are assigned exclusively to this tenant.",
  "**In Vorbereitung.** Der Adapter für {p} ist fertig gebaut. Für die echte Verbindung fehlen noch die Zugangsdaten und die offizielle Plattform-Freigabe (App Review). Wir zeigen bewusst keinen „Verbunden“-Status, solange nichts echt geprüft wurde.": "**In preparation.** The adapter for {p} is built. The real connection still needs the credentials and the official platform approval (App Review). We deliberately show no “Connected” status until something has actually been verified.",
  "**In Vorbereitung.** Der Adapter für {p} ist fertig gebaut. Für die echte Verbindung fehlen noch die Zugangsdaten. Wir zeigen bewusst keinen „Verbunden“-Status, solange nichts echt geprüft wurde.": "**In preparation.** The adapter for {p} is built. The real connection still needs the credentials. We deliberately show no “Connected” status until something has actually been verified.",
  "Kanal buchen": "Book channel",
  "Dieser Kanal ist im Paket des Kunden enthalten.": "This channel is included in the customer’s plan.",
  "Dieser Kanal ist als Zusatzkanal buchbar.": "This channel can be booked as an add-on channel.",
  "Gebucht": "Booked",
  "Buchung entfernen": "Remove booking",
  "Kanal jetzt buchen": "Book channel now",
  "Buchung nur durch Super Admin möglich.": "Booking only possible by Super Admin.",
  "Was wird benötigt?": "What is needed?",
  "Zugangsdaten aus dem Entwicklerportal der Plattform.": "Credentials from the platform’s developer portal.",
  "So bekommen Sie die Zugangsdaten": "How to get the credentials",
  "ausblenden": "hide",
  "anzeigen": "show",
  "{p} öffnen": "Open {p}",
  "Sobald die Plattform freigeschaltet ist, wird dieser Schritt aktiv.": "This step becomes active once the platform is enabled.",
  "Verbinden — bald verfügbar": "Connect — available soon",
  "Sobald die Zugangsdaten hinterlegt und die Plattform freigeschaltet ist, wird der Verbinden-Schritt aktiv — genau wie bei Meta.": "Once the credentials are stored and the platform is enabled, the connect step becomes active — just like with Meta.",
  "Provider-Status": "Provider status",
  "Keine Daten verfügbar": "No data available",
  "Alle Provider anzeigen": "Show all providers",
  "Nur Super Admin": "Super Admin only",
  "Verbindungseinladung senden": "Send connection invitation",
  "Integrations-Management": "Integration management",
  "Globale System-Integrationen": "Global system integrations",
  "Letzter Test:": "Last test:",
  "Offene Punkte:": "Open items:",
  "Verwaltung nur durch Super Admin": "Managed by Super Admin only",
  "Noch nicht verfügbar": "Not available yet",
  "Testen": "Test",
  "Zuletzt geprüft": "Last checked",
  "Verbunden seit": "Connected since",
  "Globale Verwaltung nur durch Super Admin — hier nur Statusansicht.": "Global management by Super Admin only — status view only here.",
  "Wird geladen…": "Loading…",
  "Noch keine Ereignisse für diese Integration.": "No events for this integration yet.",
  "grün": "green",
  "rot": "red",
  "Prüfpunkte": "Checkpoints",
  "Cutover-Protokoll als CSV exportiert.": "Cutover log exported as CSV.",
  "Export fehlgeschlagen.": "Export failed.",
  "Cutover-Bereitschaft geprüft.": "Cutover readiness checked.",
  "Prüfung fehlgeschlagen.": "Check failed.",
  "Stabilitäts-Schwelle: {n} Tage.": "Stability threshold: {n} days.",
  "Speichern fehlgeschlagen.": "Saving failed.",
  "Cutover durchgeführt — zentrale Datenquelle aktiv.": "Cutover completed — central data source active.",
  "Cutover fehlgeschlagen.": "Cutover failed.",
  "Verifikation fehlgeschlagen.": "Verification failed.",
  "Cutover zurückgenommen — Legacy-Fallback wieder aktiv.": "Cutover reverted — legacy fallback active again.",
  "Rollback fehlgeschlagen.": "Rollback failed.",
  "Diagnose wird geladen…": "Loading diagnostics…",
  "System-Diagnose · Cutover-Bereitschaft": "System diagnostics · cutover readiness",
  "Live-Zähler der genutzten Statusquelle · Ziel: Legacy-Fallback dauerhaft 0 → sicherer Umstieg auf die zentrale Datenquelle.": "Live counters of the status source in use · goal: legacy fallback permanently at 0 → safe switch to the central data source.",
  "Cutover-bereit — kein Legacy-Fallback aktiv.": "Cutover-ready — no legacy fallback active.",
  "Noch nicht cutover-bereit.": "Not cutover-ready yet.",
  "Alle Provider werden aus der zentralen Zeile bedient. Beobachtung fortsetzen, dann Umstieg freigeben.": "All providers are served from the central row. Keep observing, then approve the switch.",
  "Legacy-Fallback wurde {n}× verwendet.": "Legacy fallback was used {n}×.",
  "Nicht alle Provider haben eine gültige zentrale Zeile.": "Not all providers have a valid central row.",
  "Default-Auflösung {n}× — zentrale Zeilen prüfen.": "Default resolution {n}× — check the central rows.",
  "Stabilitäts-Beobachtung": "Stability observation",
  "Cutover-Aufgabe erstellt": "Cutover task created",
  "Bereit": "Ready",
  "Stabil seit **{d}** Tagen · Ziel **{s}** Tage.": "Stable for **{d}** days · target **{s}** days.",
  "Letzter Fallback:": "Last fallback:",
  "Kein Fallback registriert.": "No fallback recorded.",
  "Schwelle (Tage):": "Threshold (days):",
  "Jetzt prüfen": "Check now",
  "Datenquellen-Modus": "Data source mode",
  "Aktiv: **zentrale Datenquelle** — Legacy-Fallback deaktiviert": "Active: **central data source** — legacy fallback disabled",
  "Aktiv: **Spiegelung + Legacy-Fallback** — zentrale Zeilen führend, Settings-Blob als Sicherheitsnetz.": "Active: **mirroring + legacy fallback** — central rows lead, settings blob as safety net.",
  "Rückgängig machen": "Undo",
  "Blockiert: nicht alle Provider haben eine zentrale Zeile": "Blocked: not all providers have a central row",
  "Cutover jetzt durchführen": "Run cutover now",
  "Provider": "Provider",
  "Konfiguriert": "Configured",
  "Zentrale Zeile": "Central row",
  "Aufgelöste Quelle": "Resolved source",
  "Ja": "Yes",
  "Nein": "No",
  "Cutover-Protokoll": "Cutover log",
  "CSV-Export": "CSV export",
  "Noch keine Cutover-Ereignisse protokolliert.": "No cutover events logged yet.",
  "Cutover zurücknehmen?": "Revert cutover?",
  "Der Legacy-Settings-Fallback wird wieder aktiviert und die Stabilitäts-Beobachtung neu gestartet. Es gehen keine Daten verloren (der Settings-Blob wurde nie gelöscht).": "The legacy settings fallback is re-enabled and the stability observation restarts. No data is lost (the settings blob was never deleted).",
  "Cutover jetzt durchführen?": "Run cutover now?",
  "Ab jetzt ist **integration_connections** die einzige persistente Statusquelle. Der Legacy-Settings-Fallback wird deaktiviert (aber nicht gelöscht — jederzeit per „Rückgängig machen\" umkehrbar).": "From now on **integration_connections** is the only persistent status source. The legacy settings fallback is disabled (but not deleted — reversible any time via “Undo”).",
  "Verifikations-Kette": "Verification chain",
  "Recheck & Konsistenzprüfung läuft…": "Recheck & consistency check running…",
  "Blockiert: Nicht alle Provider haben eine gültige zentrale Zeile.": "Blocked: not all providers have a valid central row.",
  "Verifikation nicht grün. Umstellung nur als bewusster Super-Admin-Override (Force).": "Verification not green. Switch only as a deliberate Super Admin override (Force).",
  "Verifikation grün, aber Beobachtungsphase noch offen (stabil {d} / {s} Tage). Bewusst früher umstellen (Force).": "Verification green, but observation phase still open (stable {d} / {s} days). Deliberately switch earlier (Force).",
  "Cutover durchführen": "Run cutover",
  "Trotzdem durchführen (Force)": "Run anyway (Force)",
  "System-Health": "System health",
  "Alle Systeme online": "All systems online",
  "Systeme eingeschränkt": "Systems degraded",
  "Details konnten nicht geladen werden": "Details could not be loaded",
  "Kanal gebucht": "Channel booked",
  "Buchung entfernt": "Booking removed",
  "Änderung fehlgeschlagen": "Change failed",
  "Meta nicht konfiguriert": "Meta not configured",
  "Buchung, Verbindungsstatus, Tests und Audit-Verlauf je Plattform.": "Booking, connection status, tests and audit history per platform.",
  "Kundennr.:": "Customer no.:",
  "Paket:": "Plan:",
  "Verantwortlich:": "Responsible:",
  "Nächste beste Aktion": "Next best action",
  "Provider folgt (Phase 3)": "Provider coming (phase 3)",
  "Im Paket": "In plan",
  "Nicht im Paket": "Not in plan",
  "Verbinden": "Connect",
  "Audit-Verlauf": "Audit history",
  "Noch keine Integrationsänderungen.": "No integration changes yet.",
  "Kundensicht": "Customer view",
  "an {e}": "to {e}",
  "{n} / {t} Plattformen": "{n} / {t} platforms",
  "Testbeitrag veröffentlicht": "Test post published",
  "Veröffentlichung fehlgeschlagen": "Publishing failed",
  "Testbeitrag gelöscht": "Test post deleted",
  "Löschen fehlgeschlagen": "Deletion failed",
  "Nachweis per E-Mail gesendet": "Proof sent by email",
  "Keine verbundenen Plattformen zum Prüfen.": "No connected platforms to check.",
  "Kundennummer kopiert": "Customer number copied",
  "Kein Kunde ausgewählt": "No customer selected",
  "Wählen Sie in der Kundenübersicht oder in „Ihre nächsten Aktionen\" einen Kunden.": "Select a customer in the customer overview or in “Your next actions”.",
  "Kundennr.": "Customer no.",
  "{p} Paket": "{p} plan",
  "Nicht zugewiesen": "Not assigned",
  "Verbindungsübersicht": "Connection overview",
  "Nicht gebucht": "Not booked",
  "Im Paket enthalten": "Included in plan",
  "Als Zusatzkanal buchbar": "Bookable as add-on channel",
  "Beitrag ansehen": "View post",
  "Erfolgsnachweis (PDF)": "Success proof (PDF)",
  "Senden…": "Sending…",
  "Nachweis per E-Mail senden": "Send proof by email",
  "Löschen…": "Deleting…",
  "Testbeitrag löschen": "Delete test post",
  "Ein öffentlich sichtbarer Testbeitrag wird auf {p} veröffentlicht.": "A publicly visible test post will be published on {p}.",
  "Posten…": "Posting…",
  "Jetzt posten": "Post now",
  "Test-Veröffentlichung": "Test publication",
  "Noch keine Integrationsaktivitäten.": "No integration activity yet.",
  "Keine internen Notizen hinterlegt.": "No internal notes stored.",
  "Plattform verbinden": "Connect platform",
  "Verbindung prüfen": "Check connection",
  "Onboarding abgeschlossen – Details öffnen": "Onboarding complete – open details",
  "Zur nächsten offenen Onboarding-Aktion springen": "Jump to the next open onboarding action",
  "Kundenübersicht nach Plattformstatus": "Customer overview by platform status",
  "Kunden mit abgeschlossenem Test, aber noch ohne versendeten Nachweis": "Customers with completed test but no proof sent yet",
  "Fast fertig (2/3)": "Almost done (2/3)",
  "Gesamtstatus": "Overall status",
  "Letzte Prüfung": "Last check",
  "Kd.": "No.",
  "Fehler:": "Error:",
  "Detail öffnen": "Open detail",
  "Buchung bearbeiten": "Edit booking",
  "Letzte Prüfungen anzeigen": "Show recent checks",
  "Handlungsbedarf: {n} offen": "Action needed: {n} open",
  "Noch keine Kanäle gebucht": "No channels booked yet",
  "Verbindung abschließen": "Complete connection",
  "Health-Check ausstehend": "Health check pending",
  "Alles funktionsfähig": "Everything working",
  "Funktionsf.": "Working",
  "Details öffnen": "Open details",
  "{s}–{e} von {n} Kunden": "{s}–{e} of {n} customers",
  "{n} pro Seite": "{n} per page",
  "Meta-Daten konnten nicht geladen werden": "Meta data could not be loaded",
  "Einladung erstellt und per E-Mail an {e} gesendet.": "Invitation created and emailed to {e}.",
  "Erinnerung an {e} gesendet.": "Reminder sent to {e}.",
  "Meta-Verbindung & Kundeneinladung": "Meta connection & customer invitation",
  "Einladungslink für {n} — der Kunde verbindet sein eigenes Meta-Konto (ohne Passwort-Weitergabe).": "Invitation link for {n} — the customer connects their own Meta account (no password sharing).",
  "Einladungslink für diesen Mandanten — der Kunde verbindet sein eigenes Meta-Konto (ohne Passwort-Weitergabe).": "Invitation link for this tenant — the customer connects their own Meta account (no password sharing).",
  "Demo-Mandant: keine echte Meta-Verbindung möglich.": "Demo tenant: no real Meta connection possible.",
  "E-Mail-Dienst (Brevo) ist nicht verbunden — der Versand wird übersprungen. Der Link wird trotzdem erstellt.": "Email service (Brevo) is not connected — sending is skipped. The link is still created.",
  "Einladungs-Historie": "Invitation history",
  "{n} offen": "{n} open",
  "Noch keine Einladungen. Erzeugen Sie oben einen Link.": "No invitations yet. Create a link above.",
  "{n}× erinnert": "{n}× reminded",
  "Verschlüsselte, einmalig gültige Einladungen · nur Super Admin": "Encrypted single-use invitations · Super Admin only",
  "Token-Übersicht": "Token overview",
  "Alle Tokens verwalten": "Manage all tokens",
  "Heute, {t}": "Today, {t}",
  "Gestern, {t}": "Yesterday, {t}",
  "So verbinden Sie Instagram/Facebook – ca. 5 Minuten.": "How to connect Instagram/Facebook – approx. 5 minutes.",
  "Meta for Developers öffnen": "Open Meta for Developers",
  "Meta-App erstellen": "Create Meta app",
  "developers.facebook.com › Meine Apps › App erstellen › Typ Business.": "developers.facebook.com › My Apps › Create App › Type Business.",
  "Produkte aktivieren": "Enable products",
  "In der App die Produkte Facebook Login und Instagram Graph API hinzufügen.": "Add the products Facebook Login and Instagram Graph API to the app.",
  "App-ID & App-Geheimnis kopieren": "Copy App ID & App Secret",
  "Einstellungen › Allgemein: App-ID und App-Geheimnis (App Secret) kopieren.": "Settings › Basic: copy the App ID and App Secret.",
  "App-ID": "App ID",
  "App-Geheimnis (App Secret)": "App Secret",
  "Integrationen-Seite › Meta verbinden": "Integrations page › Connect Meta",
  "So aktivieren Sie Zahlungen (Stripe/TWINT) – ca. 5 Minuten.": "How to enable payments (Stripe/TWINT) – approx. 5 minutes.",
  "Stripe API-Schlüssel öffnen": "Open Stripe API keys",
  "Stripe-Dashboard öffnen": "Open Stripe dashboard",
  "dashboard.stripe.com › Entwickler › API-Schlüssel. Oben rechts den Testmodus aktiviert lassen.": "dashboard.stripe.com › Developers › API keys. Keep test mode enabled at the top right.",
  "Geheimen Schlüssel kopieren": "Copy secret key",
  "Den geheimen Schlüssel (beginnt mit sk_test_…) kopieren.": "Copy the secret key (starts with sk_test_…).",
  "Geheimer Schlüssel": "Secret key",
  "Abrechnungseinstellungen › Stripe": "Billing settings › Stripe",
  "So aktivieren Sie den E-Mail-Versand (Brevo) – ca. 3 Minuten.": "How to enable email sending (Brevo) – approx. 3 minutes.",
  "Brevo API-Schlüssel öffnen": "Open Brevo API keys",
  "Brevo-Konto öffnen": "Open Brevo account",
  "app.brevo.com › SMTP & API › API-Schlüssel.": "app.brevo.com › SMTP & API › API keys.",
  "API-Schlüssel erzeugen": "Generate API key",
  "Neuen API-Schlüssel generieren (v3) und den Schlüssel (xkeysib-…) kopieren.": "Generate a new API key (v3) and copy the key (xkeysib-…).",
  "Absender verifizieren": "Verify sender",
  "In Brevo die Absender-Adresse bzw. Domain verifizieren (BREVO_SENDER_EMAIL).": "In Brevo, verify the sender address or domain (BREVO_SENDER_EMAIL).",
  "Brevo API-Key": "Brevo API key",
  "Allgemeine Einstellungen › E-Mail (Brevo)": "General settings › Email (Brevo)",
  "So verbinden Sie Ihre WordPress-Website – ca. 3 Minuten.": "How to connect your WordPress website – approx. 3 minutes.",
  "API-Key erzeugen": "Generate API key",
  "Unten Zur Konfiguration öffnen › WordPress-Verbindungen › API-Key erzeugen und kopieren.": "Below, open Go to configuration › WordPress connections › generate and copy the API key.",
  "Im WordPress-Connector eintragen": "Enter in the WordPress connector",
  "Auf Ihrer WordPress-Website im SocialCheck-Connector den API-Key (Header X-SocialCheck-Key) hinterlegen.": "On your WordPress website, store the API key (header X-SocialCheck-Key) in the SocialCheck connector.",
  "Zurück in SocialCheck den Test ausführen – bei Erfolg wird der Status Verbunden.": "Back in SocialCheck, run the test – if successful, the status becomes Connected.",
  "Zugangsdaten bereithalten": "Have credentials ready",
  "Halten Sie die erforderlichen Schlüssel/IDs des Anbieters bereit.": "Have the provider’s required keys/IDs ready.",
  "Integrations-Guide": "Integrations guide",
  "Schritt-für-Schritt Einrichtung": "Step-by-step setup",
  "API & Zugangsrechte": "API & access rights",
  "Übersicht & Verwaltung": "Overview & management",
  "Troubleshooting": "Troubleshooting",
  "Hilfe bei typischen Fehlern": "Help with common errors",
  "Best Practices": "Best practices",
  "Empfehlungen & Leitfäden": "Recommendations & guides",
  "7 Tage": "7 days",
  "90 Tage": "90 days",
  "Montag": "Monday",
  "Dienstag": "Tuesday",
  "Mittwoch": "Wednesday",
  "Donnerstag": "Thursday",
  "Freitag": "Friday",
  "Samstag": "Saturday",
  "Sonntag": "Sunday",
  "Client Key & Client Secret aus dem TikTok-Entwicklerportal.": "Client Key & Client Secret from the TikTok developer portal.",
  "Auf developers.tiktok.com anmelden und eine App registrieren.": "Sign in at developers.tiktok.com and register an app.",
  "Produkt „Content Posting API“ (Login Kit) hinzufügen und zur Prüfung einreichen.": "Add the product “Content Posting API” (Login Kit) and submit it for review.",
  "Client Key + Client Secret kopieren und die Redirect-URI eintragen.": "Copy Client Key + Client Secret and enter the redirect URI.",
  "Client ID & Client Secret aus dem LinkedIn-Entwicklerportal.": "Client ID & Client Secret from the LinkedIn developer portal.",
  "Auf linkedin.com/developers eine App erstellen (mit LinkedIn-Firmenseite verknüpfen).": "Create an app at linkedin.com/developers (linked to the LinkedIn company page).",
  "Produkte „Share on LinkedIn“ / „Sign In with LinkedIn“ anfordern.": "Request the products “Share on LinkedIn” / “Sign In with LinkedIn”.",
  "Client ID + Client Secret kopieren und die Redirect-URI eintragen.": "Copy Client ID + Client Secret and enter the redirect URI.",
  "App ID & App Secret aus dem Pinterest-Entwicklerportal.": "App ID & App Secret from the Pinterest developer portal.",
  "Auf developers.pinterest.com eine App anlegen und Standard-Zugriff beantragen.": "Create an app at developers.pinterest.com and request standard access.",
  "Benötigte Scopes (boards:read, pins:write) auswählen.": "Select the required scopes (boards:read, pins:write).",
  "App ID + App Secret kopieren und die Redirect-URI eintragen.": "Copy App ID + App Secret and enter the redirect URI.",
  "OAuth Client ID & Secret aus der Google Cloud Console (YouTube Data API).": "OAuth Client ID & Secret from the Google Cloud Console (YouTube Data API).",
  "In der Google Cloud Console ein Projekt anlegen und die „YouTube Data API v3“ aktivieren.": "In the Google Cloud Console, create a project and enable the “YouTube Data API v3”.",
  "OAuth-Zustimmungsbildschirm konfigurieren und zur Verifizierung einreichen.": "Configure the OAuth consent screen and submit it for verification.",
  "OAuth-Client-ID + Secret erstellen und die Redirect-URI eintragen.": "Create an OAuth Client ID + Secret and enter the redirect URI.",
  "OAuth 2.0 Client ID & Secret aus dem X-Entwicklerportal (kostenpflichtig).": "OAuth 2.0 Client ID & Secret from the X developer portal (paid).",
  "Auf developer.x.com ein Projekt + App im Developer-Portal erstellen (API-Zugang ist kostenpflichtig).": "On developer.x.com, create a project + app in the Developer Portal (API access is paid).",
  "OAuth 2.0 aktivieren und die benötigten Scopes (tweet.write, users.read) setzen.": "Enable OAuth 2.0 and set the required scopes (tweet.write, users.read).",
  "OAuth Client ID & Secret aus der Google Cloud Console (Business Profile API).": "OAuth Client ID & Secret from the Google Cloud Console (Business Profile API).",
  "In der Google Cloud Console die „Business Profile API“ aktivieren und Zugriff beantragen.": "In the Google Cloud Console, enable the “Business Profile API” and request access.",
  "OAuth-Zustimmungsbildschirm konfigurieren und einreichen.": "Configure the OAuth consent screen and submit it.",
  "Verfügbar": "Available",
  "Test starten": "Start test",
  "Tokens erneuern": "Renew tokens",
  "Kunde verbinden": "Connect customer",
  "Adapter-Live": "Adapter live",
  "Legacy-Fallback": "Legacy fallback",
  "Default": "Default",
  "Statisch": "Static",
  "Recheck ausgeführt": "Recheck run",
  "Zentrale Zeilen vorhanden": "Central rows present",
  "Kein Fallback nötig": "No fallback needed",
  "Zentrale Zeile == Live-Recheck": "Central row == live recheck",
  "Übersicht konsistent": "Overview consistent",
  "Matrix-Isolation": "Matrix isolation",
  "Verlauf abrufbar": "History retrievable",
  "Down": "Down",
  "Aktivitäten": "Activities",
  "Getestet": "Tested",
  "Nachweis versendet": "Proof sent",
  "Funktionsfähig": "Working",
  "Probleme": "Issues",
  "Ablaufende Tokens": "Expiring tokens",
  "3/3 · Fertig": "3/3 · Done",
  "2/3 · Getestet": "2/3 · Tested",
  "1/3 · Verbunden": "1/3 · Connected",
  "Ablaufende Tokens (7 Tage)": "Expiring tokens (7 days)",
  "Ablaufende Tokens (30 Tage)": "Expiring tokens (30 days)",
  "Gültige Tokens": "Valid tokens",
  "Vorbereitet": "Prepared",
  "Adapter vorbereitet": "Adapter prepared",
  "Getrennt": "Disconnected",
  "Alles OK": "All OK",
  "Teilweise OK": "Partially OK",
  "Adapter": "Adapter",
  "Verbindung": "Connection",
  "Health": "Health",
  "E-Mail (Brevo)": "Email (Brevo)",
  "Verbunden · Versand aktiv": "Connected · sending active",
  "API-Key hinterlegen zum Aktivieren": "Store API key to activate",
  "Instagram / Facebook (Meta)": "Instagram / Facebook (Meta)",
  "App-ID & Secret erforderlich": "App ID & secret required",
  "Eigenen Stripe-Key hinterlegen (STRIPE_SECRET_KEY)": "Store your own Stripe key (STRIPE_SECRET_KEY)",
  "Cloudflare Turnstile": "Cloudflare Turnstile",
  "Verbunden · Spam-Schutz aktiv": "Connected · spam protection active",
  "Secret erforderlich": "Secret required",
  "WordPress-Anbindung": "WordPress connection",
  "Verbunden · Lead-Intake aktiv": "Connected · lead intake active",
  "API-Key generieren": "Generate API key",
  "System-Integrationen migriert (Backfill)": "System integrations migrated (backfill)",
  "Cutover-Bereitschaft erreicht": "Cutover readiness reached",
  "Cutover-Schwelle geändert": "Cutover threshold changed",
  "Cutover durchgeführt": "Cutover completed",
  "Cutover zurückgenommen": "Cutover reverted",
  "Cutover-Verifikation ausgeführt": "Cutover verification run",
  "Meta-Überwachung: Aufgabe erstellt": "Meta monitoring: task created",
  "Adapter vorbereitet – Credentials fehlen": "Adapter prepared – credentials missing",
  "Bereit für Verbindung": "Ready to connect",
  "Credentials fehlen (Meta-Keys)": "Credentials missing (Meta keys)",
  "Unterstützt": "Supported",
  "Nicht konfiguriert – Meta-Keys fehlen": "Not configured – Meta keys missing",
  "Vorbereitet – Kundenzustimmung fehlt": "Prepared – customer consent missing",
  "Token abgelaufen – neu verbinden": "Token expired – reconnect",
  "Nicht begonnen": "Not started",
  "In Entwicklung": "In development",
  "Adapter bereit": "Adapter ready",
  "Zugangsdaten fehlen": "Credentials missing",
  "Zugangsdaten hinterlegt": "Credentials stored",
  "Zugangsdaten ungültig": "Credentials invalid",
  "Zugangsdaten gültig": "Credentials valid",
  "Verbindung läuft": "Connecting",
  "Autorisierung abgelaufen": "Authorization expired",
  "Verbindungsfehler": "Connection error",
  "Nicht getestet": "Not tested",
  "Nicht prüfbar": "Not checkable",
  "Eine zentrale KEWAG-Schnittstelle – sichere Einzelverbindungen zu jeder Plattform": "One central KEWAG interface – secure individual connections to every platform",
  "Dry-Run – keine externe Veröffentlichung (Facebook/Instagram/LinkedIn/TikTok etc. NICHT aufgerufen).": "Dry run – no external publishing (Facebook/Instagram/LinkedIn/TikTok etc. NOT called).",
  "Verbindungsprobleme": "Connection problems",
  "Token/Zugriff prüfen, erneut verbinden": "Check token/access, reconnect",
  "Fehlende API-Zugangsdaten": "Missing API credentials",
  "System-Zugangsdaten hinterlegen": "Store system credentials",
  "Verbindung vorbereitet": "Connection prepared",
  "Gebuchte Kanäle autorisieren": "Authorize booked channels",
  "Unklare Buchungen bestätigen": "Confirm unclear bookings",
  "Kunden ohne verbundene Plattformen": "Customers without connected platforms",
  "Kanäle einrichten": "Set up channels",
  "Tests ausstehend": "Tests pending",
  "Standard · Integration in Vorbereitung": "Standard · integration in preparation",
  "Meta (Instagram/Facebook)": "Meta (Instagram/Facebook)",
  "Integration aktiv": "Integration active",
  "Keine Aktion nötig.": "No action needed.",
  "Noch keine Kanäle gebucht.": "No channels booked yet.",
  "Alle gebuchten Kanäle sind funktionsfähig — keine Aktion nötig.": "All booked channels are working — no action needed.",
  "Erneut verbinden": "Reconnect",
  "Veröffentlichung blockiert": "Publishing blocked",
  "Keine Veröffentlichung möglich": "No publishing possible",
  "Noch keine Veröffentlichung": "No publication yet",
  "Einrichtung erforderlich": "Setup required",
  "Bestätigung erforderlich": "Confirmation required",
  "Zuverlässigkeit unklar": "Reliability unclear",
  "Technische Integration für diese Plattform ist noch nicht verfügbar.": "Technical integration for this platform is not available yet.",
  "Meta ist nicht konfiguriert (App-ID & Secret fehlen).": "Meta is not configured (App ID & secret missing).",
  "Keine aktive Meta-Verbindung für diesen Kanal.": "No active Meta connection for this channel.",
  "Verbindung erfolgreich geprüft.": "Connection checked successfully.",
  "Verbindung fehlgeschlagen – bitte erneut verbinden.": "Connection failed – please reconnect.",
  "Verbindung ausstehend.": "Connection pending.",
  "Diese Integration ist technisch noch nicht verfügbar.": "This integration is not technically available yet.",
  "Integrations-Engine": "Integration engine",
  "Übersicht & Statusmaschine aktiv": "Overview & state machine active",
  "Token-Service": "Token service",
  "Verschlüsselung aktiv": "Encryption active",
  "Encryption-Key fehlt — Verbindungen gesperrt": "Encryption key missing — connections locked",
  "Webhook-Listener": "Webhook listener",
  "Echtzeit-Webhooks folgen (Phase 6)": "Real-time webhooks coming (phase 6)",
  "Keine Verbindung": "No connection",
  "Stripe-Key ungültig oder nicht erreichbar.": "Stripe key invalid or unreachable.",
  "Cutover jetzt möglich: System-Integrationen auf zentrale Datenquelle umstellen": "Cutover now possible: switch system integrations to the central data source",
  "Legacy-Fallback erneut aufgetreten – Beobachtung neu gestartet": "Legacy fallback occurred again – observation restarted",
  "Recheck aller Provider ausgeführt": "Recheck of all providers run",
  "Ein Provider-Recheck warf einen Fehler.": "A provider recheck raised an error.",
  "Alle Provider haben eine gültige zentrale Zeile": "All providers have a valid central row",
  "Mindestens ein Provider ohne zentrale Zeile.": "At least one provider without a central row.",
  "Kein Legacy-Fallback nötig (Quelle: zentrale Zeile)": "No legacy fallback needed (source: central row)",
  "Mindestens ein Provider wird via Fallback aufgelöst.": "At least one provider is resolved via fallback.",
  "Zentrale Zeile stimmt mit Live-Recheck überein": "Central row matches the live recheck",
  "Abweichung zwischen zentraler Zeile und Recheck.": "Mismatch between central row and recheck.",
  "Übersicht/Karten baubar & konsistent": "Overview/cards buildable & consistent",
  "Übersicht konnte nicht konsistent gebaut werden.": "Overview could not be built consistently.",
  "Keine System-Zeilen in der Mandanten-Matrix": "No system rows in the tenant matrix",
  "System-Dokumente ohne scope=system gefunden.": "System documents without scope=system found.",
  "Verlauf je Provider abrufbar": "History retrievable per provider",
  "Verlaufsabfrage fehlgeschlagen.": "History query failed.",
  "Meta noch nicht konfiguriert — App-ID & Secret fehlen": "Meta not configured yet — App ID & secret missing",
  "Bereit zum Verbinden": "Ready to connect",
  "Meta-Grundlage aktiviert und verschlüsselt gespeichert – OAuth kann gestartet werden.": "Meta foundation enabled and stored encrypted – OAuth can be started.",
  "Config-ID gespeichert – OAuth nutzt jetzt Login for Business (config_id, kein scope).": "Config ID saved – OAuth now uses Login for Business (config_id, no scope).",
  "Config-ID entfernt – OAuth nutzt wieder scope.": "Config ID removed – OAuth uses scope again.",
  "Keine Facebook-Seite zurückgegeben — Berechtigung 'pages_show_list' fehlt.": "No Facebook page returned — permission 'pages_show_list' missing.",
  "Keine Facebook-Seite zurückgegeben. Das verbundene Meta-Konto verwaltet keine Seite oder ist nicht als Seiten-Admin zugeordnet.": "No Facebook page returned. The connected Meta account manages no page or is not assigned as page admin.",
  "Meta OAuth": "Meta OAuth",
  "Verbindung gültig.": "Connection valid.",
  "Instagram-Testbeitrag benötigt ein Bild/Video (Content-Container). Nutzen Sie für den Textnachweis bitte Facebook — der Instagram-Test folgt.": "The Instagram test post needs an image/video (content container). Please use Facebook for the text proof — the Instagram test will follow.",
  "Testbeitrag erfolgreich veröffentlicht.": "Test post published successfully.",
  "Testbeitrag gelöscht.": "Test post deleted.",
  "Keine Verbindung.": "No connection.",
  "Token konnte nicht entschlüsselt werden.": "Token could not be decrypted.",
  "Token nicht lesbar.": "Token not readable.",
  "System (Meta-Überwachung)": "System (Meta monitoring)",
  "Meta-Überwachung": "Meta monitoring",
  "Keine Kunden in Stufe 2 (2/3)": "No customers in stage 2 (2/3)",
  "Autorisierung erforderlich": "Authorization required",
  "Aufmerksamkeit erforderlich": "Attention required",
  "App & API": "App & API",
  "WordPress Verbindung": "WordPress connection",
  "KI-Dienst": "AI service",
  "E-Mail Dienst": "Email service",
  "Social (Meta)": "Social (Meta)",
  "Speicherplatz": "Storage",
  "FastAPI · Antwort ok": "FastAPI · response ok",
  "MongoDB · verbunden": "MongoDB · connected",
  "Authentifizierung": "Authentication",
  "Sitzungsspeicher nicht erreichbar": "Session store unreachable",
  "Objektspeicher": "Object storage",
  "OpenAI Vision · einsatzbereit": "OpenAI Vision · ready",
  "Kein Schlüssel hinterlegt": "No key stored",
  "Geplante Jobs": "Scheduled jobs",
  "Tages-Digest · Monatsreport; Kanal-Veröffentlichung noch nicht verfügbar": "Daily digest · monthly report; channel publishing not available yet",
  "Kein automatisches Backup konfiguriert": "No automatic backup configured",
  "Kontaktpflege (Brevo)": "Contact management (Brevo)",
  "Status nicht lesbar": "Status not readable",
  "Marken-Profil geändert": "Brand profile changed",
  "Brand-Asset hochgeladen": "Brand asset uploaded",
  "Team informiert": "Team notified",
  "Beitrag geändert": "Post changed",
  "Beitrag reaktiviert": "Post reactivated",
  "Freigabe-Entscheidung": "Approval decision",
  "Sammel-Freigabe": "Bulk approval",
  "Fotoauftrag-Aktion": "Photo assignment action",
  "Fotoauftrag erstellt": "Photo assignment created",
  "Medien hochgeladen": "Media uploaded",
  "Medien gelöscht": "Media deleted",
  "Rechnungs-Aktion": "Invoice action",
  "Preise aktualisiert": "Prices updated",
  "Ticket-Antwort": "Ticket reply",
  "Mandant geändert": "Tenant changed",
  "Mandant gesperrt": "Tenant locked",
  "Mandant aktiviert": "Tenant activated",
  "Passwort geändert": "Password changed",
  "Passwort zurückgesetzt (Admin)": "Password reset (admin)",
  "Notfall-Zugang ausgelöst": "Emergency access triggered",
  "Passwort per Notfall-Zugang zurückgesetzt": "Password reset via emergency access",
  "Passwort-Reset angefordert": "Password reset requested",
  "Passwort selbst zurückgesetzt": "Password reset by user",
  "E-Mail Versand": "Email sending",
  "Konfiguriert · verbindbar": "Configured · connectable",
  "Meta-Einladung erstellt": "Meta invitation created",
  "Meta-Einladung widerrufen": "Meta invitation revoked",
  "Noch nicht konfiguriert (Google OAuth erforderlich)": "Not configured yet (Google OAuth required)",
  "Stripe-Key erforderlich": "Stripe key required",
  "Technische Integration noch nicht verfügbar": "Technical integration not available yet",
  "gebucht": "booked",
  "nicht gebucht": "not booked",
  // --- Phase 4 supplement ---
  "Setzen Sie ein neues Passwort für": "Set a new password for",
  "nicht erreichbar": "not reachable",
  // --- Phase 4 (Super-Admin-Werkzeuge) ---
  "Kopieren nicht möglich – bitte manuell markieren.": "Unable to copy – please select the link manually.",
  "Erzeugung fehlgeschlagen.": "Generation failed.",
  "Konto-Wiederherstellung": "Account recovery",
  "Notfall-Zugang · nur Passwort-Reset des Systeminhabers": "Emergency access · owner password reset only",
  "Dieses Konto ist ausschliesslich für den Notfall.": "This account is for emergencies only.",
  "Sie können **nur** einen Reset-Link für **{who}** erzeugen.": "You can **only** generate a reset link for **{who}**.",
  "den Systeminhaber": "the system owner",
  "Der Link ist **15 Minuten** gültig und wird **nur einmal** hier angezeigt.": "The link is valid for **15 minutes** and is shown here **only once**.",
  "Sie legen **kein Passwort** selbst fest – das erledigt der Inhaber über den Link.": "You do **not** set a password yourself – the owner does so via the link.",
  "Nach erfolgreicher Nutzung wird dieses Konto **automatisch deaktiviert**.": "After successful use, this account is **automatically deactivated**.",
  "Verfügbarkeit wird geprüft…": "Checking availability…",
  "Die Wiederherstellung ist derzeit nicht verfügbar. Sie ist entweder nicht scharfgeschaltet oder wurde bereits verwendet.": "Recovery is currently unavailable. It is either not armed or has already been used.",
  "Wird erzeugt…": "Generating…",
  "Einmaligen Reset-Link erzeugen": "Generate one-time reset link",
  "Reset-Link für **{email}** erzeugt. Gültig für **{minutes} Minuten**. Kopieren Sie ihn jetzt – er wird nicht erneut angezeigt.": "Reset link generated for **{email}**. Valid for **{minutes} minutes**. Copy it now – it will not be shown again.",
  "Einmaliger Reset-Link": "One-time reset link",
  "Öffnen Sie den Link im Browser und vergeben Sie dort ein neues Passwort für den Systeminhaber. Dieses Notfall-Konto ist danach automatisch deaktiviert.": "Open the link in the browser and set a new password for the system owner there. This emergency account is then automatically deactivated.",
  "Status konnte nicht geladen werden.": "Could not load status.",
  "Bitte bestätigen Sie mit Ihrem eigenen Passwort.": "Please confirm with your own password.",
  "Das Recovery-Passwort muss mindestens 8 Zeichen lang sein.": "The recovery password must be at least 8 characters long.",
  "Die beiden Recovery-Passwörter stimmen nicht überein.": "The two recovery passwords do not match.",
  "Notfall-Wiederherstellung scharfgeschaltet.": "Emergency recovery armed.",
  "Scharfschalten fehlgeschlagen.": "Arming failed.",
  "Notfall-Wiederherstellung deaktiviert.": "Emergency recovery deactivated.",
  "Deaktivieren fehlgeschlagen.": "Deactivation failed.",
  "Notfall-Wiederherstellung einrichten": "Set up emergency recovery",
  "Unabhängiges Zweitkonto, das im Ernstfall Ihren Zugang wiederherstellt.": "Independent secondary account that restores your access in an emergency.",
  "Diese Funktion ist ausschliesslich für den Systeminhaber (Super Admin) verfügbar.": "This function is available exclusively to the system owner (super admin).",
  "Die Konto-Wiederherstellung ist in dieser Umgebung nicht aktiviert.": "Account recovery is not enabled in this environment.",
  "Status:": "Status:",
  "Scharfgeschaltet ✔": "Armed ✔",
  "Nicht scharfgeschaltet": "Not armed",
  "Notfall-Konto:": "Emergency account:",
  "Zuletzt verwendet:": "Last used:",
  "Das Notfall-Konto darf **nur** Ihren Passwort-Reset auslösen – **keinen** App-/Datenzugriff.": "The emergency account may **only** trigger your password reset – **no** app or data access.",
  "Das Recovery-Passwort setzen **Sie selbst** und bewahren es sicher auf (nie im Chat).": "**You set** the recovery password yourself and keep it safe (never in chat).",
  "Nach einer erfolgreichen Wiederherstellung wird das Konto **automatisch deaktiviert**.": "After a successful recovery, the account is **automatically deactivated**.",
  "Ihr eigenes Passwort (Bestätigung)": "Your own password (confirmation)",
  "Passwort von admin@socialcheck.ch": "Password of admin@socialcheck.ch",
  "Recovery-Passwort (für das Notfall-Konto)": "Recovery password (for the emergency account)",
  "Mindestens 8 Zeichen": "At least 8 characters",
  "Recovery-Passwort bestätigen": "Confirm recovery password",
  "Recovery-Passwort wiederholen": "Repeat recovery password",
  "Wird gespeichert…": "Saving…",
  "Recovery-Passwort neu setzen": "Set new recovery password",
  "Scharfschalten": "Arm",
  "Notfall-Wiederherstellung deaktivieren": "Deactivate emergency recovery",
  "Das Passwort muss mindestens 8 Zeichen lang sein.": "The password must be at least 8 characters long.",
  "Die beiden Passwörter stimmen nicht überein.": "The two passwords do not match.",
  "Das Zurücksetzen ist fehlgeschlagen.": "The reset failed.",
  "Passwort sicher zurücksetzen": "Reset password securely",
  "SocialCheck · Konto-Wiederherstellung": "SocialCheck · Account recovery",
  "Reset-Link wird geprüft…": "Checking reset link…",
  "Dieser Reset-Link ist ungültig, abgelaufen oder wurde bereits verwendet.": "This reset link is invalid, expired or has already been used.",
  "Zur Anmeldung": "To sign-in",
  "Setzen Sie ein neues Passwort für **{email}**.": "Set a new password for **{email}**.",
  "das Konto": "the account",
  "Neues Passwort": "New password",
  "Passwort bestätigen": "Confirm password",
  "Passwort wiederholen": "Repeat password",
  "Wird gesetzt…": "Setting…",
  "Passwort setzen": "Set password",
  "Das Passwort wurde sicher zurückgesetzt. Alle bisherigen Sitzungen wurden abgemeldet. Das Notfall-Konto ist jetzt wieder deaktiviert.": "The password has been reset securely. All previous sessions have been signed out. The emergency account is now deactivated again.",
  "Bitte bestätigen Sie Ihr Passwort.": "Please confirm your password.",
  "Notfall-Zugang · Super-Admin-Passwort": "Emergency access · super admin password",
  "Einmaliger, sicherer Reset ohne E-Mail-Versand.": "One-time, secure reset without sending an email.",
  "Diese Funktion ist ausschliesslich für das KEWAG-Admin-Konto verfügbar.": "This function is available exclusively to the KEWAG admin account.",
  "Wichtige Sicherheitshinweise": "Important security notes",
  "Der Reset-Link wird **nur einmalig** hier angezeigt und ist **15 Minuten** gültig.": "The reset link is shown here **only once** and is valid for **15 minutes**.",
  "Sie legen **kein Passwort** selbst fest – das erledigt der Super-Admin über den Link.": "You do **not** set a password yourself – the super admin does so via the link.",
  "Nach dem Erzeugen ist der Notfall-Zugang **dauerhaft deaktiviert**.": "After generation, the emergency access is **permanently deactivated**.",
  "Der Link darf **nicht** per Chat oder E-Mail weitergegeben werden.": "The link must **not** be shared via chat or email.",
  "Der einmalige Notfall-Zugang ist nicht (mehr) verfügbar. Er wurde bereits verwendet und ist dauerhaft deaktiviert.": "The one-time emergency access is no longer available. It has already been used and is permanently deactivated.",
  "Der Notfall-Zugang ist für Teamkonten geschlossen. Teamkonten erhalten keinen Reset-Link für den Systeminhaber.": "Emergency access is closed for team accounts. Team accounts never receive a reset link for the system owner.",
  "Zugangskonfiguration & Einrichtung": "Access configuration & setup",
  "Wertfreier Status: nur Schlüsselnamen und maskierte Konten – keine Secret-Werte.": "Value-free status: key names and masked accounts only – no secret values.",
  "Konfigurationswerte allein sind kein Nachweis eines erfolgreichen Logins.": "Configuration values alone do not prove a successful login.",
  "Zugangsstatus konnte nicht geladen werden.": "Access status could not be loaded.",
  "Owner (Super Admin)": "Owner (Super Admin)",
  "Altkonto ADMIN_EMAIL": "Legacy account ADMIN_EMAIL",
  "Altname": "legacy name",
  "Schlüssel": "Key",
  "Konfiguration": "Configuration",
  "Einrichtung": "Setup",
  "Login beobachtet": "Login observed",
  "kein Konto": "no account",
  "konfiguriert": "configured",
  "Kunden": "Customers",
  "aus": "off",
  "Konflikt": "Conflict",
  "eingerichtet": "set up",
  "Erstanmeldung offen": "First login pending",
  "Konto fehlt": "Account missing",
  "übernommen": "applied",
  "unverändert": "unchanged",
  "Ausgangsstand erfasst": "baseline recorded",
  "nicht gesetzt": "not set",
  "nur Altname gesetzt": "only legacy name set",
  "ungültig – nicht übernommen": "invalid – not applied",
  "Übernahme läuft": "applying",
  "Geheimnis-Übernahme": "Secret sync",
  "zuletzt übernommen: {date}": "last applied: {date}",
  "Ausgangsstand: {date}": "baseline: {date}",
  "Letzte Prüfung beim Backend-Start: {date}": "Last check at backend start: {date}",
  "Neue Werte wirken erst nach Backend-Neustart (Vorschau) bzw. Neu veröffentlichen (LIVE); Speichern allein ändert kein Passwort.": "New values take effect only after a backend restart (preview) or re-publishing (LIVE); saving alone does not change any password.",
  "Passwort fehlt": "Password missing",
  "Ihr Passwort ({email}) zur Bestätigung": "Your password ({email}) for confirmation",
  "Passwort eingeben": "Enter password",
  "Öffnen Sie den Link im Browser des Super-Admins und vergeben Sie dort ein neues Passwort. Der Notfall-Zugang ist jetzt dauerhaft deaktiviert.": "Open the link in the super admin's browser and set a new password there. The emergency access is now permanently deactivated.",
  "Einrichtung konnte nicht gestartet werden.": "Could not start setup.",
  "Code ungültig. Bitte erneut versuchen.": "Invalid code. Please try again.",
  "Zwei-Faktor-Authentifizierung erforderlich": "Two-factor authentication required",
  "Deine Organisation verlangt 2FA. Bitte richte sie jetzt ein, um fortzufahren.": "Your organization requires 2FA. Please set it up now to continue.",
  "2FA ist jetzt aktiv. Bewahre diese Backup-Codes sicher auf – jeder ist einmalig nutzbar.": "2FA is now active. Keep these backup codes safe – each can be used once.",
  "Codes kopieren": "Copy codes",
  "Weiter zur App": "Continue to the app",
  "1. Öffne deine Authenticator-App (z. B. Google Authenticator, Microsoft Authenticator).": "1. Open your authenticator app (e.g. Google Authenticator, Microsoft Authenticator).",
  "2. Scanne den QR-Code oder gib den Schlüssel manuell ein.": "2. Scan the QR code or enter the key manually.",
  "3. Trage den angezeigten 6-stelligen Code ein.": "3. Enter the 6-digit code shown.",
  "2FA QR-Code": "2FA QR code",
  "Manueller Schlüssel": "Manual key",
  "Authenticator-Code": "Authenticator code",
  "2FA aktivieren & fortfahren": "Enable 2FA & continue",
  "Neu": "New",
  "Geprüft": "Verified",
  "Demo aktiv": "Demo active",
  "Einladungslink kopiert": "Invitation link copied",
  "Neuer Einladungslink kopiert (48 h gültig)": "New invitation link copied (valid 48 h)",
  "Als geprüft markiert": "Marked as reviewed",
  "Anfrage freigegeben": "Request approved",
  "Demo-Zugang erstellt": "Demo access created",
  "Zugangs- und Demo-Anfragen von socialcheck.ch prüfen, freigeben und Demo-Zugänge erstellen.": "Review and approve access and demo requests from socialcheck.ch and create demo accesses.",
  "E-Mail-Dienst verbunden": "Email service connected",
  "E-Mail-Dienst nicht verbunden": "Email service not connected",
  "Firma, Name, E-Mail…": "Company, name, email…",
  "Keine Demo-Anfragen für diesen Filter.": "No demo requests for this filter.",
  "Upgrade gewünscht": "Upgrade requested",
  "Nur Super Admin kann bearbeiten": "Only super admin can edit",
  "Prüfen": "Review",
  "Link": "Link",
  "Einladung senden": "Send invitation",
  "Audit": "Audit",
  "Ein 24-Stunden-Demo-Zugang wurde angelegt.": "A 24-hour demo access has been created.",
  "Die Einladung wurde per E-Mail versendet.": "The invitation has been sent by email.",
  "E-Mail-Dienst nicht verbunden — bitte Einladungslink manuell teilen.": "Email service not connected — please share the invitation link manually.",
  "E-Mail versendet": "Email sent",
  "Einladungslink (24 h gültig)": "Invitation link (valid 24 h)",
  "Zu den Demo-Zugängen": "To demo accesses",
  "Kein Mandant für dieses Demo-Konto vorhanden": "No tenant available for this demo account",
  "Kunden-Cockpit geöffnet (nur-lesend)": "Customer cockpit opened (read-only)",
  "Als Kunde angelegt — Nr. {num}, {plan}": "Created as customer — no. {num}, {plan}",
  "/Mt.": "/mo",
  "Demo um 24 Stunden verlängert": "Demo extended by 24 hours",
  "Demo sofort abgelaufen": "Demo expired immediately",
  "Demo-Zugang gesperrt": "Demo access blocked",
  "Demo gelöscht (anonymisiert)": "Demo deleted (anonymized)",
  "Demo-Zugängen": "demo accesses",
  "Aktive und abgelaufene 24-Stunden-Demo-Mandanten verwalten.": "Manage active and expired 24-hour demo tenants.",
  "Noch keine Demo-Zugänge erstellt.": "No demo accesses created yet.",
  "Nicht aktiviert": "Not activated",
  "Läuft ab in {x}": "Expires in {x}",
  "Ablauf:": "Expiry:",
  "gültig bis {d}": "valid until {d}",
  "Min": "min",
  "Jetzt ablaufen": "Expire now",
  "Sperren": "Block",
  "Demo löschen": "Delete demo",
  "E-Mail-Dienst nicht verbunden – Link kann kopiert werden": "Email service not connected – link can be copied",
  "Demo löschen (anonymisieren)?": "Delete demo (anonymize)?",
  "Name, E-Mail und Firma von {name} werden anonymisiert, der Zugang gesperrt und aus den Listen ausgeblendet. Das Audit-Log bleibt erhalten. Diese Aktion kann nicht rückgängig gemacht werden.": "Name, email and company of {name} will be anonymized, access blocked and hidden from the lists. The audit log is retained. This action cannot be undone.",
  "In zahlenden Kunden umwandeln": "Convert to paying customer",
  "{name} wird zu einem echten Mandanten mit Kundennummer und Vollzugang (ohne 24h-Ablauf). Die Demo-Inhalte bleiben erhalten. Bitte Paket & Preis wählen.": "{name} becomes a real tenant with a customer number and full access (without 24-hour expiry). The demo content is retained. Please choose plan & price.",
  "Paket wählen": "Choose plan",
  "Preis (CHF / Monat)": "Price (CHF / month)",
  "z. B. 290": "e.g. 290",
  "Vorschlag aus der Paket-Preisliste — überschreibbar. Preise pflegbar unter „Einstellungen“.": "Suggestion from the plan price list — editable. Prices are managed under “Settings”.",
  "DB-Backup erstellt ({n} Collections)": "Database backup created ({n} collections)",
  "Backup fehlgeschlagen": "Backup failed",
  "{v} als aktuelle Version erfasst (Live-Stand {c})": "{v} recorded as current version (live state {c})",
  "Erfassung fehlgeschlagen": "Recording failed",
  "{v} ist jetzt aktuell · {n} als „ersetzt“ markiert": "{v} is now current · {n} marked as “replaced”",
  "Zeitüberschreitung": "Timed out",
  "{v} ist jetzt die aktuelle Version": "{v} is now the current version",
  "{v} archiviert": "{v} archived",
  "Datenbank wiederhergestellt": "Database restored",
  "Rollback-Plan bestätigt & protokolliert.": "Rollback plan confirmed & logged.",
  "Produktion (Live)": "Production (live)",
  "Preview (Entwicklung)": "Preview (development)",
  "Umgebungen · Live-Status": "Environments · live status",
  "Erneut prüfen": "Check again",
  "Diese Umgebung": "This environment",
  "Prüfe…": "Checking…",
  "Erreichbar · Live": "Reachable · live",
  "Nicht erreichbar": "Not reachable",
  "Deploy erforderlich ·": "Deploy required ·",
  "{n} Commit(s) hinter Preview": "{n} commit(s) behind preview",
  "Produktion {pv} · Preview {prev}": "Production {pv} · preview {prev}",
  "Auf Preview-Stand": "At preview state",
  "Produktion neuer als Preview": "Production newer than preview",
  "Commit unterschiedlich": "Commit differs",
  "Vergleich nicht möglich": "Comparison not possible",
  "Preview-Identität nicht ermittelt · Commit fehlt": "Preview identity not determined · commit missing",
  "Produktions-Identität nicht ermittelt · Commit fehlt": "Production identity not determined · commit missing",
  "Ungültige Commit-ID gemeldet": "Invalid commit ID reported",
  "Commit nicht in der Historie · Vergleich nicht möglich": "Commit not in history · comparison not possible",
  "Jetzt deployen · Kurzanleitung": "Deploy now · quick guide",
  "Preview gründlich testen (dieser Stand: **{v}**).": "Test the preview thoroughly (this state: **{v}**).",
  "In Emergent oben auf **Deploy** klicken.": "In Emergent, click **Deploy** at the top.",
  "**„Deploy Now“** bestätigen und den bestehenden Deployment-Slot (app.socialcheck.ch) ersetzen.": "Confirm **“Deploy Now”** and replace the existing deployment slot (app.socialcheck.ch).",
  "~10–15 Min warten → Produktion läuft danach auf **{v}**, diese Karte zeigt dann automatisch „Auf Preview-Stand“.": "Wait ~10–15 min → production then runs on **{v}**, and this card automatically shows “At preview state”.",
  "Update eines bestehenden Deployments = **keine Zusatzkosten** (50 Credits/Monat pro App). Rollback jederzeit möglich. Ich (Assistent) deploye nicht selbst — das bleibt bewusst deine Freigabe.": "Updating an existing deployment = **no extra cost** (50 credits/month per app). Rollback possible at any time. I (the assistant) do not deploy myself — that remains deliberately your approval.",
  "Commit": "Commit",
  "Branch": "Branch",
  "Gemeldete Umgebung": "Reported environment",
  "verbunden": "connected",
  "Von hier aus nicht erreichbar": "Not reachable from here",
  ". Mögliche Gründe: Cloudflare/CORS blockiert den Cross-Abruf, die Umgebung wurde noch nicht mit dieser Version deployt, oder sie ist offline.": ". Possible reasons: Cloudflare/CORS blocks the cross-origin call, the environment has not yet been deployed with this version, or it is offline.",
  "Kein erfundener Status": "No made-up status",
  "— öffne die Umgebung direkt, um ihren echten Live-Status zu sehen.": "— open the environment directly to see its real live status.",
  "Jede Karte ruft den echten öffentlichen Endpoint": "Each card calls the real public endpoint",
  "der jeweiligen Umgebung ab (Version/Commit/Branch/DB). Die Umgebung, die du gerade geöffnet hast, ist immer 100 % live & prüfbar.": "of the respective environment (version/commit/branch/DB). The environment you have just opened is always 100 % live & verifiable.",
  "Aktuelle Version": "Current version",
  "entspricht dem laufenden Code": "matches the running code",
  "Stand ist echt & live.": "The state is real & live.",
  "Laufender Code ist neuer als die als „aktuell“ markierte Version": "The running code is newer than the version marked “current”",
  "Der Commit der aktuellen Version {v} ist in dieser Umgebung nicht auffindbar.": "The commit of the current version {v} cannot be found in this environment.",
  "Live-Stand ist {n} Commit(s) neuer als {v}.": "The live state is {n} commit(s) newer than {v}.",
  "Laufender Commit": "Running commit",
  "committet": "committed",
  "Als aktuelle Version erfassen": "Record as current version",
  "Vorherige stabile": "Previous stable",
  "Vorschau (RC)": "Preview (RC)",
  "Stabil": "Stable",
  "Ersetzt": "Replaced",
  "Archiviert": "Archived",
  "Versionen gesamt": "Total versions",
  "HEAD-Commit": "HEAD commit",
  "Versionen vergleichen: {n}/2 ausgewählt": "Compare versions: {n}/2 selected",
  "{n} Vorschau-Version(en) offen — jüngste: **{v}**": "{n} preview version(s) open — most recent: **{v}**",
  "Alle Vorschau als aktuell übernehmen": "Adopt all previews as current",
  "Ersetzte/archivierte ausblenden": "Hide replaced/archived",
  "Ersetzte/archivierte anzeigen ({n})": "Show replaced/archived ({n})",
  "Aktuell": "Current",
  "Geschützt": "Protected",
  "Build": "Build",
  "DB-Migration:": "DB migration:",
  "ja": "yes",
  "nein": "no",
  "Test:": "Test:",
  "Rollback": "Rollback",
  "verfügbar": "available",
  "n/v": "n/a",
  "Ausgewählt": "Selected",
  "Vergleichen": "Compare",
  "Screenshots ({n})": "Screenshots ({n})",
  "Rollback vorbereiten": "Prepare rollback",
  "Als aktuell setzen": "Set as current",
  "Snapshots & DB-Backups": "Snapshots & DB backups",
  "Backup jetzt erstellen": "Create backup now",
  "Noch keine Backups registriert.": "No backups registered yet.",
  "{n} Collections": "{n} collections",
  "Auto": "Auto",
  "fehlt": "missing",
  "Beim Start jedes Arbeitspakets wird automatisch ein Vorher-Snapshot angelegt und mit der jeweiligen Version verlinkt — als 1-Klick-Rollback-Ziel.": "At the start of each work package, a before-snapshot is created automatically and linked to the respective version — as a one-click rollback target.",
  "Echte Versionsdaten (Code + Backup + Belege).": "Real version data (code + backup + evidence).",
  "Umgebung": "Environment",
  "Autor": "Author",
  "DB-Migration": "DB migration",
  "DB-Backup": "DB backup",
  "Teststatus": "Test status",
  "Rollback verfügbar / getestet": "Rollback available / tested",
  "Module": "Modules",
  "Dateien seit dieser Version geändert": "Files changed since this version",
  "Screenshots": "Screenshots",
  "Golden-Master-Referenzen dieser Version.": "Golden master references of this version.",
  "Vergleich": "Comparison",
  "Echter git-Vergleich zwischen zwei Versionen.": "Real git comparison between two versions.",
  "Geänderte Dateien: {n}": "Changed files: {n}",
  "Version archivieren?": "Archive version?",
  "{v} wird als „archiviert“ markiert. Die Version bleibt erhalten und wiederherstellbar.": "{v} will be marked as “archived”. The version is retained and restorable.",
  "Alle Vorschau-Versionen übernehmen?": "Adopt all preview versions?",
  "Die jüngste Vorschau wird zur aktuellen Version; ältere Vorschau-Versionen werden als „ersetzt“ markiert (bleiben erhalten).": "The most recent preview becomes the current version; older preview versions are marked as “replaced” (they are retained).",
  "Wird aktuell": "Becomes current",
  "Als „ersetzt“ markiert": "Marked as “replaced”",
  "Als aktuelle Version setzen?": "Set as current version?",
  "{v} wird zur aktuellen Version. Ein „-rc“-Suffix wird zur Release-Version. Die bisherige aktuelle Version bleibt erhalten (stabil/vorherige).": "{v} becomes the current version. A “-rc” suffix becomes the release version. The previous current version is retained (stable/previous).",
  "Rollback vorbereiten — {v}": "Prepare rollback — {v}",
  "Kein automatischer Rollback. Prüfe den Plan und bestätige ausdrücklich.": "No automatic rollback. Review the plan and confirm explicitly.",
  "Zielversion": "Target version",
  "Betroffene Module": "Affected modules",
  "Betroffene Dateien": "Affected files",
  "vorhanden": "present",
  "Rollback-Plan": "Rollback plan",
  "Wiederherstellungsplan": "Recovery plan",
  "Ich habe den Plan geprüft und möchte den Rollback über den Plattform-Rollback vorbereiten.": "I have reviewed the plan and want to prepare the rollback via the platform rollback.",
  "Datenbank auf Snapshot zurücksetzen (optional, destruktiv)": "Reset database to snapshot (optional, destructive)",
  "Vorschau wird geladen…": "Loading preview…",
  "Kein verlinktes Backup für diese Version — DB-Wiederherstellung nicht verfügbar. (Nur Code-Rollback möglich.)": "No linked backup for this version — DB restore unavailable. (Only code rollback possible.)",
  "Snapshot:": "Snapshot:",
  "Collections": "collections",
  "Entfernt würden:": "Would be removed:",
  "Nicht im Backup (bleiben erhalten):": "Not in backup (retained):",
  "Zum Bestätigen „WIEDERHERSTELLEN“ eintippen": "Type “WIEDERHERSTELLEN” to confirm",
  "Datenbank wiederherstellen": "Restore database",
  "Vor der Wiederherstellung wird automatisch ein Sicherheits-Snapshot des aktuellen Standes erstellt.": "Before restoring, a safety snapshot of the current state is created automatically.",
  "Rollback-Plan bestätigen": "Confirm rollback plan",
  "Aktuellen Stand als Version festhalten": "Capture current state as version",
  "Erfasst den echten laufenden Git-Stand (Commit": "Records the real running Git state (commit",
  ") als neue aktuelle Version.": ") as the new current version.",
  "{n} Commit(s) seit {who}.": "{n} commit(s) since {who}.",
  "der letzten Version": "the last version",
  "Bezeichnung": "Label",
  "z. B. Integrationen-Schaltzentrale Phase 2": "e.g. Integrations control center phase 2",
  "Ohne Eingabe wird automatisch „Laufender Stand · {c}“ verwendet.": "Without input, “Running state · {c}” is used automatically.",
  "Notiz (optional)": "Note (optional)",
  "Was wurde in diesem Stand abgeschlossen?": "What was completed in this state?",
  "Version erfassen": "Record version",
  "{d} Tg. {h} Std.": "{d} d {h} h",
  "{h} Std.": "{h} h",
  "Jury-Link kopiert.": "Jury link copied.",
  "Kopieren nicht möglich – Link bitte manuell markieren.": "Unable to copy – please select the link manually.",
  "Öffentlicher Jury-Link kopiert.": "Public jury link copied.",
  "QR-Code ist noch nicht bereit.": "The QR code is not ready yet.",
  "QR-Code als PNG heruntergeladen.": "QR code downloaded as PNG.",
  "Download nicht möglich.": "Download not possible.",
  "A4-Poster (PDF) erstellt.": "A4 poster (PDF) created.",
  "Poster konnte nicht erstellt werden.": "Poster could not be created.",
  "A4-Poster (4 Sprachen) erstellt.": "A4 poster (4 languages) created.",
  "Erledigt.": "Done.",
  "Jury-Demo für 7 Tage aktiviert.": "Jury demo activated for 7 days.",
  "Neuer Einladungslink erzeugt.": "New invitation link generated.",
  "Jury-Demo entsperrt.": "Jury demo unblocked.",
  "Neue Zugänge gesperrt.": "New accesses blocked.",
  "Alle Jury-Sitzungen beendet.": "All jury sessions ended.",
  "Zugang ist wieder öffentlich.": "Access is public again.",
  "Zugang jetzt nur über Einladungslink.": "Access now only via invitation link.",
  "Neuen Einladungslink erzeugen? Der bisherige Link wird sofort ungültig.": "Generate a new invitation link? The previous link becomes invalid immediately.",
  "Neue Jury-Zugänge sperren? Laufende Sitzungen bleiben bis zum Timeout aktiv.": "Block new jury accesses? Ongoing sessions remain active until timeout.",
  "Wirklich ALLE laufenden Jury-Sitzungen sofort beenden und deren Demo-Mandanten löschen?": "Really end ALL ongoing jury sessions immediately and delete their demo tenants?",
  "Jury-Demo-Kontrolle": "Jury demo control",
  "Steuern Sie die passwortlose, isolierte Jury-Demo (Preview). Der Zugang ist standardmässig öffentlich über den Jury-Link erreichbar und läuft automatisch nach 7 Tagen ab.": "Control the passwordless, isolated jury demo (preview). Access is public by default via the jury link and expires automatically after 7 days.",
  "endet in {x}": "ends in {x}",
  "kein aktiver Zeitraum": "no active period",
  "{n} aktive Sitzung(en)": "{n} active session(s)",
  "nur mit Einladungslink": "invitation link only",
  "öffentlich": "public",
  "Sprache des Links / QR-Codes": "Language of the link / QR code",
  "Die Jury-Demo startet automatisch in der gewählten Sprache (Auto = Sprache des Geräts).": "The jury demo starts automatically in the selected language (Auto = device language).",
  "Einladungslink für die Jury": "Invitation link for the jury",
  "Öffentlicher Jury-Link": "Public jury link",
  "Showcase-Ziel (Produktion): https://app.socialcheck.ch/jury-demo": "Showcase target (production): https://app.socialcheck.ch/jury-demo",
  "QR-Code zum Öffnen der öffentlichen, passwortlosen Jury-Demo": "QR code to open the public, passwordless jury demo",
  "QR-Code für die Jury": "QR code for the jury",
  "Mit dem Smartphone scannen, um die öffentliche, passwortlose Jury-Demo zu öffnen. Der Code enthält ausschliesslich den öffentlichen Link – keine Sitzungs-IDs, Tokens oder personenbezogenen Daten.": "Scan with your smartphone to open the public, passwordless jury demo. The code contains only the public link – no session IDs, tokens or personal data.",
  "Öffentlichen Jury-Link kopieren": "Copy public jury link",
  "QR als PNG herunterladen": "Download QR as PNG",
  "QR-Code als PNG herunterladen": "Download QR code as PNG",
  "A4-Handout (PDF)": "A4 handout (PDF)",
  "A4-Poster mit QR als PDF herunterladen": "Download A4 poster with QR as PDF",
  "A4-Poster (4 Sprachen)": "A4 poster (4 languages)",
  "A4-Poster mit QR in vier Sprachen (DE/FR/IT/EN) als PDF herunterladen": "Download A4 poster with QR in four languages (DE/FR/IT/EN) as PDF",
  "Aktivieren (7 Tage)": "Activate (7 days)",
  "Neuen Link erzeugen": "Generate new link",
  "Öffentlich freigeben": "Make public",
  "Mit Einladungslink absichern": "Secure with invitation link",
  "Entsperren": "Unblock",
  "Alle Sitzungen beenden": "End all sessions",
  // --- Backups + Zusatzleistungen (Phase 3) ---
  "Sicherungsstatus der SocialCheck-Plattform.": "Backup status of the SocialCheck platform.",
  "Kein manuelles Backup-Modul aktiv": "No manual backup module active",
  "Datensicherungen werden automatisch auf Infrastruktur-Ebene durch KEWAG bzw. den Betreiber verwaltet. Ein Self-Service-Export ist derzeit nicht verfügbar.": "Data backups are managed automatically at the infrastructure level by KEWAG or the operator. A self-service export is currently unavailable.",
  "Verwaltung": "Management",
  "KEWAG / Infrastruktur": "KEWAG / Infrastructure",
  "Automatische Sicherung": "Automatic backup",
  "Datenbank": "Database",
  "Serverseitig gespeichert": "Stored server-side",
  "Self-Service-Export": "Self-service export",
  "Nicht verfügbar": "Not available",
  "Auf Anfrage bei KEWAG": "On request from KEWAG",
  "Objektspeicher (Medien & Uploads) wird redundant und persistent gespeichert.": "Object storage (media & uploads) is stored redundantly and persistently.",
  "Für einen vollständigen Datenexport oder eine Wiederherstellung kontaktieren Sie bitte das KEWAG-Team.": "For a full data export or a restore, please contact the KEWAG team.",
  "Ein integriertes Backup-/Restore-Modul mit Historie kann bei Bedarf ergänzt werden.": "An integrated backup/restore module with history can be added if needed.",
  "Service": "Service",
  "Als erledigt markiert": "Marked as done",
  "Anfrage abgelehnt": "Request rejected",
  "Upgrade- und Zusatz-Anfragen der Kunden — Speicher, Kanäle, Services und Pakete.": "Customers' upgrade and add-on requests — storage, channels, services and packages.",
  "Keine Anfragen.": "No requests.",
  "Anfragen": "Requests",
  // --- KiWorkspace Compliance (Phase 3) ---
  "Compliance": "Compliance",
  // --- KiWorkspace suppl (Phase 3) ---
  "Termin entfernen": "Remove date",
  // --- KiWorkspace (Phase 3) ---
  "Markensicher": "Brand-safe",
  "Storytelling": "Storytelling",
  "Verkaufsstark": "Sales-driven",
  "Kreativ": "Creative",
  "Brauchbar": "Usable",
  "Verbesserung": "Needs improvement",
  "Problematisch": "Problematic",
  "Nicht verwenden": "Do not use",
  "Manuelle Prüfung": "Manual review",
  "von 100": "of 100",
  "Diese Aktion verbraucht echte KI-Credits": "This action uses real AI credits",
  "Verbraucht KI-Credits": "Uses AI credits",
  "Laden fehlgeschlagen": "Loading failed",
  "Lauf wurde bereits ausgeführt — keine doppelten Credits.": "Run was already executed — no duplicate credits.",
  "Keine neu zu bewertenden Bilder.": "No new images to assess.",
  "{done} Bild(er) bewertet": "{done} image(s) assessed",
  "{done} Bild(er) bewertet, {failed} fehlgeschlagen": "{done} image(s) assessed, {failed} failed",
  "Bewertung fehlgeschlagen": "Assessment failed",
  "Bitte mindestens ein Bild auswählen": "Please select at least one image",
  "KI-Vorschlag erstellt": "AI suggestion created",
  "KI-Generierung fehlgeschlagen": "AI generation failed",
  "KI-Limits für diesen Mandanten gespeichert": "AI limits saved for this customer",
  "Änderungen übernommen": "Changes applied",
  "Ungültiges Datum.": "Invalid date.",
  "Der geplante Termin darf nicht in der Vergangenheit liegen.": "The scheduled date cannot be in the past.",
  "Freigabe-Beitrag aktualisiert": "Approval post updated",
  "Beitrag geplant & zur Kundenfreigabe vorbereitet": "Post scheduled and prepared for customer approval",
  "Beitrag zur Kundenfreigabe vorbereitet": "Post prepared for customer approval",
  "Als Entwurf gespeichert": "Saved as draft",
  "Vorschlag abgelehnt": "Suggestion rejected",
  "Aktion fehlgeschlagen": "Action failed",
  "Bitte zuerst einen Kunden auswählen.": "Please select a customer first.",
  "KI-Service nicht verbunden — Analyse und Vorschläge sind derzeit nicht verfügbar.": "AI service not connected — analysis and suggestions are currently unavailable.",
  "Marken-Profil nur zu {pct}% vollständig. Vorschläge benötigen verstärkte manuelle Prüfung.": "Brand profile only {pct}% complete. Suggestions require increased manual review.",
  "KI-Service nicht verbunden.": "AI service not connected.",
  "KI-Läufe wurden durch KEWAG (global) pausiert.": "AI runs were paused by KEWAG (globally).",
  "KI-Läufe wurden für diesen Mandanten pausiert.": "AI runs were paused for this customer.",
  "Bestehende Ergebnisse bleiben lesbar; kostenpflichtige KI-Läufe sind gesperrt.": "Existing results remain readable; paid AI runs are blocked.",
  "KI-Limits": "AI limits",
  "Achtung: {pct}% des KI-Monatslimits erreicht. Weitere Läufe werden bei 100% gesperrt.": "Warning: {pct}% of the monthly AI limit reached. Further runs will be blocked at 100%.",
  "1. Bilder & Auswahl": "1. Images & selection",
  "Bewerte…": "Assessing…",
  "Auswahl bewerten": "Assess selection",
  "Massenanalyse: alle {n} unbewerteten": "Bulk analysis: all {n} unassessed",
  "Empfohlen": "Recommended",
  "Keine Bilder in dieser Ansicht.": "No images in this view.",
  "Unbewertet": "Unassessed",
  "Noch nicht bewertet": "Not yet assessed",
  "2. KI-Vorschläge": "2. AI suggestions",
  "Erzeuge…": "Generating…",
  "Neuen Vorschlag erzeugen": "Generate new suggestion",
  "KI erstellt den Vorschlag…": "AI is creating the suggestion…",
  "Erzeuge aus der Bildauswahl einen Beitrag im Modus „{mode}“.": "Generate a post from the image selection in “{mode}” mode.",
  "Wähle links Bilder aus, um einen Vorschlag zu erzeugen.": "Select images on the left to generate a suggestion.",
  "Vorschlag mit KI erzeugen": "Generate suggestion with AI",
  "Empfohlene Bildauswahl": "Recommended image selection",
  "Keine Bilder ausgewählt.": "No images selected.",
  "Titel / Hook": "Title / hook",
  "Titel": "Title",
  "Hook (optional)": "Hook (optional)",
  "Haupttext": "Main text",
  "Call-to-Action": "Call to action",
  "Call-to-Action (optional)": "Call to action (optional)",
  "Hashtags (durch Leerzeichen getrennt)": "Hashtags (separated by spaces)",
  "z. B. bern gastronomie frisch": "e.g. bern gastronomy fresh",
  "Plattformvarianten": "Platform variants",
  "Zeichen": "characters",
  "Hashtags": "hashtags",
  "Begründung & verwendete Markenregeln": "Rationale & applied brand rules",
  "Offene Fragen": "Open questions",
  "Übernehmen": "Apply",
  "Vorschlag bearbeiten": "Edit suggestion",
  "Deep-Link zu diesem Vorschlag kopieren": "Copy deep link to this suggestion",
  "Link kopiert — direkt zu diesem KI-Vorschlag": "Link copied — directly to this AI suggestion",
  "Kopieren fehlgeschlagen": "Copy failed",
  "Link kopieren": "Copy link",
  "Bearbeitet von {by}": "Edited by {by}",
  "3. Prüfung & Aktionen": "3. Review & actions",
  "Noch keine Prüfung — erst einen Vorschlag erzeugen.": "No review yet — generate a suggestion first.",
  "Gesamtbewertung · Konfidenz": "Overall score · confidence",
  "Marken-Passung": "Brand fit",
  "Inhaltsqualität": "Content quality",
  "Bildqualität": "Image quality",
  "Erkannte Stärken": "Detected strengths",
  "Hinweise": "Notes",
  "Risiken": "Risks",
  "Keine kritischen Risiken erkannt": "No critical risks detected",
  "Compliance-Problem": "Compliance issue",
  "Blockiert: {reason} — kann nicht zur Kundenfreigabe vorbereitet werden.": "Blocked: {reason} — cannot be prepared for customer approval.",
  "Geplant für (optional)": "Scheduled for (optional)",
  "Leer = ungeplant zur Freigabe · Zeitzone Europe/Zürich · Beitrag, Freigabe & Kalender teilen denselben Termin.": "Empty = unscheduled for approval · time zone Europe/Zurich · post, approval and calendar share the same date.",
  "Freigabe-Beitrag aktualisieren": "Update approval post",
  "Zur Freigabe vorbereiten": "Prepare for approval",
  "Variante": "Variant",
  "Vorschlag ablehnen": "Reject suggestion",
  "KI-Verbrauch & Limits": "AI usage & limits",
  "KI-Limits für diesen Mandanten": "AI limits for this customer",
  "KI-Service nicht verbunden": "AI service not connected",
  "KI-Läufe (Monat)": "AI runs (month)",
  "davon Bildbewertungen": "of which image assessments",
  "davon Vorschläge": "of which suggestions",
  "Läufe gesamt": "Total runs",
  "Global pausiert": "Paused globally",
  "Für diesen Mandanten pausiert": "Paused for this customer",
  "Optionaler Grund (wird im Audit-Log protokolliert).": "Optional reason (logged in the audit log).",
  "z. B. Bildauswahl passt nicht, Text zu werblich …": "e.g. image selection doesn't fit, text too promotional …",
  "Kostenpflichtigen KI-Lauf bestätigen": "Confirm paid AI run",
  "Bitte vor dem Start prüfen.": "Please review before starting.",
  "Umfang": "Scope",
  "{n} Bild(er)": "{n} image(s)",
  "1 Vorschlag": "1 suggestion",
  "Anbieter / Modell": "Provider / model",
  "Voraussichtliche Kosten": "Estimated costs",
  "Echte Kosten derzeit nicht verfügbar (Abrechnung über KI-Läufe)": "Actual costs currently unavailable (billed via AI runs)",
  "Diese Aktion verbraucht echte KI-Credits.": "This action uses real AI credits.",
  "Kostenpflichtigen KI-Lauf starten": "Start paid AI run",
  "Mandantenbezogene Kostenkontrolle. Der globale KEWAG-Schalter hat immer Vorrang.": "Per-customer cost control. The global KEWAG switch always takes precedence.",
  "KI für diesen Mandanten aktiviert": "AI enabled for this customer",
  "Max. KI-Läufe / Monat": "Max. AI runs / month",
  "Max. Bildanalysen / Monat": "Max. image analyses / month",
  "Max. Video-Generierungen / Monat": "Max. video generations / month",
  "Monatsbudget (CHF, optional)": "Monthly budget (CHF, optional)",
  "Leer = kein Limit. Echte Token-/Kostenwerte liefert der Anbieter nicht — es gelten die konfigurierten Lauf-Limits.": "Empty = no limit. The provider does not supply real token/cost values — the configured run limits apply.",
  // --- MarkenProfil (Phase 3) ---
  "Kein Profil": "No profile",
  "Ort / Region": "Location / region",
  "Google Business Link": "Google Business link",
  "Social-Kanäle": "Social channels",
  "Manuell": "Manual",
  "Google Business": "Google Business",
  "Leistungen": "Services",
  "Zielkunden": "Target customers",
  "Tonalität (z. B. sachlich, freundlich, edel, lokal)": "Tone (e.g. factual, friendly, premium, local)",
  "Verbotene Begriffe (nie verwenden)": "Forbidden terms (never use)",
  "No-Go-Themen": "No-go topics",
  "Beispieltexte, die gut waren": "Example texts that worked well",
  "Besonderheiten / Saisonalität": "Specifics / seasonality",
  "vor {n} Minute": "{n} minute ago",
  "vor {n} Minuten": "{n} minutes ago",
  "vor {n} Stunde": "{n} hour ago",
  "vor {n} Stunden": "{n} hours ago",
  "vor {n} Tag": "{n} day ago",
  "Kundenfreigabe angefordert": "Customer approval requested",
  "KI-Vorschau aktualisiert": "AI preview updated",
  "Profil {version} aktiviert – Freigabe-E-Mail an {recipient} gesendet": "Profile {version} activated – approval e-mail sent to {recipient}",
  "Brevo nicht verbunden – keine E-Mail": "Brevo not connected – no e-mail",
  "kein Kunden-Kontakt hinterlegt – keine E-Mail": "no customer contact stored – no e-mail",
  "E-Mail-Versand fehlgeschlagen": "E-mail delivery failed",
  "Profil {version} aktiviert": "Profile {version} activated",
  "Profil {version} aktiviert ({reason})": "Profile {version} activated ({reason})",
  "Version {version} wiederhergestellt": "Version {version} restored",
  "Alle Kunden und deren Markenprofile": "All customers and their brand profiles",
  "Freig.": "Appr.",
  "Nächste": "Next",
  "Profil": "Profile",
  "Marken-Profilen": "brand profiles",
  "Kundenübersicht – Zusammenfassung": "Customer overview – summary",
  "Höchste Vollständigkeit": "Highest completeness",
  "Nächste Veröffentlichung": "Next publication",
  "Bitte Kunde wählen.": "Please select a customer.",
  "Die Kunden-Stimme für {name}. Wird für bessere Content-Vorschläge verwendet.": "The customer voice for {name}. Used for better content suggestions.",
  "Zuletzt gespeichert:": "Last saved:",
  "Nicht gespeicherte Änderungen": "Unsaved changes",
  "Profil-Vollständigkeit": "Profile completeness",
  "Version": "Version",
  "Zuletzt aktualisiert": "Last updated",
  "Freigegeben vom Kunden": "Approved by customer",
  "am": "on",
  "Noch offen": "Still pending",
  "Kundenfreigabe anfordern": "Request customer approval",
  "Vorherige Version": "Previous version",
  "Profil-Export": "Export profile",
  "Als JSON": "As JSON",
  "Unternehmens- & Markeninformationen": "Company & brand information",
  "Bevorzugte Sprache": "Preferred language",
  "Positionierung / Markenversprechen": "Positioning / brand promise",
  "Visuelle Markenregeln": "Visual brand rules",
  "Logo": "Logo",
  "Kein Logo hinterlegt": "No logo stored",
  "Farbpalette": "Color palette",
  "Fertig": "Done",
  "Keine Farben hinterlegt": "No colors stored",
  "Bildsprache": "Visual style",
  "Unerwünschte Bildmotive": "Unwanted image motifs",
  "Entwurf speichern": "Save draft",
  "Änderungen vergleichen": "Compare changes",
  "Profil aktivieren": "Activate profile",
  "KI mit Profil testen": "Test AI with profile",
  "Nur das KEWAG-Team kann bearbeiten.": "Only the KEWAG team can edit.",
  "Freigabe-E-Mail zuletzt an {to} · {datetime}": "Last approval e-mail to {to} · {datetime}",
  "Beim Aktivieren wird automatisch eine Freigabe-E-Mail mit PDF-Richtlinie an den Kunden gesendet.": "Upon activation, an approval e-mail with the PDF guideline is automatically sent to the customer.",
  "Freigabe-E-Mail mit PDF-Richtlinie geht automatisch an den Kunden, sobald Brevo verbunden ist.": "The approval e-mail with the PDF guideline is sent to the customer automatically as soon as Brevo is connected.",
  "KI-Vorschau": "AI preview",
  "Vorschau aktualisieren": "Refresh preview",
  "Noch keine Angaben – Profil ausfüllen.": "No details yet – fill in the profile.",
  "Kanäle & Quellen": "Channels & sources",
  "Fehlt": "Missing",
  "Live-Sync (Reichweite/Interaktionen) wird nach Anbindung von Meta / Google Business aktiv.": "Live sync (reach/interactions) becomes active after connecting Meta / Google Business.",
  "Freigaberegeln & Compliance": "Approval rules & compliance",
  "Regeln bearbeiten": "Edit rules",
  "Regel hinzufügen": "Add rule",
  "Keine Regeln hinterlegt.": "No rules stored.",
  "Beispiel-Post-Vorschau": "Example post preview",
  "Kein Beispielbild": "No example image",
  "Noch kein Beispieltext hinterlegt.": "No example text stored yet.",
  "Versionsverlauf": "Version history",
  "Aktuelle Version: {version}. Eine frühere Version wiederherstellen.": "Current version: {version}. Restore an earlier version.",
  "Noch keine gespeicherten Versionen. Versionen entstehen beim Aktivieren des Profils.": "No saved versions yet. Versions are created when the profile is activated.",
  "Wiederherstellen": "Restore",
  "Vergleich mit zuletzt aktivierter Version {version}.": "Comparison with the last activated version {version}.",
  "Noch keine aktivierte Vorversion – zeigt alle gefüllten Felder als neu.": "No activated previous version yet – shows all filled fields as new.",
  "Keine Unterschiede – Profil entspricht der aktivierten Version.": "No differences – the profile matches the activated version.",
  "Beispiel": "Example",
  // --- Phase 3 Batch 5 ---
  "Jetzt neu verbinden": "Reconnect now",
  // --- Phase 3 Batch 4: MetaConnect Restlabels ---
  "Konto:": "Account:",
  "Token gültig bis:": "Token valid until:",
  // --- Phase 3 Batch 3: MetaConnect + Backend-Meldungen ---
  "Betriebsbereit": "Ready",
  "Handlungsbedarf": "Action required",
  "Verbindung ausstehend": "Connection pending",
  "Deaktiviert": "Disabled",
  "Meta App-ID (META_APP_ID)": "Meta App ID (META_APP_ID)",
  "Meta App-Secret (META_APP_SECRET)": "Meta App Secret (META_APP_SECRET)",
  "Redirect-URI (META_REDIRECT_URI)": "Redirect URI (META_REDIRECT_URI)",
  "OAuth-Konfig-ID (META_OAUTH_CONFIG_ID) — optional": "OAuth config ID (META_OAUTH_CONFIG_ID) — optional",
  "Token-Verschlüsselung (INTEGRATION_ENCRYPTION_KEY)": "Token encryption (INTEGRATION_ENCRYPTION_KEY)",
  "Handlung": "Action",
  "Offen · gültig": "Open · valid",
  "Verwendet": "Used",
  "Widerrufen": "Revoked",
  "Ungültig": "Invalid",
  "Kopieren nicht möglich": "Could not copy",
  "Meta-Grundlage aktiviert.": "Meta foundation activated.",
  "Meta-Grundlage deaktiviert.": "Meta foundation deactivated.",
  "Bitte zuerst einen Testmandanten wählen.": "Please select a test tenant first.",
  "Meta-Verbindung getrennt.": "Meta connection disconnected.",
  "Trennen fehlgeschlagen": "Disconnect failed",
  "Asset-Auswahl gespeichert.": "Asset selection saved.",
  "Speichern fehlgeschlagen": "Save failed",
  "Geprüft:": "Checked:",
  "Handlungsbedarf:": "Action required:",
  "Läuft bald ab:": "Expiring soon:",
  "Aufgaben erstellt:": "Tasks created:",
  "Token-Überwachung gespeichert.": "Token monitoring saved.",
  "Einladung erstellt und per E-Mail an {email} gesendet.": "Invitation created and sent by e-mail to {email}.",
  "Einladung erstellt. E-Mail-Dienst (Brevo) ist nicht verbunden — bitte Link manuell teilen.": "Invitation created. E-mail service (Brevo) is not connected — please share the link manually.",
  "Einladung erstellt, E-Mail konnte nicht gesendet werden — bitte Link manuell teilen.": "Invitation created, e-mail could not be sent — please share the link manually.",
  "Einladungslink erstellt (72 h gültig).": "Invitation link created (valid for 72 h).",
  "Einladung fehlgeschlagen": "Invitation failed",
  "Erinnerung an {email} gesendet.": "Reminder sent to {email}.",
  "E-Mail-Dienst (Brevo) ist nicht verbunden — Erinnerung konnte nicht gesendet werden.": "E-mail service (Brevo) is not connected — reminder could not be sent.",
  "Erinnerung konnte nicht gesendet werden.": "Reminder could not be sent.",
  "Erinnerung fehlgeschlagen": "Reminder failed",
  "Einladung widerrufen.": "Invitation revoked.",
  "Widerrufen fehlgeschlagen": "Revoke failed",
  "Meta Business Suite — Verbindung (Preview)": "Meta Business Suite — connection (preview)",
  "System-Grundlage (global) + echte OAuth-Verbindung pro Mandant · nur Super Admin": "System foundation (global) + real OAuth connection per tenant · super admin only",
  "System-Grundlage": "System foundation",
  "Kanonische Redirect-URI (in der Meta App exakt eintragen)": "Canonical redirect URI (enter exactly in the Meta app)",
  "Kopieren": "Copy",
  "Token-Überwachung (täglich automatisch)": "Token monitoring (daily, automatic)",
  "Läuft ein Token ab oder wird ungültig, erstellt das System automatisch eine KEWAG-Aufgabe — und schliesst sie bei Erholung wieder.": "If a token expires or becomes invalid, the system automatically creates a KEWAG task — and closes it again upon recovery.",
  "Vorwarnung (Tage vor Ablauf)": "Advance warning (days before expiry)",
  "Auto-Erinnerung Einladung (Std.)": "Auto reminder invitation (hrs)",
  "Alle Tokens jetzt prüfen": "Check all tokens now",
  "Meta-Grundlage aktivieren (direkt hier — verschlüsselt gespeichert)": "Activate Meta foundation (right here — stored encrypted)",
  "App-ID und App-Geheimcode aus der Meta-Konsole (Einstellungen → Allgemeines). Wird live bei Meta geprüft und verschlüsselt gespeichert — kein Emergent-Secrets-Umweg nötig.": "App ID and app secret from the Meta console (Settings → Basic). Verified live at Meta and stored encrypted — no detour via Emergent secrets.",
  "App-ID (z. B. 1558250479126312)": "App ID (e.g. 1558250479126312)",
  "App-Geheimcode (App Secret)": "App secret (App Secret)",
  "Tipp: Bei „Error validating client secret“ ist der Geheimcode falsch/unvollständig — in der Meta-Konsole neben „App-Geheimcode“ auf „Anzeigen“/„Zurücksetzen“ und vollständig kopieren. Alternativ als Emergent-Secrets möglich ({vars}).": "Tip: if you see “Error validating client secret”, the secret is wrong/incomplete — in the Meta console next to “App secret”, click “Show”/“Reset” and copy it in full. Also possible via Emergent secrets ({vars}).",
  "Aktiviert & verschlüsselt gespeichert": "Activated & stored encrypted",
  "Ampel-Übersicht · alle Mandanten-Verbindungen": "Traffic-light overview · all tenant connections",
  "Noch keine Mandanten-Meta-Verbindungen. Verbinde unten einen Testmandanten.": "No tenant Meta connections yet. Connect a test tenant below.",
  "Verbindung · interner Testmandant": "Connection · internal test tenant",
  "Testmandant": "Test tenant",
  "— Mandant wählen —": "— Select tenant —",
  "(Demo — nicht verbindbar)": "(Demo — not connectable)",
  "Zuerst Zugangsdaten hinterlegen": "Add credentials first",
  "Verbindung teilen mit Kunde": "Share connection with customer",
  "Erzeugen Sie einen sicheren, einmalig gültigen Einladungslink (72 Stunden). Der Kunde autorisiert sein eigenes Meta-Konto selbst — ohne Passwort-Weitergabe an KEWAG.": "Generate a secure, single-use invitation link (72 hours). The customer authorizes their own Meta account — without sharing a password with KEWAG.",
  "Auch per E-Mail an den Kunden senden": "Also send to the customer by e-mail",
  "Vorschlag: Haupt-Kontakt des Mandanten — überschreibbar.": "Suggestion: main contact of the tenant — editable.",
  "E-Mail-Dienst (Brevo) ist nicht verbunden — der Versand wird übersprungen. Der Link wird trotzdem erstellt und angezeigt.": "E-mail service (Brevo) is not connected — sending is skipped. The link is still created and shown.",
  "Einladungslink erzeugen (72 h)": "Generate invitation link (72 h)",
  "Hinweis: Der Kunde kann den Link erst zur Verbindung nutzen, sobald die Meta-Grundlage aktiviert ist.": "Note: the customer can only use the link to connect once the Meta foundation is activated.",
  "Neuer Einladungslink — an den Kunden senden:": "New invitation link — send to the customer:",
  "erstellt": "created",
  "gültig bis": "valid until",
  "verwendet": "used",
  "gesendet": "sent",
  "vorgemerkt": "queued",
  "erinnert": "reminded",
  "Erinnerung senden": "Send reminder",
  "Noch keine Verbindung für diesen Mandanten. Klicke „Mit Meta verbinden“, um den OAuth-Flow zu starten.": "No connection for this tenant yet. Click “Connect with Meta” to start the OAuth flow.",
  "Token läuft in {d} Tag(en) ab — Meta-Tokens gelten ~60 Tage. Jetzt neu verbinden, um Unterbrechungen zu vermeiden.": "Token expires in {d} day(s) — Meta tokens last ~60 days. Reconnect now to avoid interruptions.",
  "Token gültig — noch ca. {d} Tage (Meta-Tokens laufen nach ~60 Tagen ab).": "Token valid — approx. {d} more days (Meta tokens expire after ~60 days).",
  "Gewährte Berechtigungen": "Granted permissions",
  "Fehlende Berechtigungen": "Missing permissions",
  "Business-Portfolios": "Business portfolios",
  "Keine Portfolios gefunden.": "No portfolios found.",
  "Referenz-Abgleich (sichere Zuordnung)": "Reference match (secure assignment)",
  "Ziel-Seite (Referenz)": "Target page (reference)",
  "Portfolio (Referenz)": "Portfolio (reference)",
  "Business-Asset (Referenz)": "Business asset (reference)",
  "Von der Meta Pages API zurückgegebene Seiten:": "Pages returned by the Meta Pages API:",
  "Keine Seite geladen — Endpoint": "No page loaded — endpoint",
  "Meta-Fehlercode": "Meta error code",
  "fehlende Berechtigung:": "missing permission:",
  "Noch keine Seiten geladen (Verbindung ausstehend).": "No pages loaded yet (connection pending).",
  "stimmt mit Referenz überein ✓": "matches reference ✓",
  "Asset auswählen (Facebook-Seite + verknüpftes Instagram)": "Select asset (Facebook page + linked Instagram)",
  "— Seite wählen —": "— Select page —",
  "(kein IG)": "(no IG)",
  "Auswahl speichern": "Save selection",
  "Ausgewählt:": "Selected:",
  "Verbindung testen": "Test connection",
  "Verbindung trennen": "Disconnect",
  "Verbindung jetzt prüfen": "Check connection now",
  "Meta App im Entwicklungsmodus angenommen — nur autorisierte Admin-/Tester-/Developer-Konten können sich verbinden. Kein Advanced Access / kein App Review bestätigt.": "Meta app assumed in development mode — only authorized admin/tester/developer accounts can connect. No advanced access / no app review confirmed.",
  "WordPress API-Key vorhanden.": "WordPress API key present.",
  "Kein WordPress API-Key hinterlegt.": "No WordPress API key stored.",
  "Meta ist konfiguriert und verbindbar.": "Meta is configured and connectable.",
  "Meta App-ID & Secret fehlen.": "Meta App ID & Secret are missing.",
  "E-Mail-Versand ist konfiguriert.": "E-mail delivery is configured.",
  "Brevo API-Key fehlt.": "Brevo API key is missing.",
  "Kein gültiger Stripe-Key hinterlegt.": "No valid Stripe key stored.",
  "Nicht konfiguriert – Zugangsdaten fehlen.": "Not configured – credentials missing.",
  // --- Phase 3 Batch 2: Integrationen/MetaConnectDialog ---
  "Kundenverbindungen": "Customer connections",
  "Provider & Systeme": "Providers & systems",
  "Verlauf & Prüfungen": "History & checks",
  "Integrationen konnten nicht geladen werden": "Integrations could not be loaded",
  "Meta erfolgreich verbunden.": "Meta connected successfully.",
  "Meta-Verbindung fehlgeschlagen.": "Meta connection failed.",
  "Kein Instagram-Business-Konto mit der Facebook-Seite verknüpft.": "No Instagram business account linked to the Facebook page.",
  "Keine Facebook-Seite gefunden.": "No Facebook page found.",
  "Zuletzt geprüft – bitte kurz warten.": "Recently checked – please wait a moment.",
  "Integrationen neu geprüft.": "Integrations re-checked.",
  "Ansicht aktualisiert.": "View refreshed.",
  "Prüfung fehlgeschlagen": "Check failed",
  "Test fehlgeschlagen": "Test failed",
  "Dokumentation wird vorbereitet.": "Documentation is being prepared.",
  "Zentrale Verwaltung von Plattform-Verbindungen, Berechtigungen und Integrations-Health.": "Central management of platform connections, permissions and integration health.",
  "Letzte Aktualisierung:": "Last update:",
  "Prüfungen & Diagnose sind nur für den Super Admin sichtbar.": "Checks & diagnostics are only visible to the super admin.",
  "Audit-Log öffnen": "Open audit log",
  "Zeiten in Tabellen und Karten entsprechen der letzten erfolgreichen Synchronisation oder Prüfung.": "Times in tables and cards reflect the last successful synchronization or check.",
  "Ehrlicher Live-Status: nur echte, geprüfte Verbindungen werden als verbunden angezeigt.": "Honest live status: only real, verified connections are shown as connected.",
  "System-Setup": "System setup",
  "OAuth & Assets": "OAuth & assets",
  "Test & Speichern": "Test & save",
  "Meta-Status konnte nicht geladen werden": "Meta status could not be loaded",
  "diesem Kunden": "this customer",
  "OAuth konnte nicht gestartet werden": "OAuth could not be started",
  "Einladungslink erstellt (72 Stunden, einmalig).": "Invitation link created (72 hours, single-use).",
  "Einladungslink fehlgeschlagen": "Invitation link failed",
  "Link kopiert": "Link copied",
  "Bitte App-ID und App-Secret eingeben.": "Please enter the App ID and App Secret.",
  "Meta aktiviert und verschlüsselt gespeichert — Sie können jetzt verbinden.": "Meta activated and stored encrypted — you can now connect.",
  "Aktivierung fehlgeschlagen": "Activation failed",
  "Redirect-URI kopiert": "Redirect URI copied",
  "Bitte numerische Configuration ID eingeben.": "Please enter a numeric Configuration ID.",
  "Config-ID gespeichert — OAuth nutzt jetzt Login for Business (config_id).": "Config ID saved — OAuth now uses Login for Business (config_id).",
  "Config-ID konnte nicht gespeichert werden": "Config ID could not be saved",
  "Config-ID entfernt — OAuth nutzt wieder scope.": "Config ID removed — OAuth uses scope again.",
  "Config-ID konnte nicht entfernt werden": "Config ID could not be removed",
  "Meta App ID (global)": "Meta App ID (global)",
  "Meta App Secret (global)": "Meta App Secret (global)",
  "OAuth Redirect URI (global)": "OAuth Redirect URI (global)",
  "OAuth Config ID (global)": "OAuth Config ID (global)",
  "Hinterlegt": "Provided",
  "Erforderlich": "Required",
  "Optional": "Optional",
  "Business Portfolio": "Business Portfolio",
  "Facebook-Seite": "Facebook page",
  "Instagram-Konto": "Instagram account",
  "Gewährte Scopes": "Granted scopes",
  "Letzter Health-Check": "Last health check",
  "Meta (Instagram / Facebook) verbinden": "Connect Meta (Instagram / Facebook)",
  "System-Setup global · Verbindung nur für den ausgewählten Kunden": "Global system setup · connection only for the selected customer",
  "Ausgewählter Kunde": "Selected customer",
  "Die Verbindung wird ausschliesslich diesem Mandanten zugeordnet.": "The connection is assigned exclusively to this tenant.",
  "System-Setup (global)": "System setup (global)",
  "Einmalig für das System – nicht pro Kunde speichern.": "One-time for the system – not saved per customer.",
  "OAuth Config ID (Login for Business)": "OAuth Config ID (Login for Business)",
  "Config-ID entfernen (zurück zu scope)": "Remove config ID (back to scope)",
  "Neue Configuration ID (überschreibt)": "New Configuration ID (overwrites)",
  "z. B. 1234567890123456": "e.g. 1234567890123456",
  "Meta-Systemgrundlage: Betriebsbereit": "Meta system foundation: ready",
  "Meta-Systemgrundlage: Nicht betriebsbereit": "Meta system foundation: not ready",
  "Meta App-ID und App-Secret fehlen — bitte als Emergent-Secret hinterlegen.": "Meta App ID and App Secret are missing — please store them as an Emergent secret.",
  "Verschlüsselung nicht aktiv (INTEGRATION_ENCRYPTION_KEY) — Tokens könnten nicht sicher gespeichert werden.": "Encryption not active (INTEGRATION_ENCRYPTION_KEY) — tokens could not be stored securely.",
  "Meta-Grundlage betriebsbereit — OAuth kann gestartet werden.": "Meta foundation ready — OAuth can be started.",
  "Jetzt aktivieren — kein Neustart nötig": "Activate now — no restart required",
  "Wo finde ich das?": "Where do I find this?",
  "App-ID und App-Secret aus Ihrer Meta-App eingeben. Wir prüfen sie live bei Meta und speichern sie verschlüsselt — ohne Emergent-Secret, ohne Rebuild.": "Enter the App ID and App Secret from your Meta app. We verify them live at Meta and store them encrypted — without an Emergent secret, without a rebuild.",
  "Anleitung ausblenden": "Hide guide",
  "Schritt-für-Schritt: Wo finde ich App-ID & Secret?": "Step by step: where do I find the App ID & Secret?",
  "Meta App-ID": "Meta App ID",
  "Meta App-Secret": "Meta App Secret",
  "wird verschlüsselt gespeichert": "stored encrypted",
  "Aktivieren & prüfen": "Activate & verify",
  "Verbindung für diesen Kunden": "Connection for this customer",
  "Verbindungsart": "Connection type",
  "Einladungslink": "Invitation link",
  "72 Stunden · einmalig": "72 hours · single-use",
  "Nur nach erfolgreichem OAuth werden Token und Assets diesem Kunden zugeordnet.": "Only after a successful OAuth are tokens and assets assigned to this customer.",
  "Einladungslink erstellen": "Create invitation link",
  "OAuth ist erst möglich, wenn die Systemgrundlage betriebsbereit ist.": "OAuth is only possible once the system foundation is ready.",
  "Gültig bis": "Valid until",
  "· nur einmal verwendbar": "· usable only once",
  "Nach erfolgreicher Autorisierung": "After successful authorization",
  "Bestätigen & zu Meta": "Confirm & go to Meta",
  "OAuth starten": "Start OAuth",
  "Später": "Later",
  "Wichtig:": "Important:",
  "Meta ist aktiv!": "Meta is active!",
  "Diese Redirect-URI in der Meta-App unter „Valid OAuth Redirect URIs\" eintragen:": "Enter this redirect URI in the Meta app under “Valid OAuth Redirect URIs”:",
  "Meta für {name} verbinden": "Connect Meta for {name}",
  "Klicken Sie jetzt unten auf „Meta für {name} verbinden“ und melden Sie sich mit Ihrem Facebook-Konto an.": "Now click “Connect Meta for {name}” below and sign in with your Facebook account.",
  "Die globale Meta-Systemintegration bleibt getrennt. OAuth-Verbindung, Tokens und Assets werden ausschliesslich beim aktuell ausgewählten Kunden ({name}) gespeichert.": "The global Meta system integration remains separate. OAuth connection, tokens and assets are stored exclusively for the currently selected customer ({name}).",
  "Sie verbinden Meta jetzt ausschliesslich mit dem Mandanten {name}.": "You are now connecting Meta exclusively to the tenant {name}.",
  "<strong>Instagram und Facebook gehören beide zu Meta.</strong> Sie richten das hier nur <strong>einmal</strong> ein — egal, ob Sie vorher auf die Instagram- oder die Facebook-Kachel geklickt haben. Diese eine Aktivierung schaltet <strong>beide</strong> Kanäle frei.": "<strong>Instagram and Facebook both belong to Meta.</strong> You set this up <strong>only once</strong> here — no matter whether you previously clicked the Instagram or the Facebook tile. This single activation unlocks <strong>both</strong> channels.",
  "Bei <strong class='text-foreground/80'>Facebook Login for Business</strong> erforderlich: numerische Configuration ID aus dem Meta-Dashboard. Der OAuth-Flow nutzt dann <code class='text-foreground/80'>config_id</code> statt <code class='text-foreground/80'>scope</code>. Öffentliche Kennung – kein Secret, kein Neustart nötig.": "Required for <strong class='text-foreground/80'>Facebook Login for Business</strong>: numeric Configuration ID from the Meta dashboard. The OAuth flow then uses <code class='text-foreground/80'>config_id</code> instead of <code class='text-foreground/80'>scope</code>. Public identifier – no secret, no restart required.",
  "Eine Art <strong class='text-foreground/80'>Benutzername</strong> Ihrer Meta-App (nur Zahlen).": "A kind of <strong class='text-foreground/80'>username</strong> for your Meta app (numbers only).",
  "Das dazugehörige <strong class='text-foreground/80'>Passwort</strong> — wird verschlüsselt gespeichert und nie angezeigt.": "The corresponding <strong class='text-foreground/80'>password</strong> — stored encrypted and never displayed.",
  "<li><strong class='text-foreground'>1.</strong> Öffnen Sie <a class='text-primary hover:underline' href='https://developers.facebook.com/apps/' target='_blank' rel='noreferrer'>developers.facebook.com/apps</a> und wählen Sie Ihre App (oder „App erstellen“ → Typ <em>Business</em>).</li><li><strong class='text-foreground'>2.</strong> Links im Menü: <em>App-Einstellungen → Grundlegendes</em>. Ganz oben steht die <strong class='text-foreground'>App-ID</strong>.</li><li><strong class='text-foreground'>3.</strong> Beim <strong class='text-foreground'>App-Geheimnis</strong> auf „Anzeigen“ klicken (Passwort nötig) — oder „Zurücksetzen“ für ein neues, falls das alte nicht mehr stimmt. Wert vollständig kopieren.</li><li><strong class='text-foreground'>4.</strong> Beide Werte hier oben eintragen und <em>„Aktivieren &amp; prüfen“</em> klicken. Fertig — Instagram &amp; Facebook sind aktiv.</li><li class='pt-1 text-[10px]'>Tipp: Für den späteren Login-Schritt muss zusätzlich das Produkt „Facebook Login“ hinzugefügt und die unten stehende Redirect-URI eingetragen sein.</li>": "<li><strong class='text-foreground'>1.</strong> Open <a class='text-primary hover:underline' href='https://developers.facebook.com/apps/' target='_blank' rel='noreferrer'>developers.facebook.com/apps</a> and select your app (or “Create app” → type <em>Business</em>).</li><li><strong class='text-foreground'>2.</strong> In the left menu: <em>App settings → Basic</em>. The <strong class='text-foreground'>App ID</strong> is at the very top.</li><li><strong class='text-foreground'>3.</strong> For the <strong class='text-foreground'>App secret</strong>, click “Show” (password required) — or “Reset” for a new one if the old one is no longer valid. Copy the full value.</li><li><strong class='text-foreground'>4.</strong> Enter both values above and click <em>“Activate &amp; verify”</em>. Done — Instagram &amp; Facebook are active.</li><li class='pt-1 text-[10px]'>Tip: for the later login step, the “Facebook Login” product must also be added and the redirect URI below entered.</li>",
  // --- Phase 3 Batch 1: NextActions/Nachrichten/MetaConnectPublic/Abrechnung ---
  "Ihre nächsten Aktionen": "Your next actions",
  "Problem": "Issue",
  "Fällig": "Due",
  "Meta-Verbindung starten": "Start Meta connection",
  "Alle Aktionen anzeigen →": "Show all actions →",
  "Noch keine offenen Aktionen. Alles im grünen Bereich.": "No open actions yet. All clear.",
  "Verbindung unterbrochen": "Connection interrupted",
  "Verbindung getrennt": "Disconnected",
  "Autorisierung ausstehend": "Authorization pending",
  "Nicht konfiguriert": "Not configured",
  "Buchung prüfen": "Review booking",
  "Test ausstehend": "Test pending",
  "Keine Aktion nötig": "No action needed",
  "Health-Check ausführen": "Run health check",
  "Erneut verbinden / Token erneuern": "Reconnect / renew token",
  "Verbinden (Kundenzustimmung)": "Connect (customer consent)",
  "Zugangsdaten hinterlegen": "Add credentials",
  "Provider-Freigabe abwarten": "Await provider approval",
  "Neu verbinden": "Reconnect",
  "Bei Bedarf buchen": "Book if needed",
  "Demo anfragen": "Request demo",
  "Pocket": "Pocket",
  "gerade eben": "just now",
  "vor {n} Min": "{n} min ago",
  "vor {n} Std": "{n} h ago",
  "vor {n} Tagen": "{n} d ago",
  "Benachrichtigungen & Aktivitäten aus SocialCheck — alles live aus echten Daten.": "Notifications & activity from SocialCheck — all live from real data.",
  "Inhalt wartet auf Freigabe": "item awaiting approval",
  "Inhalte warten auf Freigabe": "items awaiting approval",
  "Zu den Freigaben": "Go to approvals",
  "Keine Nachrichten in dieser Kategorie.": "No messages in this category.",
  "Neuer Lead": "New lead",
  "Fotoauftrag": "Photo assignment",
  "Support-Ticket": "Support ticket",
  "Sichere Autorisierung über Meta. Sie geben KEWAG kein Passwort — die Verbindung erfolgt direkt bei Meta über Ihr eigenes Konto.": "Secure authorization via Meta. You don't share any password with KEWAG — the connection is made directly at Meta through your own account.",
  "Link abgelaufen": "Link expired",
  "Dieser Einladungslink ist nicht mehr gültig (72 Stunden überschritten). Bitte fordern Sie bei KEWAG einen neuen Link an.": "This invitation link is no longer valid (72 hours exceeded). Please request a new link from KEWAG.",
  "Link zurückgezogen": "Link revoked",
  "Dieser Einladungslink wurde von KEWAG zurückgezogen. Bitte wenden Sie sich an Ihr KEWAG-Team.": "This invitation link was revoked by KEWAG. Please contact your KEWAG team.",
  "Bereits verbunden": "Already connected",
  "Diese Einladung wurde bereits verwendet. Ihr Meta-Konto ist verbunden — es ist nichts weiter zu tun.": "This invitation has already been used. Your Meta account is connected — nothing more to do.",
  "Ungültiger Link": "Invalid link",
  "Dieser Einladungslink ist ungültig. Bitte prüfen Sie den Link oder fordern Sie bei KEWAG einen neuen an.": "This invitation link is invalid. Please check the link or request a new one from KEWAG.",
  "Der Link konnte nicht geprüft werden.": "The link could not be checked.",
  "Die Verbindung konnte nicht gestartet werden.": "The connection could not be started.",
  "Einladung wird geprüft…": "Checking invitation…",
  "Etwas ist schiefgelaufen": "Something went wrong",
  "Meta-Konto verbinden": "Connect Meta account",
  "KEWAG möchte Ihre Facebook-Seite und Ihr Instagram-Konto betreuen. Verbinden Sie Ihr Meta-Konto mit einem Klick — sicher und ohne Passwort-Weitergabe.": "KEWAG would like to manage your Facebook page and Instagram account. Connect your Meta account with one click — securely and without sharing a password.",
  "Die Verbindung ist derzeit vorübergehend nicht verfügbar. Bitte versuchen Sie es in Kürze erneut oder kontaktieren Sie KEWAG.": "The connection is temporarily unavailable. Please try again shortly or contact KEWAG.",
  "Mit Meta verbinden": "Connect with Meta",
  "Verschlüsselte, einmalig gültige Einladung": "Encrypted, single-use invitation",
  "Vielen Dank — verbunden!": "Thank you — connected!",
  "Verbindung nicht abgeschlossen": "Connection not completed",
  "Ihr Meta-Konto wurde erfolgreich mit KEWAG verbunden. Sie können dieses Fenster jetzt schliessen — KEWAG kümmert sich um den Rest.": "Your Meta account was successfully connected to KEWAG. You can close this window now — KEWAG takes care of the rest.",
  "Die Verbindung konnte nicht abgeschlossen werden. Bitte öffnen Sie Ihren Einladungslink erneut oder kontaktieren Sie Ihr KEWAG-Team.": "The connection could not be completed. Please reopen your invitation link or contact your KEWAG team.",
  "Nur für KEWAG Admin / Super Admin.": "For KEWAG admins / super admins only.",
  "Fehler beim Laden.": "Error while loading.",
  "Abrechnungseinstellungen gespeichert.": "Billing settings saved.",
  "Zurück zu Rechnungen": "Back to invoices",
  "Firmendaten, IBAN & QR-Rechnung, Nummernkreis und E-Mail-Versand — Voraussetzung für Swiss QR-Rechnungen.": "Company details, IBAN & QR invoice, number range and e-mail delivery — prerequisite for Swiss QR invoices.",
  "Swiss QR-Rechnung ist bereit — der QR-Zahlteil wird dem Rechnungs-PDF angehängt.": "Swiss QR invoice is ready — the QR payment part is attached to the invoice PDF.",
  "QR-Rechnung noch nicht bereit: Firma, IBAN/QR-IBAN, PLZ und Ort ausfüllen und „QR aktiviert“ einschalten.": "QR invoice not ready yet: fill in company, IBAN/QR-IBAN, postal code and city, and enable “QR enabled”.",
  "Brevo E-Mail-Versand ist verbunden — Rechnungen können per E-Mail versendet werden.": "Brevo e-mail delivery is connected — invoices can be sent by e-mail.",
  "Brevo noch nicht verbunden: E-Mail-Versand wird aktiv, sobald BREVO_API_KEY & BREVO_SENDER_EMAIL gesetzt sind.": "Brevo not connected yet: e-mail delivery activates as soon as BREVO_API_KEY & BREVO_SENDER_EMAIL are set.",
  "Absender / Firma": "Sender / company",
  "Erscheint als Zahlungsempfänger (Kreditor) auf Rechnung und QR-Zahlteil.": "Appears as the payee (creditor) on the invoice and QR payment part.",
  "Firmenname": "Company name",
  "Strasse": "Street",
  "Nr.": "No.",
  "Zusätzliche Adresszeilen (PDF-Kopf)": "Additional address lines (PDF header)",
  "Optional, eine Zeile pro Eintrag.": "Optional, one line per entry.",
  "Steuer": "Tax",
  "UID/MWST-Nummer und Normalsatz.": "UID/VAT number and standard rate.",
  "UID / MWST-Nummer": "UID / VAT number",
  "z. B. CHE-123.456.789 MWST": "e.g. CHE-123.456.789 VAT",
  "MwSt-Satz (%)": "VAT rate (%)",
  "Bankverbindung": "Bank details",
  "IBAN für normale QR-Rechnung oder QR-IBAN für QR-Referenz-Verfahren.": "IBAN for a standard QR invoice or QR-IBAN for the QR reference procedure.",
  "Normale IBAN (ohne QR-Referenz).": "Standard IBAN (without QR reference).",
  "Für QR-Referenz-Verfahren (IID 30000–31999). Hat Vorrang.": "For the QR reference procedure (IID 30000–31999). Takes precedence.",
  "Swiss QR-Zahlteil im Rechnungs-PDF aktivieren": "Enable the Swiss QR payment part in the invoice PDF",
  "Rechnungsstellung": "Invoicing",
  "Nummernkreis, Zahlungsfrist und Bedingungen.": "Number range, payment term and conditions.",
  "Rechnungsnummern-Präfix": "Invoice number prefix",
  "Ergibt z. B. INV-2026-0001": "Results in e.g. INV-2026-0001",
  "Zahlungsfrist (Tage)": "Payment term (days)",
  "Währung": "Currency",
  "Zahlungsbedingungen (PDF-Fusszeile)": "Payment terms (PDF footer)",
  "Zahlbar innert 30 Tagen netto.": "Payable net within 30 days.",
  "E-Mail-Versand": "E-mail delivery",
  "Anzeige-Absender & Antwort-Adresse. Der technische Versand läuft über Brevo (Server-Zugangsdaten).": "Display sender & reply address. Technical delivery runs through Brevo (server credentials).",
  "Absender-Name": "Sender name",
  "Absender-E-Mail (Anzeige)": "Sender e-mail (display)",
  "Technischer Absender = BREVO_SENDER_EMAIL (verifiziert).": "Technical sender = BREVO_SENDER_EMAIL (verified).",
  "Antwort-an (Reply-To)": "Reply-To",
  "Einstellungen speichern": "Save settings",
  "Benutzer anlegen": "Create user",
  "Zugang erstellt.": "Access created.",
  "Neuen Zugang erstellen – per Einladungslink oder mit direkt gesetztem Passwort.": "Create a new access – via invitation link or with a directly set password.",
  "Konto": "Account",
  "ist aktiv. Bitte diese Zugangsdaten sicher an die Person weitergeben:": "is active. Please share these credentials securely with the person:",
  "wurde aktualisiert und ist aktiv. Bitte diese Zugangsdaten sicher weitergeben:": "has been updated and is active. Please share these credentials securely:",
  "Passwort": "Password",
  "Das Passwort wird aus Sicherheitsgründen nur jetzt angezeigt. Empfehlung: Person ändert es beim ersten Login und aktiviert 2FA.": "For security reasons the password is shown only now. Recommendation: the person changes it on first login and enables 2FA.",
  "Einladung wurde per E-Mail versendet.": "The invitation has been sent by email.",
  "Brevo nicht verbunden – bitte diesen Aktivierungslink weitergeben:": "Brevo not connected – please share this activation link:",
  "Schliessen": "Close",
  "Name": "Name",
  "Rolle": "Role",
  "Kunde / Mandant": "Customer / tenant",
  "Kunde wählen…": "Choose customer…",
  "Bereich / Team (optional)": "Area / team (optional)",
  "z. B. Kundenbetreuung": "e.g. customer care",
  "Passwort direkt setzen (optional)": "Set password directly (optional)",
  "leer = Einladungslink": "empty = invitation link",
  "Generieren": "Generate",
  "Mit Passwort wird das Konto sofort aktiv (kein Einladungslink). Ohne Passwort geht eine Einladung raus.": "With a password the account is active immediately (no invitation link). Without a password an invitation is sent.",
  "Abbrechen": "Cancel",
  "Konto anlegen": "Create account",
  "Einladen": "Invite",
  "Team": "Team",
  "Super Admin": "Super Admin",
  "Admin": "Admin",
  "Finanzen": "Finance",
  "Editor": "Editor",
  "Reviewer": "Reviewer",
  "Mitarbeiter": "Staff member",
  "Viewer": "Viewer",
  "Starkes Passwort generiert": "Strong password generated",
  "Kopiert": "Copied",
  "Name und E-Mail erforderlich": "Name and email required",
  "Bitte einen Kunden wählen": "Please choose a customer",
  "Passwort: mindestens 8 Zeichen.": "Password: at least 8 characters.",
  "Konto aktiv angelegt – Passwort direkt gesetzt": "Account created and active – password set directly",
  "Bestehendes Konto aktualisiert – Passwort neu gesetzt": "Existing account updated – password reset",
  "Einladung per E-Mail gesendet": "Invitation sent by email",
  "Einladung erstellt (Brevo nicht verbunden – Link kopieren)": "Invitation created (Brevo not connected – copy link)",
  "Diese E-Mail ist bereits vergeben": "This email address is already in use",
  "Diese E-Mail ist bereits vergeben. Bitte die Aktualisierung des bestehenden Kontos bestätigen.": "This email address is already in use. Please confirm updating the existing account.",
  "Fortfahren aktualisiert das bestehende Konto und setzt das Passwort neu.": "Continuing updates the existing account and resets the password.",
  "Fortfahren aktualisiert das bestehende Konto / sendet eine neue Einladung.": "Continuing updates the existing account / sends a new invitation.",
  "Ich möchte das bestehende Konto aktualisieren.": "I want to update the existing account.",
  "Diese E-Mail ist bereits vergeben. Bestehende Konten über die Benutzerliste verwalten.": "This email address is already in use. Manage existing accounts via the user list.",
  "Bestehende Konten werden über die Benutzerliste verwaltet (Rolle, Passwort, erneute Einladung) – nicht über dieses Formular.": "Existing accounts are managed via the user list (role, password, re-invitation) – not via this form.",
  "Aktiv": "Active",
  "Nicht aktiv": "Inactive",
  "Inaktiv": "Inactive",
  "Eingeladen": "Invited",
  "Ausstehend": "Pending",
  "Abgelaufen": "Expired",
  "Gesperrt": "Blocked",
  "Alle gelesen": "Mark all read",
  "offen seit": "open for",
  "Tagen": "days",
  "Tage": "days",
  "Mt.": "mo",
  "T.": "d",
  "Offene Angebote ohne Reaktion seit ≥": "Open offers without response for ≥",
  "Angebot(e)": "offer(s)",
  "Angebote verwalten": "Manage offers",
  "Keine Angebote in": "No offers in",
  "Nachfass-Erinnerung an Kunde + intern senden": "Send follow-up reminder to customer + internal",
  "E-Mail-Versand aktiv, sobald Brevo verbunden ist": "Email sending active once Brevo is connected",
  "Nachfass-Erinnerung gesendet": "Follow-up reminder sent",
  "Betreuer": "Manager",
  "Dashboard": "Dashboard",
  "Live": "Live",
  "Onboarding": "Onboarding",
  "Orange · seit > 3 Tagen": "Orange · for > 3 days",
  "Lauf": "run",
  "Kundennummer": "Customer number",
  "Kunde wählen": "Choose customer",
  "Demo erstellen": "Create demo",
  "Support öffnen": "Open support",
  "Beiträge zur Freigabe": "Posts to approve",
  "Neue Demo-Anfragen": "New demo requests",
  "Demos laufen heute ab": "Demos expiring today",
  "Mein Kundenfeedback": "My customer feedback",
  "Überfällige Freigaben": "Overdue approvals",
  "offen in meinem Eingang": "open in my inbox",
  "Geplante Beiträge ": "Scheduled posts ",
  "Nachfassen fällig": "Follow-up due",
  "Nachfassen": "Follow up",
  "Annehmen": "Accept",
  "Ablehnen": "Decline",
  "Zurücksetzen": "Reset",
  "Zum Kunden": "Go to customer",
  "In Kunde umwandeln": "Convert to customer",
  "Angenommen": "Accepted",
  "Abgelehnt": "Declined",
  "Gesendet": "Sent",
  "angenommen": "accepted",
  "abgelehnt": "declined",
  "nachfassen fällig": "follow-up due",
  "Offenes Volumen:": "Open volume:",
  "Abschlussquote:": "Close rate:",
  "Ø bis Abschluss:": "Ø to close:",
  "Gewonnen (Monat):": "Won (month):",
  "Trend · 6 Monate": "Trend · 6 months",
  "Gewonnen": "Won",
  "Verloren": "Lost",
  "Gefiltert auf": "Filtered to",
  "Angebote ausblenden": "Hide offers",
  "Die E-Mail-Nachfass-Erinnerung (an Kunde + intern) wird automatisch versendet, sobald Brevo verbunden ist. Die Übersicht hier ist bereits live.": "The email follow-up reminder (to customer + internal) is sent automatically once Brevo is connected. The overview here is already live.",
  "Verworfen": "Discarded",
  "Angebot angenommen": "Offer accepted",
  "Angebot abgelehnt": "Offer declined",
  "Status zurückgesetzt": "Status reset",
  // === Video-Seiten EN (Final Showdown i18n) — Dashboard / Kunden / AppShell / CockpitShell / AuditLog / SystemHealth ===
  "Umsatz-Übersicht": "Revenue overview",
  "Wiederkehrender Monatsumsatz aktiver Kunden": "Recurring monthly revenue from active customers",
  "MRR · Monatsumsatz": "MRR · monthly revenue",
  "ARR": "ARR",
  "aktive Abos": "active subscriptions",
  "Noch keine aktiven Abos.": "No active subscriptions yet.",
  "Mandantenmodus": "Tenant mode",
  "KI-Läufe": "AI runs",
  "Bildbewertungen": "Image ratings",
  "Vorschläge": "Suggestions",
  "Videos": "Videos",
  "KI-Kostenkontrolle": "AI cost control",
  "KI aktiv": "AI active",
  "KI pausiert": "AI paused",
  "Verbunden": "Connected",
  "Nicht verbunden": "Not connected",
  "Kostenpflichtige KI-Läufe pausiert (globaler Kill-Switch)": "Paid AI runs paused (global kill switch)",
  "Fehlgeschlagen": "Failed",
  "Globale Lauf-Auslastung": "Global run usage",
  "kein Limit": "no limit",
  "KI-Läufe pro Tag": "AI runs per day",
  "Mandanten nahe am Limit": "Tenants near the limit",
  "Letzte KI-Aktivität": "Recent AI activity",
  "Noch keine KI-Läufe in diesem Zeitraum": "No AI runs in this period yet",
  "Kosten: Abrechnung über reale Läufe (keine erfundenen Beträge)": "Costs: billed on real runs (no invented amounts)",
  "KI-Vorschläge": "AI suggestions",
  "KI-Läufe am": "AI runs on",
  "Keine KI-Läufe an diesem Tag": "No AI runs on this day",
  "Heute wichtig": "Important today",
  "Deine Prioritäten – direkt bearbeiten": "Your priorities – handle them directly",
  "offen": "open",
  "Alles erledigt – keine offenen Prioritäten.": "All done – no open priorities.",
  "Mein Tag": "My day",
  "Deine offenen To-dos – Aufgaben, Feedback & überfällige Freigaben": "Your open to-dos – tasks, feedback & overdue approvals",
  "Alles erledigt – dein Tag ist frei.": "All done – your day is clear.",
  "Live-Übersicht für": "Live overview for",
  "Portfolioweite Live-Übersicht über alle Mandanten": "Portfolio-wide live overview across all tenants",
  "Integration wartet auf Aktivierung": "Integration awaiting activation",
  "Integrationen warten auf Aktivierung": "Integrations awaiting activation",
  "Zugangsdaten getestet – Secret hinterlegen & Backend neu starten.": "Credentials tested – add the secret & restart the backend.",
  "Demo-Zugänge": "Demo access",
  "Anfragen von socialcheck.ch verwalten": "Manage requests from socialcheck.ch",
  "Upgrade-Anfragen": "Upgrade requests",
  "E-Mail verbunden": "Email connected",
  "E-Mail nicht verbunden": "Email not connected",
  "Neue Anfragen": "New requests",
  "Aktive Demos": "Active demos",
  "Heute ablaufend": "Expiring today",
  "Abgelaufen": "Expired",
  "Unprofitabel": "Unprofitable",
  "KI-Kosten übersteigen den Umsatz": "AI costs exceed revenue",
  "Hauptbetreuer zugewiesen": "Primary manager assigned",
  "Begrüssungs-E-Mail an den Kunden gesendet": "Welcome email sent to the customer",
  "Zuweisung ok – Begrüssungs-E-Mail konnte nicht gesendet werden.": "Assignment ok – welcome email could not be sent.",
  "Kein persönlicher Betreuer": "No personal manager",
  "Dieser Kunde sieht aktuell nur den KEWAG-Team-Kontakt. Weisen Sie eine:n Hauptbetreuer:in zu.": "This customer currently only sees the KEWAG team contact. Assign a primary manager.",
  "KEWAG-Team wählen…": "Select KEWAG team member…",
  "Zuweisen": "Assign",
  "Begrüssungs-E-Mail an den Kunden senden": "Send welcome email to the customer",
  "Brevo nicht verbunden": "Brevo not connected",
  "Intern": "Internal",
  "Kundendaten bearbeiten": "Edit customer details",
  "Arbeitsbereich öffnen": "Open workspace",
  "Kunden-Cockpit ansehen": "View customer cockpit",
  "Profitabilität": "Profitability",
  "MRR": "MRR",
  "KI-Kosten": "AI costs",
  "Läufe": "Runs",
  "Marge": "Margin",
  "KI-Kostensatz nicht konfiguriert — im Finanz-Cockpit einstellen.": "AI cost rate not configured — set it in the finance cockpit.",
  "Domain": "Domain",
  "Begrüssung gesendet": "Welcome sent",
  "Kein persönlicher KEWAG-Betreuer zugewiesen – der Kunde sieht nur den KEWAG-Team-Kontakt.": "No personal KEWAG manager assigned – the customer only sees the KEWAG team contact.",
  "Kein Betreuer": "No manager",
  "Cockpit": "Cockpit",
  "Arbeitsbereich": "Workspace",
  "Entwurf verworfen": "Draft discarded",
  "Kunde anlegen": "Add customer",
  "Entwürfe fortsetzen": "Resume drafts",
  "Fortsetzen": "Resume",
  "Verwerfen": "Discard",
  "Nur Kunden ohne persönlichen KEWAG-Betreuer anzeigen": "Show only customers without a personal KEWAG manager",
  "offene Freigabe(n)": "open approval(s)",
  "überfällig": "overdue",
  "Keine offenen Freigaben": "No open approvals",
  "Mandanten mit offenen Freigaben": "Tenants with open approvals",
  "aktiv": "active",
  "Demo & Test": "Demo & test",
  "KEWAG-Team öffnen": "Open KEWAG team",
  "Ihr Testzugang läuft bald ab.": "Your trial access is expiring soon.",
  "Testzugang, keine echte Veröffentlichung. Alle Social-Aktionen sind Dry-Run.": "Trial access, no real publishing. All social actions are dry-run.",
  "Läuft ab:": "Expires:",
  "Upgrade angefragt — KEWAG meldet sich": "Upgrade requested — KEWAG will get in touch",
  "Wird gesendet…": "Sending…",
  "Jetzt upgraden": "Upgrade now",
  "KEWAG-Team · Kunden-Cockpit": "KEWAG team · customer cockpit",
  "In der Kundensicht deaktiviert – im echten Kundenlogin aktiv": "Disabled in customer view – active in the real customer login",
  "Zurück zum KEWAG Control OS": "Back to KEWAG Control OS",
  "Sie sehen aktuell das Kunden-Cockpit von": "You are currently viewing the customer cockpit of",
  "Super Admin · Kundenaktionen aktiv": "Super admin · customer actions active",
  "Nur-Lese-Modus": "Read-only mode",
  "Aktionen sind deaktiviert – zum Bearbeiten zurück ins KEWAG Control OS.": "Actions are disabled – return to KEWAG Control OS to edit.",
  "Zurück zum Beitrag im Control OS": "Back to the post in Control OS",
  "Intern bearbeiten": "Edit internally",
  "Kundenansicht verlassen": "Exit customer view",
  "Zum Bearbeiten wechseln": "Switch to editing",
  "Lückenloses Protokoll aller Aktionen und Ampel-Änderungen.": "Complete log of all actions and traffic-light changes.",
  "Nur Integrationen": "Integrations only",
  "Nur Kundenansicht (Super Admin)": "Customer view only (super admin)",
  "Aktueller Mandant": "Current tenant",
  "Integration": "Integration",
  "Integrations-Ereignis": "Integration event",
  "SA · Kundenansicht": "SA · customer view",
  "Vom Super Admin in der Kundenansicht ausgeführt": "Performed by the super admin in customer view",
  "Keine Einträge.": "No entries.",
  "Zeit": "Time",
  "Nutzer": "User",
  "Ziel": "Target",
  "Einträgen": "entries",
  "Ampel": "Traffic light",
  "Orange": "Orange",
  "Beitrag erstellt": "Post created",
  "Beitrag bearbeitet": "Post edited",
  "Beitrag gelöscht": "Post deleted",
  "Beitrag dupliziert": "Post duplicated",
  "Beitrag archiviert": "Post archived",
  "Beitrag wiederhergestellt": "Post restored",
  "Automatisch veröffentlicht": "Auto-published",
  "Kommentar": "Comment",
  "Mitglied hinzugefügt": "Member added",
  "Mitglied entfernt": "Member removed",
  "Rolle geändert": "Role changed",
  "Mandant erstellt": "Tenant created",
  "Benachrichtigungen geändert": "Notifications changed",
  "Integration verbunden": "Integration connected",
  "Integration getestet": "Integration tested",
  "Zugangsdaten geprüft": "Credentials checked",
  "Aktivierung offen": "Activation pending",
  "Integrationen neu geprüft": "Integrations re-checked",
  "Kanal-Buchung geändert": "Channel booking changed",
  "Integrationen migriert": "Integrations migrated",
  "Meta-OAuth gestartet": "Meta OAuth started",
  "Meta verbunden": "Meta connected",
  "Meta-Asset ausgewählt": "Meta asset selected",
  "Meta neu geprüft": "Meta re-checked",
  "Meta getrennt": "Meta disconnected",
  "Meta-Überwachung: Aufgabe": "Meta monitoring: task",
  "Meta-Überwachung konfiguriert": "Meta monitoring configured",
  "OK": "OK",
  "Eingeschränkt": "Limited",
  "Betrieb normal — alle Kerndienste laufen.": "Operating normally — all core services are running.",
  "Betrieb eingeschränkt — ein Kerndienst meldet eine Warnung.": "Operating with limitations — a core service reports a warning.",
  "Störung — mindestens ein Kerndienst ist nicht verfügbar.": "Outage — at least one core service is unavailable.",
  "Systemstatus": "System status",
  "Ehrlicher Live-Status — echte Prüfungen, keine statischen Werte.": "Honest live status — real checks, no static values.",
  "Letzte Prüfung:": "Last check:",
  "Auto-Aktualisierung alle 60 s": "Auto-refresh every 60 s",
  "Aktualisieren": "Refresh",
  "Versionen & Wiederherstellung": "Versions & recovery",
  "Systemstatus konnte nicht geladen werden": "System status could not be loaded",
  "Erneut versuchen": "Try again",
  "Systemstatus konnte nicht geladen werden.": "System status could not be loaded.",
  "Status wird geprüft…": "Checking status…",
  "Kerndienste OK:": "Core services OK:",
  "Integrationen verbunden": "Integrations connected",
  "Kerndienste": "Core services",
  "Verwalten": "Manage",
  "Jury-Demo · Puls": "Jury demo · pulse",
  "Restlaufzeit": "Time remaining",
  "Aktive Sitzungen": "Active sessions",
  "Zugang: öffentlich": "Access: public",
  "Zugang: nur mit Einladungslink": "Access: invite link only",
  "Wird geprüft…": "Checking…",
  "Läuft bald ab": "Expiring soon",
  "Gesperrt": "Blocked",
  "Inaktiv": "Inactive",
  "endet am": "ends on",
  "Tg.": "d",
  "Std.": "h",
  "Min.": "min",
  "Sek.": "s",
  "abgelaufen": "expired",
  "Das Jury-Fenster ist abgelaufen – die Demo ist aktuell nicht erreichbar. Jetzt reaktivieren.": "The jury window has expired – the demo is currently unavailable. Reactivate now.",
  "Erinnerung: Das Jury-Fenster läuft in": "Reminder: the jury window expires in",
  "Rechtzeitig verlängern, damit die Demo online bleibt.": "Extend in time to keep the demo online.",
  "Reaktivieren (7 Tage)": "Reactivate (7 days)",
  "Um 7 Tage verlängern": "Extend by 7 days",
  "Neue Jury-Zugänge sind gesperrt. Entsperren unter": "New jury access is blocked. Unblock under",
  "Jury-Demo für 7 Tage reaktiviert.": "Jury demo reactivated for 7 days.",
  "Um 7 Tage verlängert.": "Extended by 7 days.",
  "Aktion fehlgeschlagen.": "Action failed.",
  "„Nicht verbunden\" ist kein Fehler — diese Dienste werden nach Eintragen der Schlüssel automatisch aktiv.": "\"Not connected\" is not an error — these services activate automatically once the keys are added.",
  "Operative Kennzahlen": "Operational metrics",
  "Offene Leads": "Open leads",
  "Offene Fotoaufträge": "Open photo orders",
  "Inhalte zur Freigabe": "Content awaiting approval",
  "Serverantwort": "Server response",
  // === Ende Video-Seiten EN ===
  // === Navigation + Ampel-Zentrale EN (Final Showdown i18n) ===
  "Finanz-Übersicht": "Finance overview",
  "Abos & Pakete": "Subscriptions & plans",
  "Abrechnungseinstellungen": "Billing settings",
  "Kunden / Mandanten": "Customers / tenants",
  "KEWAG-Arbeitsbereich": "KEWAG workspace",
  "Beitragsübersicht": "Post overview",
  "Freigaben / Ampel": "Approvals / traffic light",
  "Medienbibliothek": "Media library",
  "Meine Aufgaben": "My tasks",
  "Kundenübersicht": "Customer overview",
  "KEWAG-Team": "KEWAG team",
  "Demo-Anfragen": "Demo requests",
  "Supportfälle": "Support cases",
  "Systemübersicht": "System overview",
  "WordPress-Verbindungen": "WordPress connections",
  "Allgemeine Einstellungen": "General settings",
  "Backup-Einstellungen": "Backup settings",
  "KEWAG Finanzen": "KEWAG Finance",
  "Kunden-Mitarbeiter": "Customer staff",
  "Änderung/Blocker": "Change/blocker",
  "Offene Frage": "Open question",
  "Rot · Änderung/Blocker": "Red · change/blocker",
  "Blau · offene Frage": "Blue · open question",
  "Orange · wartet auf Freigabe": "Orange · awaiting approval",
  "Grün · freigegeben": "Green · approved",
  "Neutral · keine offene Freigabe": "Neutral · no open approval",
  "Mandanten-Ampel": "Tenant traffic light",
  "Live-Kundenentscheidungsstatus · klicken zum Filtern · Zeile öffnet den Mandanten.": "Live customer decision status · click to filter · row opens the tenant.",
  "Aktive Mandanten": "Active tenants",
  "Kunde suchen…": "Search customer…",
  "Kunde suchen": "Search customer",
  "Alle Pakete": "All plans",
  "Paket filtern": "Filter by plan",
  "Alle Verantwortlichen": "All managers",
  "Verantwortlicher filtern": "Filter by manager",
  "Neutral": "Neutral",
  "Die Mandanten-Ampel konnte nicht geladen werden.": "The tenant traffic light could not be loaded.",
  "Keine Mandanten entsprechen den aktuellen Filtern.": "No tenants match the current filters.",
  "Ampelfilter entfernen": "Remove traffic-light filter",
  "Alle Filter zurücksetzen": "Reset all filters",
  "Seite": "Page",
  // === Ende Navigation + Ampel-Zentrale EN ===
  "Ihre Anfragen an das KEWAG-Team – wir sind für Sie da.": "Your requests to the KEWAG team – we're here for you.",
  "Neue Support-Anfrage": "New support request",
  "Ihre Nachricht": "Your message",
  "Support-Anfrage senden": "Send request",
  "Keine Anfragen": "No requests",
  "Sie haben eine Frage oder einen Wunsch? Erstellen Sie eine Anfrage – das KEWAG-Team kümmert sich darum.": "Have a question or a wish? Create a request – the KEWAG team will take care of it.",
  "Aktualisiert": "Updated",
  "Anfrage auswählen": "Select a request",
  "Anfrage": "Request",
  "Ticket als PDF herunterladen": "Download ticket as PDF",
  "Als PDF": "As PDF",
  "Kundensicht ist nur-lesend – Antworten sind hier deaktiviert.": "Customer view is read-only – replies are disabled here.",
  "Diese Anfrage ist erledigt. Für ein neues Anliegen bitte eine neue Anfrage erstellen.": "This request is completed. For a new matter, please create a new request.",
  "Nachricht an das KEWAG-Team schreiben…": "Write a message to the KEWAG team…",
  "Foto aufnehmen": "Take a photo",
  "Bild wählen": "Choose image",
  "PDF / Datei": "PDF / file",
  "Erlaubt: Bilder, PDF, Word, Excel, TXT, CSV · max. 20 MB pro Datei, 10 Anhänge.": "Allowed: images, PDF, Word, Excel, TXT, CSV · max. 20 MB per file, 10 attachments.",
  "Dieser Dateityp ist nicht erlaubt.": "This file type is not allowed.",
  "Datei zu gross – maximal 20 MB pro Datei.": "File too large – maximum 20 MB per file.",
  "Maximal 10 Anhänge pro Nachricht.": "Maximum 10 attachments per message.",
  "Anhänge überschreiten 100 MB pro Nachricht.": "Attachments exceed 100 MB per message.",
  "Upload fehlgeschlagen": "Upload failed",
  "Bitte warten, bis alle Anhänge hochgeladen sind.": "Please wait until all attachments are uploaded.",
  "Anfrage gesendet – KEWAG meldet sich.": "Request sent – KEWAG will get back to you.",
  "(Anhang)": "(Attachment)",
  "Datei nicht verfügbar": "File not available",
  "PDF-Erstellung fehlgeschlagen": "PDF creation failed",
  "Erneut": "Retry",
  "Fehler": "Error",
  "Erledigt": "Completed",
  "Lädt…": "Loading…",
  "Hauptmenü": "Main menu", "Kommunikation": "Communication", "Abrechnung": "Billing",
  "System": "System", "Arbeit": "Work", "Auswertung & Aufgaben": "Analytics & tasks",
  "Übersicht": "Overview", "Leads": "Leads", "Kunden": "Customers", "Fotoaufträge": "Photo jobs",
  "Inhalte": "Content", "Freigaben": "Approvals", "Kalender": "Calendar",
  "Marken-Profil": "Brand profile", "Reports": "Reports", "Speicher & Medien": "Storage & media",
  "Support": "Support", "Nachrichten": "Messages", "Abos / Pakete": "Subscriptions / plans",
  "Rechnungen": "Invoices", "Integrationen": "Integrations", "Benutzer & Rollen": "Users & roles",
  "Audit-Log": "Audit log", "System Health": "System health", "Einstellungen": "Settings",
  "Tagesübersicht": "Daily overview", "WordPress-Leads": "WordPress leads",
  "Inhalte bearbeiten": "Edit content", "Aufgaben": "Tasks", "Monatsreports": "Monthly reports",
  "Support-Tickets": "Support tickets",
  // super_admin Nav (Golden Master) + Shell-Extras
  "Start": "Start", "Vertrieb & Kunden": "Sales & customers", "Content & Freigaben": "Content & approvals",
  "Heute wichtig / Aufgaben": "Today's priorities / tasks", "Leads & Verkauf": "Leads & sales",
  "Angebote": "Offers", "Beiträge / Inhalte": "Posts / content", "Medien & Speicher": "Media & storage",
  "Marken-Profile": "Brand profiles", "Zahlungen": "Payments", "Zusatzleistungen": "Add-on services",
  "WordPress": "WordPress", "Backups": "Backups",
  "VORSCHAU · Testdaten": "PREVIEW · Test data", "Alle Rechte vorbehalten": "All rights reserved",
  "Datenschutz": "Privacy", "AGB": "Terms", "Impressum": "Legal notice", "Hilfe & Support": "Help & support",
  "Mandant": "Tenant", "Alle Mandanten": "All tenants", "Mandant wechseln": "Switch tenant",
  // Sidebar-Abschnitte (Admin + Cockpit)
  "Tägliche Arbeit": "Daily work", "Kunden & Mandanten": "Customers & tenants",
  "Inhalte & Freigaben": "Content & approvals", "Finanzen & Vertrieb": "Finance & sales",
  "Kommunikation & Service": "Communication & service", "System & Sicherheit": "System & security",
  "Einstellungen & Konfiguration": "Settings & configuration",
  "Meine Übersicht": "My overview", "Inhalte & Planung": "Content & planning",
  "Abrechnung": "Billing", "Service & Konto": "Service & account",
  "Kein Mandant zugewiesen.": "No tenant assigned.", "Ihre Rolle": "Your role",
  "Angemeldet als": "Signed in as",
  "roleHint": "Roles & permissions are managed under “Users & roles”.",
  "Super Admin": "Super Admin", "KEWAG Admin": "KEWAG Admin", "KEWAG Editor": "KEWAG Editor",
  "KEWAG Support": "KEWAG Support", "Kunde / Reviewer": "Customer / reviewer", "Viewer": "Viewer",
  "Kunden-Cockpit öffnen": "Open client cockpit", "Benachrichtigungen": "Notifications",
  "Abmelden": "Log out", "Menü einklappen": "Collapse menu", "Menü ausklappen": "Expand menu",
  "Kundensicht aktiv:": "Client view active:",
  "— du siehst die App als dieser Kunde.": "— you're viewing the app as this customer.",
  "Zurück zur Admin-Ansicht": "Back to admin view",
  "Guten Morgen": "Good morning", "Guten Tag": "Good afternoon", "Guten Abend": "Good evening",
  "Hier ist die aktuelle Live-Übersicht": "Here is the current live overview", "für": "for",
  "Bericht erstellen": "Create report", "Neue Leads": "New leads", "Zur Freigabe": "For approval",
  "Geplante Beiträge": "Scheduled posts", "Offene Tickets": "Open tickets",
  "heute": "today", "aktuell": "current", "Sehr gut": "Very good", "Gut": "Good", "Achtung": "Warning",
  "Neue Leads aus socialcheck.ch": "New leads from socialcheck.ch",
  "Fotoaufträge vom Handy": "Photo jobs from mobile", "System Status": "System status",
  "Freigaben (Ampel)": "Approvals (traffic light)",
  "Geplante Beiträge (nächste 7 Tage)": "Scheduled posts (next 7 days)",
  "Offene Support-Tickets": "Open support tickets", "Mandanten-Übersicht": "Tenant overview",
  "Aktivität": "Activity", "Alle Leads": "All leads", "Alle anzeigen": "Show all",
  "Details": "Details", "Öffnen": "Open", "Alle Kunden": "All customers",
  "Keine neuen Leads.": "No new leads.", "Keine Fotoaufträge.": "No photo jobs.",
  "Keine offenen Tickets.": "No open tickets.", "Keine Aktivität.": "No activity.",
  "Lädt aktuelle Daten…": "Loading current data…",
  "Rot · Entwurf": "Red · Draft", "Orange · Zur Freigabe": "Orange · For approval",
  "Grün · Freigegeben": "Green · Approved", "Blau · Veröffentlicht": "Blue · Published", "Gesamt": "Total",
  "Paket": "Plan", "Status": "Status", "Beiträge": "Posts", "Geplant": "Scheduled",
  "Nächste Aktion": "Next action", "Zeige": "Showing", "bis": "to", "von": "of", "Mandanten": "tenants",
  "Zurück": "Back", "Weiter": "Next",
  "Demo-Anfrage": "Demo request", "Paket-Anfrage": "Plan request", "Kontakt": "Contact",
  "Website-Check": "Website check", "Beratung": "Consulting", "Zugang": "Access",
  "Zur Prüfung": "For review", "Freigegeben": "Approved", "Veröffentlicht": "Published",
  "Hoch": "High", "Mittel": "Medium", "Niedrig": "Low",
  "In Arbeit": "In progress", "Aktiv": "Active",
  // Kunden
  "Kunde": "Customer", "Testphase": "Trial", "Ausstehend": "Pending", "Gekündigt": "Canceled",
  "seit": "since", "diesen Monat": "this month", "ø unverändert": "ø unchanged",
  "Aktive Kunden": "Active customers", "Kunden in Testphase": "Customers in trial",
  "Ausstehende Aktivierung": "Pending activation", "Gekündigte Kunden": "Canceled customers",
  "Kundenverwaltung": "Customer management",
  "Alle Kunden, Abos und Aktivitäten zentral im Blick.": "All customers, subscriptions and activity at a glance.",
  "Exportieren": "Export", "exportiert": "exported", "Verantwortlich": "Owner", "Alle": "All",
  "Suche nach Kunde, Domain, E-Mail…": "Search customer, domain, email…",
  "Weitere Filter": "More filters", "Erweiterte Filter folgen in Kürze.": "Advanced filters coming soon.",
  "Kontakt": "Contact", "Nächster Schritt": "Next step", "Aktionen": "Actions",
  "Keine Kunden gefunden.": "No customers found.", "Details anzeigen": "Show details",
  "Cockpit öffnen": "Open cockpit", "Abo verwalten": "Manage subscription",
  "Kundendetails": "Customer details", "Kundennummer:": "Customer no.:", "Zum Kunden-Cockpit": "To client cockpit",
  "Vertrag": "Contract", "Rechnung": "Invoice", "Notizen": "Notes", "Mehr": "More", "Verlauf": "History",
  "Firma": "Company", "Telefon": "Phone", "Sprache": "Language", "Vertrag seit": "Contract since",
  "Nächste Rechnung": "Next invoice", "Paket ändern": "Change plan", "Nutzung": "Usage",
  "Benutzer": "Users", "Speicher": "Storage", "API-Aufrufe (Monat)": "API calls (month)",
  "Nicht erfasst": "Not tracked", "Bearbeiten": "Edit", "Keine Notizen hinterlegt.": "No notes recorded.",
  "Vertrag gestartet": "Contract started", "geplant": "scheduled", "Keine offenen Aufgaben.": "No open tasks.",
  "Bearbeitung im Kunden-Cockpit möglich.": "Editable in the client cockpit.",
  "Offene Freigaben": "Open approvals", "Fotoaufträge zu prüfen": "Photo jobs to review",
  "Heute, ": "Today, ", "Morgen, ": "Tomorrow, ",
  // Support
  "Tickets zwischen": "Tickets between", "KEWAG und Kunden": "KEWAG and customers", "dir und KEWAG": "you and KEWAG",
  "Neues Ticket": "New ticket", "Support-Anfrage": "Support request", "Betreff": "Subject",
  "Priorität": "Priority", "Nachricht": "Message", "Senden": "Send", "Normal": "Normal",
  "Betreff und Nachricht erforderlich": "Subject and message required", "Ticket erstellt": "Ticket created",
  "Keine Tickets. Erstelle ein neues Ticket bei Fragen.": "No tickets. Create one if you have questions.",
  "Offen": "Open", "In Bearbeitung": "In progress", "Geschlossen": "Closed",
  "Antworten…": "Reply…", "Antwort senden": "Send reply", "Status aktualisiert": "Status updated",
  "Ampel-Kurzhilfe": "Traffic-light quick help", "Rot": "Red", "Grün": "Green", "Blau": "Blue",
  "Entwurf / intern.": "Draft / internal.", "Zur Kundenfreigabe.": "For customer approval.",
  "Freigegeben / bereit / live.": "Approved / ready / live.", "Veröffentlicht / reportet.": "Published / reported.",
  "Lädt…": "Loading…",
  "Nachricht(en)": "message(s)",
  // ---- KundenDetail (Control OS customer detail) ----
  "Kunde seit": "Customer since", "Kundensicht öffnen": "Open client view", "Zu Freigaben": "To approvals",
  "Ampel-Verteilung": "Traffic-light distribution",
  "Rot – Entwurf / intern": "Red – Draft / internal", "Orange – Zur Kundenfreigabe": "Orange – For client approval",
  "Grün – Freigegeben / bereit / live": "Green – Approved / ready / live", "Blau – Veröffentlicht / reportet": "Blue – Published / reported",
  "Abo & nächste Aktion": "Subscription & next action", "Abo-Status": "Subscription status", "Preis": "Price",
  "Nächste Aktion:": "Next action:", "Freigaben offen": "approvals open", "keine offene Aktion": "no open action",
  "Mandanten-Details": "Tenant details", "Kunden-Cockpit": "Client cockpit",
  "Direkt zum öffentlichen Bereich dieses Kunden.": "Direct link to this customer's public area.",
  "Direkt zum Kunden-Cockpit": "Directly to client cockpit", "Ansprechpartner": "Contact person",
  "E-Mail": "Email", "Website": "Website", "Sprachen": "Languages", "Vertragsstatus": "Contract status",
  "Vertrag bis": "Contract until", "monatlich kündbar": "cancelable monthly", "Storage": "Storage",
  "API Calls (Monat)": "API calls (month)", "Details & Einstellungen": "Details & settings",
  "Letzte Aktivitäten": "Recent activity", "Noch keine Aktivitäten protokolliert.": "No activity logged yet.",
  "Nächste Schritte": "Next steps", "Alle Aufgaben": "All tasks",
  "Monatsreport prüfen": "Review monthly report", "Report ist bereit zur Prüfung.": "report is ready for review.",
  "Neues Shooting einplanen": "Plan new shooting", "Nächsten Fotoauftrag planen und briefen.": "Plan and brief the next photo job.",
  "Rechnungsstatus prüfen": "Check invoice status", "Nächste Rechnung am": "Next invoice on", "fällig.": "due.",
  "Anzeigen": "Show", "Planen": "Plan", "Tickets offen": "Open tickets", "Änderungswünsche": "Change requests",
  "vs. Vormonat": "vs. last month",
  "Entwürfe": "Drafts", "Warten auf Freigabe": "Awaiting approval", "Veröffentlicht diesen Monat": "Published this month",
  "Beitrag erstellen": "Create post", "Kanal": "Channel", "Alle Kanäle": "All channels", "Alle Status": "All statuses",
  "Entwurf": "Draft", "Zeitraum": "Period", "Liste": "List", "Planung": "Planning",
  "Beiträge suchen...": "Search posts...", "Filter zurücksetzen": "Reset filters",
  "Beitrag": "Post", "Geplant für": "Scheduled for", "Keine Beiträge gefunden.": "No posts found.",
  "Vorschau": "Preview", "Freigeben": "Approve", "Als Entwurf": "As draft",
  "Keinen Beitrag ausgewählt.": "No post selected.", "Beitrags-Vorschau & Details": "Post preview & details",
  "Beitragstext": "Post text", "Mehr anzeigen": "Show more", "Weniger anzeigen": "Show less", "Kanäle": "Channels",
  "Geplanter Veröffentlichungstermin": "Scheduled publish date", "Nicht geplant": "Not scheduled", "Medien": "Media",
  "Wartet auf Freigabe": "Awaiting approval", "Heute fällig": "Due today", "Diesen Monat freigegeben": "Approved this month",
  "Freigabe-Eingang": "Approval inbox", "Sortierung: Fälligkeitsdatum": "Sort: due date", "Sortierung: Status": "Sort: status",
  "Alle Freigaben anzeigen": "Show all approvals", "von:": "by:",
  "Keine Freigabe ausgewählt.": "No approval selected.", "Live-Vorschau": "Live preview", "In neuem Tab öffnen": "Open in new tab",
  "Content-Check": "Content check", "Keine Prüfung hinterlegt.": "No check recorded.",
  "Erstellt von": "Created by", "Erstellt am": "Created on", "Geplant am": "Scheduled on", "Anhang": "Attachment",
  "Entscheidung & Kundenfeedback": "Decision & client feedback", "Kundenfeedback": "Client feedback",
  "Noch kein Kundenfeedback.": "No client feedback yet.", "Frist": "Deadline",
  "Inhalt ohne Änderungen freigeben und publizieren.": "Approve and publish content without changes.",
  "Freigabe mit Auflagen": "Approve with conditions",
  "Mit Kommentaren freigeben und zur Umsetzung zurücksenden.": "Approve with comments and send back for changes.",
  "Nicht freigeben": "Don't approve", "Inhalt ablehnen und zur Überarbeitung zurücksenden.": "Reject content and send back for revision.",
  "Freigabe-Verlauf": "Approval history", "Gesamten Verlauf anzeigen": "Show full history",
  "Offene Aufträge": "Open jobs", "gesamt": "total", "Diese Woche geplant": "Planned this week",
  "Uploads ausstehend": "Uploads pending", "Bilder": "Images", "Bilder freigegeben": "Images approved",
  "Neuer Auftrag": "New job", "Abgeschlossen": "Completed", "Archiv": "Archive", "Keine Aufträge.": "No jobs.",
  "Alle Fotoaufträge anzeigen": "Show all photo jobs", "Kein Auftrag ausgewählt.": "No job selected.",
  "Briefing & Shotlist": "Briefing & shot list", "Briefing bearbeiten": "Edit briefing", "Ziel des Shootings": "Shooting goal",
  "Shooting-Datum": "Shooting date", "Fotograf": "Photographer", "Benötigte Motive": "Required shots",
  "Mood & Referenzen": "Mood & references", "Referenz": "Reference", "Ablauf & Timing": "Schedule & timing",
  "Briefing offen": "Briefing open", "Termin fixiert": "Date fixed", "Shooting erfolgt": "Shooting done",
  "Auswahl offen": "Selection open", "Final freigegeben": "Final approved", "Storniert": "Canceled",
  "Uploads & Auswahl": "Uploads & selection", "Upload-Status": "Upload status", "Bilder hochgeladen": "images uploaded",
  "Letzter Upload:": "Last upload:", "Upload öffnen": "Open upload", "Auswahl & Freigabe": "Selection & approval",
  "Auswahlstatus": "Selection status", "Vom Kunden ausgewählt": "Selected by client", "Kunden-Feedback": "Client feedback",
  "Kunde zur Auswahl senden": "Send to client for selection", "Finalisierung": "Finalization",
  "Nach Kundenfreigabe Final auswählen & freigeben.": "After client approval, select final & approve.",
  "Final freigeben": "Approve final", "Duplizieren": "Duplicate", "Archivieren": "Archive", "Abbrechen": "Cancel",
  "Hohe Priorität": "High priority", "Warten auf Kunde": "Waiting for client", "Gelöst diesen Monat": "Resolved this month",
  "Ticket-Queue": "Ticket queue", "In Bearb.": "In prog.", "Warten": "Waiting", "Gelöst": "Resolved",
  "Tickets durchsuchen...": "Search tickets...", "Keine Tickets.": "No tickets.", "Alle Tickets anzeigen": "Show all tickets",
  "Kritisch": "Critical", "Kein Ticket ausgewählt.": "No ticket selected.", "Erstellt:": "Created:",
  "Antwort an Kunde": "Reply to client", "Interne Notiz": "Internal note",
  "Interne Notiz schreiben (nur für das Team sichtbar)...": "Write internal note (visible to team only)...",
  "Nachricht an den Kunden schreiben...": "Write a message to the client...", "Anhänge": "Attachments",
  "Dateien anhängen oder hier ablegen": "Attach files or drop them here", "Notiz speichern": "Save note",
  "Ticket-Details": "Ticket details", "Ziel:": "Target:", "Fällig:": "Due:", "Zugewiesen an": "Assigned to",
  "Modul": "Module", "Kategorie": "Category", "Login & Zugriff": "Login & access", "Veröffentlichung": "Publishing",
  "Technik": "Technical", "Änderungswunsch": "Change request", "Allgemein": "General", "Letzte Aktivität": "Last activity",
  "Erstellt": "Created", "Schnellaktionen": "Quick actions", "Ticket zuweisen": "Assign ticket", "Ticket schliessen": "Close ticket",
  "Offener Betrag": "Open amount", "Rechnungen offen": "invoices open", "Fällige Rechnungen": "Due invoices",
  "Rechnung überfällig": "invoice overdue", "Rechnungen überfällig": "invoices overdue", "Bezahlt diesen Monat": "Paid this month",
  "Nächste Abbuchung": "Next charge", "Abo:": "Sub:", "Suchen…": "Search…", "Bezahlt": "Paid", "Überfällig": "Overdue",
  "In Mahnung": "In dunning", "Rechnung #": "Invoice #", "Betrag": "Amount", "Fällig am": "Due on", "Aktion": "Action",
  "Erinnern": "Remind", "Bezahlen": "Pay", "Keine Rechnungen für diesen Filter.": "No invoices for this filter.",
  "/ Seite": "/ page", "Zahlungsverlauf": "Payment history", "Datum": "Date", "Beschreibung": "Description",
  "Beleg/Rechnung": "Receipt/Invoice", "Methode": "Method", "Erfolgreich": "Successful", "Noch keine Zahlungen.": "No payments yet.",
  "Alle Zahlungen anzeigen": "Show all payments", "Abo & Zahlung": "Subscription & payment", "Monatlicher Preis": "Monthly price",
  "Zahlungsmethode": "Payment method", "Rechnungsadresse": "Billing address", "Aktueller Mahnstatus": "Current dunning status",
  "Nutzung (aktuell)": "Usage (current)", "Offene Posten & Aktionen": "Open items & actions", "Gesamt offen": "Total open",
  "davon überfällig": "of which overdue", "Rechnung senden": "Send invoice", "Zahlung prüfen": "Check payment",
  "Mahnung senden": "Send dunning", "Paket anpassen": "Adjust plan",
  "Stripe/TWINT vorbereitet — nicht verbunden. Aktionen werden im Protokoll erfasst.": "Stripe/TWINT prepared — not connected. Actions are logged.",
  "Branche": "Industry", "Tonalität": "Tone", "Typische Begriffe": "Typical terms", "Noch kein Marken-Profil hinterlegt.": "No brand profile yet.",
  "Noch keine Benutzer.": "No users yet.", "Name": "Name", "Rolle": "Role",
  "Benutzer & Rollen werden unter „Benutzer & Rollen\" verwaltet.": "Users & roles are managed under \u201cUsers & roles\u201d.",
  "Kein Protokoll vorhanden.": "No log available.", "Kein Abo": "No subscription",
  "Kein Ansprechpartner für Kundensicht vorhanden": "No contact for client view", "Kundensicht geöffnet": "Client view opened",
  "Ampel-Status aktualisiert": "Traffic-light status updated", "Inhalt freigegeben": "Content approved",
  "Mit Auflagen zurückgesendet": "Sent back with conditions", "Inhalt abgelehnt": "Content rejected",
  "Auswahl an Kunden gesendet": "Selection sent to client", "Final freigegeben (Meldung)": "Final approved",
  "Auftrag archiviert": "Job archived", "Auftrag dupliziert": "Job duplicated", "Auftrag abgebrochen": "Job canceled",
  "Aktion ausgeführt": "Action executed", "Interne Notiz gespeichert": "Internal note saved",
  "Antwort an Kunde gesendet": "Reply sent to client", "Ticket aktualisiert": "Ticket updated",
  "Neuer Fotoauftrag – Formular folgt.": "New photo job – form coming soon.", "Upload-Bereich folgt.": "Upload area coming soon.",
  "Zuweisung – Auswahl folgt.": "Assignment – selection coming soon.", "Mehr Filter": "More filters",
  "Inhalte warten auf Kundenfreigabe.": "Content is awaiting client approval.", "Region": "Region", "Location": "Location", "mehr": "more",
  // --- RollenRechte Phase-3 EN ---
  "2FA aktiv": "2FA active",
  "2FA für diese Rolle verpflichtend": "2FA required for this role",
  "2FA für diesen Benutzer verpflichtend": "2FA required for this user",
  "2FA inaktiv": "2FA inactive",
  "2FA ist aktiv": "2FA is active",
  "2FA ist nicht eingerichtet": "2FA is not set up",
  "2FA verwalten": "Manage 2FA",
  "2FA wurde zurückgesetzt": "2FA has been reset",
  "2FA zurücksetzen (Gerät verloren)": "Reset 2FA (device lost)",
  "2FA-Pflicht": "2FA requirement",
  "2FA-Pflicht (Rollen) gespeichert": "2FA requirement (roles) saved",
  "2FA-Pflicht entfernt": "2FA requirement removed",
  "2FA-Pflicht für Benutzer aktiviert": "2FA requirement enabled for user",
  "2FA-Pflicht für alle setzen": "Require 2FA for all",
  "2FA-Pflicht offen": "2FA requirement pending",
  "2FA-Pflicht pro Rolle": "2FA requirement per role",
  "2FA: Alle": "2FA: all",
  "Abgelaufene Einladungen": "Expired invitations",
  "Akteur / Ziel suchen…": "Search actor / target…",
  "Aktion: Alle": "Action: all",
  "Aktive Kunden / Mandanten": "Active customers / tenants",
  "Aktive Mandanten ohne Freigabe-Berechtigten.": "Active tenants without an approver.",
  "Aktive Rollen": "Active roles",
  "Aktivieren": "Activate",
  "Aktiviert": "Enabled",
  "Aktivierungslink ist abgelaufen – erneut senden.": "Activation link has expired – resend.",
  "Aktuell keine Zugriffsprobleme – es würde keine Mail versendet.": "No access issues at the moment – no e-mail would be sent.",
  "Aktueller Status": "Current status",
  "Alle Benutzer sind zugewiesen.": "All users are assigned.",
  "Alle Bereiche deaktiviert: Dieser Editor kann keine Kundendaten bearbeiten.": "All areas disabled: this editor cannot edit any customer data.",
  "Alle Rollen & Rechte": "All roles & permissions",
  "Alle Rollen & Rechte anzeigen": "Show all roles & permissions",
  "Alle Team-Mitglieder haben 2FA aktiv.": "All team members have 2FA active.",
  "Alle aktiven Kunden haben einen Reviewer.": "All active customers have a reviewer.",
  "Alle mit 2FA": "All with 2FA",
  "Alle rollen- und benutzerbezogenen Änderungen.": "All role- and user-related changes.",
  "Alle sichtbaren auswählen": "Select all visible",
  "Auch mit Freigabe kann der Editor kritische Felder nicht ändern — nur nicht-kritische Angaben im jeweiligen Bereich.": "Even when enabled, the editor cannot change critical fields — only non-critical details in the respective area.",
  "Audit-Log exportiert": "Audit log exported",
  "Ausstehende Aktionen": "Pending actions",
  "Auswahl aufheben": "Clear selection",
  "Bearbeitungsrechte gespeichert.": "Edit permissions saved.",
  "Beginn, Laufzeit, Kündigungsfrist, Intervall, Verlängerung": "Start, term, notice period, interval, renewal",
  "Beim nächsten Login wird die Einrichtung erzwungen.": "Setup will be enforced at next login.",
  "Benutzer aktiviert": "User activated",
  "Benutzer aktualisiert": "User updated",
  "Benutzer bearbeiten": "Edit user",
  "Benutzer deaktiviert": "User deactivated",
  "Benutzer einladen": "Invite user",
  "Benutzer exportiert": "users exported",
  "Benutzer löschen": "Delete user",
  "Benutzer ohne Zuweisung": "Users without assignment",
  "Benutzer suchen…": "Search users…",
  "Benutzer verwalten, Rollen steuern, Kunden zuweisen und Einladungen zentral bearbeiten.": "Manage users, control roles, assign customers and handle invitations centrally.",
  "Benutzerdetails": "User details",
  "Benutzern": "users",
  "Bereich": "Area",
  "Bereich / Team": "Area / team",
  "Bereich / Team: Alle": "Area / team: all",
  "Betreuer: Alle": "Manager: all",
  "Betreuung": "Manager",
  "Bitte einen Kunden / Mandanten wählen": "Please choose a customer / tenant",
  "Brevo nicht verbunden – Vorschau-Modus": "Brevo not connected – preview mode",
  "Das Konto, alle Sitzungen und Kundenzuweisungen werden entfernt. Diese Aktion kann nicht rückgängig gemacht werden.": "The account, all sessions and customer assignments will be removed. This action cannot be undone.",
  "Deaktivieren": "Deactivate",
  "Deaktiviert oder wegen Fehlversuchen gesperrt.": "Deactivated or locked due to failed attempts.",
  "Details ansehen": "View details",
  "Editor darf nur zugewiesene Kunden bearbeiten. Kritische Felder (Paket, Preis, Vertrag, Rechnung, Sperre, Login-E-Mail) bleiben ausschliesslich dem Super Admin vorbehalten.": "The editor may only edit assigned customers. Critical fields (package, price, contract, invoice, block, login e-mail) remain reserved exclusively for the super admin.",
  "Effektive Rechte": "Effective permissions",
  "Eingeschr.": "Limited",
  "Einladung abgelaufen": "Invitation expired",
  "Einladung erneut senden": "Resend invitation",
  "Einladung gesendet": "Invitation sent",
  "Einladung neu erstellt (Brevo nicht verbunden – kein Versand)": "Invitation recreated (Brevo not connected – no sending)",
  "Einladung per E-Mail erneut gesendet": "Invitation resent by e-mail",
  "Einladungen": "Invitations",
  "Einladungen aktiviert": "invitations activated",
  "Einladungen neu gesendet": "Invitations resent",
  "Einladungsstatus": "Invitation status",
  "Einladungsstatus: Alle": "Invitation status: all",
  "Einträge": "entries",
  "Empfänger": "recipients",
  "Empfänger gesendet.": "recipient(s).",
  "Report an": "Report sent to",
  "Endgültig löschen": "Delete permanently",
  "Erneut senden": "Resend",
  "Filter aufheben": "Clear filter",
  "Firmenname, Domain, Adresse, Betreuer, Notiz, Sprache": "Company name, domain, address, manager, note, language",
  "Gefiltert: nur Mitglieder ohne aktives 2FA": "Filtered: only members without active 2FA",
  "Gesamtbenutzer": "Total users",
  "Gestern": "Yesterday",
  "Gilt systemweit über alle Mandanten – keine Mandantenzuweisung nötig. „Super Admin\" ist reserviert und nicht vergebbar.": "Applies system-wide across all tenants – no tenant assignment needed. “Super Admin” is reserved and cannot be assigned.",
  "Global": "Global",
  "Globale (Team-/System-)Rollen kann nur der Super Admin vergeben.": "Only the super admin can assign global (team/system) roles.",
  "Globale Rolle": "Global role",
  "Globale Rolle (KEWAG-Team / System)": "Global role (KEWAG team / system)",
  "Hauptbetreuer": "Primary manager",
  "Hauptbetreuer ändern": "Change primary manager",
  "Heute": "Today",
  "Hinweis: Gilt bereits über die Rollen-Pflicht.": "Note: already applies via the role requirement.",
  "Inaktive / gesperrte Konten": "Inactive / locked accounts",
  "Interne KEWAG-Mitarbeitende mit Zugriff auf alle Mandanten. Kunden-Nutzer findest du weiter unten unter „Benutzerverwaltung\".": "Internal KEWAG staff with access to all tenants. Customer users can be found below under “User management”.",
  "Jetzt testen/senden": "Test/send now",
  "KEWAG-Team / Mitarbeiter": "KEWAG team / staff",
  "Keine": "None",
  "Keine Benutzer.": "No users.",
  "Keine Einträge gefunden.": "No entries found.",
  "Keine Kunden zugewiesen.": "No customers assigned.",
  "Keine Kundenbenutzer gefunden.": "No customer users found.",
  "Keine Rolle/Zuweisung – keine Rechte.": "No role/assignment – no permissions.",
  "Keine abgelaufenen Einladungen.": "No expired invitations.",
  "Keine inaktiven Konten.": "No inactive accounts.",
  "Keine offenen Einladungen.": "No open invitations.",
  "Keine Änderungen protokolliert.": "No changes logged.",
  "Klicken: nur Mitglieder ohne aktives 2FA anzeigen": "Click: show only members without active 2FA",
  "Kontaktdaten": "Contact details",
  "Konten, Sitzungen und Kundenzuweisungen werden entfernt. Dein eigenes Konto und der letzte Super Admin werden automatisch übersprungen. Diese Aktion kann nicht rückgängig gemacht werden.": "Accounts, sessions and customer assignments will be removed. Your own account and the last super admin are skipped automatically. This action cannot be undone.",
  "Kunde: Alle": "Customer: all",
  "Kunden & Zuweisungen": "Customers & assignments",
  "Kunden entfernen": "Remove customers",
  "Kunden hinzufügen": "Add customers",
  "Kunden ohne Reviewer": "Customers without a reviewer",
  "Kunden suchen…": "Search customers…",
  "Kunden zuweisen": "Assign customers",
  "Kunden-Cockpit konnte nicht geöffnet werden": "Could not open the customer cockpit",
  "Kundenbenutzer": "Customer users",
  "Kundenbenutzer ohne Mandant – Kunde zuweisen.": "Customer users without a tenant – assign a customer.",
  "Kundenbezogene Rolle": "Customer-specific role",
  "Kundenbezogene Rolle (pro Mandant)": "Customer-specific role (per tenant)",
  "Kundendaten-Bearbeitungsrechte": "Customer data edit permissions",
  "Kundendaten-Rechte": "Customer data permissions",
  "Kundenprofil bearbeiten": "Edit customer profile",
  "Kundenseitige Benutzer (Reviewer, Kunden-Mitarbeiter, Viewer …). KEWAG-Team findest du im Tab „KEWAG-Team\".": "Customer-side users (reviewer, customer staff, viewer …). The KEWAG team is in the “KEWAG team” tab.",
  "Kundenstammdaten": "Customer master data",
  "Kundenzuweisungen": "Customer assignments",
  "Kundenübersicht & Zuweisungen": "Customer overview & assignments",
  "Lege fest, für welche Rollen die Zwei-Faktor-Authentifizierung verpflichtend ist. Betroffene Benutzer ohne 2FA müssen sie beim nächsten Login einrichten. „Super Admin\" ist zum Schutz nicht listbar – aktiviere 2FA dort direkt am Konto.": "Define for which roles two-factor authentication is required. Affected users without 2FA must set it up at their next login. “Super Admin” is not listed for protection – enable 2FA directly on that account.",
  "Letzte Änderung": "Last change",
  "Letzte Änderungen": "Recent changes",
  "Löschen": "Delete",
  "Mitarbeiter (Kunde)": "Staff (customer)",
  "Name A–Z": "Name A–Z",
  "Name und Bereich / Team anpassen.": "Adjust name and area / team.",
  "Noch kein Hauptbetreuer festgelegt.": "No primary manager set yet.",
  "Noch keine Benutzer zugewiesen.": "No users assigned yet.",
  "Noch keine Team-Mitglieder.": "No team members yet.",
  "Offene Einladungen": "Open invitations",
  "Offene und abgelaufene Einladungen zentral verwalten.": "Manage open and expired invitations centrally.",
  "Onboarding-Fortschritt": "Onboarding progress",
  "Paket & Kontingente": "Package & quotas",
  "Paket, gebuchte Plattformen, Kontingente": "Package, booked platforms, quotas",
  "Paket: Alle": "Package: all",
  "Pflicht": "Required",
  "Preis, Zahlungsziel, Rechnungsadresse & -E-Mail": "Price, payment terms, billing address & e-mail",
  "Profil-Status": "Profile status",
  "Quelle je Spalte: „Global\" = KEWAG-Teamrolle (alle Mandanten) · sonst pro Kunde/Mandant vererbt.": "Source per column: “Global” = KEWAG team role (all tenants) · otherwise inherited per customer/tenant.",
  "Rechnungsdaten": "Billing data",
  "Recht": "Permission",
  "Report deaktiviert": "Report deactivated",
  "Report-Vorschau": "Report preview",
  "Reviewer (Kunde)": "Reviewer (customer)",
  "Rolle ändern": "Change role",
  "Rolle, Zuweisungen, Status und effektive Rechte.": "Role, assignments, status and effective permissions.",
  "Rolle: Alle": "Role: all",
  "Rollen & Rechte": "Roles & permissions",
  "Rollen & Rechte – Schnellübersicht": "Roles & permissions – quick overview",
  "Rollen geändert": "Roles changed",
  "Rollenerklärung": "Role explanation",
  "Rollentyp": "Role type",
  "Rollenverteilung": "Role distribution",
  "Schließen": "Close",
  "So sähe die E-Mail aus. In Produktion (Brevo verbunden) wird sie automatisch versendet.": "This is what the e-mail would look like. In production (Brevo connected) it is sent automatically.",
  "Sortierung: Rolle": "Sort: role",
  "Speichern": "Save",
  "Status ändern": "Change status",
  "Status: Alle": "Status: all",
  "Super Admin – Vollzugriff auf alle Kunden.": "Super Admin – full access to all customers.",
  "Systeminhaber": "System owner",
  "Systeminhaber – einziger Super Admin": "System owner – sole super admin",
  "Systeminhaber – geschützt": "System owner – protected",
  "Team verwalten": "Manage team",
  "Team-Mitglied": "Team member",
  "Team-Mitglied anlegen": "Add team member",
  "Tipp: Wenn die Person nur vorübergehend keinen Zugriff haben soll, nutze stattdessen „Deaktivieren\".": "Tip: if the person should only temporarily lose access, use “Deactivate” instead.",
  "Unternehmens- und Abo-Status": "Company and subscription status",
  "Unverändert": "Unchanged",
  "Vertragsdaten": "Contract data",
  "Vollständige Berechtigungs-Matrix aller Bereiche und Rollen.": "Complete permission matrix of all areas and roles.",
  "Vollständiges Audit-Log": "Full audit log",
  "Vorname, Nachname, Telefon, Funktion": "First name, last name, phone, function",
  "Vorschau erstellt (Brevo nicht verbunden – kein realer Versand).": "Preview created (Brevo not connected – no real sending).",
  "Weitere Aktionen": "More actions",
  "Weitere Teammitglieder": "Other team members",
  "Wöchentlicher Report aktiviert": "Weekly report enabled",
  "Wöchentlicher Report an KEWAG Admins": "Weekly report to KEWAG admins",
  "Zugewiesene Benutzer": "Assigned users",
  "Zugewiesene Kunden": "Assigned customers",
  "Zugriffsprobleme": "Access issues",
  "Zuletzt aktiv": "Last active",
  "Zuweisungen bearbeiten": "Edit assignments",
  "Zuweisungen öffnen": "Open assignments",
  "Zuweisungs- & Profil-Status": "Assignment & profile status",
  "Zwei-Faktor-Authentifizierung für": "Two-factor authentication for",
  "ausgewählt": "selected",
  "gesperrt": "locked",
  "ggü. Vorwoche": "vs. previous week",
  "inaktiv": "inactive",
  "ohne 2FA": "without 2FA",
  "vollständig": "complete",
  "vs. letzter 30 Tage": "vs. last 30 days",
  "weitere": "more",
  "zuletzt": "last",
  "Übersicht der Berechtigungen pro Rolle.": "Overview of permissions per role.",
  "übersprungen": "skipped",
  "übersprungen (Systeminhaber/geschützt)": "skipped (system owner/protected)",
  "von": "of",
  "offen": "open",
  "Seite": "Page",
  "KEWAG-Betreuung": "KEWAG support",
  "Benutzer gelöscht": "users deleted",
  "Benutzer „{name}\" gelöscht": "User “{name}” deleted",
  "2FA-Pflicht für {n} Mitglied(er) gesetzt": "2FA requirement set for {n} member(s)",
  "Editor (Team)": "Editor (team)",
  "Account Manager": "Account Manager",
  "Möchtest du {name} ({email}) endgültig löschen? Das Konto, alle Sitzungen und Kundenzuweisungen werden entfernt. Diese Aktion kann nicht rückgängig gemacht werden.": "Do you want to permanently delete {name} ({email})? The account, all sessions and customer assignments will be removed. This action cannot be undone.",
  "Möchtest du die {n} ausgewählten Benutzer endgültig löschen? Konten, Sitzungen und Kundenzuweisungen werden entfernt. Dein eigenes Konto und der letzte Super Admin werden automatisch übersprungen. Diese Aktion kann nicht rückgängig gemacht werden.": "Do you want to permanently delete the {n} selected users? Accounts, sessions and customer assignments will be removed. Your own account and the last super admin are skipped automatically. This action cannot be undone.",
  "Beiträge & Inhalte": "Posts & content",
  "Alle Mandanten sehen": "See all tenants",
  "Kunden & Rollen verwalten": "Manage customers & roles",
  "Rechnungen, Abos & Abrechnung": "Invoices, subscriptions & billing",
  "Inhalte erstellen / bearbeiten": "Create / edit content",
  "Fotos & Freigaben verwalten": "Manage photos & approvals",
  "Zur Freigabe einreichen": "Submit for approval",
  "Freigaben / Änderungen wünschen": "Approvals / request changes",
  "Kommentare": "Comments",
  "Integrationen & System": "Integrations & system",
  "Nur Ansicht": "View only",
  "Voller Zugriff auf alle Mandanten, Benutzer, Rollen und Systemeinstellungen.": "Full access to all tenants, users, roles and system settings.",
  "Verwaltet KEWAG-administrative Aufgaben und hat erweiterten Systemzugriff.": "Manages KEWAG administrative tasks and has extended system access.",
  "Verwaltet Rechnungen, Abos und Abrechnung über alle Mandanten; liest Kundenstammdaten und Reports. Kein Zugriff auf Inhalte, Freigaben oder Benutzerverwaltung.": "Manages invoices, subscriptions and billing across all tenants; reads customer master data and reports. No access to content, approvals or user management.",
  "Erstellt und bearbeitet Inhalte und verwaltet Fotoaufträge.": "Creates and edits content and manages photo assignments.",
  "Unterstützt Nutzer, verwaltet Freigaben und Änderungen.": "Supports users, manages approvals and changes.",
  "Reicht Inhalte ein, prüft und gibt Inhalte frei.": "Submits content, reviews and approves it.",
  "Kundenseitiger Mitarbeiter: sieht Inhalte, kann kommentieren und Fotos senden – gibt aber NICHT frei (Freigabe bleibt beim Reviewer).": "Customer-side staff: sees content, can comment and send photos – but does NOT approve (approval stays with the reviewer).",
  "Nur lesender Zugriff auf zugewiesene Inhalte.": "Read-only access to assigned content.",
  "Finanzen (Rechnungen/Abos)": "Finance (invoices/subscriptions)",
  "Benutzer & Rollen verwalten": "Manage users & roles",
  "Kunde/Mandant verwalten": "Manage customer/tenant",
  "Inhalte erstellen/bearbeiten": "Create/edit content",
  "Freigeben (Ampel grün)": "Approve (green light)",
  "Kommentieren": "Comment",
  "Ansehen / Lesen": "View / read",
  "Benutzer hinzugefügt": "User added",
  "Benutzer geändert": "User changed",
  "Benutzer entfernt": "User removed",
  "Benutzer eingeladen": "User invited",
  "Status geändert": "Status changed",
  "Einladung erneut gesendet": "Invitation resent",
  "Hauptbetreuer gesetzt": "Primary manager set",
  "Hauptbetreuer entfernt": "Primary manager removed",
  "Begrüssungs-E-Mail gesendet": "Welcome e-mail sent",
  "Kundenprofil vollständig ausgefüllt und Benutzer optimal zugewiesen.": "Customer profile fully completed and users optimally assigned.",
  "Profil und Benutzer-Zuweisungen noch nicht vollständig.": "Profile and user assignments not yet complete.",
  "Kundenprofil und Zuweisungen weitgehend offen.": "Customer profile and assignments largely incomplete.",
  "Vollzugriff": "Full access",
  "Nur Freigabe": "Approval only",
  "Kommentieren & Fotos": "Comment & photos",
  "Supportzugriff": "Support access",
  "Nur Lesen": "Read only",
  "Kundendaten": "Customer data",
  "Abo & Preis": "Subscription & price",
  "Plattformen & Speicher": "Platforms & storage",
  "Vertrag & Abrechnung": "Contract & billing",
  "Prüfung & Erstellen": "Review & create",
  "Weiter zu Abo & Preis": "Next: Subscription & price",
  "Weiter zu Plattformen & Speicher": "Next: Platforms & storage",
  "Weiter zu Vertrag & Abrechnung": "Next: Contract & billing",
  "Weiter zu Prüfung & Erstellen": "Next: Review & create",
  "Angebot geladen – Kunde aus angenommenem Angebot erstellen.": "Offer loaded – create the customer from the accepted offer.",
  "Angebot konnte nicht geladen werden.": "Could not load the offer.",
  "Entwurf geladen – du kannst fortfahren.": "Draft loaded – you can continue.",
  "Entwurf konnte nicht geladen werden.": "Could not load the draft.",
  "Firmenname / Projektname ist erforderlich": "Company / project name is required",
  "Kunde „{name}\" angelegt (Nr. {nr}).": "Customer “{name}” created (no. {nr}).",
  "Einladung gesendet.": "Invitation sent.",
  "Einladung vorbereitet.": "Invitation prepared.",
  "Entwurf gespeichert": "Draft saved",
  "Bitte zuerst den Firmennamen erfassen": "Please enter the company name first",
  "Angebot-PDF erstellt": "Offer PDF created",
  "PDF konnte nicht erstellt werden": "Could not create the PDF",
  "Bitte eine Kunden-E-Mail erfassen": "Please enter a customer e-mail",
  "Angebot an {to} gesendet": "Offer sent to {to}",
  "Konfiguration konnte nicht geladen werden.": "Could not load the configuration.",
  "Neuen SocialCheck-Kunden mit Abo, Plattformen, Speicher und Abrechnung erfassen.": "Set up a new SocialCheck customer with subscription, platforms, storage and billing.",
  "Abo & Paket wählen": "Choose subscription & package",
  "Zusatzleistungen (Add-ons)": "Add-on services",
  "Plattformen in den Paketen": "Platforms in the packages",
  "Speicherpaket & Limits": "Storage package & limits",
  "Zusammenfassung": "Summary",
  "Firmenname / Projektname": "Company / project name",
  "Hauptansprechpartner": "Main contact",
  "Kundentyp": "Customer type",
  "Rechnungsadresse · Strasse": "Billing address · street",
  "App-Domain (optional)": "App domain (optional)",
  "PLZ": "ZIP",
  "Ort": "City",
  "UID / MWST (optional)": "VAT no. (optional)",
  "Land": "Country",
  "Zeitzone": "Time zone",
  "Abo-Start": "Subscription start",
  "Vertragslaufzeit": "Contract term",
  "Rechnungsintervall": "Billing interval",
  "Zahlungsziel": "Payment term",
  "Rabatt (%)": "Discount (%)",
  "Setup-Gebühr (einmalig)": "Setup fee (one-time)",
  "Serie / Erneuerung": "Series / renewal",
  "z. B. Blütenzauber GmbH": "e.g. Blütenzauber GmbH",
  "Vor- und Nachname": "First and last name",
  "Branche wählen": "Select industry",
  "Strasse und Nr.": "Street and no.",
  "Laufzeit": "Term",
  "Intervall": "Interval",
  "Erneuerung": "Renewal",
  "Weitere Kontaktpersonen hinzufügen": "Add more contact persons",
  "Beliebt": "Popular",
  "Beiträge / Monat": "posts / month",
  "GB Speicher": "GB storage",
  "einmalig": "one-time",
  "/ Monat": "/ month",
  "Inklusive": "Included",
  "Nicht inklusive": "Not included",
  "Plattform": "Platform",
  "Speicherpaket wählen": "Choose storage package",
  "Speichernutzung – Warnstufen": "Storage usage – warning levels",
  "Vollständigkeit": "Completeness",
  "Sehr gut!": "Very good!",
  "Fast geschafft": "Almost done",
  "Weiter ausfüllen": "Keep filling in",
  "Fast bereit zum Erstellen.": "Almost ready to create.",
  "Pflichtfelder ausfüllen.": "Fill in the required fields.",
  "Ausgewähltes Paket": "Selected package",
  "Inkludierte Plattformen": "Included platforms",
  "Plattformen inklusive": "platforms included",
  "Speicherpaket": "Storage package",
  "(monatlich)": "(monthly)",
  "Einmalige Kosten": "One-time costs",
  "Monatliche Gesamtkosten": "Total monthly cost",
  "inkl. MwSt.": "incl. VAT",
  "Angebot als PDF": "Offer as PDF",
  "Angebot senden": "Send offer",
  "E-Mail-Versand (Brevo) nicht verbunden – PDF herunterladen und manuell versenden.": "E-mail sending (Brevo) not connected – download the PDF and send it manually.",
  "Angebot als PDF per E-Mail senden": "Send the offer as PDF by e-mail",
  "E-Mail-Versand (Brevo) ist nicht verbunden": "E-mail sending (Brevo) is not connected",
  "inklusive": "included",
  "Einmalig (Setup / Kurzcheck)": "One-time (setup / quick check)",
  "Rabatt": "Discount",
  "/ Mt.": "/ mo.",
  "{pct}% ausgefüllt · Schritt {step} von 5": "{pct}% filled · step {step} of 5",
  "Als Entwurf speichern": "Save as draft",
  "2 GB Speicher": "2 GB storage",
  "Auf Anfrage": "On request",
  "auf Anfrage": "on request",
  "2 GB + {n} GB": "2 GB + {n} GB",
  "Neukunde": "New customer",
  "Bestehender Kunde": "Existing customer",
  "Aus Lead umgewandelt": "Converted from lead",
  "Demo-Kunde": "Demo customer",
  "Monatlich kündbar": "Cancellable monthly",
  "12 Monate": "12 months",
  "24 Monate": "24 months",
  "36 Monate": "36 months",
  "Monatlich": "Monthly",
  "Quartalsweise": "Quarterly",
  "Halbjährlich": "Semi-annually",
  "Jährlich": "Annually",
  "10 Tage": "10 days",
  "14 Tage": "14 days",
  "30 Tage": "30 days",
  "60 Tage": "60 days",
  "Off": "Off",
  "Automatisch verlängern": "Auto-renew",
  "Manuell prüfen": "Review manually",
  "Deutsch": "German",
  "Französisch": "French",
  "Italienisch": "Italian",
  "Englisch": "English",
  "Schweiz": "Switzerland",
  "Liechtenstein": "Liechtenstein",
  "Deutschland": "Germany",
  "Österreich": "Austria",
  "Blumen & Floristik": "Flowers & floristry",
  "Gastronomie & Restaurant": "Gastronomy & restaurant",
  "Handwerk & Bau": "Trades & construction",
  "Gesundheit & Praxis": "Health & practice",
  "Beauty & Wellness": "Beauty & wellness",
  "Detailhandel & Retail": "Retail",
  "Immobilien": "Real estate",
  "Beratung & Dienstleistung": "Consulting & services",
  "Bildung & Coaching": "Education & coaching",
  "Sport & Fitness": "Sports & fitness",
  "Auto & Mobilität": "Automotive & mobility",
  "Garten & Landschaft": "Garden & landscape",
  "Technologie & IT": "Technology & IT",
  "Andere": "Other",
  "Zusatzkanal": "Extra channel",
  "Zusatzbeitrag": "Extra post",
  "Design-Beitrag": "Design post",
  "Reel": "Reel",
  "Community Management": "Community Management",
  "2 GB inklusive": "2 GB included",
  "+10 GB": "+10 GB",
  "+50 GB": "+50 GB",
  "+100 GB": "+100 GB",
  "Mehr als 100 GB": "More than 100 GB",
  "Warnung": "Warning",
  "Starke Warnung": "Strong warning",
  "Erste Warnung bei 80%.": "First warning at 80%.",
  "Zweite Warnung bei 95%.": "Second warning at 95%.",
  "Upload blockieren oder Speichererweiterung verlangen.": "Block upload or require storage extension.",
};

const REL = {
  de: { now: "gerade eben", min: (n) => `vor ${n} Min`, hour: (n) => `vor ${n} Std`, day: (n) => `vor ${n} Tag`, days: (n) => `vor ${n} Tagen` },
  fr: { now: "à l'instant", min: (n) => `il y a ${n} min`, hour: (n) => `il y a ${n} h`, day: (n) => `il y a ${n} j`, days: (n) => `il y a ${n} j` },
  it: { now: "proprio ora", min: (n) => `${n} min fa`, hour: (n) => `${n} h fa`, day: (n) => `${n} g fa`, days: (n) => `${n} g fa` },
  en: { now: "just now", min: (n) => `${n} min ago`, hour: (n) => `${n} h ago`, day: (n) => `${n} day ago`, days: (n) => `${n} days ago` },
};
const LOCALE = { de: "de-CH", fr: "fr-CH", it: "it-CH", en: "en-GB" };

export const adminTranslations = {
  de: { labels: {}, rel: REL.de, locale: LOCALE.de },
  fr: { labels: FR, rel: REL.fr, locale: LOCALE.fr },
  it: { labels: IT, rel: REL.it, locale: LOCALE.it },
  en: { labels: EN, rel: REL.en, locale: LOCALE.en },
};
